import { SignInButton, SignUpButton, useUser, useAuth, useClerk } from '@clerk/clerk-react';
import { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useProvisions, isPendingCatalogId, normalizeOccasion } from './hooks/useProvisions';
import { useKeyboardInset } from './hooks/useKeyboardInset';
import { NAV_DOORS, COLUMN_MIN_WIDTH, COLUMN_MAX_WIDTH, useScrollCompact, WRAP_UP_HASH, hashForView, viewForHash } from './nav';
import { ActiveHouseholdProvider, useActiveHousehold } from './contexts/ActiveHouseholdContext';
import { ConnectivityProvider } from './contexts/ConnectivityContext';
import { ConnectivityPill } from './components/ConnectivityPill';
import { HeaderAction } from './components/HeaderAction';
import { WrapUpBar } from './components/WrapUpBar';
import { SheetPanel } from './components/SheetPanel';
import { CategorySelect } from './components/CategorySelect';
import { GiftCard } from './components/GiftCard';
import { GIFT_KEY } from './lib/preRum';
import { useConnectivity } from './contexts/ConnectivityContext';
import { useAuthHealth, useSessionLive, resetAuthHealth, markDeliberateSignOut, consumeDeliberateSignOut, isPollingOpen, REJECTED_HOLD_MS } from './lib/authHealth';
import { parseSteps } from './lib/parseSteps';
import { downscaleToJpeg } from './lib/image';
import { filesFromPicker, stagePhotos, PHOTO_CAP, HINT_UNREADABLE } from './lib/stagePhotos';
import { countFlaggedRows, bannerCheckLine } from './lib/importBanner';
import { IMPORT_HINTS, GALLEY_HINTS } from './lib/importErrors';
import { galleyMode, inventTooLong, TEXT_CAP, MODES, MODE_LABEL, MODE_ECHO, MODE_RUM, INVENT_TOO_LONG_ECHO, STARTER_PROMPTS } from './lib/galleyMode';
import { displayTitle } from './lib/displayTitle';
import { OCCASION_ORDER, OCCASION_WORD, OCCASION_PILL, OCCASION_TINT, OccasionIcon, occasionTint, PlusGlyph, LibraryItem } from './components/LibraryCards';
import { leftToBuy, recipeBlockFor, REMOVE_LABEL } from './lib/recipePlan';
import { isNoShop, NO_SHOP_LABELS, noShopLabel, noShopName, boardCardState } from './lib/boardState';
import { rowCountsFromProvenance } from './lib/mealLinks';
import { addAllOnHandLine } from './lib/onHand';
import { cookDateStart, cookHistoryLine } from './lib/cookDate';
import { photosByMeal, photosFingerprint, photoCaption, photoDetails, replaceSteps, isShowcase, liveCookCount, atPhotoCap } from './lib/mealPhotos';
import { mealShowcase, noteForGive, NOTE_MAX, giftSharePayload, whatTheySee, closeDisabled as giveCloseDisabled } from './lib/giveMeal';
import { giftDraft, giftBanner, giftNoteQuote, placeLine, alreadyAddedLine, givingPlaceLine, givingDeletedLine, thankYouPayload } from './lib/giftArrival';
import { canSaveToPhotos, createPhotoSaver, saveLabel } from './lib/savePhoto';
import { createSignedUrlCache } from './lib/signedUrls';
import { ingredientTag, ingredientSummary, boughtForThisMeal, wasAddedThisPlan, planAnchor } from './lib/ingredientTags';
import { resolveLibraryView, otherView, TOGGLE_LABEL, readStoredView, writeStoredView } from './lib/libraryView';
import { nextInsetTop } from './lib/standaloneInset';
import { swipeRemoveConfirm } from './lib/askPicks';
import { trace } from '@opentelemetry/api';

// Same proxy pattern as ActiveHouseholdContext: binds to the provider rum.js
// registers; a no-op provider stands in with no RUM token.
const tracer = trace.getTracer("ourprovisions-app");

// Maps Supabase category names → display names with emoji
const CATEGORY_DISPLAY = {
  "Produce": "🥦 Produce",
  "Meat & Seafood": "🥩 Meat & Seafood",
  "Dairy": "🥛 Dairy",
  "Pantry": "🥫 Pantry",
  "Beverages": "🧃 Beverages",
  "Household": "🧹 Household",
  "Bakery": "🍞 Bakery & Bread",
};

// Preferred sort order for categories
const CATEGORY_ORDER = [
  "Produce", "Meat & Seafood", "Dairy", "Pantry", "Beverages", "Household", "Bakery"
];

// Category glyph lookup, keyed on the normalized (trim + lowercase) category value.
// The 📦 fallback is REQUIRED, not defensive padding: `catalog_items.category` is an
// open set — users create their own — so an exhaustive map is a map that breaks on
// the next user-created category.
const CATEGORY_GLYPH = {
  "staples": "⭐",
  "produce": "🥦",
  "meat & seafood": "🥩",
  "dairy": "🥛",
  "pantry": "🥫",
  "beverages": "🧃",
  "bakery": "🍞",
  "bakery & bread": "🍞",
  "household": "🧹",
};
// Web Speech API, resolved ONCE at module load. Support is genuinely uneven (Safari and
// most iOS browsers have none), so this is a real branch, not defensive padding: when it
// is null the mic button is never rendered at all and the typed path is exactly what it
// was before this feature existed. A disabled mic that silently does nothing would be
// worse than no mic — it promises something the browser cannot deliver.
const SPEECH_RECOGNITION_CTOR =
  typeof window !== "undefined"
    ? (window.SpeechRecognition || window.webkitSpeechRecognition || null)
    : null;

const CATEGORY_GLYPH_FALLBACK = "📦";
const categoryGlyph = (value) =>
  CATEGORY_GLYPH[String(value ?? "").trim().toLowerCase()] || CATEGORY_GLYPH_FALLBACK;

// Emoji-free category labels. The glyph renders separately in a fixed-width box,
// so labels here never carry one. CATEGORY_DISPLAY above keeps the combined
// "🥦 Produce" form that the section headers still use.
const CATEGORY_LABEL = {
  "Produce": "Produce",
  "Meat & Seafood": "Meat & Seafood",
  "Dairy": "Dairy",
  "Pantry": "Pantry",
  "Beverages": "Beverages",
  "Household": "Household",
  "Bakery": "Bakery & Bread",
};
const categoryLabel = (raw) => CATEGORY_LABEL[raw] || String(raw ?? "");

// Rail-only shortening — the row is space-constrained. Display string ONLY;
// the stored category value never changes.
const CATEGORY_RAIL_SHORT = { "Bakery & Bread": "Bakery" };
const categoryRailLabel = (raw) => {
  const full = categoryLabel(raw);
  return CATEGORY_RAIL_SHORT[full] || full;
};

// Add-item category picker — the same pills as the Browse rail, the OPPOSITE
// layout, and the divergence is deliberate: scroll where you're grazing, show
// everything where you're choosing. The rail scrolls because the user is
// browsing and may pick several; this wraps and renders every category because
// the user is completing a task and must pick exactly one. A category hidden
// off-screen is a category they won't file under, and misfiling degrades the
// catalog permanently. Do NOT unify these two surfaces.
function CategoryPickerGrid({ categories, selected, onSelect, onNewCategory }) {
  return (
    <div className="cat-grid">
      {categories.map(cat => (
        <button
          key={cat.rawName}
          type="button"
          className={`cat-pill${selected === cat.rawName ? " on" : ""}`}
          onClick={() => onSelect(cat.rawName)}
        >
          {/* Full label here — the rail's "Bakery" shortening is for a
              space-constrained row; accuracy matters more at filing time. */}
          <span className="cat-em">{categoryGlyph(cat.rawName)}</span>
          {categoryLabel(cat.rawName)}
        </button>
      ))}
      {/* This tile is the surface that produced today's stray categories, so it
          stays — but it now reads as a deliberate option rather than the only
          escape from a list that didn't have the right answer. */}
      <button type="button" className="cat-pill cat-pill-new" onClick={onNewCategory}>
        <span className="cat-em">+</span>New category
      </button>
    </div>
  );
}

const SWIPE_THRESHOLD = 60;

// Device-local text-size steps (SPEC_global_text_size: one knob, every surface). Index (0–4)
// is persisted under the ORIGINAL key op_list_text_size — the key is internal and existing
// users keep their step; the scale drives --op-text-scale on documentElement.
const TEXT_STEPS = [0.9, 1.0, 1.2, 1.45, 1.75];
const TEXT_LABELS = ["Compact", "Default", "Large", "XL", "XXL"];
// ts(px, k) — the one way an inline style follows the text-size knob
// (SPEC_global_text_size §2). k is how strongly the role follows the knob:
// small text (eyebrow, meta, body) k = 1; buttons 0.8; card titles 0.5;
// page titles 0.4; chrome k = 0 is simply a plain px value. CSS rules write the
// same expressions by hand — see the role table in ARCHITECTURE.
//   ts(16)      → "calc(16px * var(--op-text-scale))"
//   ts(20, 0.5) → "calc(20px * (1 + (var(--op-text-scale) - 1) * 0.5))"
const ts = (px, k = 1) =>
  k === 1
    ? `calc(${px}px * var(--op-text-scale))`
    : `calc(${px}px * (1 + (var(--op-text-scale) - 1) * ${k}))`;

function SwipeToRemove({ onRemove, onEdit, onStaple, isStaple, canEdit = true, removeLabel = "Hide", style: outerStyle, children }) {
  // Reveal width scales with the number of actions the caller actually wired,
  // at a constant 80px per action. Counting HANDLERS (not rendered buttons) is
  // deliberate: `canEdit` hides the Edit button on non-editable catalog rows,
  // and counting renders would shrink those panels 240→160, changing a surface
  // this generalization is not meant to touch. Every existing call site passes
  // all three handlers, so they stay at exactly 240.
  const ACTION_WIDTH = 80;
  const actionCount = (onEdit ? 1 : 0) + (onStaple ? 1 : 0) + (onRemove ? 1 : 0);
  const REVEAL_WIDTH = actionCount > 0 ? actionCount * ACTION_WIDTH : 240;
  const [offsetX, setOffsetX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [removing, setRemoving] = useState(false);
  const startX = useRef(null);
  const startY = useRef(null);
  const isHoriz = useRef(false);
  const baseOffset = useRef(0);
  // Live mirror of offsetX. The gesture logic MUST NOT read the state value:
  // `mousemove`/`touchmove` are continuous-priority events in React 18, so
  // their setOffsetX updates are scheduled rather than flushed synchronously.
  // A `mouseup` (discrete) can therefore run before the last move has been
  // committed, leaving the closure's offsetX behind the position the row was
  // actually dragged to. The row then snaps to a spot computed from a stale
  // value and seeds the NEXT gesture's baseOffset from there, compounding
  // until it wedges — and any unrelated re-render flushes the backlog, which
  // is why touching another row appeared to "fix" it.
  // The ref is written in lockstep with every setOffsetX, so it is correct
  // regardless of flush timing. State stays purely for rendering.
  const offsetXRef = useRef(0);
  const applyOffset = (v) => { offsetXRef.current = v; setOffsetX(v); };

  const handleStart = (clientX, clientY) => {
    startX.current = clientX;
    startY.current = clientY;
    isHoriz.current = false;
    baseOffset.current = offsetXRef.current;
    setSwiping(true);
  };

  const handleMove = (clientX, clientY) => {
    if (startX.current === null) return;
    const dx = clientX - startX.current;
    const dy = clientY - startY.current;
    if (!isHoriz.current) {
      if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      isHoriz.current = Math.abs(dx) > Math.abs(dy);
    }
    if (!isHoriz.current) return;
    if (onEdit) {
      applyOffset(Math.min(0, Math.max(baseOffset.current + dx, -REVEAL_WIDTH)));
    } else {
      if (dx < 0) applyOffset(Math.max(dx, -120));
    }
  };

  const handleEnd = () => {
    startX.current = null;
    setSwiping(false);
    if (onEdit) {
      // The flat 60px SWIPE_THRESHOLD was tuned when REVEAL_WIDTH was always
      // 240 — a quarter of the travel. On a 1-action row (80px) it becomes
      // three quarters, so any drag shorter than 60px snaps the row back OPEN
      // and it reads as stuck. Scale the threshold to the panel: catalog rows
      // compute min(60, 80) = 60 and are unchanged; a meal row gets ~27px,
      // widening its close window from 20px to 54px.
      // Deliberately scoped to this branch — the `else` below is the
      // swipe-to-REMOVE path, where a lower threshold would make an
      // irreversible action easier to trigger by accident.
      const threshold = Math.min(SWIPE_THRESHOLD, REVEAL_WIDTH / 3);
      const delta = offsetXRef.current - baseOffset.current; // + = right, - = left
      const startedOpen = baseOffset.current <= -REVEAL_WIDTH / 2;
      if (startedOpen) {
        // open row: a right drag past threshold closes; otherwise stay open
        if (delta > threshold) applyOffset(0);
        else applyOffset(-REVEAL_WIDTH);
      } else {
        // closed row: a left drag past threshold opens; otherwise stay closed
        if (delta < -threshold) applyOffset(-REVEAL_WIDTH);
        else applyOffset(0);
      }
    } else {
      if (offsetXRef.current < -SWIPE_THRESHOLD) {
        setRemoving(true);
        applyOffset(-400);
        setTimeout(() => {
          onRemove();
          setRemoving(false);
          applyOffset(0);
        }, 400);
      } else {
        applyOffset(0);
      }
    }
  };

  const close = () => applyOffset(0);

  const handleRemove = () => {
    if (!onRemove) return;
    setRemoving(true);
    applyOffset(-400);
    setTimeout(() => { onRemove(); setRemoving(false); applyOffset(0); }, 400);
  };

  const isRevealing = offsetX < -10;

  return (
    <div style={{ position: "relative", overflow: "hidden", borderRadius: "8px", marginBottom: "0", ...outerStyle }}>
      {onEdit ? (
        <div style={{
          position: "absolute", top: 0, right: 0, bottom: 0, width: `${REVEAL_WIDTH}px`,
          display: "flex", opacity: isRevealing ? 1 : 0, transition: "opacity 0.15s",
          pointerEvents: offsetX === 0 ? "none" : "auto"
        }}>
          {canEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); close(); onEdit(); }}
              style={{
                flex: 1, background: "#c8973a", border: "none", color: "white",
                fontFamily: "'Lato', sans-serif", fontSize: "0.8rem", fontWeight: 700,
                letterSpacing: "1px", textTransform: "uppercase", cursor: "pointer"
              }}
            >Edit</button>
          )}
          {onStaple && (
          <button
            onClick={(e) => { e.stopPropagation(); close(); onStaple(); }}
            style={{
              flex: 1, background: isStaple ? "#0D9488" : "#6B7E8F", border: "none", color: "white",
              fontFamily: "'Lato', sans-serif", fontSize: "0.75rem", fontWeight: 700,
              letterSpacing: "1px", textTransform: "uppercase", cursor: "pointer"
            }}
          >⭐ Staple</button>
          )}
          {onRemove && (
          <button
            onClick={(e) => { e.stopPropagation(); handleRemove(); }}
            style={{
              flex: 1, background: "#8A7968", border: "none", color: "white",
              fontFamily: "'Lato', sans-serif", fontSize: "0.8rem", fontWeight: 700,
              letterSpacing: "1px", textTransform: "uppercase", cursor: "pointer"
            }}
          >{removeLabel}</button>
          )}
        </div>
      ) : (
        <div style={{
          position: "absolute", inset: 0, background: "#8A7968", borderRadius: "8px",
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          paddingRight: "18px", opacity: isRevealing ? 1 : 0, transition: "opacity 0.15s"
        }}>
          <span style={{ color: "white", fontFamily: "'Lato', sans-serif", fontSize: "0.8rem", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>{removeLabel}</span>
        </div>
      )}
      <div
        style={{
          transform: `translateX(${offsetX}px)`,
          transition: swiping ? "none" : "transform 0.3s ease",
          touchAction: offsetX < 0 ? "none" : "pan-y",
          cursor: "grab",
          opacity: removing ? 0 : 1,
          pointerEvents: "auto",
        }}
        onTouchStart={(e) => handleStart(e.touches[0].clientX, e.touches[0].clientY)}
        onTouchMove={(e) => handleMove(e.touches[0].clientX, e.touches[0].clientY)}
        onTouchEnd={handleEnd}
        onMouseDown={(e) => handleStart(e.clientX, e.clientY)}
        onMouseMove={(e) => { if (e.buttons === 1) handleMove(e.clientX, e.clientY); }}
        onMouseUp={handleEnd}
        onMouseLeave={() => { if (swiping) handleEnd(); }}
      >
        {children}
      </div>
    </div>
  );
}

const VELAYO_LOGO_TEAL = "data:image/png;base64,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";

// Splash scene — Resolve-in-Place (SPEC_splash_resolve_v3, supersedes v2).
// The whole lockup — arch, wordmark, tagline, VELAYO INC. foot — resolves as ONE
// slow gesture: a blur→sharp IN PLACE on one easing curve (~2.5s), no translation,
// no per-element choreography, no self-drawing arch ("the word arrives into
// itself"). The wordmark is centered on its OWN optical center; the arch is a
// close crown at a locked 2:1 spacing above it. The readiness gate is kept from v2
// unchanged; the measured wordmark→header hand-off (§6b) that shipped beside it was
// DELETED 2026-08-30 (SPEC_wordmark_earned_our.md) — the splash now fades out and the
// header renders independently. A prefers-reduced-motion variant resolves to the
// final static state (no blur).
const OP_EASE = "cubic-bezier(0.16,1,0.3,1)";
const OP_EASE_RESOLVE = "cubic-bezier(0.22,1,0.36,1)"; // §1 the resolve curve — long, slow tail
// Timing (ms), all from mount (the scene auto-plays on cold start — no tap gate).
const OP_MIN_VISIBLE = 2800; // §6: never dissolve before the ~2.5s resolve has finished
const OP_REVEAL_MS = 3100;   // §6: resolve (2.5s) + a short settled hold (~0.6s) before the gate arms
const OP_FAILSAFE_MS = 5000; // §6 max: a stuck load must never trap the user (comfortably > reveal)
const OP_REDUCED_HOLD = 400; // reduced-motion: brief settle before the gate
const OP_FADE_MS = 700;      // reduced-motion dissolve fade duration
const OP_SURFACE_MS = 1900;  // surfacing dissolve: scene fade, then unmount
const OP_GROUP_CENTER = 0.46; // §3 the WORDMARK's optical center, as a fraction of the VISIBLE viewport (a hair above true middle)
function SplashScreen({ onDone, ready }) {
  const reduced = typeof window !== "undefined" && window.matchMedia
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // No tap gate: the scene auto-plays on cold start. 'crest' (reveal) →
  // 'surface' (the simple surfacing dissolve).
  const [phase, setPhase] = useState("crest");
  const [revealDone, setRevealDone] = useState(false);
  const [fading, setFading] = useState(false);
  const mountRef = useRef(Date.now());
  const exitedRef = useRef(false);
  const wmRef = useRef(null);      // splash wordmark — measured for its own placement
  const wmWrapRef = useRef(null);  // wordmark wrapper — transform-free box to measure
  const rootRef = useRef(null);    // splash root — carries the measured position vars

  // Lock body scroll while the full-screen splash is mounted. The app tree renders
  // BENEATH this fixed overlay; its content is taller than the viewport, so the
  // document's vertical scrollbar would show at the right edge and — worse — toggle
  // on/off as the app loads, each ~15px change reflowing the full-width overlay
  // sideways (the "shake"). A full-screen overlay shouldn't let the body scroll
  // behind it anyway. Restored to its prior value on unmount, so the app scrolls
  // normally once the splash dissolves.
  //
  // 2026-08-10: the scrollbar half of that rationale is now handled globally and
  // properly by `scrollbar-gutter: stable` on `html` (src/index.css) — including
  // the restore above, which used to BE the moment the scrollbar appeared and the
  // column jumped. This lock stays for its own reason: no scrolling behind a
  // full-screen overlay. Do not re-derive it as the shimmy fix.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Single dissolve path — idempotent so the readiness gate and the failsafe can
  // both point here without racing to double-fire onDone. Full motion does the
  // simple surfacing (scene recedes, wordmark fades with it); reduced motion falls
  // back to a plain fade. onDone always fires on a HARD timer.
  //
  // 2026-08-30: the measured splash→header travel/clone hand-off was DELETED here
  // (SPEC_wordmark_earned_our.md). It existed to land the splash wordmark on a
  // header that always read "OurProvisions"; the header is now earned — solo
  // households read "Provisions" — so there is no longer a fixed target to travel
  // to, and a clone landing on the wrong word is worse than no clone. The splash
  // now simply finishes and unmounts; the header renders independently of it.
  const exit = useCallback(() => {
    if (exitedRef.current) return;
    exitedRef.current = true;
    if (reduced) {
      setFading(true);
      setTimeout(() => onDone(), OP_FADE_MS);
      return;
    }
    setPhase("surface");                     // espresso recedes, cream revealed below
    setTimeout(() => onDone(), OP_SURFACE_MS);
  }, [reduced, onDone]);

  // Reduced motion: no parallax — the resolved scene shows, settles briefly, then
  // dissolves on readiness (failsafe still bounds it).
  useEffect(() => {
    if (!reduced) return;
    const rt = setTimeout(() => setRevealDone(true), OP_REDUCED_HOLD);
    const ft = setTimeout(() => exit(), OP_FAILSAFE_MS);
    return () => { clearTimeout(rt); clearTimeout(ft); };
  }, [reduced, exit]);

  // Full motion: the reveal auto-plays from mount; let it run, then arm the §5
  // failsafe. revealDone gates the readiness dissolve below.
  useEffect(() => {
    if (reduced || phase !== "crest") return;
    const rt = setTimeout(() => setRevealDone(true), OP_REVEAL_MS);
    const ft = setTimeout(() => exit(), OP_FAILSAFE_MS);
    return () => { clearTimeout(rt); clearTimeout(ft); };
  }, [reduced, phase, exit]);

  // Readiness gate (§5): dissolve only once the reveal has settled AND the app is
  // ready (!loading, auth resolved) — and never before the minimum visible time.
  useEffect(() => {
    if (!revealDone || !ready) return;
    const remaining = OP_MIN_VISIBLE - (Date.now() - mountRef.current);
    if (remaining > 0) {
      const t = setTimeout(() => exit(), remaining);
      return () => clearTimeout(t);
    }
    exit();
  }, [revealDone, ready, exit]);

  // Place the lockup by composing off the WORDMARK's real box — the arch and
  // tagline are satellites; the wordmark alone owns "center" (§3). Past builds
  // centered the group's bounding box (OP_GROUP_CENTER of the box), but the lockup
  // is bottom-weighted (the arch is a thin crown; the wordmark carries the mass),
  // so the wordmark sat BELOW the box center and always read "too low." Fix: land
  // the wordmark's own optical center at OP_GROUP_CENTER (§3), then derive the arch
  // and tagline from it at a locked 2:1 spacing (§2). Re-measured on resize /
  // orientation / font load; the lockup is opacity 0 until the crest, so setting
  // these after paint causes no jump.
  const measure = useCallback(() => {
    const root = rootRef.current, wrap = wmWrapRef.current, wm = wmRef.current;
    if (!root || !wrap || !wm) return;
    const wmH = wrap.getBoundingClientRect().height || 40; // wordmark box height (position-independent)
    const wmW = wm.offsetWidth || 238;                     // wordmark width — the arc scales off THIS, never the viewport
    const TAG_GAP = 0.72;                                  // §2 wordmark bottom → tagline top (base dial, locked)
    // Approved horizon arc (public/splash_arc.png — v2 @1x, 645×318, transparent, outer
    // 34px feathered to zero alpha, no background veil). Placed as part of the LOCKUP:
    // its width tracks the wordmark, NOT the viewport — viewport scaling is what
    // flattened the earlier SVG redraw. Constants are measured from the source PNG's
    // alpha (visible arc = 0.79× canvas, base at 0.853 down) and the reference
    // screenshot (arc base ≈ 0.85 cap-height above the wordmark caps).
    const ARC_W_RATIO = 1.25;                              // arc canvas width ÷ wordmark width (makes the visible arc ≈ wordmark width)
    const ARC_ASPECT = 318 / 645;                          // PNG intrinsic aspect (H/W)
    const ARC_BASE_FRAC = 0.853;                           // arc's visible base (tips) as a fraction down the PNG canvas
    const ARC_GAP = 0.41;                                  // gap: arc base → wordmark box top, in wordmark-heights (the vertical dial — tune by eye)
    const arcW = ARC_W_RATIO * wmW;
    const arcH = arcW * ARC_ASPECT;
    // Center against the VISIBLE viewport (visualViewport), NOT the layout viewport:
    // on mobile a position:fixed overlay is sized to the taller layout viewport, so
    // % / getBoundingClientRect heights overstate what's on screen and the cluster
    // read high. visualViewport.height is exactly what the user sees; the overlay's
    // top aligns with the visible top, so px-from-top maps 1:1 to the visible area.
    const vv = window.visualViewport;
    const visH = (vv && vv.height) || window.innerHeight || root.getBoundingClientRect().height;
    const wmTop = OP_GROUP_CENTER * visH - wmH / 2;         // §3 wordmark optical center at 46%
    const wmBottom = wmTop + wmH;
    root.style.setProperty("--op-wm-top", wmTop.toFixed(1) + "px");
    // Arc: width = 1.25× wordmark, centered on the wordmark axis (the PNG's apex glow
    // sits ~29% across, so it lands left-of-centre exactly as the reference). Top is set
    // so the arc's visible BASE sits ARC_GAP wordmark-heights above the wordmark box top.
    root.style.setProperty("--op-arc-w", arcW.toFixed(1) + "px");
    root.style.setProperty("--op-arc-top", (wmTop - ARC_GAP * wmH - ARC_BASE_FRAC * arcH).toFixed(1) + "px");
    root.style.setProperty("--op-tag-top", (wmBottom + TAG_GAP * wmH).toFixed(1) + "px");
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    const vv = window.visualViewport;
    if (vv) vv.addEventListener("resize", measure);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure).catch(() => {});
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      if (vv) vv.removeEventListener("resize", measure);
    };
  }, [measure]);

  const crested = !reduced && (phase === "crest" || phase === "surface"); // hold reveal end-states through the surfacing
  const surfacing = !reduced && phase === "surface";
  const rootClass = `op-splash${crested ? " op-crest" : ""}${surfacing ? " op-surface" : ""}${reduced ? " op-reduced" : ""}`;

  return (
    <div
      ref={rootRef}
      className={rootClass}
      style={{
        opacity: fading ? 0 : 1,
        // Once surfacing, the overlay is transparent but briefly still mounted —
        // let taps reach the app revealed beneath.
        pointerEvents: (surfacing || fading) ? "none" : undefined,
      }}
    >
      <style>{`
        .op-splash {
          position: fixed; inset: 0; z-index: 999; overflow: hidden;
          transition: opacity 0.7s ease;
        }
        /* Scene container — everything that must FADE AWAY as the app is revealed
           (ground, atmosphere, arch, tagline, footer). The wordmark lives OUTSIDE
           it so it stays the still point and travels to the header while the scene
           recedes beneath it. */
        .op-scene { position: absolute; inset: 0; z-index: 1; }
        /* Open espresso ground — on its own fadeable layer so the surfacing can
           dissolve it to reveal the app beneath. */
        /* Reference ground: a warm espresso bloom sits behind the arc/wordmark in the
           upper-middle (~#301C0B) and DARKENS DOWNWARD to a near-black base (#1B1109) at
           the bottom — so the lower third recedes instead of competing with the wordmark.
           (The prior build's ground brightened toward the bottom; corrected here.) */
        .op-ground {
          position: absolute; inset: 0; z-index: 0;
          background: radial-gradient(125% 110% at 50% 40%, #331E0C 0%, #301C0B 34%, #1B1109 100%);
        }
        /* MOTION 1 — the horizon. Depth opens / vignette retreats / bloom line
           arrives, establishing the world. GPU-friendly props ONLY — opacity +
           transform (no gradient/box-shadow tweening). The ground holds the OPEN
           gradient; the closed depth layer sits over it and fades away to reveal it. */
        .op-depth {
          position: absolute; inset: 0; z-index: 0; pointer-events: none;
          background: radial-gradient(80% 55% at 50% 55%, #24140a 0%, #1A0E06 45%, #0d0602 100%);
          transform: scale(1.15);
        }
        .op-crest .op-depth { animation: opDepthOpen 1.25s ${OP_EASE} forwards; }
        @keyframes opDepthOpen { to { opacity: 0; transform: scale(1); } }
        /* Vignette as a scalable radial mask — tight in threshold, retreats on crest
           (scale out + fade), so no box-shadow paint tweening. */
        .op-vignette {
          position: absolute; inset: -20%; z-index: 1; pointer-events: none;
          background: radial-gradient(ellipse 60% 55% at 50% 50%, transparent 40%, rgba(0,0,0,0.92) 100%);
          transform: scale(1);
        }
        .op-crest .op-vignette { animation: opVigOpen 1.2s ${OP_EASE} forwards; }
        @keyframes opVigOpen { to { transform: scale(1.6); opacity: 0.35; } }
        @keyframes opFadeOut { to { opacity: 0; } }
        /* Horizon arc — the APPROVED raster (public/splash_arc.png), placed as part of
           the lockup. It is NOT redrawn in SVG: the redraw lost the apex light and
           flattened the curve. Width tracks the wordmark (--op-arc-w, set in measure()),
           NEVER the viewport — viewport scaling is what flattened it before. Centered on
           the wordmark axis; the baked apex glow lands left-of-centre on its own. Warm
           gold left tip → near-white apex → teal right tip, all baked into the pixels.
           Resolves blur→sharp WITH the wordmark in the one pass — no separate animation. */
        .op-arc {
          position: absolute; left: 50%; top: var(--op-arc-top, 34%);
          transform: translateX(-50%);
          width: var(--op-arc-w, 298px); height: auto; z-index: 8; pointer-events: none;
          opacity: 0; filter: blur(12px); /* resolves from blur; the asset is already drawn — no self-draw (§1) */
        }
        /* Wordmark — centered by its wrapper (text-align), so its own transform is
           free for the surface animation. Anchored proportionally (~430/844). */
        .op-wm-wrap {
          /* top is the measured GROUP position (see measure()); fallback pre-JS only. */
          position: absolute; left: 0; right: 0; top: var(--op-wm-top, 50.95%);
          text-align: center; pointer-events: none; z-index: 4; /* above scene(1), sheet(2), canvas(3) */
          font-size: 40px; line-height: 1; /* strut matches the wordmark so its measured box is tight */
        }
        .op-wm {
          display: inline-block; font-family: 'Playfair Display', serif;
          font-style: italic; color: #FAF4EC; white-space: nowrap;
          font-size: 40px; line-height: 1; letter-spacing: 0.02em;
          /* Resolves IN PLACE — no translation: from soft to sharp where it already
             sits ("the word arriving into itself"). Since the §6b hand-off was deleted
             there is now NO travel in the sequence at all — the wordmark resolves, holds,
             then fades. Starts at the deepest blur (§1); satellites use a lighter one. */
          opacity: 0; filter: blur(18px);
        }
        .op-wm .o { font-weight: 400; }
        .op-wm .p { font-weight: 700; }
        .op-tag {
          /* Wordmark-relative (measured), a locked TAG_GAP below the wordmark — not a
             viewport-% that would drift on resize. Fallback pre-JS only. */
          position: absolute; left: 0; right: 0; top: var(--op-tag-top, 62%); text-align: center; z-index: 6;
          font-family: 'Lato', sans-serif; font-weight: 300; font-size: 11px;
          letter-spacing: 4px; text-transform: uppercase; color: #C9A97A;
          opacity: 0; filter: blur(10px);
        }
        /* Foot colophon (§5) — a typeset VELAYO INC. line, NO glyph: the full Velayo
           lockup is a hero asset (illegible at foot size, and its built-in crest arc
           would echo the OurProvisions crown — a repeated glyph). Bottom-pinned,
           floating on the espresso; resolves with the lockup. */
        .op-foot {
          position: absolute; left: 0; right: 0; bottom: 48px; z-index: 6; text-align: center;
          opacity: 0; filter: blur(10px);
        }
        .op-foot .op-vt {
          font-family: 'Lato', sans-serif; font-weight: 400; font-size: 10px;
          letter-spacing: 5px; text-transform: uppercase; color: #C9A97A; opacity: 0.75;
        }
        /* ── ONE resolve (§1): the whole lockup sharpens blur→sharp IN PLACE, on ONE
           curve, ~2.5s, no translation and no self-draw — a single slow gesture, never
           a staged build. A whisper of internal offset (<120ms) keeps it from being a
           hard flash without reintroducing beats. The depth/vignette ambient layers
           still open underneath (≤2.5s) as texture, concurrent with the resolve — not
           a distinct "Motion 1." GPU-friendly props ONLY (opacity + filter). ── */
        .op-crest .op-wm   { animation: opResolve 2.5s ${OP_EASE_RESOLVE}   0ms forwards; }
        .op-crest .op-arc  { animation: opResolve 2.5s ${OP_EASE_RESOLVE} 60ms forwards; }
        .op-crest .op-tag  { animation: opResolve 2.5s ${OP_EASE_RESOLVE}  90ms forwards; }
        .op-crest .op-foot { animation: opResolve 2.5s ${OP_EASE_RESOLVE} 120ms forwards; }
        @keyframes opResolve     { to { opacity: 1;    filter: blur(0); } }
        /* Reduced motion: the resolved final static state — no blur, no animation. */
        .op-reduced .op-wm { opacity: 1; transform: none; filter: none; }
        .op-reduced .op-arc { opacity: 1; filter: none; }
        .op-reduced .op-tag { opacity: 1; filter: none; }
        .op-reduced .op-foot { opacity: 1; filter: none; }

        /* ── Surfacing dissolve (§6a) ── The espresso scene recedes (fades) to
           reveal the cream app body and the espresso header beneath. A simple
           surfacing — no water wash, no particles (those move to Trip Complete). */
        .op-surface .op-scene { animation: opSceneFade 1.4s ${OP_EASE} 0.3s forwards; }
        @keyframes opSceneFade { to { opacity: 0; } }
        /* The wordmark fades out with the surfacing. It used to travel to the header
           title's measured rect and hold there (the §6b clone hand-off); that is
           DELETED as of SPEC_wordmark_earned_our.md — the header wordmark is now
           earned from member count, so there is no fixed word to land on. A clean
           dissolve, and the header renders on its own terms underneath. */
        .op-surface .op-wm-wrap { animation: opFadeOut 0.6s ease 1.5s forwards; }
      `}</style>

      {/* Scene — everything that recedes as the app surfaces. The ground carries the
          espresso; atmosphere is full-motion only (reduced collapses to a fade). */}
      <div className="op-scene">
        <div className="op-ground" aria-hidden="true" />
        {!reduced && <div className="op-depth" aria-hidden="true" />}
        {!reduced && <div className="op-vignette" aria-hidden="true" />}

        {/* Approved horizon arc raster — width/position measured off the wordmark. */}
        <img className="op-arc" src={`${process.env.PUBLIC_URL}/splash_arc.png`} alt="" aria-hidden="true" />

        <div className="op-tag">Shop smarter. Shop faster.</div>

        <div className="op-foot">
          <div className="op-vt">VELAYO INC.</div>
        </div>
      </div>

      {/* Wordmark — the still point everything resolves around; it fades out with
          the surfacing. Sits above the scene so the name stays intact as the espresso
          recedes beneath it. Always the full "OurProvisions": the splash is a BRAND
          surface and the app's name is not conditional — only the HEADER wordmark is
          earned from member count (SPEC_wordmark_earned_our.md). */}
      <div className="op-wm-wrap" ref={wmWrapRef}>
        <span className="op-wm" ref={wmRef}>
          <span className="o">Our</span><span className="p">Provisions</span>
        </span>
      </div>
    </div>
  );
}

function HouseholdDebugLog() {
  const { myHouseholds, activeHouseholdId, loadingHouseholds } = useActiveHousehold();
  useEffect(() => {
    if (!loadingHouseholds) {
    }
  }, [loadingHouseholds, activeHouseholdId, myHouseholds]);
  return null;
}

// ── Shared catalog search — Browse AND the Shop Add sheet ──
// The search box and the results list are one component pair consumed by both
// surfaces, so the add path — searchResults → hiddenMatch → addSearchedItem
// — cannot fork (SPEC_shop_lens_instore_capture.md). Two rules live HERE, so no
// caller can skip them:
//   1. The hidden-item reveal (F1b). A hidden item whose name is an exact
//      normalized match for the query surfaces as a reveal card — whether it is
//      live on the shared list (un-hide only) or not (un-hide and add at 1) —
//      never as a false "add as new" that would fork the catalog row.
//   2. Exact-match-wins. "Add '<query>' as a new item" is offered whenever the
//      query is NOT an exact normalized match of any result or hidden item,
//      partial results or not; never when an exact match exists. (Partial
//      matches used to suppress create entirely — "Test" against Test33/Test4
//      had no create row.) insert_custom_catalog_item (018) dedups by the same
//      normalization against seed AND household rows, so this is the UI-side
//      guard in front of the DB-side one, not the only one.
// What differs per surface is presentation only: how a result row renders
// (`renderRow`) and what the create slot offers (`createSlot` — Browse's
// category picker, Shop's one-tap "add as a new item" under `Other`).
function CatalogSearchBox({ value, onChange, onClear, placeholder = "Search your catalog…", inputRef }) {
  return (
    <div style={{
      flex: 1, minWidth: 0,
      display: "flex", alignItems: "center",
      background: "#F5EDE0",
      border: `1.5px solid ${value ? "#A0724A" : "#E8D5B7"}`,
      borderRadius: "12px",
      padding: "0 12px", gap: "8px", height: "46px",
      boxShadow: value ? "0 0 0 3px rgba(160,114,74,0.12)" : "none",
      transition: "border-color 0.2s, box-shadow 0.2s",
    }}>
      <span style={{ color: "#C9A97A", fontSize: "15px", flexShrink: 0 }}>🔍</span>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          flex: 1, minWidth: 0, border: "none", background: "none",
          // Body role, k 1 (pass 2). The box keeps its 46px height: 26px text at XXL
          // still fits; a long placeholder clips at the input's edge, as inputs do.
          fontFamily: "'Lato', sans-serif", fontSize: ts(15),
          color: "#2C1A0E", outline: "none",
        }}
      />
      {value && (
        <span
          onClick={onClear}
          // Full strength: at .7 over the field's #F5EDE0 the glyph measured 2.75:1; it is 4.78:1 as is.
          style={{ color: "#8A5F3A", fontSize: "16px", cursor: "pointer", flexShrink: 0 }}
        >✕</span>
      )}
    </div>
  );
}

// Eyebrow role (SPEC_global_text_size): 12.5 at the floor, k 1, #8A5F3A. Was 10px #C9A97A.
const SEARCH_EYEBROW_STYLE = { padding: "0 0 8px", fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), letterSpacing: "1.5px", textTransform: "uppercase", color: "#8A5F3A" };

const normalizeSearchName = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");

function SearchResultsList({ query, results, hiddenMatch, onReveal, renderRow, createSlot, listClassName, showCount = true }) {
  const normQuery = normalizeSearchName(query);
  const exactInResults = results.some(r => normalizeSearchName(r.name) === normQuery);
  // Exact-match-wins: create is offered only when NOTHING matches exactly.
  const offerCreate = !hiddenMatch && !exactInResults;
  return (
    <>
      {hiddenMatch && (
        /* ── HIDDEN exact match: reveal card. Live → un-hide only (never write
              quantity across the person boundary); not live → un-hide and add. ── */
        <>
          <div style={SEARCH_EYEBROW_STYLE}>
            {hiddenMatch.live ? "On your list — hidden from your view" : "In your catalog — hidden from your view"}
          </div>
          <div
            onClick={() => onReveal(hiddenMatch)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter") onReveal(hiddenMatch); }}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              borderRadius: "10px", border: "1.5px solid #C9A97A",
              background: "rgba(201,169,122,0.06)", padding: "12px 14px",
              marginBottom: "6px", cursor: "pointer",
            }}
          >
            <div>
              {/* Body 15 on the knob so the name stays above the 14 hint below it at every step. */}
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(15), color: "#8A5F3A" }}>
                <strong>{hiddenMatch.item.name}</strong>{hiddenMatch.live ? ` ×${hiddenMatch.qty}` : ""}
              </div>
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#8A5F3A", marginTop: "2px" }}>
                {hiddenMatch.live ? "Hidden from your view — tap to reveal" : "Hidden from your view — tap to reveal and add"}
              </div>
            </div>
            <span style={{ color: "#8A5F3A", fontSize: "18px" }}>↺</span>
          </div>
        </>
      )}
      {results.length > 0 && (
        <>
          {showCount && (
            <div style={SEARCH_EYEBROW_STYLE}>
              {results.length} result{results.length !== 1 ? "s" : ""} for "{query}"
            </div>
          )}
          <div className={listClassName}>
            {results.map(renderRow)}
          </div>
        </>
      )}
      {results.length === 0 && !hiddenMatch && (
        <div style={SEARCH_EYEBROW_STYLE}>No results for "{query}"</div>
      )}
      {/* ── CREATE: the surface decides what "add as new" looks like ── */}
      {offerCreate && createSlot}
    </>
  );
}

// ── Shop tab: lens, In cart tray, store prompt (SPEC_shop_lens_instore_capture.md) ──
// D3: a control shows its STATE, never its next state — both words visible,
// the active one filled. Replaces the tri-state CycleIcon on Shop only.
// ── The Helm (SPEC_nav_helm.md) ─────────────────────────────────────────────
// Nav lives in a floating pill at the bottom edge (D1) — the thumb is at the
// bottom, the header keeps one job (household identity). Doors come from
// NAV_DOORS (src/nav.js) so the pill and the wide rail can never drift (D6).
// Icon + label always in the navigational posture (D2): Plan/Browse/Shop are
// not universal glyphs.
//
// D8 — motion answers a tap: transitions are armed on the first pointer/key
// interaction, never on mount. A landing-effect view change on a cold load
// therefore paints the final posture with no animation.
// D3′ (v2, 2026-09-12) — the pill is IDENTICAL on every door. No posture change
// on navigation, ever; a tab tap is the most ordinary act in the app. The v1
// session posture (slim on Shop, + and Wrap up riding in the pill) is retired —
// the trip's controls are part of the list (Shop header row, D4′), the pill is
// chrome.
// D9′/D11 (v2, amended) — `compact` is true exactly when the current door's
// control row is off-screen (useScrollCompact, position-based), never by the door
// itself. In the compact state a cream + appears at the right end
// and does the CURRENT door's add (`onPlus`, from the doorAdd map); when the
// door has no add (Home), no + renders. At rest, no + on the pill.
function Helm({ view, onChange, badgeCount, compact = false, onPlus = null }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (armed) return undefined;
    const arm = () => setArmed(true);
    const opts = { once: true, passive: true };
    window.addEventListener("pointerdown", arm, opts);
    window.addEventListener("touchstart", arm, opts);
    window.addEventListener("wheel", arm, opts);
    window.addEventListener("keydown", arm, { once: true });
    return () => {
      window.removeEventListener("pointerdown", arm);
      window.removeEventListener("touchstart", arm);
      window.removeEventListener("wheel", arm);
      window.removeEventListener("keydown", arm);
    };
  }, [armed]);
  return (
    <>
      {/* Cream fade above the pill so the last row stays legible as it scrolls under (§6). */}
      <div className="helm-fade" aria-hidden="true" />
    <nav className={`helm ${compact ? "compact" : ""} ${armed ? "" : "no-anim"}`} aria-label="Main">
      {NAV_DOORS.map(({ key, label, view: v, Icon, badge }) => {
        const active = view === v;
        return (
          <button
            key={key}
            type="button"
            className={`helm-door ${active ? "active" : ""}`}
            aria-current={active ? "page" : undefined}
            onClick={() => onChange(v)}
          >
            <Icon size={20} />
            <span className="helm-label">{label}</span>
            {badge && badgeCount > 0 && <span className="badge helm-badge">{badgeCount}</span>}
          </button>
        );
      })}
      {compact && onPlus && (
        <>
          <span className="helm-divider" aria-hidden="true" />
          <button type="button" className="helm-plus" aria-label={onPlus.label} onClick={onPlus.run}>+</button>
        </>
      )}
    </nav>
    </>
  );
}

// HOME v1 ESSENTIALS (SPEC_home_v1_essentials.md; supersedes the Tonight card
// + list card of 5a7a255). Four things, top to bottom, nothing else (D1):
// greeting + date, the ON-DECK card, the LIST LINE, the BUDGET LINE. "On deck"
// is the board's first open card (boardMeals[0]): Plan stores no planned_for
// date until Days v2, so position decides, never time of day (D2). `ready` is
// homeReady — this household's board, list AND provenance have loaded (D10) —
// and nothing renders under the date until it is true: the three essentials
// appear together, never one popping in above another, and never a state the
// data then contradicts (a To-buy meal reading as Planned for a beat).
function Home({ firstName, ready, deck, list, budget }) {
  const hour = new Date().getHours();
  const part = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
  const dateLine = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  return (
    <div className="home">
      <div className="home-greeting">Good {part}{firstName ? `, ${firstName}` : ""}</div>
      <div className="home-date">{dateLine}</div>
      {ready && (
        <>
          <OnDeckCard {...deck} />
          <HomeListLine {...list} />
          {budget && <HomeBudgetLine {...budget} />}
        </>
      )}
    </div>
  );
}

// D4 (SPEC_auth_state_ui_gating.md) — Home is the signed-out surface. The
// wordmark in the splash's own Playfair italic, one line of what this is, and
// the ONE sign-in pair the app shows while signed out (the header carries none
// since 2026-10-02 — "Signed-out entry cleanup"). Same initialValues as before,
// so a pre-filled arrival still pre-fills. Placeholder copy by design: a
// designed welcome is a Home session, not this slice (spec §Open). Rendered in
// place of the door content on every door whenever the session is not live —
// signed_out, session_lost AND booting (see signedOutWelcome in ProvisionsApp).
//   `ready` = Clerk loaded. Until then the two buttons render DISABLED with the
//   same classes and dimensions — a click can't hit a not-yet-wired modal
//   trigger, and they go live with no layout jump when Clerk finishes. A
//   returning signed-in user on a slow Clerk sees this greyed pair past the
//   splash, then hands off to the signed-in shell in ONE render (useUser flips
//   isLoaded and isSignedIn from the same snapshot) — the buttons are never
//   live for them, so no sign-in modal can open under a signed-in session.
function HomeWelcome({ signUpInitialValues, ready }) {
  const signIn = <button type="button" className="home-welcome-btn ghost" disabled={!ready}>Sign In</button>;
  const signUp = <button type="button" className="home-welcome-btn solid" disabled={!ready}>Sign Up</button>;
  return (
    <div className="home home-welcome">
      <div className="home-wm"><span className="o">Our</span><span className="p">Provisions</span></div>
      <p className="home-welcome-line">Your household’s living grocery list.</p>
      <div className="home-welcome-actions">
        {ready ? <SignInButton mode="modal">{signIn}</SignInButton> : signIn}
        {ready ? <SignUpButton mode="modal" initialValues={signUpInitialValues}>{signUp}</SignUpButton> : signUp}
      </div>
    </div>
  );
}

// THE ON-DECK CARD — position 1 of the open queue under the eyebrow "On deck"
// in every state (D2). Its states MIRROR the board card's, from the SAME
// derivation (boardCardState — D3); only the actions differ, because this card
// has no ⋯ and no chip. The truth table, first match wins:
//   0  boardCards[0] cooked this load   name · ✓ Cooked (teal outline, disabled) · no Switch
//      — the afterglow (D6): cookedHere is cleared by the meals load effect on
//      view entry, so the next card is on deck on the next Home load. No timer.
//      Since 2026-10-03 the set drawn is afterglowIds — an id leaves the moment
//      its placement reopens (re-plan), so an open placement always wins.
//   1  no open card                     "What sounds good?" · Add a meal (deep sand,
//      the library) · Hold-a-night buttons ("OR HOLD A NIGHT")
//   2  no-shop (held night)             the board's title + context line · × and
//      Switch, no primary (D9) — it leaves by ×, as on the board
//   3  Ready                            "Everything's in — go cook" · Cooked it (teal)
//   4  To buy                           "N to buy" / "B of N in cart" · NO button (D4):
//      the list line directly underneath carries Let's shop →
//   5  Planned, has ingredients         "Not on the list yet" · Add to Shop (outline;
//      the board's handler, so on-hand meals still get the on-hand prompt)
//   6  Planned, no ingredients          "Nothing to shop for" · Cooked it (teal) —
//      on the board this exit lives behind ⋯; here it is the one action
// Switch (D8) renders only with 2+ open cards and swaps 1 and 2 through the
// board's reorderBoard — no new write path. Tapping the body of a meal card
// (rows 3–6) opens the meal as v1's "View meal →" did; a no-shop card has no
// recipe and no body tap. No destructive control at rest on a meal card
// (09-21): Remove stays on the board's ⋯. Busy: the card dims and its buttons
// disable while a write is in flight (the board's busyMealId pattern).
// TEAL (#0D9488) appears ONLY on Cooked it / ✓ Cooked (D5) — teal means the
// household finished something.
// RUM: .deck-primary / .deck-switch / .deck-x are prod-unmasked and carry FIXED
// copy only — meal names live in .deck-title / .deck-status, which are not on
// the allow-list, and no allow-listed element has household text in its
// aria-label either. The body tap target (.deck-body) is masked.
export function OnDeckCard({
  boardMeals, boardCards, placements, rows, mealById, cookedIds, busyMealId, switching, holdNight,
  onAdd, onView, onLockIn, onCooked, onSkip, onSwitch, onLeftovers, onOut, onOther, afterglow,
}) {
  // Row 0 — the afterglow reads the RENDERED list: boardMeals has already
  // dropped the cooked meal, boardCards keeps it until the next load.
  const head = boardCards[0];
  if (head && cookedIds?.has(head.id)) {
    return (
      <section className="deck cooked" aria-label="On deck">
        <div className="deck-eyebrow">On deck</div>
        <div className="deck-body">
          <h3 className="deck-title">{displayTitle(head.name)}</h3>
        </div>
        <div className="deck-actions">
          <button type="button" className="deck-primary done" disabled>✓ Cooked</button>
        </div>
        {/* The afterglow appears wherever Cooked it is tapped (Dan, 2026-10-21 walk): the board's own
            component, same events stopped. P5 narrowed: Home never SHOWS a meal photo, but it may
            capture one here — so "Photo added" carries no thumbnail on Home. */}
        {afterglow && afterglow.cookIdFor(head.id) && (
          <BoardAfterglow mealId={head.id} cookId={afterglow.cookIdFor(head.id)} ag={afterglow} showThumb={false} />
        )}
      </section>
    );
  }
  const m = boardMeals[0];
  // Row 1 — nothing on deck: an invitation into Plan's library, never an add
  // input of its own; the same three hold buttons as the board's welcome.
  if (!m) {
    return (
      <section className="deck" aria-label="On deck">
        <div className="deck-eyebrow">On deck</div>
        <div className="deck-body">
          <h3 className="deck-title">What sounds good?</h3>
          <p className="deck-status">Plan a meal and we'll turn it into your list.</p>
        </div>
        <div className="deck-actions">
          <button type="button" className="deck-primary add" onClick={onAdd}>Add a meal</button>
        </div>
        {holdNight && <HoldANightButtons label="OR HOLD A NIGHT" onLeftovers={onLeftovers} onOut={onOut} onOther={onOther} />}
      </section>
    );
  }
  // Rows 2–6 — the board's derivation, verbatim.
  const st = boardCardState(m, { placements, rows, mealById, cookedIds });
  const busy = busyMealId === m.id || switching;
  const canSwitch = boardMeals.length >= 2;
  let primary = null;
  if (st.noShop || st.toBuy) {
    primary = null;                                   // D9 / D4
  } else if (st.ready || !st.canAdd) {
    // Ready (row 3) — and a planned meal with nothing to shop for (row 6).
    primary = <button type="button" className="deck-primary teal" disabled={busy} onClick={() => { if (!busy) onCooked(m.id); }}>Cooked it</button>;
  } else {
    primary = <button type="button" className="deck-primary" disabled={busy} onClick={() => { if (!busy) onLockIn(m.id); }}>Add to Shop</button>;
  }
  const open = () => { if (!busy && !st.noShop) onView(m); };
  return (
    <section className={`deck${st.noShop ? " noshop" : ""}${busy ? " busy" : ""}`} aria-label="On deck">
      <div className="deck-eyebrow">On deck</div>
      {st.noShop ? (
        <div className="deck-body">
          <h3 className="deck-title">{st.title}</h3>
          {st.line && <p className="deck-status">{st.line}</p>}
        </div>
      ) : (
        <div
          className="deck-body tap"
          role="button"
          tabIndex={0}
          onClick={open}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
        >
          {/* Display casing only (displayTitle, visual v2 D11) — the meal branch; a held night's title is its label. */}
          <h3 className="deck-title">{displayTitle(st.title)}</h3>
          {st.line && <p className="deck-status">{st.line}</p>}
        </div>
      )}
      {(primary || st.noShop || canSwitch) && (
        <div className="deck-actions">
          {primary}
          {st.noShop && (
            <button type="button" className="deck-x" aria-label="Remove this night" disabled={busy} onClick={() => { if (!busy) onSkip(m.id, false); }}>×</button>
          )}
          {canSwitch && (
            <button type="button" className="deck-switch" disabled={busy} onClick={() => { if (!busy) onSwitch(); }}>Switch</button>
          )}
        </div>
      )}
    </section>
  );
}

// THE LIST LINE. `openCount` is the Helm's Shop badge number (live rows minus
// checked), so Home and the badge can never disagree. `firstItem` is the first
// unchecked row in Shop's Aisles order (pendingItems[0]: CATEGORY_ORDER, then
// name) — what you'd reach first. `shopping` = this user has an open session.
//   0 open, 0 in cart  "Nothing on your list · Start a list →"   (Browse)
//   0 open, N in cart  "N in the cart · Finish the trip →"       (Shop — SPEC_list_counts D3 row 4:
//                      the list is NOT empty, it is fully in the cart; Home never wraps up, the
//                      one Wrap Up at 100% is Shop's All done card, so the link only opens Shop)
//   shopping           "N left to find · Let's shop →"
//   otherwise          "N thing(s) to get · {first item} · Let's shop →"
// The link is a LEAF button carrying fixed copy only — the RUM unmask rule
// matches the tapped element and its ancestors, so the count and the item name
// sit beside it in .home-line-text (masked), never inside it.
function HomeListLine({ openCount, checkedCount, firstItem, shopping, onStartList, onShop }) {
  if (!openCount && checkedCount > 0) {
    return (
      <div className="home-line">
        <span className="home-line-text">{checkedCount} in the cart</span>
        <span className="home-line-tail">· <button type="button" className="home-line-link" onClick={onShop}>Finish the trip →</button></span>
      </div>
    );
  }
  if (!openCount) {
    return (
      <div className="home-line">
        <span className="home-line-text">Nothing on your list</span>
        {/* The separator travels with the link as one non-wrapping unit, so a wrap never
            strands a trailing dot at the end of the text. */}
        <span className="home-line-tail">· <button type="button" className="home-line-link" onClick={onStartList}>Start a list →</button></span>
      </div>
    );
  }
  const n = openCount;
  const text = shopping
    ? `${n} left to find`
    : `${n} thing${n === 1 ? "" : "s"} to get${firstItem ? ` · ${firstItem}` : ""}`;
  return (
    <div className="home-line">
      <span className="home-line-text">{text}</span>
      <span className="home-line-tail">· <button type="button" className="home-line-link" onClick={onShop}>Let's shop →</button></span>
    </div>
  );
}

// THE BUDGET LINE (D7) — measures exactly what Shop's Trip Total measures, and
// renders only when Shop would show it (the caller gates on showPrices,
// budget_goal set, list non-empty). No new arithmetic: totalCost, budgetNum,
// budgetPct, overBudget, budgetRemaining and hasEstimatedPrices are the budget
// banner's own values; the bar colours are its thresholds. Not tappable in v1.
function HomeBudgetLine({ totalCost, budgetNum, budgetPct, overBudget, budgetRemaining, hasEstimatedPrices }) {
  const fill = overBudget ? "#e05c5c" : budgetPct > 85 ? "#C9A97A" : "#A0724A";
  const label = overBudget
    ? `$${Math.abs(budgetRemaining).toFixed(2)} over $${budgetNum.toFixed(0)}`
    : `${hasEstimatedPrices ? "~" : ""}$${totalCost.toFixed(2)} of $${budgetNum.toFixed(0)}`;
  return (
    <div className="home-budget" role="status" aria-label="Budget">
      <div className="home-budget-bar" aria-hidden="true"><div className="home-budget-fill" style={{ width: `${budgetPct}%`, background: fill }} /></div>
      <div className={`home-budget-label${overBudget ? " over" : ""}`}>{label}</div>
    </div>
  );
}

// The wide rail (<Rail />, D5) was retired 2026-09-20: on desktop the app is a
// phone-width column and the helm lives inside it at every width. A nav rail
// outside a framed column is a second navigation for the same four doors.
// NAV_DOORS now has exactly one renderer.

function ShopLensSegment({ lens, onChange }) {
  return (
    <div className="shop-seg" role="group" aria-label="List view">
      <button type="button" className={lens === "aisles" ? "on" : ""} aria-pressed={lens === "aisles"} onClick={() => onChange("aisles")}>Aisles</button>
      <button type="button" className={lens === "az" ? "on" : ""} aria-pressed={lens === "az"} onClick={() => onChange("az")}>A–Z</button>
    </div>
  );
}

// D2: checked items get a PLACE, not a toggle. Collapsed by default, hidden
// entirely at 0. Rows un-check from inside the tray. `initialFor` returns a
// small initial for rows the current user did not check (partner's check).
function InCartTray({ items, count, motionClass = () => "", open, onToggle, onUncheck, initialFor }) {
  if (items.length === 0 && count === 0) return null;
  return (
    <div className="in-cart-tray">
      <button type="button" className="tray-head" onClick={onToggle} aria-expanded={open}>
        <span className="tray-cb">✓</span>
        <span className="tray-title">In cart<span className="tray-sub">{count} {count === 1 ? "item" : "items"}</span></span>
        <span className="tray-chev">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="tray-body">
          {items.map((item) => {
            const initial = initialFor(item.listItemId);
            return (
              <div key={item.name} className={`list-item shop-row-in${motionClass(item.listItemId)}`}>
                <div className="checkbox checked" onClick={() => onUncheck(item)}>
                  <span className="checkmark">✓</span>
                </div>
                <div className="li-name" style={{ cursor: "pointer" }} onClick={() => onUncheck(item)}>{item.name}</div>
                {item.qty > 1 && <span className="li-qty">×{item.qty}</span>}
                {initial && <span className="tray-initial" title="Checked by another member">{initial}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// D10: ask the store, don't guess it. Chips are the partner's currently-open
// session store first (if any), then the household's recent stores. Never
// prefilled. Skip is honest — GPS was still captured at session start.
// With no real store chips (a household's first trip) the name field shows
// directly under a plain label; "Somewhere else…" exists only as the trailing
// chip after at least one real store chip.
function StorePrompt({ chips, onPick, onSkip, busy }) {
  const hasChips = chips.length > 0;
  const [otherOpen, setOtherOpen] = useState(false);
  const [other, setOther] = useState("");
  const submitOther = () => { if (other.trim()) onPick(other.trim()); };
  const fieldOpen = !hasChips || otherOpen;
  return (
    <div className="store-prompt">
      <div className="store-prompt-q">Where are you shopping?</div>
      {hasChips && (
        <div className="store-chips">
          {chips.map((name) => (
            <button type="button" key={name} className="store-chip" disabled={busy} onClick={() => onPick(name)}>{name}</button>
          ))}
          {!otherOpen && (
            <button type="button" className="store-chip other" disabled={busy} onClick={() => setOtherOpen(true)}>Somewhere else…</button>
          )}
        </div>
      )}
      {fieldOpen && (
        <div style={{ marginTop: hasChips ? "10px" : 0 }}>
          {!hasChips && <div className="store-field-label">Store name</div>}
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              type="text"
              className="store-input"
              value={other}
              onChange={e => setOther(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") submitOther(); }}
              placeholder={hasChips ? "Store name" : "e.g. Market Basket"}
              aria-label="Store name"
            />
            <button type="button" className="store-chip" disabled={busy || !other.trim()} onClick={submitOther}>Save</button>
          </div>
        </div>
      )}
      <button type="button" className="store-skip" onClick={onSkip}>Skip — you can set it later from the list.</button>
    </div>
  );
}

// Shared catalog row body (the inner .item-row, NOT the SwipeToRemove wrapper).
// Rendered from both Browse and Search so the two can't drift apart.
function CatalogItemRow({
  item, qty, rawCategory, showPrices, price, isEditing, priceInput,
  centsToDisplay, onUpdateQty, onPriceInput, onCommitPrice, onCancelEditPrice,
}) {
  return (
    <div className={`item-row ${qty > 0 ? "has-qty" : ""}`}>
      <div className="item-top">
        <span className="item-name">{item.name}</span>
        {qty === 0 ? (
          <button className="add-btn" onClick={(e) => { onUpdateQty(item.name, 1, rawCategory); e.currentTarget.blur(); }}>Add</button>
        ) : (
          <div className="qty-controls">
            <button className="qty-btn" onClick={() => onUpdateQty(item.name, qty - 1, rawCategory)}>−</button>
            <span className="qty-display">{qty}</span>
            <button className="qty-btn" onClick={() => onUpdateQty(item.name, qty + 1, rawCategory)}>+</button>
          </div>
        )}
      </div>
      {showPrices && (
        <div className="price-row">
          {isEditing ? (
            <div className="price-edit-wrap">
              <span style={{ fontFamily: "'Lato',sans-serif", fontSize: "0.82rem", color: "#8a7a60" }}>$</span>
              <input
                className="price-input" type="tel" inputMode="numeric"
                value={centsToDisplay(priceInput)} autoFocus
                onChange={(e) => onPriceInput(e.target.value.replace(/[^0-9]/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter") onCommitPrice(item.name); if (e.key === "Escape") onCancelEditPrice(); }}
              />
              <button className="price-save-btn" onClick={() => onCommitPrice(item.name)}>Save</button>
            </div>
          ) : (
            <span className="price-display">${price.toFixed(2)} each</span>
          )}
        </div>
      )}
      {showPrices && qty > 0 && <div className="item-subtotal">Subtotal: ${(qty * price).toFixed(2)}</div>}
    </div>
  );
}

// ── Shared declutter cycle control ──
// One 46×46 icon used identically on Browse and Shop. Encodes both axes:
// background light→dark = filter off→on (phase 0 vs 1/2); line shape tapering
// (funnel ∨) → equal = grouped → flat (phase 2). Each tap advances 0→1→2→0.
function CycleIcon({ phase, onAdvance }) {
  return (
    <button
      className={`cyc-ico ${phase !== 0 ? "on" : ""}`}
      onClick={onAdvance}
      aria-label="Declutter view"
      title="Declutter view"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
        {phase === 2 ? (
          <>
            <line x1="4" y1="6" x2="20" y2="6"/>
            <line x1="4" y1="12" x2="20" y2="12"/>
            <line x1="4" y1="18" x2="20" y2="18"/>
          </>
        ) : (
          <>
            <line x1="4" y1="6" x2="20" y2="6"/>
            <line x1="7" y1="12" x2="17" y2="12"/>
            <line x1="10" y1="18" x2="14" y2="18"/>
          </>
        )}
      </svg>
    </button>
  );
}

// Flat-view header shared by both tabs' phase-2 render.
// `count` is dropped when the descriptor above already states it — the eyebrow
// sat ~40px under "… — 12 items" and printed the same 12 a second time.
function FlatHeader({ count, showCount = true }) {
  return (
    <div className="flat-header">
      A–Z{showCount ? <> · {count} {count === 1 ? "item" : "items"}</> : null}
    </div>
  );
}

// ── Browse · Meals lens (read + "Add all", add-path only — migration 025) ──
// Read-only card list of the household's meals. "Add all" folds a meal's
// ingredients into the shared list at flat quantities (servings dial deferred).
// Create-a-meal is the next PR; until then meals are seeded via
// migrations/fixtures/dev_meals_seed.sql on dev.
// Feature flag — Meals lens (migration 025). Was false while 025/026 had not reached
// prod (RELEASE-2026-08 B6): nothing then rendered the lens, fetched `meals`, read
// `list_item_meals`, or called add_meal_to_list. Both migrations shipped to prod
// 2026-08-18, so the flag is on and the lens lives on the PLAN tab.
const MEALS_ENABLED = true;

// ── Plan (SPEC_meal_planning_v2_pick_commit_cook.md — Pick, Commit, Cook) ──
// Meals → Board → List → Cook. The library is where you decide WHAT (one
// verb: Plan). The board is where you decide WHEN (one primary per card:
// Add to Shop / See on list / Cooked it). The list handles what you need.
// Nothing in 047–052 changes: the board is still meal_placements in
// sort_order; the list still supplies each card's state; ready_at is still
// earned at Wrap up. 055 adds meals.kind for the two no-shop cards.
//
// TEAL = THE HOUSEHOLD FINISHED SOMETHING. On this surface it appears on
// exactly two things: Cooked it (then ✓ Cooked) and the stocked banner.
// Plan, Add to Shop, See on list, filters, links, counts — espresso or outline.
//
// TILE TONES. One numbered tile per card carries "01" + the rail word on the
// meal's tone; the number makes the absent photo designed and reinforces
// drag order (a household photo later slides UNDER the number). Six house
// tones; text is whichever of #FAF4EC / #2C1A0E contrasts better on that tone
// (precomputed — clay tops out at 3.97:1 either way, the palette's one miss;
// the large numeral clears AA-large at 3:1). Sand is deliberately a step
// darker than the chrome sand (#C9A97A) and the add button (--op-add): a tile
// must never read as a button. Keyed by the meal's CATEGORY
// WORD so two meals in one category share a tone — the spec's rule. NOTE the
// spec assumed `meals.category`; that column does not exist. The word is
// derived from the meal's dominant ingredient category (falling back to the
// name), so it is the same in the library and on the board. A stored per-meal
// colour replaces mealTone and nothing else.
const MEAL_TONES = [
  { name: "espresso", bg: "#6f5a45", fg: "#FAF4EC" },
  { name: "sand",     bg: "#B8945F", fg: "#2C1A0E" },   // darker than --op-add (#D9BC8C) on purpose: no tile may match the add button
  { name: "clay",     bg: "#A0724A", fg: "#2C1A0E" },
  { name: "stone",    bg: "#9a9384", fg: "#2C1A0E" },
  { name: "olive",    bg: "#5f6b4f", fg: "#FAF4EC" },
  { name: "slate",    bg: "#7d8fa0", fg: "#2C1A0E" },
];
// No-shop kinds (leftovers / out / other) share ONE cream tile — colour means
// food to cook, and these plans cook nothing.
const NO_SHOP_TONE = { name: "cream", bg: "#EFE9DE", fg: "#6f5a45" };
// Leftovers window (SPEC_replan_cook_history.md D4): a meal cooked within this
// many days is offered as a leftovers source. Leftovers are a fridge fact, not a
// shopping-cycle fact — the old rule counted wrap-ups, so a household that rarely
// wrapped up saw weeks-old meals. 4 is a STARTING POINT, and Leftovers is a
// helper, not essential: tune it from what households say, not by reasoning.
// The one place this number lives.
const LEFTOVER_WINDOW_DAYS = 4;
// Tile word: the kind for leftovers / out; for 'other' the name itself,
// uppercased by CSS, cut at ~10 chars; OTHER when blank.
function noShopTileWord(m) {
  if (m?.kind !== "other") return noShopLabel(m);
  const n = noShopName(m);
  if (!n) return "Other";
  return n.length > 10 ? `${n.slice(0, 10)}…` : n;
}
// Leftovers caption from from_meal_ids (056), no articles: "From Chicken
// Curry" / "From Chicken Curry, Pizza" / "From Chicken Curry, Pizza + 1".
// Empty string when nothing resolves.
function mealCategoryWord(meal) {
  const tally = {};
  (meal?.meal_ingredients || []).forEach((mi) => {
    const c = String(mi.catalog_items?.category || "").trim();
    if (c) tally[c] = (tally[c] || 0) + 1;
  });
  let best = null;
  Object.entries(tally).forEach(([c, n]) => {
    if (!best || n > best[1] || (n === best[1] && c < best[0])) best = [c, n];
  });
  return best ? categoryLabel(best[0]).replace(/^[^\p{L}\p{N}]+/u, "").trim() || best[0] : (meal?.name || "Meal");
}
function mealTone(meal) {
  if (isNoShop(meal)) return NO_SHOP_TONE;
  const word = mealCategoryWord(meal);
  let h = 0;
  for (let i = 0; i < word.length; i += 1) h = (h * 31 + word.charCodeAt(i)) >>> 0;
  return MEAL_TONES[h % MEAL_TONES.length];
}
// Rail words by position: 0 → Up next; last → Later (from three cards);
// everything between → Then. One card: Up next. Two: Up next, Then.
function railWord(i, n) {
  if (i === 0) return "Up next";
  if (n >= 3 && i === n - 1) return "Later";
  return "Then";
}

// HOLD A NIGHT — Leftovers / Eating out / Something else (055–057) under a
// ruled label. One component, two homes: the board's foot ("HOLD A NIGHT") and
// the empty-week welcome ("OR HOLD A NIGHT"). Icon + label, solid sand border,
// 64px tall; dashed borders stay on the no-shop CARDS themselves. .plan-noshop
// is the RUM selector for these three — keep the class.
function HoldANightButtons({ label, onLeftovers, onOut, onOther }) {
  return (
    <div className="hold-night" role="group" aria-label={label}>
      <div className="hold-night-rule" aria-hidden="true"><span /><span className="hold-night-label">{label}</span><span /></div>
      <div className="hold-night-btns">
        <button type="button" className="plan-noshop" onClick={onLeftovers}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="9" width="16" height="11" rx="2" /><path d="M3 9h18l-1.5-4h-15z" /><line x1="10" y1="13" x2="14" y2="13" /></svg>
          Leftovers
        </button>
        <button type="button" className="plan-noshop" onClick={onOut}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 3v8M5 3v5a2 2 0 0 0 4 0V3M7 11v10" /><path d="M17 21V3c-2 1-3 4-3 7h3" /></svg>
          Eating out
        </button>
        <button type="button" className="plan-noshop" onClick={onOther}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="5" width="16" height="15" rx="2" /><line x1="4" y1="10" x2="20" y2="10" /><line x1="9" y1="3" x2="9" y2="7" /><line x1="15" y1="3" x2="15" y2="7" /></svg>
          Something else
        </button>
      </div>
    </div>
  );
}

// THE WELCOME — the empty week (PATCH_plan_tab_welcome, 2026-09-23;
// mockup_plan_tab_welcome.html was the visual authority). Renders in place of
// the board only once meals AND placements have loaded and nothing is on the
// board; the moment a card exists the normal board renders with no remnant.
// Three dashed ghost cards teach "you're building a week" (decorative:
// aria-hidden, not tappable), then the headline, the one deep-sand add button
// (opens the library) and, as children, Hold a night. The headline never
// changes; only the button copy does — "Add your first meal" until the
// household has cooked anything, then "Add a meal". Onboarding copy lives
// here and nowhere else: once a household has shown it understands, the UI
// stops coaching.
const WELCOME_GHOSTS = [["01", "UP NEXT", "62%", "38%"], ["02", "THEN", "50%", "30%"], ["03", "LATER", "56%", "34%"]];
function PlanWelcome({ firstMeal, onAdd, children }) {
  return (
    <div className="plan-welcome">
      <div className="plan-ghosts" aria-hidden="true">
        {WELCOME_GHOSTS.map(([n, word, w1, w2]) => (
          <div key={n} className="plan-ghost">
            <div className="plan-ghost-tile"><span className="plan-ghost-num">{n}</span><span className="plan-ghost-word">{word}</span></div>
            <div className="plan-ghost-lines"><span style={{ width: w1 }} /><span style={{ width: w2 }} /></div>
          </div>
        ))}
      </div>
      <h3 className="plan-welcome-title">Let's plan your week.</h3>
      <p className="plan-welcome-sub">Pick a few meals you'd like to make. We'll turn them into your shopping list.</p>
      <button type="button" className="plan-welcome-add" onClick={onAdd}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
        {firstMeal ? "Add your first meal" : "Add a meal"}
      </button>
      {children}
    </div>
  );
}

// THE BOARD — the queue of open placements (050), in the household's shared
// order. `meals` arrives derived and sorted (boardMeals in App) and includes
// no-shop rows (055); `rows` = per-meal { total, bought } live list rows;
// `mealById` resolves a leftovers card's from_meal_ids (056; ids that no
// longer resolve — a hard-deleted source — are skipped, never rendered).
//
// CARD STATES (052 conditions, v2 chrome — one primary button per state):
//   Planned    open placement, no live rows, ready_at null
//              chip PLANNED · "Not on the list yet" · Add to Shop (outline)
//              Cooked it (freezer pizza) lives behind ⋯, not beside it.
//   A meal card's right side is exactly: chip + grip (top row), action + ⋯
//   (bottom row). NO destructive control at rest — Remove is inside ⋯.
//   To buy     ≥ 1 live pending row
//              chip TO BUY · "N to buy" / "B of N in cart" · See on list
//   Ready      ready_at set (earned at Wrap up, never at an in-cart tap)
//              NO chip (a chip shows only while the state is incomplete) ·
//              "Everything's in — go cook" · Cooked it (teal)
//   Cooked     this board load only: muted in place, ✓ Cooked (teal outline,
//              disabled), no grip, no number; gone on the next board load
//   Leftovers  meals.kind = 'leftovers'   cream tile, dashed card, NO chip and
//   Eating out meals.kind = 'out'         NO state line — name, one context
//   Something  meals.kind = 'other' (057)  line (From X / Oak House / the free
//     else                                 text), × and grip. They DRAG like
//              any card, hold the night, never touch the list, never become
//              Ready, have no outcome action. Kinds of plan, not states.
// Skip = Remove (⋯) on a meal card, × on a no-shop card. On a to-buy card it
// zeroes the meal's pending rows first; on a no-shop card the hook also
// soft-deletes the meals row (one-shot).
//
// DRAG TO REORDER — long-press (350ms) lifts, a tap opens (a meal card only —
// a no-shop card has no recipe to open). Long-press is never the door to
// anything else. Pointer events, translateY on the lifted card, the cards it
// passes slide out of its way, reorder on drop. Order is SHARED (per
// household): drop calls onReorder with the full id list and the hook
// renumbers 0..n-1. While lifted the board renders a SNAPSHOT of the order
// taken at lift time so the 2s poll cannot re-sort under a moving finger.
// touch-action pan-y lets a plain swipe scroll; a finger that moves more than
// a few px before the timer fires cancels the press; once lifted a
// non-passive touchmove listener eats the browser's scroll and contextmenu is
// suppressed. The click after a lift is swallowed. Reduced-motion: drag still
// works; the slide transitions are off.
const BOARD_PRESS_MS = 350;
const BOARD_SLOP_PX = 8;
const BOARD_CONTROLS = ".board-x, .board-cook, .board-lock, .board-see, .board-more, .board-menu";


export function PlanBoard({ meals, rows, placements, mealById, cookedIds, onOpen, onSkip, onCooked, onLockIn, onSeeList, onReorder, busyMealId, afterglow }) {
  // A card cooked THIS board load stays in place, muted, until the next load
  // (cookedIds, owned by App). It is not in the queue: no drag, no number, no
  // rail word, excluded from upNext / counts / numbering.
  const isCooked = (m) => !!cookedIds?.has(m.id);
  const openCount = meals.filter((m) => !isCooked(m)).length;
  const [drag, setDrag] = useState(null);   // { id, from, to, dy, snapshot, slots }
  const [menuFor, setMenuFor] = useState(null);   // meal id whose ⋯ menu is open
  const pressRef = useRef(null);            // { id, index, pointerId, x, y, timer, el }
  const dragRef = useRef(null);             // mirrors drag for the pointer handlers
  const swallowClickRef = useRef(false);
  dragRef.current = drag;

  const clearPress = useCallback(() => {
    const pr = pressRef.current;
    if (pr?.timer) clearTimeout(pr.timer);
    pressRef.current = null;
  }, []);

  // Eat the browser's scroll while a card is lifted. Non-passive by necessity —
  // React's synthetic touch handlers are passive, so this has to be a raw listener.
  useEffect(() => {
    if (!drag) return undefined;
    const block = (e) => { e.preventDefault(); };
    document.addEventListener("touchmove", block, { passive: false });
    return () => document.removeEventListener("touchmove", block);
  }, [drag]);

  const lift = useCallback((pr) => {
    const board = pr.el.parentElement;
    const cards = Array.from(board.querySelectorAll(".board-card"));
    const slots = cards.map((c) => { const r = c.getBoundingClientRect(); return { top: r.top, height: r.height }; });
    try { pr.el.setPointerCapture(pr.pointerId); } catch (_e) { /* capture is best-effort */ }
    swallowClickRef.current = true;
    setMenuFor(null);
    setDrag({ id: pr.id, from: pr.index, to: pr.index, dy: 0, snapshot: meals, slots });
  }, [meals]);

  const onPointerDown = (e, m, index) => {
    if (e.button != null && e.button !== 0) return;
    if (e.target.closest(BOARD_CONTROLS)) return;
    if (isCooked(m)) return;                  // a cooked card has left the queue
    if (openCount < 2) return;                // nothing to reorder
    clearPress();
    const pr = { id: m.id, index, pointerId: e.pointerId, x: e.clientX, y: e.clientY, el: e.currentTarget, timer: null };
    pr.timer = setTimeout(() => { if (pressRef.current === pr) lift(pr); }, BOARD_PRESS_MS);
    pressRef.current = pr;
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) {
      const pr = pressRef.current;
      if (pr && (Math.abs(e.clientX - pr.x) > BOARD_SLOP_PX || Math.abs(e.clientY - pr.y) > BOARD_SLOP_PX)) clearPress();
      return;
    }
    const pr = pressRef.current;
    if (!pr) return;
    const dy = e.clientY - pr.y;
    // Where does the lifted card's centre now fall among the others?
    const centre = d.slots[d.from].top + d.slots[d.from].height / 2 + dy;
    let to = 0;
    d.slots.forEach((sl, i) => { if (i !== d.from && sl.top + sl.height / 2 < centre) to += 1; });
    if (dy !== d.dy || to !== d.to) setDrag({ ...d, dy, to });
  };

  const onPointerEnd = () => {
    const d = dragRef.current;
    clearPress();
    if (!d) return;
    setDrag(null);
    if (d.to !== d.from) {
      const ids = d.snapshot.map((m) => m.id);
      const [moved] = ids.splice(d.from, 1);
      ids.splice(d.to, 0, moved);
      onReorder(ids.filter((id) => !cookedIds?.has(id)));
    }
  };

  if (meals.length === 0) {
    return <div className="board board-empty">Nothing planned yet</div>;
  }
  const list = drag ? drag.snapshot : meals;
  const gap = 8;
  // Numbering runs over the OPEN cards in their projected order (the finger's
  // order while a drag is live), so a cooked card never holds a number and the
  // rest renumber around it immediately.
  const projected = drag
    ? (() => { const a = list.slice(); const [mv] = a.splice(drag.from, 1); a.splice(drag.to, 0, mv); return a; })()
    : list;
  const openOrder = projected.filter((x) => !isCooked(x)).map((x) => x.id);
  return (
    <div className="board">
      {/* The fixed VERB halves of the board's aria-labelledby names, rendered once, outside every
          button (2026-10-19, the library's 10-16 recipe). The meal's half is the card's masked
          title (id board-title-{uuid}); no name ever sits in an attribute the recorder copies. */}
      <span id="board-verb-remove" className="sr-only">Remove</span>
      <span id="board-verb-more" className="sr-only">More for</span>
      <span id="board-verb-cooked" className="sr-only">cooked</span>
      {menuFor && <div className="board-menu-backdrop" onClick={(e) => { e.stopPropagation(); setMenuFor(null); }} />}
      {list.map((m, i) => {
        const busy = busyMealId === m.id;
        // ONE derivation for every card, shared with Home's on-deck card (D3).
        const { noShop, cooked, ready, planned, toBuy, canAdd, rc, chip, title, line } = boardCardState(m, { placements, rows, mealById, cookedIds });
        const ordinal = openOrder.indexOf(m.id);
        const tone = mealTone(m);
        const lifted = drag && drag.id === m.id;
        // Slide transforms while a drag is live; the number and rail word come
        // from `ordinal` (openOrder, the same projected order), so what the eye
        // sees moving and what the tile says always agree.
        let transform;
        if (drag) {
          if (lifted) {
            transform = `translateY(${drag.dy}px)`;
          } else {
            const h = drag.slots[drag.from].height + gap;
            if (drag.from < drag.to && i > drag.from && i <= drag.to) transform = `translateY(${-h}px)`;
            else if (drag.to < drag.from && i >= drag.to && i < drag.from) transform = `translateY(${h}px)`;
          }
        }
        // A cooked card opens the recipe view too (ruling 2026-10-16: the recipe is
        // still worth reading after the cook); only a held night has nothing to open.
        const open = () => { if (!noShop) onOpen(m); };
        return (
          <div
            key={m.id}
            className={`board-card${lifted ? " lifted" : ""}${ready ? " ready" : ""}${noShop ? " noshop" : ""}${cooked ? " cooked" : ""}`}
            role={noShop ? undefined : "button"}
            tabIndex={noShop ? undefined : 0}
            onClick={() => { if (swallowClickRef.current) { swallowClickRef.current = false; return; } open(); }}
            onKeyDown={(e) => { if (!noShop && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); open(); } }}
            onPointerDown={(e) => onPointerDown(e, m, i)}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerEnd}
            onPointerCancel={onPointerEnd}
            onContextMenu={(e) => e.preventDefault()}
            style={{ ...(busy ? { opacity: 0.5 } : null), ...(transform ? { transform } : null) }}
          >
            <div className="board-tile" style={{ background: tone.bg, color: tone.fg }} aria-hidden="true">
              <span className="board-num">{cooked ? "✓" : String(ordinal + 1).padStart(2, "0")}</span>
              {!cooked && <span className="board-rail">{noShop ? noShopTileWord(m) : railWord(ordinal, openOrder.length)}</span>}
            </div>
            <div className="board-main">
              <div className="board-top">
                {/* Display casing only (displayTitle, visual v2 D11) on a meal; a held night's title is its label. */}
                <span id={`board-title-${m.id}`} className="board-card-title">{noShop ? title : displayTitle(title)}</span>
                {chip && <span className={`board-chip${ready ? " ready" : ""}`}>{chip}</span>}
                {/* × at rest only on a no-shop card — it is that card's one action.
                    A meal card shows NO destructive control at rest: Remove lives
                    behind ⋯ (the rule, logged 2026-09-21). */}
                {noShop && (
                  <button
                    type="button"
                    className="board-x"
                    aria-labelledby={`board-verb-remove board-title-${m.id}`}
                    disabled={busy}
                    onClick={(e) => { e.stopPropagation(); if (!busy) onSkip(m.id, false); }}
                  >×</button>
                )}
                {/* Grip — the drag affordance, same grey as ×. Decorative: the
                    whole card is the long-press target. Hidden once cooked, and
                    hidden while fewer than two open cards (nothing to reorder —
                    the same gate startPress uses). */}
                {!cooked && openCount >= 2 && (
                  <svg className="board-grip" width="10" height="16" viewBox="0 0 10 16" aria-hidden="true">
                    <circle cx="3" cy="3" r="1.4" /><circle cx="7" cy="3" r="1.4" />
                    <circle cx="3" cy="8" r="1.4" /><circle cx="7" cy="8" r="1.4" />
                    <circle cx="3" cy="13" r="1.4" /><circle cx="7" cy="13" r="1.4" />
                  </svg>
                )}
              </div>
              {line && <div className="board-line">{line}</div>}
              {/* Cooked this load: the card mutes in place; the button reads ✓ Cooked
                  (teal outline, disabled). It leaves on the next board load. */}
              {cooked && (
                <div className="board-actions">
                  <button type="button" className="board-cook done" disabled aria-labelledby={`board-title-${m.id} board-verb-cooked`}>✓ Cooked</button>
                </div>
              )}
              {/* Photos slice 2 (S4): "How was it?" + "Add a meal photo" under ✓ Cooked — only with THIS
                  device's cook id (S3: no id, no controls). Outside the 55% dimming (.board-tile / -top / -line). */}
              {cooked && afterglow && afterglow.cookIdFor(m.id) && (
                <BoardAfterglow mealId={m.id} cookId={afterglow.cookIdFor(m.id)} ag={afterglow} />
              )}
              {!noShop && !cooked && (
              <div className="board-actions">
                {canAdd && (
                  <button
                    type="button"
                    className="board-lock"
                    disabled={busy}
                    onClick={(e) => { e.stopPropagation(); if (!busy) onLockIn(m.id); }}
                  >Add to Shop</button>
                )}
                {/* At 0 left (everything in the cart, not wrapped up) the same door reads "Wrap up →"
                    — the recipe view's nudge, word for word (Amendment C, ruling 4). Wording only. */}
                {toBuy && (
                  <button
                    type="button"
                    className="board-see"
                    onClick={(e) => { e.stopPropagation(); onSeeList(); }}
                  >{rc.bought >= rc.total ? "Wrap up →" : "See on list"}</button>
                )}
                {ready && (
                  <button
                    type="button"
                    className="board-cook"
                    disabled={busy}
                    onClick={(e) => { e.stopPropagation(); if (!busy) onCooked(m.id); }}
                  >Cooked it</button>
                )}
                <span className="board-actions-gap" />
                <button
                  type="button"
                  className="board-more"
                  aria-labelledby={`board-verb-more board-title-${m.id}`}
                  aria-expanded={menuFor === m.id}
                  onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === m.id ? null : m.id); }}
                >⋯</button>
              </div>
              )}
              {/* ⋯ — Remove, Cooked it (Planned only), Open recipe. Remove is the
                  skip path (on a to-buy card it zeroes the meal's pending rows first);
                  Cooked it is the freezer-pizza exit; Open recipe opens the recipe
                  view, the same as a card tap (SPEC_recipe_view, 2026-10-18). */}
              {menuFor === m.id && (
                <div className="board-menu" role="menu" onClick={(e) => e.stopPropagation()}>
                  <button type="button" role="menuitem" disabled={busy}
                    onClick={(e) => { e.stopPropagation(); setMenuFor(null); if (!busy) onSkip(m.id, toBuy); }}>
                    {toBuy ? "Remove (and take its items off Shop)" : "Remove"}
                  </button>
                  {planned && (
                    <button type="button" role="menuitem" disabled={busy}
                      onClick={(e) => { e.stopPropagation(); setMenuFor(null); if (!busy) onCooked(m.id); }}>Cooked it</button>
                  )}
                  <button type="button" role="menuitem"
                    onClick={(e) => { e.stopPropagation(); setMenuFor(null); onOpen(m); }}>Open recipe</button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// SHEET CLOSE (2026-10-04, found with VoiceOver on the dev walk: the Profile
// sheet could not be closed). There is no shared sheet component — every modal
// is an inline `.modal-overlay > .modal` pair, and the Profile sheet is a bespoke
// bottom sheet — so the shared level is this button plus the dialog attributes
// on each container. Rules: a real <button>, 44px target, aria-label "Close",
// focused on open so it is the first VoiceOver stop (a button takes focus
// without raising the keyboard — the no-autoFocus rule is about inputs), and on
// unmount focus returns to whatever had it when the sheet opened. Backdrop taps
// and the existing Cancel buttons stay for sighted users.
function SheetClose({ onClose, disabled = false, label = "Close" }) {
  const ref = useRef(null);
  useEffect(() => {
    const opener = document.activeElement;
    if (ref.current) ref.current.focus({ preventScroll: true });
    return () => {
      if (opener && typeof opener.focus === "function" && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, []);
  return (
    <button ref={ref} type="button" className="sheet-close" aria-label={label} disabled={disabled} onClick={onClose}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
    </button>
  );
}

// PLAN'S TITLE SWITCH (SPEC_plan_week_meals_switch, D1–D3, D10). This Week and
// Meals are two views of ONE activity, so both names are always on screen at
// the size of a page title: two serif links, the active one ink with a 3px clay
// underline the width of the word, the other muted. No segmented pill (it cost
// ~60px and repeated the title), no back arrow, no breadcrumb, and Meals is not
// a fifth door. Rendered ONCE for both views so they cannot drift. The row is
// also the Helm's compact sentinel (controlRowRef) on both views. Real buttons
// inside a labelled nav, aria-current on the active one; the vertical padding
// (not smaller type) makes the ≥44px tap target.
function PlanSwitch({ screen, onSwitch, subtitle, action, rowRef, className }) {
  const link = (key, label) => {
    const on = screen === key;
    return (
      <button
        type="button"
        className={`plan-view${on ? " on" : ""}`}
        aria-current={on ? "page" : undefined}
        onClick={() => { if (!on) onSwitch(key); }}
      >{label}</button>
    );
  };
  return (
    <div className={`plan-ctl${className ? ` ${className}` : ""}`} ref={rowRef}>
      <div className="plan-ctl-text">
        <nav className="plan-switch" aria-label="Plan views">
          {link("board", "This Week")}
          {link("library", "Meals")}
        </nav>
        {subtitle && <div className="plan-sub">{subtitle}</div>}
      </div>
      {action}
    </div>
  );
}

// THE LIBRARY — "What sounds good?" (SPEC_meal_library_v1 + the v1.1 amendment).
// Where you decide WHAT; This Week (the board) decides WHEN. A two-column card
// grid of LIGHT cards (visual v2, 2026-10-16): a tinted slot (the occasion
// mark), the name in the body, a meta line "{Occasion} · {n} items" and the
// ONE action, the round "+" (→ ✓ when planned). The card body opens Edit
// Meal, planned or not. Above the grid: search, a Filter button (From / Made
// before / Ours live in a sheet; active ones show as removable chips) and the
// occasion rail — All plus only the occasions some meal carries; with none
// tagged the rail hides behind a one-line prompt until the first tag. Below:
// one Create tile (one door — the New Meal sheet, Galley inside). ZERO TEAL:
// planning isn't finishing. "Planned" = an open placement (onBoardIds), so a
// ✓ Cooked afterglow card reads Plan again. Favorites is OMITTED, not disabled:
// no data exists yet and a greyed pill promises a table that hasn't been decided.
// RUM: .lib-occ / .lib-fbtn / .lib-vtog / .lib-plan / .lib-create / .lib-show / .lib-clear
// (and the switch row's .plan-view / .hdr-action) carry fixed copy only; the
// card body (meal name) and the From chips
// (first names) are NOT allow-listed — keep names out of those classes.
const NO_LIB_FILTERS = { from: null, made: false, ours: false };
// The "Something new?" tile coaches until the library has this many meals.
const LIBRARY_COACH_MAX = 6;
// The one search predicate (spec): case-insensitive substring on the name and on
// the occasion words, singular and plural ("dessert", "snacks"). Smarter search
// replaces this function, not the UI. `q` arrives trimmed and lower-cased.
function matchesQuery(meal, q) {
  if (!q) return true;
  if ((meal?.name || "").toLowerCase().includes(q)) return true;
  return (meal?.occasion || []).some((o) => o.includes(q)
    || (OCCASION_WORD[o] || "").toLowerCase().includes(q)
    || (OCCASION_PILL[o] || "").toLowerCase().includes(q));
}

// Browse's rail, reused: the same .cat-rail-wrap / .cat-rail classes (scroll-snap,
// pan-x, the styled scrollbar, the edge fades), so the scroll behaviour is not
// forked — only the pills differ (.lib-occ: espresso on-state, no glyph). The
// edge sync mirrors Browse's syncRailEdges for this rail's own node; the resting
// position is keyed off the PILL SET (not the selection) exactly as Browse does,
// so it never yanks a rail the user is mid-scroll.
function OccasionRail({ present, value, onChange }) {
  const node = useRef(null);
  const [edges, setEdges] = useState({ atStart: true, atEnd: true });
  const sync = useCallback(() => {
    const rail = node.current;
    if (!rail) return;
    const max = rail.scrollWidth - rail.clientWidth;
    const atStart = rail.scrollLeft <= 1;
    const atEnd = rail.scrollLeft >= max - 1;
    setEdges((prev) => (prev.atStart === atStart && prev.atEnd === atEnd ? prev : { atStart, atEnd }));
  }, []);
  const presentKey = present.join(",");
  useLayoutEffect(() => {
    const rail = node.current;
    if (!rail) return;
    const active = rail.querySelector('[data-active="1"]');
    rail.scrollLeft = active ? Math.max(0, active.offsetLeft - rail.offsetLeft) : 0;
    sync();
  }, [presentKey, sync]);
  useEffect(() => {
    const rail = node.current;
    if (!rail) return;
    rail.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    return () => { rail.removeEventListener("scroll", sync); window.removeEventListener("resize", sync); };
  }, [sync]);
  const pills = [["all", "All"], ...present.map((k) => [k, OCCASION_PILL[k]])];
  return (
    <div className={`cat-rail-wrap lib-rail${edges.atStart ? " at-start" : ""}${edges.atEnd ? " at-end" : ""}`}>
      <div className="cat-rail" ref={node} role="tablist" aria-label="Good for">
        {pills.map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={value === k}
            data-active={value === k ? "1" : undefined}
            className={`lib-occ${value === k ? " on" : ""}`}
            onClick={() => onChange(k)}
          >{label}</button>
        ))}
      </div>
    </div>
  );
}


// FILTER SHEET (mockup screen 3). FROM = current household members with ≥ 1
// library meal (created_by — who ADDED it to this library, not whose recipe it
// is), single-select, tapping the selected chip clears it; a member who left
// takes their chip with them, never the meals. HISTORY = Made before (the cook
// log, 058) and Ours (v2's predicate, unchanged). Applies on Show; Clear resets
// these three in the draft only — never the occasion or the search. The count
// on Show is the live grid count with the draft applied (occasion and search
// included), so a chip that would return nothing says so before it does.
function LibraryFilterSheet({ draft, setDraft, fromMembers, countFor, onShow, onClose }) {
  const n = countFor(draft);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="Filter meals" onClick={(e) => e.stopPropagation()}>
        <SheetClose onClose={onClose} />
        <h2>Filter meals</h2>
        <div className="lib-sheet-lbl">From</div>
        {fromMembers.length > 0 && (
          <div className="lib-chips">
            {fromMembers.map((mb) => {
              const on = draft.from === mb.id;
              return (
                <button
                  key={mb.id}
                  type="button"
                  className={`lib-chef${on ? " on" : ""}`}
                  aria-pressed={on}
                  onClick={() => setDraft({ ...draft, from: on ? null : mb.id })}
                ><span className="lib-mini" aria-hidden="true">{mb.initial}</span>{mb.firstName}</button>
              );
            })}
          </div>
        )}
        <div className="lib-sheet-note">Only people with meals in your library show here.</div>
        <div className="lib-sheet-lbl">History</div>
        <button type="button" role="switch" aria-checked={draft.made} className="lib-sw" onClick={() => setDraft({ ...draft, made: !draft.made })}>
          <span><span className="lib-sw-l">Made before</span><span className="lib-sw-d">Meals you've cooked from This Week</span></span>
          <span className="lib-track" aria-hidden="true" />
        </button>
        <button type="button" role="switch" aria-checked={draft.ours} className="lib-sw" onClick={() => setDraft({ ...draft, ours: !draft.ours })}>
          <span><span className="lib-sw-l">Ours</span><span className="lib-sw-d">Made in this household, not shared in</span></span>
          <span className="lib-track" aria-hidden="true" />
        </button>
        <div className="lib-sheet-btns">
          <button type="button" className="lib-clear" onClick={() => setDraft({ ...NO_LIB_FILTERS })}>Clear</button>
          <button type="button" className="lib-show" onClick={onShow}>Show {n} {n === 1 ? "meal" : "meals"}</button>
        </div>
      </div>
    </div>
  );
}

// GRID / LIST (visual v2 D8–D9). The default follows the text-size step (≥ Large
// → list); the toggle's choice is stored on this device and then wins. One
// state: the stored override (null = derived). The resolver is pure and tested
// (src/lib/libraryView.js); this hook only owns the React state and the write.
function useLibraryView(textStep) {
  const [override, setOverride] = useState(readStoredView);
  const view = resolveLibraryView(override, textStep);
  const toggle = useCallback(() => {
    const next = otherView(view);
    writeStoredView(next);
    setOverride(next);
  }, [view]);
  return [view, toggle];
}
const ListGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M8 6h12M8 12h12M8 18h12" /><circle cx="4" cy="6" r="1" fill="currentColor" stroke="none" /><circle cx="4" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="4" cy="18" r="1" fill="currentColor" stroke="none" /></svg>
);
const GridGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="4" width="6.5" height="6.5" rx="1.5" /><rect x="13.5" y="4" width="6.5" height="6.5" rx="1.5" /><rect x="4" y="13.5" width="6.5" height="6.5" rx="1.5" /><rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.5" /></svg>
);

// "SET MEAL TYPE" (Amendment A5): a small bottom sheet, not Edit Meal — the
// occasions (eight since 069 added drink) as icon chips, multi-select, the first tapped becomes
// occasion[1] (the card word, 061), Save. The draft is local until Save; Cancel,
// the backdrop, swipe-down and Close all discard it. Save with nothing chosen is
// disabled (Cancel is the way to leave a meal untagged).
function SetTypeSheet({ meal, onSave, onClose }) {
  const [sel, setSel] = useState(() => (meal.occasion || []).slice());
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  return (
    <div className="lib-type-scrim" onClick={onClose}>
      <SheetPanel onClose={onClose} label="Set meal type" className="lib-type-sheet">
        <SheetClose onClose={onClose} />
        <div className="lib-type-handle" aria-hidden="true" />
        <h2 className="lib-type-h">Set meal type</h2>
        <div className="lib-type-name">{displayTitle(meal.name)}</div>
        <div className="lib-type-chips" role="group" aria-label="Good for">
          {OCCASION_ORDER.map((k) => {
            const on = sel.includes(k);
            const tint = OCCASION_TINT[k];
            return (
              <button
                key={k}
                type="button"
                className={`lib-type-chip${on ? " on" : ""}`}
                aria-pressed={on}
                onClick={() => toggle(k)}
              >
                <span style={{ color: on ? "#FAF4EC" : tint.ink, display: "inline-flex" }} aria-hidden="true"><OccasionIcon occasion={k} size={22} tint={on ? "#6f5a45" : "#FFFDF9"} /></span>
                {OCCASION_WORD[k]}
                {on && sel[0] === k && <span className="gf-oncard">On card</span>}
              </button>
            );
          })}
        </div>
        <div className="lib-type-help">Choose any that fit. The first appears on the card.</div>
        <div className="lib-type-btns">
          <button type="button" className="lib-type-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="lib-type-save" disabled={sel.length === 0} onClick={() => { onSave(sel); onClose(); }}>Save</button>
        </div>
      </SheetPanel>
    </div>
  );
}

// THE RECIPE VIEW (SPEC_recipe_view, built 2026-10-18 — slice (a)). Tapping a meal
// opens THIS, from every door that shows one: the library card body (grid and
// list), the This Week card body (a cooked card too — ruling 2026-10-16; held
// nights never) and Home's on-deck card body. Edit sits top-right and opens the
// existing Edit Meal sheet unchanged (D1). One full-height SheetPanel — Close
// top-left, swipe-down, handle; no back arrow, no route (D2). Two halves in this
// order (D3): DECIDE — hero, title, byline, Added by, meta, proof, the plan
// action; MAKE — the "Make it" seam, Ingredients, Steps, Your cooks.
//   hero     the occasion tint + mark, as on the library card; no photo path
//            yet (Photos v1), no placeholder text, no camera (D5)
//   byline   "from {attribution}" — hidden when empty; never a possessive, never
//            created_by (D6)
//   Added by "Added by {first name}" from created_by — only in a household of
//            2+ current members and only while the adder is still one (D7)
//   meta     "{Occasion} · {n} ingredients · {n} steps" — occasion omitted when
//            untagged, steps omitted at 0 (D8)
//   proof    "Last cooked {date}", "· {N} times" at 2+ (A4, 2026-10-18 — was
//            "Cooked {n} times"); from meal_cooks; hidden when never cooked
//            (D11). No average. Your cooks shows only at 2+ cooks (A4).
//   plan     the board's OWN derivation (boardCardState, D9): not on This Week →
//            sand "+ Add to This Week"; planned → "✓ On This Week" (un-plans);
//            to buy → "{n} left to buy · See list", n the board's own count, the
//            tap the board card's See on list handler (A5 revision, 2026-10-18);
//            everything in the cart (n = 0), ready, or planned with nothing to
//            shop for → teal "Cooked it" (the cook_meal path, one row, as the
//            board); cooked this load → "+ Plan again", the same handler as Add
//            (A5 revision: every pill acts; information lives in text). Never
//            Cooked it while items are still to buy, never for an unplanned
//            meal (D9a).
//   tags     while the meal is on This Week and not cooked only (A2 / A2-R,
//            2026-10-18; src/lib/ingredientTags.js): "{First} bought it" / "You
//            bought it" / "Bought" (this meal's link, status bought — archived
//            rows included — named from the latest checked event in the window,
//            nameless when solo or unrecorded) → "On the shopping list" (live
//            pending link) → "Already have it" (on_hand) → nothing. The
//            summary under INGREDIENTS: "{n} · {k} on the shopping list" /
//            "{n} · All here", k the board's own count.
//   cooks    one row per cook, newest first, DATE ONLY — meal_cooks.cooked_by
//            is the tapper, not the cook, and is never shown (D10); 5 rows then
//            "Show all {n}"; hidden when never cooked (D14)
// Every gated element (photos, stars, notes, names, as_written, Shared by)
// renders NOTHING until its data exists. Landing by door is slice (b); Cook
// mode is slice (c).
// RUM: .rv-edit / .rv-plan / .rv-remove / .rv-showall are allow-listed and carry fixed
// copy (Show all's count at most); .rv-see ("{n} left to buy") and .rv-status are not
// (Amendment B, R4). The title, byline, Added by, proof, ingredients and steps
// are household content: masked, never in an attribute — the dialog is named
// by aria-labelledby → #rv-title, never aria-label.
// Text roles (the 10-04 role table, re-mapped 2026-10-18 — the mockup's pixel
// sizes read a size too large on the phone): title = sheet title 22.4 at k 0.5;
// section headings = the Edit Meal sheet's .modal-label (12.5 uppercase, k 1);
// byline / Added by / meta / proof = meta 14 at k 1; ingredient rows, step text
// and cook rows = body 16 at k 1 on 44 px rows; Edit / the plan pill / Show all
// = button 14 at k 0.8; tags and the seam 12.5. Nothing under the 12.5 floor.
// Cook mode (slice c) steps the step text up one size from here.
const RV_COOKS_SHOWN = 5;
// A member's first name: the profile's full name, else the email's local part. With `members`, two
// members who share a first name read as first name + last initial ("Dan H", "Dan T") on every
// {First} surface (Dan, 2026-10-31) — fixed copy around household text, masked for replay.
const memberFirstName = (mb, members = null) => {
  const full = (mb?.users?.full_name || "").trim();
  const parts = full.split(/\s+/).filter(Boolean);
  const first = parts[0] || (mb?.users?.email ? mb.users.email.split("@")[0] : "");
  if (!first || !members || parts.length < 2) return first;
  const shared = members.some((o) => o && o !== mb && o.user_id !== mb?.user_id
    && ((o.users?.full_name || "").trim().split(/\s+/)[0] || "").toLowerCase() === first.toLowerCase());
  return shared ? `${first} ${parts[parts.length - 1][0].toUpperCase()}` : first;
};
// Cook dates come from src/lib/cookDate.js (Amendment A4): relative inside the
// last week, weekday + date beyond it. Never a person (D10).
// Your cooks shows only at 2+ cooks (A4): one cook is the meta line's job.
const RV_COOKS_MIN = 2;
// THE HERO STRIP (SPEC_meal_photos_v1, 2026-10-20 amendment R2 / R5; slice 1). With 1+ live
// photos the hero IS the strip — newest first (D2: the first is the card), swipeable
// (native scroll-snap), dots at 2+, object-fit cover. With none it is today's tint +
// occasion mark at 132 px, unchanged, and nothing invites a photo inside it (D8).
// PHOTO HERO (follow-on, Dan 2026-10-21; mockup "Recipe View Photo Hero" screens 2–4): with a
// photo the hero is 4:3, capped at 420 px tall on wide screens; each photo is a real
// <button> ("View photo full size", .rv-photo-open) with a small expand mark on the hero's
// corner, and opens PhotoViewer — the whole photo, uncropped, on a dark ground. Closing it
// returns to the strip at the photo the viewer ended on, focus on that photo's button.
// THE PHOTO MENU (SPEC_photo_menu + its 2026-10-25 amendment): the photo's actions live ON the
// photo — ⋯ in the hero's top-right corner (the viewer has the same one), acting on the photo in
// view: Replace photo, Photo details, Save to Photos / Download photo, a divider, Remove photo in
// red. Remove asks in a bottom sheet with the photo's thumbnail ("Remove this photo?" · Remove
// photo / Keep photo, Keep the gold default); nothing thanks a removal (never celebrate
// deleting). The credit line under the hero is gone — "Added by …" lives in Photo details. The
// row under the hero keeps only "Add a meal photo" (hidden at the cap of 3, R1). Removing the
// LAST photo leaves a dashed placeholder at the hero's height until the sheet closes; reopening
// shows the tint hero (D8 stands). Any member removes any photo (065 P3), so Remove shows to
// everyone who sees ⋯ — every signed-in member.
// Photos are gated on the household's photo read (the no-flash rule): until it resolves
// the hero is the tint and the controls are absent, so a cold open never shows the tint
// "Add" and then a photo, or a photo and then nothing.
// RUM (R5 + the amendment): .op-replay-block on every photo <img> — the confirm sheet's
// thumbnail included — and never the strip, dots or sheets. Allow-listed (fixed copy):
// .rv-photo-add, .rv-photo-replace, .rv-photo-details, .rv-photo-save, .rv-photo-remove,
// .rv-photo-confirm-remove, .rv-photo-confirm-keep, .rv-photo-placeholder. ⋯ (.rv-photo-more)
// is off the list, named by a fixed aria-label; the sheets' titles and lines are masked text,
// each sheet named by aria-labelledby; no meal name anywhere in an attribute.
// SHOWCASE PHOTOS — THE FLIP MODEL (SPEC_meal_sharing_v2 S46, Dan 2026-10-30; 072 kind + 073
// set_photo_kind). A photo is born a meal photo through the one "Add a meal photo" button (hidden
// at 3 live COOK photos, exactly as prod). Its AUTHOR flips it from the photo's ⋯: "Make showcase"
// (.rv-photo-make-showcase) opens the S13 prompt first ("Showcase photos can be seen by anyone…"),
// once per flip, and the prompt's own button (.rv-showcase-go) does the flip; on a showcase photo
// the item reads "Make it a meal photo" (.rv-photo-make-cook) and flips straight back — unless the
// meal already has 3 live cook photos, when a sheet says so in one plain line (.rv-photo-full-ok)
// and nothing is called (the server would refuse with 22023 anyway; it never ages a photo out).
// Both items show ONLY on the viewer's own photos (uploaded_by = viewerUserId); nobody can flip
// another member's photo. S14 (ruled 2026-10-30): Save to Photos / Download is gated by the same
// author test — the viewer saves their own photos only. A showcase slide is marked
// (.rv-showcase-tag, fixed copy, a sibling of the photo button); its ⋯ has no Replace (see
// replaceSteps). The strip's order is photosByMeal's: the S28 pick first.
function RecipePhotos({ meal, tint, first, members, photos, canEdit, busy, onAdd, onSetKind = null, onRemove, onReplace = null, viewerUserId = null }) {
  const [idx, setIdx] = useState(0);
  const [viewer, setViewer] = useState(null);   // null | the index the full-size viewer opened at
  const [sheet, setSheet] = useState(null);     // null | { kind: "remove" | "details" | "flip" | "full", photo }
  const [emptied, setEmptied] = useState(false); // the last photo was removed in THIS sheet (P7)
  const stripRef = useRef(null);
  const fileRef = useRef(null);
  const replaceFileRef = useRef(null);
  const replaceRef = useRef(null);   // the photo a Replace pick will stand in for
  const moreRef = useRef(null);      // the hero's ⋯ — focus comes back here (P10)
  const placeholderRef = useRef(null);
  const openRefs = useRef([]);       // each photo's "View photo full size" button, by index
  const refocusRef = useRef(null);   // the index to scroll to and focus after the viewer closes
  const saverRef = useRef(null);
  if (!saverRef.current) saverRef.current = createPhotoSaver();
  const [toPhotos] = useState(() => canSaveToPhotos());
  const live = photos.filter((p) => p.url);
  const n = live.length;
  const at = Math.min(idx, Math.max(0, n - 1));
  const shown = n > 0 ? live[at] : null;
  // A removal or a new upload changes the set: back to the newest, viewer and sheets closed.
  const setKey = live.map((p) => p.id).join(",");
  useEffect(() => { setIdx(0); setViewer(null); setSheet(null); if (stripRef.current) stripRef.current.scrollLeft = 0; }, [setKey]);
  // The viewer closed: the strip shows the photo it ended on, and focus returns to that
  // photo's button (the dialog's opener). preventScroll so the sheet does not jump.
  useEffect(() => {
    if (viewer !== null || refocusRef.current === null) return;
    const i = refocusRef.current;
    refocusRef.current = null;
    const el = stripRef.current;
    if (el) el.scrollLeft = i * el.clientWidth;
    const btn = openRefs.current[i];
    if (btn) btn.focus({ preventScroll: true });
  }, [viewer]);
  // A sheet closed (Keep, ✕, backdrop, swipe): focus back on ⋯. After Remove, again once the
  // read-back lands — on the next photo's ⋯, or on the placeholder when the last photo went.
  const hadSheet = useRef(false);
  const afterRemove = useRef(false);
  useEffect(() => {
    if (sheet) { hadSheet.current = true; return; }
    if (!hadSheet.current && !afterRemove.current) return;
    hadSheet.current = false;
    const to = moreRef.current || placeholderRef.current;
    if (to && document.contains(to)) to.focus({ preventScroll: true });
  }, [sheet]);
  useEffect(() => {
    if (!afterRemove.current) return;
    afterRemove.current = false;
    const to = moreRef.current || placeholderRef.current;
    if (to && document.contains(to)) to.focus({ preventScroll: true });
  }, [setKey]);
  const openViewer = (i) => setViewer(i);
  const closeViewer = (i) => { refocusRef.current = i; setIdx(i); setViewer(null); };
  // Desktop (a mouse cannot swipe): ‹ › and the dots step the strip. The state moves at once;
  // the scroll follows (smooth unless reduced motion) and onScroll agrees when it lands.
  const goTo = (i) => {
    const to = Math.max(0, Math.min(n - 1, i));
    scrollStripTo(stripRef.current, to);
    setIdx(to);
  };
  const onScroll = () => {
    const el = stripRef.current;
    if (!el || el.clientWidth === 0) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== at) setIdx(i);
  };
  const pick = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";   // the same file can be chosen again after a failure
    if (f && onAdd) onAdd(meal.id, f);
  };
  const pickReplace = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    const old = replaceRef.current;
    replaceRef.current = null;
    if (f && old && onReplace) onReplace(meal.id, old, f);
  };
  const addPhoto = () => { if (!busy && fileRef.current) fileRef.current.click(); };
  // The author test (S14, Save / Download only): the viewer's own photo and nobody else's. The FLIP is
  // no longer the author's (S48, 076): any member makes any live photo the showcase, or un-showcases it.
  const mine = (p) => !!p && !!viewerUserId && p.uploaded_by === viewerUserId;
  // A received row (it came with a gift, received_at set) can be un-showcased but never made the
  // showcase again — the server refuses too (22023).
  const received = (p) => !!p && !!p.received_at;
  // Another member's photo: name them at the top of its ⋯ (the "Added by" rule — 2+ members, the
  // uploader still one; otherwise no line).
  const ownerLineFor = (p) => {
    if (!p || mine(p) || (members || []).length < 2) return null;
    const m = (members || []).find((mb) => mb && mb.user_id && mb.user_id === p.uploaded_by);
    const name = m ? memberFirstName(m, members) : "";
    return name ? `Added by ${name}` : null;
  };
  // The flip itself, after the S13 prompt said yes (to showcase) or straight away (to cook).
  const flip = (p, kind) => { setSheet(null); if (p && onSetKind) onSetKind(meal.id, p.id, kind); };
  // The menu's actions, for the hero's photo in view or the viewer's (P8). The file picker opens
  // inside the tap (iOS needs the gesture); Remove, Details and the flips close the viewer first.
  const actions = {
    replace: onReplace ? (p) => { replaceRef.current = p; setViewer(null); if (replaceFileRef.current) replaceFileRef.current.click(); } : null,
    details: (p) => { setViewer(null); setSheet({ kind: "details", photo: p }); },
    save: (p) => { saverRef.current.save(p.url); },
    remove: (p) => { setViewer(null); setSheet({ kind: "remove", photo: p }); },
    prefetch: (p) => { if (p) saverRef.current.prefetch(p.url).promise.catch(() => {}); },
    makeShowcase: (p) => { setViewer(null); setSheet({ kind: "flip", photo: p }); },
    makeCook: (p) => { setViewer(null); if (atPhotoCap(live)) setSheet({ kind: "full", photo: p }); else flip(p, "cook"); },
  };
  const confirmRemove = () => {
    const p = sheet && sheet.photo;
    setSheet(null);
    if (!p || !onRemove) return;
    if (n === 1) setEmptied(true);
    afterRemove.current = true;
    onRemove(p.id);
  };
  const menuFor = (p, variant, closeKey) => (
    <PhotoMenu variant={variant} closeKey={closeKey} disabled={busy} triggerRef={variant === "hero" ? moreRef : undefined}
      saveLabel={saveLabel(toPhotos)} onOpen={() => { if (mine(p)) actions.prefetch(p); }}
      onReplace={actions.replace && !isShowcase(p) ? () => actions.replace(p) : null} onDetails={() => actions.details(p)}
      onMakeShowcase={onSetKind && !isShowcase(p) && !received(p) ? () => actions.makeShowcase(p) : null}
      onMakeCook={onSetKind && isShowcase(p) ? () => actions.makeCook(p) : null}
      onSave={mine(p) ? () => actions.save(p) : null} onRemove={() => actions.remove(p)} ownerLine={ownerLineFor(p)} />
  );
  const placeholder = canEdit && n === 0 && emptied;
  return (
    <>
      {n === 0 && !placeholder && (
        <div className="rv-hero" style={{ background: tint.bg, color: tint.ink }} aria-hidden="true">
          <OccasionIcon occasion={first} size={44} tint={tint.bg} />
        </div>
      )}
      {placeholder && (
        <div className="rv-hero-wrap">
          <button type="button" ref={placeholderRef} className="rv-photo-placeholder" disabled={busy} onClick={addPhoto}>
            <span className="rv-ph-title"><CameraGlyph />{busy ? "Adding…" : "Add a meal photo"}</span>
            <span className="rv-ph-sub">Show the household how it turned out.</span>
          </button>
        </div>
      )}
      {n > 0 && (
        <div className="rv-hero-wrap">
          <div className="rv-hero rv-strip" ref={stripRef} onScroll={onScroll} style={{ background: tint.bg }}>
            {live.map((p, i) => (
              <div className="rv-slide" key={p.id}>
                <button type="button" className="rv-photo-open" aria-label="View photo full size" ref={(el) => { openRefs.current[i] = el; }} onClick={() => openViewer(i)}>
                  <img className="rv-photo-img op-replay-block op-photo" src={p.url} alt="" draggable="false" onLoad={photoFadeIn} />
                </button>
                {isShowcase(p) && <span className="rv-showcase-tag">{p.received_at ? "Gift" : "Showcase"}</span>}
              </div>
            ))}
          </div>
          <span className="rv-hero-expand" aria-hidden="true"><ExpandGlyph /></span>
          {/* Siblings of the strip, never inside a photo's button: a click on ⋯ or an arrow never opens the viewer. */}
          {canEdit && menuFor(shown, "hero", `${setKey}#${at}`)}
          <PhotoArrows at={at} count={n} onGo={goTo} />
        </div>
      )}
      {viewer !== null && n > 0 && (
        <PhotoViewer photos={live} start={Math.min(viewer, n - 1)} members={members} viewerUserId={viewerUserId} onClose={closeViewer}
          menuFor={canEdit ? (p, k) => menuFor(p, "viewer", k) : null} />
      )}
      {n >= 2 && <PhotoDots photos={live} at={at} onGo={goTo} />}
      {canEdit && (
        <>
          {!atPhotoCap(live) && !placeholder && (
            <div className="rv-photo-ctl">
              <button type="button" className="rv-photo-add" disabled={busy} onClick={addPhoto}>
                <CameraGlyph />{busy ? "Adding…" : "Add a meal photo"}
              </button>
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={pick} />
          {onReplace && <input ref={replaceFileRef} type="file" accept="image/*" hidden onChange={pickReplace} />}
        </>
      )}
      {sheet && sheet.kind === "remove" && (
        <PhotoSheet labelledBy="rv-photo-q" onClose={() => setSheet(null)}>
          <div className="rv-psheet-head">
            <img className="rv-psheet-thumb op-replay-block op-photo" src={sheet.photo.url} alt="" draggable="false" />
            <h2 id="rv-photo-q" className="rv-psheet-h">Remove this photo?</h2>
          </div>
          <p className="rv-psheet-body">It’ll be removed for everyone in your household.</p>
          <div className="rv-psheet-btns">
            <button type="button" className="rv-photo-confirm-remove" onClick={confirmRemove}>Remove photo</button>
            <button type="button" className="rv-photo-confirm-keep" data-autofocus="true" onClick={() => setSheet(null)}>Keep photo</button>
          </div>
        </PhotoSheet>
      )}
      {sheet && sheet.kind === "details" && (
        <PhotoSheet labelledBy="rv-photo-dh" onClose={() => setSheet(null)}>
          <h2 id="rv-photo-dh" className="rv-psheet-h">Photo details</h2>
          {photoDetails(sheet.photo, members, (mb) => memberFirstName(mb, members)).map((line) => (
            <div className="rv-psheet-line" key={line}>{line}</div>
          ))}
        </PhotoSheet>
      )}
      {/* S13: the prompt, once per flip, before the photo goes showcase. Fixed copy; the meal is never named;
          the thumbnail is .op-replay-block like the Remove sheet's. */}
      {sheet && sheet.kind === "flip" && (
        <PhotoSheet labelledBy="rv-showcase-h" onClose={() => setSheet(null)}>
          <div className="rv-psheet-head">
            <img className="rv-psheet-thumb op-replay-block op-photo" src={sheet.photo.url} alt="" draggable="false" />
            <h2 id="rv-showcase-h" className="rv-psheet-h">Make this the meal’s showcase photo?</h2>
          </div>
          {/* S48's copy of record (Dan 2026-10-30): the meal has one showcase, the household's; a later change
              does not pull back copies already given. */}
          <p className="rv-psheet-body">Showcase photos can be shared outside the household. Once a meal is given, its photo goes with it.</p>
          <div className="rv-psheet-btns">
            <button type="button" className="rv-showcase-go" data-autofocus="true" onClick={() => flip(sheet.photo, "showcase")}>Make showcase</button>
            <button type="button" className="rv-showcase-cancel" onClick={() => setSheet(null)}>Not now</button>
          </div>
        </PhotoSheet>
      )}
      {/* The one plain line when a showcase photo cannot come back: the meal's 3 cook slots are taken (S26).
          Nothing was called; the server would refuse the same way and never ages a photo out. */}
      {sheet && sheet.kind === "full" && (
        <PhotoSheet labelledBy="rv-photo-full-h" onClose={() => setSheet(null)}>
          <h2 id="rv-photo-full-h" className="rv-psheet-h">Meal photos are full</h2>
          <p className="rv-psheet-body">This meal already has 3 meal photos. Remove one to make room for this one.</p>
          <div className="rv-psheet-btns">
            <button type="button" className="rv-photo-full-ok" data-autofocus="true" onClick={() => setSheet(null)}>OK</button>
          </div>
        </PhotoSheet>
      )}
    </>
  );
}
// ⋯ AND ITS MENU (SPEC_photo_menu P2 / P3 / P10) — one component for the hero and the viewer.
// 44 px target, a 30 px dark translucent circle like the expand mark. The menu closes on a tap
// anywhere outside it — that tap only dismisses, its click is swallowed so it never also presses
// what sat under the finger — on Escape (captured, so the viewer behind it stays open), and when
// the photo in view changes (closeKey). Escape and an outside tap return focus to ⋯.
// Each item closes the menu, then acts. The file is fetched for Save as the menu opens (onOpen).
// S46 (2026-10-30): "Make showcase" / "Make it a meal photo" sit after Replace, author-only (the caller
// passes null for anyone else's photo); Save is author-only too (S14) and absent when null. On another
// member's photo the menu opens with a greyed, inert "Added by {First}" line (ownerLine; Dan, 2026-10-30),
// so the shorter menu reads as "not yours" rather than broken. Masked text, not an item.
function PhotoMenu({ variant, closeKey, disabled, triggerRef, saveLabel: saveText, onOpen, onReplace, onMakeShowcase = null, onMakeCook = null, onDetails, onSave, onRemove, ownerLine = null }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const ownRef = useRef(null);
  const btnRef = triggerRef || ownRef;
  useEffect(() => { setOpen(false); }, [closeKey]);
  useEffect(() => {
    if (!open) return undefined;
    const back = () => { if (btnRef.current) btnRef.current.focus({ preventScroll: true }); };
    const onDown = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      swallowNextClick();
      setOpen(false);
      back();
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      back();
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, btnRef]);
  const act = (fn) => (e) => { e.stopPropagation(); setOpen(false); if (fn) fn(); };
  return (
    <span className={`rv-photo-menu ${variant}`} ref={wrapRef}>
      <button type="button" ref={btnRef} className="rv-photo-more" aria-label="Photo options" aria-haspopup="menu" aria-expanded={open} disabled={disabled}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => { if (!v && onOpen) onOpen(); return !v; }); }}>
        <span className="rv-photo-more-dot" aria-hidden="true"><MoreGlyph /></span>
      </button>
      {open && (
        <span className="rv-photo-pop" role="menu">
          {ownerLine && <span className="rv-photo-owner" role="presentation">{ownerLine}</span>}
          {onReplace && <button type="button" role="menuitem" className="rv-photo-item rv-photo-replace" onClick={act(onReplace)}>Replace photo</button>}
          {onMakeShowcase && <button type="button" role="menuitem" className="rv-photo-item rv-photo-make-showcase" onClick={act(onMakeShowcase)}>Make showcase</button>}
          {onMakeCook && <button type="button" role="menuitem" className="rv-photo-item rv-photo-make-cook" onClick={act(onMakeCook)}>Make it a meal photo</button>}
          <button type="button" role="menuitem" className="rv-photo-item rv-photo-details" onClick={act(onDetails)}>Photo details</button>
          {onSave && <button type="button" role="menuitem" className="rv-photo-item rv-photo-save" onClick={act(onSave)}>{saveText}</button>}
          <span className="rv-photo-div" role="separator" />
          <button type="button" role="menuitem" className="rv-photo-item rv-photo-remove" onClick={act(onRemove)}>Remove photo</button>
        </span>
      )}
    </span>
  );
}
const MoreGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5.5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="18.5" cy="12" r="2" /></svg>
);
// A SMALL SHEET OVER THE RECIPE VIEW (the Remove confirm, Photo details). Fixed inside the recipe
// view's scrim (z 1000), so it covers the same frame the recipe view does. ✕, the backdrop,
// swipe-down and Escape all close it — for Remove, all of them mean Keep (P5). Pointer events stop
// at its scrim so a swipe here never drags the recipe view down. Focus: the element marked
// data-autofocus (Keep), else ✕.
// closeDisabled (Commit 2, S34): while a gift is being made the ✕, the scrim, Escape and the swipe
// are all inert — the link is in nobody's hands yet and the sheet must stay until it is.
function PhotoSheet({ labelledBy, onClose, children, closeDisabled = false }) {
  const bodyRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = closeDisabled ? () => {} : onClose;
  const close = () => { if (!closeDisabled) onClose(); };
  // Once, on open (✕ took focus in its own effect first; Keep takes it from there).
  useEffect(() => {
    const el = bodyRef.current && bodyRef.current.querySelector("[data-autofocus]");
    if (el) el.focus({ preventScroll: true });
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); closeRef.current(); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const stop = (e) => e.stopPropagation();
  return (
    <div className="rv-psheet-scrim" onClick={(e) => { e.stopPropagation(); close(); }}
      onPointerDown={stop} onPointerMove={stop} onPointerUp={stop} onPointerCancel={stop}>
      <SheetPanel onClose={close} labelledBy={labelledBy} className="rv-psheet">
        <SheetClose onClose={close} disabled={closeDisabled} />
        <div className="lib-type-handle" aria-hidden="true" />
        <div ref={bodyRef} className="rv-psheet-in">{children}</div>
      </SheetPanel>
    </div>
  );
}
// ONE COOK in "Your cooks" (S5). The date, then one line per rater: their stars, their note beneath.
// In a household of 2+ current members each line carries the rater's first name — the viewer's own
// line reads "You" (Dan, 2026-10-21: two members can share a first name); solo, stars only.
// The viewer's own line is the editable CookRating (tap to change; the note edits in place). "How was it
// for you?" is offered only on the meal's newest cook, and only while the viewer hasn't rated it (`offer`,
// Dan 2026-10-21); it never moves to an older cook. Never a line about who didn't rate. Other members'
// stars are drawn, not buttons — nobody rates for anyone else (rate_cook has no user parameter). The
// count of stars is spoken through .sr-only text (masked), never an attribute. No averages (S7).
function CookRow({ cook, ratings, members, viewerUserId, canRate, onRate, offer = false }) {
  const multi = (members || []).length >= 2;
  const nameOf = (uid) => memberFirstName((members || []).find((mb) => mb.user_id === uid), members);
  const mine = viewerUserId ? ratings.find((r) => r.user_id === viewerUserId) : null;
  const others = ratings.filter((r) => r !== mine);
  return (
    <li className="rv-cook">
      <span className="rv-cook-date">{cookDateStart(cook.cooked_at)}</span>
      {others.map((r) => (
        <div className="rv-rater" key={r.user_id}>
          <div className="rv-rater-line">
            {multi && nameOf(r.user_id) && <span className="rv-rater-name">{nameOf(r.user_id)}</span>}
            {r.rating != null && <StaticStars n={r.rating} />}
          </div>
          {r.note && <div className="rv-rater-note">{r.note}</div>}
        </div>
      ))}
      {canRate && (mine || offer) && (
        <div className="rv-rater mine">
          <CookRating label={mine ? (multi ? "You" : "") : "How was it for you?"}
            rating={mine ? mine.rating : null} note={mine ? mine.note || "" : ""} onRate={(rt, nt) => onRate(cook.id, rt, nt)} openWhenRated={false} />
        </div>
      )}
      {!canRate && mine && (
        <div className="rv-rater">
          <div className="rv-rater-line">
            {multi && <span className="rv-rater-name">You</span>}
            {mine.rating != null && <StaticStars n={mine.rating} />}
          </div>
          {mine.note && <div className="rv-rater-note">{mine.note}</div>}
        </div>
      )}
    </li>
  );
}
const StaticStars = ({ n }) => (
  <span className="rv-stars">
    <span className="sr-only">{n} of 3</span>
    {[1, 2, 3].map((i) => <StarGlyph key={i} filled={i <= n} />)}
  </span>
);
// COOK RATING (SPEC_meal_photos_v1, 2026-10-21 amendment S1/S4/S5) — the afterglow's "How was it?" and
// the recipe view's "How was it for you?". Three star buttons (.rate-star, "Rate N of 3", 44 px); tapping
// the current star again clears it. The one-line note ("Note (optional)", 200 chars) appears only once a
// star has been tapped (or a rating / note already exists) and saves on blur or Enter — no Save button.
// Rating and note both empty → the caller's row is deleted server-side (no vote is not a rating). This
// is ALWAYS the viewer's own rating: rate_cook has no user parameter. No averages anywhere (S7).
// RUM: .rate-star is allow-listed (no text content, fixed aria-label); the note is an <input> (masked
// on prod by maskAllInputs) and never in an attribute we write; the label is masked text.
// openWhenRated: the afterglow opens the note as soon as there is a rating; "Your cooks" only when a
// note exists or a star was just tapped (no empty note field under every rated cook).
export function CookRating({ label, rating, note, onRate, busy = false, openWhenRated = true }) {
  const [opened, setOpened] = useState((openWhenRated && rating != null) || !!note);
  const [draft, setDraft] = useState(note || "");
  useEffect(() => { setDraft(note || ""); }, [note]);
  useEffect(() => { if ((openWhenRated && rating != null) || note) setOpened(true); }, [rating, note, openWhenRated]);
  const tapStar = (n) => {
    setOpened(true);
    onRate(n === rating ? null : n, draft.trim());
  };
  const saveNote = () => {
    const t = draft.trim();
    if (t !== (note || "")) onRate(rating == null ? null : rating, t);
  };
  return (
    <div className="rate-row">
      <span className="rate-label">{label}</span>
      <span className="rate-stars" role="group" aria-label="Rating">
        {[1, 2, 3].map((n) => (
          <button type="button" key={n} className={`rate-star${rating != null && n <= rating ? " on" : ""}`}
            aria-label={`Rate ${n} of 3`} aria-pressed={rating === n} disabled={busy} onClick={() => tapStar(n)}>
            <StarGlyph filled={rating != null && n <= rating} />
          </button>
        ))}
      </span>
      {opened && (
        <input className="rate-note" type="text" size={1} maxLength={200} placeholder="Note (optional)" aria-label="Note (optional)"
          value={draft} disabled={busy} onChange={(e) => setDraft(e.target.value)} onBlur={saveNote}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }} />
      )}
    </div>
  );
}
const StarGlyph = ({ filled }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3.5l2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 16.8l-5.2 2.7 1-5.8-4.2-4.1 5.8-.8z" />
  </svg>
);
// THE BOARD AFTERGLOW (S4; D6 with R6's copy). Under ✓ Cooked: "How was it?" and "Add a meal photo"
// (.board-photo-add). A photo added here is COOK-attached (D1; P1's per-cook replace), and a small
// thumbnail confirms "Photo added". At the cap of 3 the photo button is hidden (R1) — no photo is ever
// aged out silently. Every event stops here: the card is a role="button" that opens the recipe view on
// click / Enter / Space and arms a long-press drag on pointerdown, and none of that may fire from a
// star, the note or the photo button. Used by the board AND Home's on-deck card (Dan, 2026-10-21: the
// afterglow appears wherever Cooked it is tapped); on Home `showThumb` is off (P5 narrowed: Home captures
// a photo, never shows one).
function BoardAfterglow({ mealId, cookId, ag, showThumb = true }) {
  const fileRef = useRef(null);
  const mine = ag.myRating(cookId);
  const photo = ag.photoFor(mealId, cookId);
  const busy = ag.photoBusyMealId === mealId;
  const stop = (e) => e.stopPropagation();
  const pick = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (f) ag.onAddPhoto(mealId, f, cookId);
  };
  return (
    <div className="board-glow" onClick={stop} onPointerDown={stop} onPointerUp={stop} onPointerMove={stop} onKeyDown={stop} onContextMenu={stop}>
      <CookRating label="How was it?" rating={mine ? mine.rating : null} note={mine ? mine.note : ""} onRate={(r, n) => ag.onRate(cookId, r, n)} />
      {(photo || !ag.atCap(mealId)) && (
        <div className="board-glow-photo">
          {photo && (
            <span className="board-photo-done">
              {showThumb && photo.url && <img className="board-photo-thumb op-replay-block op-photo" src={photo.url} alt="" draggable="false" />}
              Photo added
            </span>
          )}
          {!ag.atCap(mealId) && (
            <button type="button" className="board-photo-add" disabled={busy} onClick={() => { if (!busy && fileRef.current) fileRef.current.click(); }}>
              <CameraGlyph />{busy ? "Adding…" : "Add a meal photo"}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={pick} />
        </div>
      )}
    </div>
  );
}
// THE FULL-SIZE VIEWER (Dan, 2026-10-21; mockup screens 3–4). A real modal dialog, portalled to
// <body> so no sheet transform or overflow clips it: a dark ground, the VIEW copy at
// object-fit: contain (uncropped — the same signed URL the strip already loaded, so no new
// download), × "Close photo" top left, "N of M" in the middle, ⋯ top right (SPEC_photo_menu P8), swipe between photos with the
// strip's dots and caption. Focus goes to × on open and Tab cycles ×, ⋯ and its menu (and ‹ › and the dots on desktop);
// Escape closes; ← / → step; closing the recipe view unmounts it. Pointer events stop at its
// root: React bubbles portal events through the React tree, and the SheetPanel's swipe-down
// handlers sit above it there. Under reduced motion, no fade.
// RUM: .op-replay-block on every viewer <img>; .rv-viewer-close joins the allow-list (fixed
// copy "×"); the count and the caption are household text and are NOT on it. The dialog is
// named by a fixed aria-label, never the meal.
function PhotoViewer({ photos, start, members, onClose, viewerUserId = null, menuFor = null }) {
  const [at, setAt] = useState(start);
  const atRef = useRef(start);
  const stripRef = useRef(null);
  const closeRef = useRef(null);
  const m = photos.length;
  useLayoutEffect(() => {
    const el = stripRef.current;
    if (el) el.scrollLeft = start * el.clientWidth;
    if (closeRef.current) closeRef.current.focus();
  // Once, on open: the start index and the first focus.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const go = (i) => {
    const to = Math.max(0, Math.min(m - 1, i));
    scrollStripTo(stripRef.current, to);
    atRef.current = to;
    setAt(to);
  };
  const rootRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(atRef.current); }
      else if (e.key === "Tab") {
        // Focus stays inside the dialog: × and, on desktop, ‹ › and the dots — cycled in order.
        e.preventDefault();
        const els = rootRef.current ? Array.from(rootRef.current.querySelectorAll("button")).filter((b) => b.offsetParent !== null || b === closeRef.current) : [];
        if (els.length === 0) return;
        const k = els.indexOf(document.activeElement);
        const next = e.shiftKey ? (k <= 0 ? els.length - 1 : k - 1) : (k + 1) % els.length;
        els[next].focus();
      }
      else if (e.key === "ArrowRight") { e.preventDefault(); go(atRef.current + 1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(atRef.current - 1); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  // go reads refs only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);
  const onScroll = () => {
    const el = stripRef.current;
    if (!el || el.clientWidth === 0) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== atRef.current) { atRef.current = i; setAt(i); }
  };
  const stop = (e) => e.stopPropagation();
  const caption = photoCaption(photos[at], members, (mb) => memberFirstName(mb, members), new Date(), viewerUserId);
  return createPortal(
    <div className="rv-viewer" role="dialog" aria-modal="true" aria-label="Photo" ref={rootRef}
      onPointerDown={stop} onPointerMove={stop} onPointerUp={stop} onPointerCancel={stop} onClick={stop}>
      <div className="rv-viewer-top">
        <button type="button" ref={closeRef} className="rv-viewer-close" aria-label="Close photo" onClick={() => onClose(atRef.current)}>×</button>
        <span className="rv-viewer-count">{at + 1} of {m}</span>
        {/* SPEC_photo_menu P8: the same ⋯ and menu, top-right, for the photo in view; it closes when the
            photo changes. Remove, Details and Replace close the viewer first. No trash icon anywhere. */}
        {menuFor ? menuFor(photos[at], at) : <span className="rv-viewer-spacer" aria-hidden="true" />}
      </div>
      <div className="rv-viewer-stage">
        <div className="rv-viewer-strip" ref={stripRef} onScroll={onScroll}>
          {photos.map((p) => (
            <div className="rv-viewer-slide" key={p.id}>
              <img className="rv-viewer-img op-replay-block op-photo" src={p.url} alt="" draggable="false" />
            </div>
          ))}
        </div>
        <PhotoArrows at={at} count={m} onGo={go} />
      </div>
      <div className="rv-viewer-foot">
        {m >= 2 && <PhotoDots photos={photos} at={at} onGo={go} className="rv-viewer-dots" />}
        {caption && <div className="rv-viewer-cap">{caption}</div>}
      </div>
    </div>,
    document.body,
  );
}
// DESKTOP STEPPING (Dan, 2026-10-21 walk: a mouse could not change photos with 2+). ‹ › sit over
// the strip and the viewer, only with 2+ photos, each hidden at its end, 44 px, fixed labels
// "Previous photo" / "Next photo". They and the dots' pointer are CSS-gated to
// (hover: hover) and (pointer: fine), so a phone shows no arrows and its dots stay the
// plain swipe indicator. The dots are buttons ("Show photo N", aria-current on the one in
// view). ← / → still step the viewer. RUM: .rv-photo-prev / .rv-photo-next / .rv-dot are
// allow-listed — no text content (aria-labels are fixed copy; "N" is a position, never content).
function scrollStripTo(el, i) {
  if (!el) return;
  const left = i * el.clientWidth;
  const smooth = typeof window !== "undefined" && window.matchMedia && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (typeof el.scrollTo === "function") el.scrollTo({ left, behavior: smooth ? "smooth" : "auto" });
  else el.scrollLeft = left;
}
function PhotoArrows({ at, count, onGo }) {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  // An arrow that steps to its end unmounts under the pointer; keyboard focus then moves to
  // the other arrow instead of falling back to <body>.
  const handOff = useRef(null);
  useEffect(() => {
    const to = handOff.current === "prev" ? prevRef.current : handOff.current === "next" ? nextRef.current : null;
    handOff.current = null;
    if (to) to.focus({ preventScroll: true });
  }, [at]);
  if (count < 2) return null;
  return (
    <>
      {at > 0 && (
        <button type="button" ref={prevRef} className="rv-photo-prev" aria-label="Previous photo"
          onClick={(e) => { e.stopPropagation(); if (at - 1 === 0) handOff.current = "next"; onGo(at - 1); }}><ChevronGlyph dir="left" /></button>
      )}
      {at < count - 1 && (
        <button type="button" ref={nextRef} className="rv-photo-next" aria-label="Next photo"
          onClick={(e) => { e.stopPropagation(); if (at + 1 === count - 1) handOff.current = "prev"; onGo(at + 1); }}><ChevronGlyph dir="right" /></button>
      )}
    </>
  );
}
function PhotoDots({ photos, at, onGo, className = "" }) {
  return (
    <div className={`rv-dots ${className}`.trim()}>
      {photos.map((p, i) => (
        <button type="button" key={p.id} className={i === at ? "rv-dot on" : "rv-dot"} aria-label={`Show photo ${i + 1}`} aria-current={i === at ? "true" : undefined}
          onClick={(e) => { e.stopPropagation(); onGo(i); }} />
      ))}
    </div>
  );
}
const ChevronGlyph = ({ dir }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={dir === "left" ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} />
  </svg>
);
const ExpandGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 4h6v6M10 20H4v-6M20 4l-6.5 6.5M4 20l6.5-6.5" /></svg>
);
// The outside tap that dismisses a photo popover must not also press what was under the
// finger. Its click arrives after pointerup (~100 ms later, after React has re-rendered), so
// the guard lives on its own: one capture listener that eats the NEXT click and removes
// itself, with a 1 s fallback for a tap that never becomes a click (a scroll, a long-press).
function swallowNextClick() {
  const eat = (e) => { e.preventDefault(); e.stopPropagation(); done(); };
  const timer = setTimeout(() => done(), 1000);
  function done() { clearTimeout(timer); document.removeEventListener("click", eat, true); }
  document.addEventListener("click", eat, true);
}
// The photo fades in once it has decoded (the tint shows behind it until then).
const photoFadeIn = (e) => e.currentTarget.classList.add("in");
const CameraGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 8h3l1.6-2.2h6.8L17 8h3v11H4z" /><circle cx="12" cy="13.2" r="3.4" /></svg>
);
// GIVE THIS MEAL — the header button (Dan 2026-10-30; supersedes Commit 2's ⋯ menu): the share icon (a
// square with an up arrow) beside Edit — .rv-give, aria-label "Give this meal", fixed copy, no meal name
// in the label, same size and weight as Edit. One tap opens the give sheet. "Stop giving" lives in the
// sheet now (its top line, while the meal has an unrevoked gift). Nothing on library cards.
const ShareGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8.5 9H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1.5" /><path d="M12 14V3" /><path d="M8.5 6.5 12 3l3.5 3.5" /></svg>
);

// GIVE THIS MEAL — the sheet (SPEC_meal_sharing_phase_a_client C1–C4; mockup_gift_preview B1–B5).
//   B1 / B2  the C1 pre-check: `photo` is the meal's live, non-received showcase row (mealShowcase, S48 —
//            the household's, whoever made it) or null. The client predicts; give_meal's pick decides;
//            B4 shows the truth. B1 names who made it the showcase (masked text). B2's nudge opens the
//            recipe view's strip (Dan, 2026-10-30) — the flip lives on the photo's own ⋯ (S46).
//   Give     (C3, first tap) give_meal, then with a photo the copy: view, then thumb, in the format
//            the photo was stored in (webp or jpeg, S47), no upsert, then attach_gift_photo marks
//            the gift (C4, give.copyPhoto). Any failure → B5's one plain line; no retry, no delete.
//            The ✕ is inert while this runs (S34: the link is in nobody's hands yet).
//   B4 / B5  "What they'll see" mirrors the public card (title as written, From · count); Send it is
//            THE share, navigator.share called inside that tap (C3); Copy link always present.
//   Replay   the thumbnails are .op-replay-block .op-photo; the meal name and the note are masked text /
//            a masked input; the gift code is never rendered — it lives in the share payload only.
//   Failure  give_meal's error shows as a line in the sheet (giveFailure), never a silent return.
//   Giving   (Dan 2026-10-30) while the meal has an unrevoked gift the sheet opens with one line at the top,
//            "You’re giving this meal · Stop giving"; Stop giving hands over to the existing confirm.
function GiveSheet({ meal, photo, members, onlyReceived = false, referralCode, give, onClose, onShowStrip, onStopGiving }) {
  // S48 / B1: "Made showcase by {First}." — the first name is household text (masked); the copy around it is fixed.
  const showcaser = photo && photo.showcased_by ? memberFirstName((members || []).find((mb) => mb && mb.user_id === photo.showcased_by), members) : "";
  const [note, setNote] = useState("");
  const [phase, setPhase] = useState("compose");   // compose | giving | copying | ready
  const [gift, setGift] = useState(null);           // { code, giftId, hasPhoto, photoFailed }
  const [line, setLine] = useState(null);           // give_meal's failure line, shown in B1/B2
  const noteValue = noteForGive(note);
  const tooLong = noteValue === false;
  const busy = giveCloseDisabled(phase);
  const close = () => { if (!busy) onClose(); };
  const doGive = async () => {
    if (busy || tooLong) return;
    setLine(null);
    setPhase("giving");
    const r = await give.give(meal.id, noteValue);
    if (!r || !r.ok) { setLine((r && r.line) || "Couldn’t give this meal. Try again."); setPhase("compose"); return; }
    let hasPhoto = false;
    let photoFailed = false;
    if (r.photoPath && r.thumbPath) {
      setPhase("copying");
      const c = await give.copyPhoto(r.giftId, r.photoPath, r.thumbPath);
      hasPhoto = !!(c && c.ok);
      photoFailed = !hasPhoto;
    }
    setGift({ code: r.code, giftId: r.giftId, hasPhoto, photoFailed });
    setPhase("ready");
    if (give.onGiven) give.onGiven(meal.id);
  };
  const payload = gift ? giftSharePayload({ mealName: meal.name, code: gift.code, origin: window.location.origin, referralCode }) : null;
  const thumb = (show) => (show && photo && photo.url
    ? <img className="give-thumb op-replay-block op-photo" src={photo.url} alt="" draggable="false" />
    : <span className="give-thumb give-thumb-empty" aria-hidden="true" />);
  return (
    <PhotoSheet labelledBy="give-k" onClose={close} closeDisabled={busy}>
      {(give.openGifts || 0) > 0 && (
        <div className="give-giving">You’re giving this meal · <button type="button" className="rv-stop-giving" disabled={busy} onClick={onStopGiving}>Stop giving</button></div>
      )}
      <div className="give-kicker" id="give-k">{phase === "ready" ? "Ready to give" : "Give this meal"}</div>
      <div className="give-name">{displayTitle(meal.name)}</div>
      {phase !== "ready" && (
        <div className="give-prow">
          <span className="give-thumb-wrap">{thumb(!!photo)}{photo && <span className="give-sc" aria-hidden="true">Showcase</span>}</span>
          <div className="give-ptext">
            {photo
              ? <><b>The meal’s showcase photo goes with it</b>{showcaser ? `Made showcase by ${showcaser}. ` : ""}Other photos stay at home.</>
              : <><b>No photo goes with it</b><button type="button" className="give-nudge" disabled={busy} onClick={onShowStrip}>{onlyReceived ? "Add a photo to send one along →" : "Make one of the photos a showcase →"}</button></>}
          </div>
        </div>
      )}
      {phase === "compose" && (
        <>
          <label className="give-flabel" htmlFor="give-note">Add a note <span>(optional)</span></label>
          <textarea id="give-note" className="give-ta" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            placeholder="Easton’s favourite. Don’t skip the pickle." />
          <div className={`give-count${tooLong ? " over" : ""}`}>{note.trim().length} / {NOTE_MAX}</div>
          {line && <div className="give-warn">{line}</div>}
          <button type="button" className="give-go" disabled={tooLong} onClick={doGive}>Give</button>
          <div className="give-fine">They get their own copy. Yours doesn’t change.</div>
        </>
      )}
      {(phase === "giving" || phase === "copying") && (
        <>
          <div className="give-steps" aria-live="polite">
            <div className={phase === "giving" ? "on" : ""}>
              <span className={phase === "giving" ? "dot" : "ok"} aria-hidden="true">{phase === "giving" ? "●" : "✓"}</span>
              {phase === "giving" ? "Making the gift…" : "Gift made"}
            </div>
            {phase === "copying" && <div className="on"><span className="dot" aria-hidden="true">●</span>Attaching your photo…</div>}
          </div>
          {noteValue && <div className="give-ta give-ta-filled">{noteValue}</div>}
          <button type="button" className="give-go" disabled><span className="give-spin" aria-hidden="true" />Getting it ready…</button>
          <div className="give-fine">A second or two.</div>
        </>
      )}
      {phase === "ready" && gift && (
        <>
          {gift.photoFailed && <div className="give-warn">The photo didn’t attach.</div>}
          <div className="give-mini-cap">What they’ll see</div>
          <div className="give-mini">
            {thumb(gift.hasPhoto)}
            <div>
              <div className="give-mini-t">{meal.name}</div>
              <div className="give-mini-m">{whatTheySee(meal)}</div>
            </div>
          </div>
          <button type="button" className="give-go give-send" onClick={() => give.share(payload)}>Send it</button>
          <button type="button" className="give-copy" onClick={() => give.copyLink(payload.url)}>Copy link</button>
          <div className="give-fine">Anyone with the link can add it. You can stop giving it any time from this sheet.</div>
        </>
      )}
    </PhotoSheet>
  );
}

// THE ARRIVAL THAT CANNOT OPEN THE FORM (Commit 3; 10-07 errors, in plain language): unknown or revoked code,
// not a member, or already added to this place — with Open it for the copy that is already here. C11 (Dan
// 2026-10-30): the giving place itself — "Already one of {Place}'s meals", Open it for the source meal while it
// still exists (mealId null = the line alone); no copy is ever made. G (b) (Dan 2026-11-01): "giving_deleted" —
// the source meal is gone (075's source_meal_id null): the Sorry line and OK, nothing else.
export function GiftArrivalSheet({ arrival, onOpen, onClose }) {
  const already = arrival.kind === "already";
  const giving = arrival.kind === "giving";
  const openable = already || (giving && !!arrival.mealId);
  return (
    <PhotoSheet labelledBy="gift-arrival-h" onClose={onClose}>
      <h2 id="gift-arrival-h" className="rv-psheet-h">{arrival.line}</h2>
      {already && <p className="rv-psheet-body">It’s here already, as your own copy.</p>}
      {giving && <p className="rv-psheet-body">This place gave it. Nothing to add.</p>}
      <div className="rv-psheet-btns">
        {openable && <button type="button" className="gift-open" data-autofocus="true" onClick={onOpen}>Open it</button>}
        <button type="button" className="gift-ok" data-autofocus={openable ? undefined : "true"} onClick={onClose}>OK</button>
      </div>
    </PhotoSheet>
  );
}

// THE THANK-YOU (S24): once, after the copy is theirs. The receiver's OWN share sheet; the app records nothing
// and tells the giver nothing. Skip = gone for good.
function ThankYouSheet({ ask, onSend, onSkip }) {
  return (
    <PhotoSheet labelledBy="thanks-h" onClose={onSkip}>
      <h2 id="thanks-h" className="rv-psheet-h">{ask}</h2>
      <p className="rv-psheet-body">Your own message, from your own share sheet. Nothing goes back through the app.</p>
      <div className="rv-psheet-btns">
        <button type="button" className="thanks-send" data-autofocus="true" onClick={onSend}>Send a thank you</button>
        <button type="button" className="thanks-skip" onClick={onSkip}>Skip</button>
      </div>
    </PhotoSheet>
  );
}

// STOP GIVING (10-07 V11): one confirm; revokes every live gift of the meal. Copies already added stay (V2).
function StopGivingSheet({ onConfirm, onClose }) {
  return (
    <PhotoSheet labelledBy="stop-giving-h" onClose={onClose}>
      <h2 id="stop-giving-h" className="rv-psheet-h">Stop giving this meal?</h2>
      <p className="rv-psheet-body">Anyone who hasn’t added it yet won’t be able to. Places that already added it keep their copy.</p>
      <div className="rv-psheet-btns">
        <button type="button" className="stop-giving-yes" onClick={onConfirm}>Stop giving</button>
        <button type="button" className="stop-giving-no" data-autofocus="true" onClick={onClose}>Keep giving</button>
      </div>
    </PhotoSheet>
  );
}

export function RecipeView({ meal, state, members, cookCount, lastCookedAt, cookRows, links, tagsReady, addedThisPlan, viewerUserId, busy, onClose, onEdit, onPlan, onAddToShop, onCooked, onRemove, onSeeList, photos, photosReady, canEditPhotos, photoBusy, onAddPhoto, onSetPhotoKind = null, onRemovePhoto, onReplacePhoto = null, ratingsFor, onRate, canRate, give = null }) {
  // Give this meal (Commit 2; the header share button since 2026-10-30): the sheet and the Stop giving
  // confirm, both over this view. Stop giving is reached from the sheet's top line; the sheet closes first.
  const [giveOpen, setGiveOpen] = useState(false);
  const [stopOpen, setStopOpen] = useState(false);
  const livePhotos = photosReady ? (photos || []) : [];
  const showcasePhoto = mealShowcase(livePhotos);   // S48: the household's, whoever made it
  // Dan 2026-10-31: when the meal's only photos came with a gift, B2 cannot point at a showcase that
  // cannot be made — the nudge reads "Add a photo to send one along" (the strip's Add a meal photo).
  const onlyReceived = livePhotos.length > 0 && livePhotos.every((p) => !!p.received_at);
  // B2's nudge: back to the strip, where the flip lives. The sheet closes (its ✕ hands focus back to the share button);
  // the view scrolls to its hero and the photo ⋯ (or Add a meal photo) takes focus a beat later.
  const showStrip = () => {
    setGiveOpen(false);
    const panel = document.querySelector(".rv-sheet");
    if (panel && panel.scrollTo) panel.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      const t = document.querySelector(".rv-photo-more, .rv-photo-add, .rv-photo-placeholder");
      if (t) t.focus({ preventScroll: true });
    }, 60);
  };
  const [allCooks, setAllCooks] = useState(false);
  const tint = occasionTint(meal);
  const first = (meal.occasion || [])[0];
  const occasionWord = first ? OCCASION_WORD[first] : null;
  const ingredients = meal.meal_ingredients || [];
  const parsed = useMemo(() => parseSteps(meal.instructions), [meal.instructions]);
  const attribution = (meal.attribution || "").trim();
  const adder = (members || []).length >= 2 ? (members || []).find((mb) => mb.user_id && mb.user_id === meal.created_by) : null;
  const adderName = adder ? memberFirstName(adder, members) : "";
  const onWeek = !!state && !state.cooked;
  // Tags and the summary's list state only once the cook reduce and this meal's links are in
  // (2026-10-18 correction) — before that the rows are bare and the heading is the count.
  const tagsOn = onWeek && !!tagsReady;
  // A2-R §3: solo = fewer than 2 current members (the earned-Our rule); a buyer is
  // named only through the members already loaded — a user who left names nobody.
  const solo = (members || []).length < 2;
  const firstNameOf = (userId) => memberFirstName((members || []).find((mb) => mb.user_id === userId), members);
  const stepCount = parsed.steps.length;
  const meta = [
    occasionWord,
    `${ingredients.length} ${ingredients.length === 1 ? "ingredient" : "ingredients"}`,
    stepCount > 0 ? `${stepCount} ${stepCount === 1 ? "step" : "steps"}` : null,
  ].filter(Boolean).join(" · ");
  const rows = cookRows || [];
  const shownCooks = allCooks ? rows : rows.slice(0, RV_COOKS_SHOWN);
  // S5: "Your cooks" from ONE cook when any cook carries a rating, a note or a photo (amends A4's 2+
  // for that case only) — or when the viewer has the newest cook still to rate (below).
  const cookRatings = (id) => (ratingsFor ? ratingsFor(id) : []).filter((r) => r.rating != null || (r.note && r.note.trim()));
  const cookHasPhoto = (id) => (photos || []).some((p) => p.meal_cook_id === id);
  // "How was it for you?" only on the meal's NEWEST cook, and only while the viewer hasn't rated it. It never
  // moves to an older cook (Dan, 2026-10-21). Rows are newest first.
  const newestCook = rows[0] || null;
  const offerCookId = newestCook && !(ratingsFor ? ratingsFor(newestCook.id) : []).some((x) => x.user_id === viewerUserId) ? newestCook.id : null;
  // The first-cook gap (Dan, 2026-10-21): a meal cooked once with nothing attached still shows "Your cooks" to
  // a member who can rate its newest cook and hasn't — so a cook made from the recipe view can be rated there.
  const offerOpen = !!canRate && !!onRate && !!offerCookId;
  const showCooks = rows.length >= RV_COOKS_MIN || offerOpen || rows.some((r) => cookRatings(r.id).length > 0 || cookHasPhoto(r.id));
  // THE ACTION BLOCK (Amendment B + Step 0 rulings, 2026-10-19; src/lib/recipePlan.js,
  // jest-proven): a status line that never acts, ONE main action, a quiet remove link.
  // Every action is the board's own handler (B3): Add to Shop = the board's Add to Shop
  // (handleAddMealToList, on-hand prompt included), Cooked it = handleCookedMeal, Remove =
  // the board's ⋯ → Remove (handleSkipMeal with the card's toBuy, so a To-buy meal's items
  // come off Shop as they do from the board). No toast on Remove, as on the board: the view
  // moving to "+ Add to This Week" / "+ Plan again" is the feedback and the undo (B4).
  // A meal with any cook reads "+ Plan again" off This Week, whether or not this load cooked it (A5).
  const block = recipeBlockFor(state, { cookedBefore: (cookCount || 0) > 0 || !!lastCookedAt });
  let plan;
  if (block.action === "add") {
    plan = <button type="button" className="rv-plan add" disabled={busy} onClick={() => { if (!busy) onPlan(meal.id); }}>+ Add to This Week</button>;
  } else if (block.action === "cooked") {
    // A5 revision: every pill acts. Cooked offers + Plan again through the same
    // handler as Add (planMeal reopens the closed placement); "Last cooked …" in
    // the meta line already says it was cooked.
    plan = <button type="button" className="rv-plan add" disabled={busy} onClick={() => { if (!busy) onPlan(meal.id); }}>+ Plan again</button>;
  } else if (block.action === "addToShop") {
    plan = <button type="button" className="rv-plan add" disabled={busy} onClick={() => { if (!busy) onAddToShop(meal.id); }}>Add to Shop</button>;
  } else if (block.action === "cookIt") {
    plan = <button type="button" className="rv-plan teal" disabled={busy} onClick={() => { if (!busy) onCooked(meal.id); }}>Cooked it</button>;
  } else if (block.action === "wrapUp") {
    // Amendment C1: everything is in the cart, the trip is not wrapped up. The view never
    // runs wrap-up itself — the nudge is the board card's own door to Shop (onSeeList →
    // goToDoor("list")), where the Wrap Up bar / All done card capture receipt, store and
    // timing as always. Fixed copy on the allow-listed .rv-plan (allow-list unchanged).
    plan = <button type="button" className="rv-plan wrap" onClick={onSeeList}>Wrap up →</button>;
  } else {
    // "{n} left to buy · See list →" — n is the board's own count (rc, one source); the tap
    // is the board card's See on list. Its own class, OFF the allow-list (R4): the count
    // never reaches click text, and the click event still names the button by class.
    // Two unbreakable halves in ONE inline wrapper (the button is flex, which would trim the
    // spaces around "·"): at XXL on 320 the line breaks at the "·", never before the arrow.
    plan = <button type="button" className="rv-see" onClick={onSeeList}><span><span className="rv-nw">{leftToBuy(state)} left to buy</span> · <span className="rv-nw">See list →</span></span></button>;
  }
  return (
    <div className="rv-scrim" onClick={onClose}>
      {/* The cap against the scrim, not the window (see .rv-sheet): SheetPanel's own calc(100dvh - 24px)
          overruns the desktop phone-frame by 24 px. Same 24 px top offset as every other full-height sheet. */}
      <SheetPanel onClose={onClose} labelledBy="rv-title" className="rv-sheet" style={{ maxHeight: "calc(100% - 24px)" }}>
        <SheetClose onClose={onClose} />
        <div className="lib-type-handle" aria-hidden="true" />
        {give && <button type="button" className="rv-give" aria-label="Give this meal" disabled={busy} onClick={() => setGiveOpen(true)}><ShareGlyph /></button>}
        <button type="button" className="rv-edit" onClick={() => onEdit(meal)}>Edit</button>
        <RecipePhotos meal={meal} tint={tint} first={first} members={members} viewerUserId={viewerUserId} photos={photosReady ? (photos || []) : []}
          canEdit={!!canEditPhotos && !!photosReady} busy={!!photoBusy} onAdd={onAddPhoto} onSetKind={onSetPhotoKind} onRemove={onRemovePhoto} onReplace={onReplacePhoto} />
        <div className="rv-head">
          <h2 id="rv-title" className="rv-title">{displayTitle(meal.name)}</h2>
          {attribution && <div className="rv-byline">from {attribution}</div>}
          {adderName && <div className="rv-added">Added by {adderName}</div>}
          <div className="rv-meta">{meta}</div>
          {/* A4: "Last cooked {date}", "· N times" at 2+; nothing when never cooked. */}
          {cookHistoryLine(lastCookedAt, cookCount) && <div className="rv-proof">{cookHistoryLine(lastCookedAt, cookCount)}</div>}
        </div>
        <div className="rv-plan-row">
          {/* Status is never a button (Amendment B): plain text, masked, no role, no tap. */}
          {block.onWeek && (
            <div className="rv-status">✓ On This Week{block.soft && <> <span className="rv-status-sep" aria-hidden="true">·</span> <span className="rv-status-soft">{block.soft}</span></>}</div>
          )}
          {plan}
          {block.remove && (
            <button type="button" className="rv-remove" disabled={busy} onClick={() => { if (!busy) onRemove(meal.id, !!state.toBuy); }}>{REMOVE_LABEL[block.remove]}</button>
          )}
        </div>

        <div className="rv-seam">Make it</div>
        {ingredients.length === 0 && stepCount === 0 && parsed.preamble.length === 0 && (
          <div className="rv-empty">No ingredients or steps yet.</div>
        )}
        {ingredients.length > 0 && (
          <>
            {/* A2: the summary replaces the bare count — "{n} · {k} on the shopping list" /
                "{n} · All here", k the board's own count so All here ⇔ Wrap up → (Amendment C: Cooked it waits for wrap-up). */}
            <h3 className="rv-sec">Ingredients <span>{ingredientSummary({ n: ingredients.length, onWeek: tagsOn, leftToBuy: leftToBuy(state), addedThisPlan })}</span></h3>
            <ul className="rv-ing">
              {ingredients.map((mi) => {
                const qty = Number(mi.quantity_per_serving) || 1;
                const name = mi.catalog_items?.name || "Unknown item";
                // A2 / A2-R: bought for this meal (archived at wrap-up; named only when the
                // trip recorded the checker; "You" for the viewer; nameless in a solo
                // household) → in the cart (checked on the open trip, not wrapped up — the
                // "cart vs bought" correction, same window and buyer) → on the shopping
                // list → already have it → nothing. The name is masked text.
                const tag = ingredientTag({
                  onWeek: tagsOn, onHand: !!mi.on_hand, link: links?.[mi.catalog_item_id] || null,
                  viewerUserId, solo, firstNameOf, addedThisPlan,
                });
                return (
                  <li key={mi.id || mi.catalog_item_id}>
                    <span className="rv-ing-name">{qty > 1 ? `${qty} × ` : ""}{name}</span>
                    {tag && <span className={`rv-tag ${tag.kind}`}>{tag.text}</span>}
                  </li>
                );
              })}
            </ul>
          </>
        )}
        {(stepCount > 0 || parsed.preamble.length > 0) && (
          <>
            <h3 className="rv-sec">Steps</h3>
            {/* The preamble: unnumbered lines before the first "1." (parseSteps) — today
                the import's "As written" block; plain lines, never step 1. */}
            {parsed.preamble.length > 0 && (
              <div className="rv-preamble">{parsed.preamble.map((line, i) => <p key={i}>{line}</p>)}</div>
            )}
            <ol className="rv-steps">
              {parsed.steps.map((s, i) => (
                <li key={i}>
                  {parsed.numbered && <span className="rv-step-n" aria-hidden="true">{i + 1}</span>}
                  <p>{s}</p>
                </li>
              ))}
            </ol>
          </>
        )}
        {showCooks && (
          <>
            <h3 className="rv-sec">Your cooks <span>{rows.length}</span></h3>
            <ul className="rv-cooks">
              {shownCooks.map((r) => (
                <CookRow key={r.id} cook={r} ratings={cookRatings(r.id)} members={members} viewerUserId={viewerUserId}
                  canRate={!!canRate && !!onRate} onRate={onRate} offer={r.id === offerCookId} />
              ))}
            </ul>
            {!allCooks && rows.length > RV_COOKS_SHOWN && (
              <button type="button" className="rv-showall" onClick={() => setAllCooks(true)}>Show all {rows.length}</button>
            )}
          </>
        )}
        <div className="rv-pad" />
        {giveOpen && give && (
          <GiveSheet meal={meal} photo={showcasePhoto} members={members} onlyReceived={onlyReceived} referralCode={give.referralCode || null} give={give}
            onClose={() => setGiveOpen(false)} onShowStrip={showStrip} onStopGiving={() => { setGiveOpen(false); setStopOpen(true); }} />
        )}
        {stopOpen && give && (
          <StopGivingSheet onConfirm={() => { setStopOpen(false); give.stopGiving(meal.id); }} onClose={() => setStopOpen(false)} />
        )}
      </SheetPanel>
    </div>
  );
}

function MealsLens({ meals, loading, onPlan, onUnplan, planningMealId, onCreate, onOpen, isSignedIn, stages, madeBefore, householdId, members, textSizeIdx, onSetOccasion }) {
  const [query, setQuery] = useState("");
  const [libView, toggleLibView] = useLibraryView(textSizeIdx);
  // The meal whose "Set meal type" sheet is open (Amendment A4/A5); null when closed.
  const [typeFor, setTypeFor] = useState(null);
  const canSetType = !!(isSignedIn && onSetOccasion);
  const [occ, setOcc] = useState("all");
  const [filters, setFilters] = useState(NO_LIB_FILTERS);
  // null while closed; the sheet's draft (a copy of `filters`) while open.
  const [draft, setDraft] = useState(null);

  // The rail shows only occasions some meal carries, in the fixed order. If the
  // selected one stops being present (last meal untagged or deleted), fall back
  // to All rather than filter to nothing.
  const present = useMemo(() => OCCASION_ORDER.filter((k) => meals.some((m) => (m.occasion || []).includes(k))), [meals]);
  const anyTagged = present.length > 0;
  useEffect(() => { if (occ !== "all" && !present.includes(occ)) setOcc("all"); }, [occ, present]);

  // From chips: current members with ≥ 1 library meal. First name from the
  // profile's full name (else the email's local part); the monogram is the
  // roster's sand circle — the app has no per-member colour, and the header's
  // teal monogram is the one colour this surface forbids.
  const fromMembers = useMemo(() => (members || [])
    .filter((mb) => mb.user_id && meals.some((m) => m.created_by === mb.user_id))
    .map((mb) => {
      const full = (mb.users?.full_name || "").trim();
      const firstName = full.split(/\s+/)[0] || (mb.users?.email ? mb.users.email.split("@")[0] : "Member");
      return { id: mb.user_id, firstName, initial: firstName[0].toUpperCase() };
    }), [members, meals]);
  // An applied From whose member left (or whose meals are gone) clears itself.
  useEffect(() => {
    if (filters.from && !fromMembers.some((mb) => mb.id === filters.from)) setFilters((f) => ({ ...f, from: null }));
  }, [filters.from, fromMembers]);

  const q = query.trim().toLowerCase();
  const passes = useCallback((m, f, o) => (o === "all" || (m.occasion || []).includes(o))
    && matchesQuery(m, q)
    && (!f.from || m.created_by === f.from)
    && (!f.made || !!madeBefore?.has(m.id))
    && (!f.ours || !m.household_id || m.household_id === householdId), [q, madeBefore, householdId]);
  const shown = useMemo(() => meals.filter((m) => passes(m, filters, occ)), [meals, filters, occ, passes]);
  const countFor = (f) => meals.filter((m) => passes(m, f, occ)).length;
  const activeCount = (filters.from ? 1 : 0) + (filters.made ? 1 : 0) + (filters.ours ? 1 : 0);
  const fromMember = filters.from ? fromMembers.find((mb) => mb.id === filters.from) : null;

  // The Create tile is a COACHING surface (Dan, 2026-10-03): it shows only
  // while the household's whole library — `meals` here is every live
  // kind='meal' row, never the filtered or searched result — has fewer than
  // LIBRARY_COACH_MAX meals. From there the head-row + Create and the Helm's
  // + are the doors. Derived on every render; no stored flag. Rendered in the
  // empty state too: a household with no meals could otherwise never make its
  // first one. Signed out it is a sign-in prompt, not a live control (every
  // identity-requiring write silently no-ops without a Clerk token); the whole
  // Plan view sits behind the signed-out welcome since 2026-10-02, so that
  // branch is kept as belt-and-braces, not walked.
  const coaching = meals.length < LIBRARY_COACH_MAX;
  const createTile = !coaching ? null : isSignedIn ? (
    <div className="lib-create-tile">
      <div className="lib-create-text">
        <div className="lib-create-t">Something new?</div>
        <div className="lib-create-s">Build your own, or let the Galley help.</div>
      </div>
      <button type="button" className="lib-create outline" aria-label="Create a new meal" onClick={onCreate}><PlusGlyph />New meal</button>
    </div>
  ) : (
    <div className="lib-create-tile gated">
      <div className="lib-create-text">
        <div className="lib-create-t">Sign in to create meals</div>
        <div className="lib-create-s">Meals are saved to your place, so they need your account.</div>
      </div>
    </div>
  );

  if (loading && meals.length === 0) {
    return (
      <div style={{ padding: "40px 20px", textAlign: "center", color: "#8a7a60",
        fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: ts(16.8, 0.5) }}>
        Loading meals…
      </div>
    );
  }
  if (meals.length === 0) {
    return (
      <div style={{ padding: "32px 0 0" }}>
        <div style={{ textAlign: "center" }}>
          <p style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic",
            fontSize: ts(19.2, 0.5), color: "#8a7a60", margin: 0 }}>No meals yet.</p>
          <p style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#8A5F3A",
            marginTop: "8px", letterSpacing: "0.5px" }}>
            Create one, plan it, and its ingredients are one tap from your list.
          </p>
        </div>
        <div style={{ marginTop: "6px" }}>{createTile}</div>
      </div>
    );
  }

  // Made before alone emptying the grid keeps its own line; everything else
  // is "Nothing matches that yet."
  const madeAlone = filters.made && !filters.from && !filters.ours && !q && occ === "all";
  return (
    <div className="lib">
      {/* The fixed VERB half of every aria-labelledby name on this screen, rendered once, outside
          every button: "Plan {meal}", "Remove from this week", "Set meal type for {meal}",
          "Open {meal}", "Remove filter: {name}'s". The other half is the masked title element
          (id meal-name-{uuid} / lib-from-chip-name). Closes the v1 .lib-plan replay gap:
          no attribute carries a meal or member name. */}
      <span id="lib-verb-plan" className="sr-only">Plan</span>
      <span id="lib-verb-unplan" className="sr-only">Remove from this week</span>
      <span id="lib-verb-settype" className="sr-only">Set meal type for</span>
      <span id="lib-verb-open" className="sr-only">Open</span>
      <span id="lib-verb-unfilter" className="sr-only">Remove filter:</span>
      <div className="lib-search-row">
        <label className="lib-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your meals…"
            aria-label="Search your meals"
          />
        </label>
        <button
          type="button"
          className="lib-fbtn"
          aria-label={activeCount ? `Filter meals, ${activeCount} active` : "Filter meals"}
          onClick={() => setDraft({ ...filters })}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true"><path d="M4 5h16l-6 7.5V19l-4 1.5v-8z" /></svg>
          {activeCount > 0 && <span className="lib-fdot" aria-hidden="true" />}
        </button>
        {/* ONE toggle (D8): it shows the view you'd switch TO and names the action. */}
        <button
          type="button"
          className="lib-vtog"
          aria-label={TOGGLE_LABEL[libView]}
          onClick={toggleLibView}
        >
          {libView === "list" ? <GridGlyph /> : <ListGlyph />}
        </button>
      </div>

      {/* Active filters are always visible (decision 10): a filtered grid must never read as a short library. */}
      {activeCount > 0 && (
        <div className="lib-active">
          {fromMember && (
            <button type="button" className="lib-chip-x" aria-labelledby="lib-verb-unfilter lib-from-chip-name" onClick={() => setFilters((f) => ({ ...f, from: null }))}>
              <span className="lib-mini" aria-hidden="true">{fromMember.initial}</span><span id="lib-from-chip-name">{fromMember.firstName}'s</span> <span aria-hidden="true">×</span>
            </button>
          )}
          {filters.made && (
            <button type="button" className="lib-chip-x" aria-label="Remove filter: Made before" onClick={() => setFilters((f) => ({ ...f, made: false }))}>Made before <span aria-hidden="true">×</span></button>
          )}
          {filters.ours && (
            <button type="button" className="lib-chip-x" aria-label="Remove filter: Ours" onClick={() => setFilters((f) => ({ ...f, ours: false }))}>Ours <span aria-hidden="true">×</span></button>
          )}
        </div>
      )}

      {anyTagged
        ? <OccasionRail present={present} value={occ} onChange={setOcc} />
        : <div className="lib-hint">Sort meals by when you eat them — open any meal to mark it Dinner, Lunch…</div>}

      {shown.length === 0 && (
        <div className="lib-empty">
          {madeAlone ? "Nothing cooked from This Week yet — Cooked it fills this." : "Nothing matches that yet."}
        </div>
      )}
      <div className={libView === "list" ? "lib-list" : "lib-grid"}>
        {shown.map((m) => (
          <LibraryItem
            kind={libView}
            key={m.id}
            onSetType={canSetType ? setTypeFor : null}
            meal={m}
            stage={stages?.[m.id] || "none"}
            busy={planningMealId === m.id}
            canPlan={!!(isSignedIn && onPlan)}
            onPlan={onPlan}
            onUnplan={onUnplan}
            onOpen={(meal) => onOpen && onOpen(meal)}
          />
        ))}
      </div>
      {createTile}

      {typeFor && (
        <SetTypeSheet
          meal={typeFor}
          onSave={(occ) => onSetOccasion(typeFor.id, occ)}
          onClose={() => setTypeFor(null)}
        />
      )}

      {draft && (
        <LibraryFilterSheet
          draft={draft}
          setDraft={setDraft}
          fromMembers={fromMembers}
          countFor={countFor}
          onShow={() => { setFilters(draft); setDraft(null); }}
          onClose={() => setDraft(null)}
        />
      )}
    </div>
  );
}

// Ask the Galley — the card's read-hint copy (IMPORT_HINTS) lives in src/lib/importErrors.js
// with the error classifier the hook uses, so the two cannot drift apart; the box's
// cap (TEXT_CAP) and every mode threshold live in src/lib/galleyMode.js.

// A Blob → the base64 the function wants in data_base64 (no data: prefix).
const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || "").replace(/^data:[^,]*,/, ""));
  reader.onerror = () => reject(reader.error || new Error("Could not read image"));
  reader.readAsDataURL(blob);
});

// Galley glyph — the eyebrow on the recipe card and the header of the ask block.
const GalleyGlyph = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" aria-hidden="true">
    <path d="M12 3v3M12 18v3M4.2 6.2l2.1 2.1M17.7 15.7l2.1 2.1M3 12h3M18 12h3M4.2 17.8l2.1-2.1M17.7 8.3l2.1-2.1" />
  </svg>
);

// GOOD FOR — the meal sheet's occasion picker (Meal Library v1 decision 13).
// Seven chips in a fixed order, multi-select, STORED IN TAP ORDER: the first
// picked carries ON CARD and is the word the library card shows; deselecting
// it promotes the next. Flexible discovery tags, not a classification —
// "choose any that fit". Espresso on-state, no teal (planning isn't finishing).
function GoodForChips({ value, onChange, style }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="modal-field" style={style}>
      <label className="modal-label">Good for</label>
      <div className="gf-chips" role="group" aria-label="Good for">
        {OCCASION_ORDER.map((k) => {
          const on = value.includes(k);
          return (
            <button key={k} type="button" className={`gf-chip${on ? " on" : ""}`} aria-pressed={on} onClick={() => toggle(k)}>
              {OCCASION_WORD[k]}
              {on && value[0] === k && <span className="gf-oncard">On card</span>}
            </button>
          );
        })}
      </div>
      <div className="gf-help">Choose any that fit. The first appears on the card.</div>
    </div>
  );
}

// One parameterized sheet for BOTH create and edit (mode: 'create' | 'edit').
// Built as one component from the start rather than retrofitting edit onto a
// create-only sheet later — the seam is nearly free now and expensive after.
//
// The whole draft lives in local state: nothing touches `meals`,
// `meal_ingredients` OR `catalog_items` until Save — no exceptions. A brand-new
// ingredient (no-results panel, or an AI draft with no catalog match) is staged
// under a client-only pending id and only becomes a real catalog row when the
// meal is committed. Until 2026-09-08 that was the one exception: the row was
// written the moment a category pill was tapped so the draft could hold a real
// id, and every Cancel left it behind as an orphan. Staged rows can now carry a
// placeholder, so the write waits for Save (SPEC_defer_catalog_write).
// gift (Phase A Commit 3; the 10-07 prefilled form): { banner, note: { quote, by } | null, placeLine, draft } — a
// received meal lands through landDraft like any outside draft (source "gift"), under a banner and the
// giver's note (not editable, never saved on the meal). The doors stay folded, Cancel reads "Not for us"
// and Save reads "Add to our meals". The receipt and the photo copy run in the caller after the save.
export function MealSheet({ mode, meal, catalogMap, categories, saving, deleting, onCancel, onCommit, onDelete, onCreateCatalogItem, onRegisterCategory, onRequestSuggestion, onRequestImport, isSignedIn, gift = null }) {
  const isEdit = mode === "edit";
  const [name, setName] = useState(isEdit ? (meal?.name || "") : "");
  const [rows, setRows] = useState(() =>
    isEdit
      ? (meal?.meal_ingredients || []).map((mi) => ({
          catalog_item_id: mi.catalog_item_id,
          name: mi.catalog_items?.name || "Unknown item",
          quantity_per_serving: Number(mi.quantity_per_serving) || 1,
          on_hand: !!mi.on_hand,
        }))
      : []
  );
  const [query, setQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  // "+ New category" is the one tile in the grid that is NOT a commit — it
  // reveals an input instead. Same two-step as Browse's live panel.
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatInput, setNewCatInput] = useState("");

  // Steps (migration 043 — the column is still `instructions`; only the label
  // changed). ALWAYS rendered now: 043 said the column was never AI-only, but
  // until 2026-09-09 the field only appeared once it had content, so a manual meal
  // had no way to get steps at all. Edit mode loads whatever is already stored.
  const [instructions, setInstructions] = useState(isEdit ? (meal?.instructions || "") : "");
  // Good for (061) — the ordered occasion list. Edit loads what is stored
  // (normalised: the client never trusts an array it did not write); create
  // starts untagged. Galley drafts arrive untagged in v1 and are tagged here.
  const [occasion, setOccasion] = useState(() => (isEdit ? normalizeOccasion(meal?.occasion) : []));
  // From (062 `meals.attribution`, SPEC_recipe_import D6): free text, editable,
  // never validated; '' saves NULL. The FIELD shows only when a draft carried an
  // attribution or the meal already has one — a hand-built meal never shows it.
  const [attribution, setAttribution] = useState(isEdit ? (meal?.attribution || "") : "");
  const [fromVisible, setFromVisible] = useState(isEdit && !!(meal?.attribution || "").trim());
  // The reader could not find a serving count and the function substituted 4
  // (servingsAssumed) — shown as an advisory chip, since "flag, don't guess"
  // applies to servings too (ruling 2026-10-09). Session-only.
  const [servingsAssumed, setServingsAssumed] = useState(false);
  // What the sheet opened with — the baseline `isDirty` compares against. Lazy
  // initialiser: captured on the first render, when the states above still
  // hold their initial values, and never updated afterwards.
  const [openedWith] = useState(() => ({ name, instructions, rows, occasion, attribution }));
  // Read renders the recipe card (or, with nothing stored, the empty chip that
  // points at both paths); edit is the textarea + Done. Opens in read — the card
  // for a meal that has steps, the chip for one that does not — and returns to
  // read after a Galley result lands. The mockup's state 1 is the chip WITH the
  // Galley live, which is why "empty" is not "edit": edit dims the Galley, and a
  // brand-new meal must be able to ask it.
  const [stepsMode, setStepsMode] = useState("read");
  // Draft provenance — where the fields were filled from: null (typed by hand),
  // "galley", "photo" or "paste" (SPEC_recipe_import: the old fromGalley flag,
  // generalised to a source value so the banner and the Steps eyebrow can say
  // where it came from). Session-only, not persisted (Galley Phase B question):
  // set when a draft lands, cleared on Never mind / Start over or when every
  // field is emptied. A hand-edit of the steps does NOT clear it — provenance is
  // per meal, not per edit.
  const [draftSource, setDraftSource] = useState(null);
  // Once a draft lands the ask block collapses to one link; the "or" divider
  // between a finished recipe and "ask AI to build it" made no sense. The link
  // re-expands it with the request text intact.
  const [galleyCollapsed, setGalleyCollapsed] = useState(false);

  // AI request state. `aiBusy` both blocks a second submit (the only cost control
  // that exists today — see the spec's open rate-limiting item) and drives the dim.
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  // The in-flight request's controller (Never mind aborts it) and the field values
  // from before it started, restored on abort. Nothing is written before the draft
  // arrives, so the snapshot is defence against the narrow window where the fetch
  // has settled and the ingredient loop is still running when abort is pressed.
  const aiAbortRef = useRef(null);
  const aiSnapshotRef = useRef(null);
  const aiCardRef = useRef(null);   // the thinking card, scrolled into view on Ask (either door)
  const galleyCardRef = useRef(null);   // the Galley card: "inside the Galley" for its focus state (galleyFocused)

  // Reading a recipe (SPEC_recipe_import chunk 2, now inside the one Galley card —
  // SPEC_galley_composer). The text box is `aiText` above, shared by every mode;
  // `galleyHint` is the card's one hint line — the reader's decline reasons and its
  // two clean failures land HERE, never as a toast, because the next move (sharper
  // screenshot, fewer of them, try again) is in this card and the staged input is
  // kept for it. `importBusy` is the in-flight read, { kind: "paste" } or
  // { kind: "photo", count }: its own flag beside aiBusy so the thinking card can
  // name what it is reading, with `busy` below for everything that only cares that
  // a draft is on its way.
  const [galleyHint, setGalleyHint] = useState("");
  // "Want ideas instead?" (G6): recipe-shaped text flipped to Invent. Sticks until
  // the text is cleared — by the ×, by deleting it, by a landed draft — so a paste
  // that was overridden does not snap back to Read on the next keystroke.
  const [ideasOverride, setIdeasOverride] = useState(false);
  // About the Galley (G9): the one honest touchpoint — a quiet link on the card
  // opens one inline sentence. Hide the jargon, never the fact: this is the only
  // place the word "AI" appears in the app.
  const [aboutOpen, setAboutOpen] = useState(false);
  useEffect(() => { if (!aiText.trim()) setIdeasOverride(false); }, [aiText]);
  // Staged screenshots, ALREADY downscaled at pick time (longest edge 1600 px, JPEG
  // q0.8 — src/lib/image.js, the OurBanner pipeline): [{ id, blob, url, bytes,
  // originalBytes }]. Downscaling at the pick rather than at Read these means the
  // row holds the real payload and a HEIC the browser cannot decode is caught the
  // moment it is chosen. Object URLs are revoked on remove / Clear / unmount.
  const [photos, setPhotos] = useState([]);
  const photosRef = useRef([]);
  const photoInputRef = useRef(null);
  const photoIdRef = useRef(0);
  const [importBusy, setImportBusy] = useState(null);
  const importAbortRef = useRef(null);
  const importSnapshotRef = useRef(null);
  // The fields as they were before an import landed, kept for Start over (the
  // banner's one way back): normally the empty form. Cleared when Start over runs.
  const startOverRef = useRef(null);
  // A draft is on its way through either door: the manual fields dim and turn to
  // skeletons, search steps aside, both doors hide behind the thinking card.
  const busy = aiBusy || !!importBusy;

  // Voice. `micBlocked` is sticky for the life of the sheet: a denied permission does
  // not resolve itself, so re-offering the button would just reproduce the same refusal.
  // `micHint` overrides the hint line — errors surface THERE, never as a toast and never
  // with a raw error code, because "no-speech" means nothing to the person holding it.
  // The box opens at three rows. Where the placeholder needs more than that (XXL
  // on a 320 px phone — the 2026-10-11 residual, measured rather than guessed) the
  // EMPTY box grows a row at a time until its placeholder fits, up to five. Typed
  // text scrolls as any textarea does; only the placeholder is ever clipped-for-good.
  const [taRows, setTaRows] = useState(3);
  const taRef = useRef(null);
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el || aiText.length > 0 || taRows >= 5) return;
    if (el.scrollHeight > el.clientHeight + 1) setTaRows(taRows + 1);
  }, [aiText, taRows]);
  const [listening, setListening] = useState(false);
  const [micBlocked, setMicBlocked] = useState(false);
  const [micHint, setMicHint] = useState("");
  const recognitionRef = useRef(null);
  // The field's contents at the moment listening started. Re-read on every result rather
  // than appending incrementally: interim results REPLACE earlier interim text, so
  // concatenating as they arrive would stack half-heard fragments on top of each other.
  const aiTextBaseRef = useRef("");

  const trimmedQuery = query.trim();

  // Empty until something is typed — the placeholder already implies typing,
  // so showing results against an empty box contradicts its own copy.
  // Staged items REMAIN in the results, marked "Added" (2026-09-01). Filtering them
  // out meant searching for something you had already staged returned nothing, which
  // reads as "we don't have that" — the one message that is definitely wrong, since
  // it is sitting in the list above. Tapping an already-staged row bumps its quantity,
  // so the obvious gesture does the obvious thing instead of silently no-oping.
  // Pending rows (staged, not yet written) are not in catalogMap, so they are
  // folded in here — otherwise re-typing a name you just staged would fall through
  // to the no-results panel, the exact wrong message the note above rules out.
  const pendingStaged = useMemo(() => rows
    .filter((r) => isPendingCatalogId(r.catalog_item_id))
    .map((r) => ({ id: r.catalog_item_id, name: r.name, category: r.category })), [rows]);
  const results = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (!q) return [];
    return [...Object.values(catalogMap), ...pendingStaged]
      .filter((it) => it && it.id && (it.name || "").toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 8);
  }, [trimmedQuery, catalogMap, pendingStaged]);

  const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
  const exactExists = trimmedQuery
    && [...Object.values(catalogMap), ...pendingStaged].some((it) => norm(it.name) === norm(trimmedQuery));
  const showNoResults = !!trimmedQuery && results.length === 0 && !exactExists;

  // `isNew` marks an item this sheet just created (no-results panel, or an AI draft
  // that had no catalog match) so the staged row can show which category it was filed
  // into. Existing catalog items don't carry the tag — the person already knows where
  // those live, and tagging every row turns a useful signal into wallpaper.
  const stageItem = (item, { isNew = false } = {}) => {
    if (!item || !item.id) return;
    setRows((prev) => prev.some((r) => r.catalog_item_id === item.id)
      // Already staged → bump, don't no-op. Same clamp as the stepper.
      ? prev.map((r) => r.catalog_item_id === item.id
          ? { ...r, quantity_per_serving: Math.max(1, r.quantity_per_serving + 1) }
          : r)
      : [...prev, {
          catalog_item_id: item.id,
          name: item.name,
          quantity_per_serving: 1,
          category: item.category || null,
          isNew,
        }]);
    setQuery("");
    setPickerOpen(false);
    setNewCatOpen(false);
    setNewCatInput("");
  };

  // Tapping a category commits create-and-add in ONE motion — no separate
  // confirm button, matching Browse's live no-results panel exactly.
  const createAndStage = async (rawCategory) => {
    if (adding) return;
    setAdding(true);
    try {
      const item = await onCreateCatalogItem(trimmedQuery, rawCategory);
      if (item) stageItem(item, { isNew: true });
    } finally {
      setAdding(false);
    }
  };

  // Submitting the new-category field lands in the SAME place a category tap
  // does — createCatalogItem with the typed string, then stage. Registering the
  // name mirrors Browse; it is local state only (no DB write), so a cancelled
  // draft leaves nothing behind but a category name in this session's picker.
  const commitNewCategory = () => {
    const newCat = newCatInput.trim();
    if (!newCat || adding) return;
    if (onRegisterCategory) onRegisterCategory(newCat);
    createAndStage(newCat);
  };

  // Any edit to a row clears its "Check this" chip — the person has looked.
  const setQty = (id, next) => {
    setRows((prev) => prev.map((r) =>
      r.catalog_item_id === id ? { ...r, quantity_per_serving: Math.max(1, next), check: null } : r));
  };

  // ── Three states, three controls (SPEC_meal_ondhand_ingredients.md) ────────
  //   normal   → on-hand : "I have this"
  //   on-hand  → normal  : "Need it"
  //   either   → removed : ×
  //
  // ⚠️ DO NOT reattach shelving to the stepper's "−" at quantity 1. That was the
  // original build and it was WRONG — found in live testing 2026-09-06. The floor
  // looked like a free gesture (at 1, "−" previously did nothing), but reaching the
  // floor is not free: an ingredient at quantity 2 could only be shelved by first
  // decrementing 2→1, and that decrement is a REAL edit. The row then shelved at 1,
  // and the spec's whole point — preserve the real recipe quantity so coming back is
  // one tap and not a re-entry — was silently destroyed by the only path to the
  // trigger. The column and the RPC were right the entire time; the interaction was
  // what lost the number.
  //
  // Shelving therefore captures whatever is showing AT THE MOMENT OF THE TAP, and
  // the stepper is plain arithmetic again at every value, 1 included.
  const shelve = (id) => {
    setRows((prev) => prev.map((r) =>
      r.catalog_item_id === id ? { ...r, on_hand: true, check: null } : r));
  };
  const needAgain = (id) => {
    setRows((prev) => prev.map((r) =>
      r.catalog_item_id === id ? { ...r, on_hand: false, check: null } : r));
  };

  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.catalog_item_id !== id));

  // A meal needs only a name, in BOTH modes. Create previously also demanded
  // at least one ingredient while edit did not — so an empty meal was
  // unreachable by creating one but reachable by emptying one, which is the
  // same end state behind two different rules. An empty meal is a legitimate
  // workflow anyway ("name it now, fill it in as I think of them"); PLAN shows
  // it as "0 ingredients" and its Add button stays disabled on count === 0.
  // Two-stage delete confirm, local to the sheet: resets whenever the
  // sheet is remounted for another meal.
  const [confirmDelete, setConfirmDelete] = useState(false);

  const stopListening = () => {
    const rec = recognitionRef.current;
    if (rec) { try { rec.stop(); } catch { /* already stopped */ } }
    // onend normally clears this; setting it here too means a browser that never fires
    // onend cannot strand the button in a permanent "listening" state.
    setListening(false);
  };

  const startListening = () => {
    if (!SPEECH_RECOGNITION_CTOR || micBlocked || listening) return;
    let rec;
    try {
      rec = new SPEECH_RECOGNITION_CTOR();
    } catch {
      setMicHint("Didn't catch that — try again or type.");
      return;
    }
    rec.continuous = false;      // one burst; the browser ends it on a natural pause
    rec.interimResults = true;   // so words appear as they are spoken, per mockup Screen 2
    rec.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";

    aiTextBaseRef.current = aiText;

    rec.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i += 1) transcript += event.results[i][0].transcript;
      const base = aiTextBaseRef.current.trim();
      const heard = transcript.trim();
      // APPEND, never replace — someone may have typed half a request before speaking
      // the rest, and eating that would be the worst possible surprise here.
      setAiText(base && heard ? `${base} ${heard}` : (heard || base));
    };

    rec.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        setMicBlocked(true);
        setMicHint("Mic access is blocked — type your request instead.");
      } else {
        // no-speech / network / aborted / audio-capture all land here. One message: the
        // person's next move is identical in every case, so distinguishing them would be
        // detail without a decision attached.
        setMicHint("Didn't catch that — try again or type.");
      }
    };

    rec.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };

    setMicHint("");
    try {
      rec.start();
    } catch {
      setMicHint("Didn't catch that — try again or type.");
      return;
    }
    recognitionRef.current = rec;
    setListening(true);
  };

  const toggleMic = () => (listening ? stopListening() : startListening());

  // Detach handlers BEFORE aborting: abort() fires onerror('aborted'), and a setState
  // from a handler on an unmounting component is a React warning for no benefit.
  useEffect(() => () => {
    const rec = recognitionRef.current;
    if (rec) {
      rec.onresult = null; rec.onerror = null; rec.onend = null;
      try { rec.abort(); } catch { /* nothing to abort */ }
    }
  }, []);

  // ONE landing for every draft that arrives from outside — the Galley, a photo
  // read, a paste (SPEC_recipe_import D2: one editor, one Save, one landing).
  // REPLACES name/instructions/ingredients rather than merging. Merging sounds kinder
  // but compounds: ask twice and you accumulate two meals' worth of ingredients with
  // no way to tell them apart. The hint under the box says so plainly, and everything
  // stays editable afterwards, so a replace is recoverable and a silent merge is not.
  // `controller` is the request's own; a landing that was cancelled mid-loop stops
  // before it writes the rows (the caller's Never mind has already restored).
  const landDraft = async (draft, source, controller) => {
    const ingredients = draft.ingredients || [];
    setName(draft.name || "");
    // THE INTERIM SAVE (ruling 2026-10-09; the three-quantity model is its own
    // design session, no schema change): the reader's verbatim `asWritten` lines go
    // into `instructions` as one "As written" block ABOVE the function's numbered
    // steps, which stay untouched. parseSteps renders it as a preamble — never
    // step 1 — because the function numbers every step 1..N itself. Both lines of
    // a duplicate row stay here even though the rows below merge. The Galley has
    // no asWritten, so its drafts land exactly as before.
    const asWritten = ingredients.map((ing) => (typeof ing.asWritten === "string" ? ing.asWritten.trim() : "")).filter(Boolean);
    const steps = draft.instructions || "";
    setInstructions(asWritten.length > 0 ? `As written\n${asWritten.join("\n")}\n\n${steps}`.trim() : steps);
    // Occasion: only when the draft carries a valid one (the hook's normaliser
    // drops anything outside the seven); otherwise the person's own tags stand.
    const occ = normalizeOccasion(draft.occasion);
    if (occ.length > 0) setOccasion(occ);
    // From: filled from the draft when it names a source; hidden otherwise. Only
    // the import carries the key (null when nothing was visible); a Galley draft
    // has no opinion and leaves an existing From alone.
    if (draft.attribution !== undefined) {
      const credit = typeof draft.attribution === "string" ? draft.attribution.trim() : "";
      setAttribution(credit);
      setFromVisible(!!credit);
    }
    setServingsAssumed(draft.servingsAssumed === true);

    // Every suggested ingredient goes through the SAME resolver the manual
    // no-results panel uses: exact-normalized match, or a pending placeholder
    // that Save turns into a real row. No parallel matching path (spec,
    // Decisions) — and no catalog write until the meal is committed.
    const staged = [];
    const known = new Set(Object.values(catalogMap).map((it) => norm(it.name)));
    for (const ing of ingredients) {
      // Snapshot BEFORE the call: "was it already there?" is a property of the
      // catalog, not of what the resolver hands back — a hidden-set match returns
      // an existing row that was never in catalogMap, and the badge should still
      // say where it went. Asked beforehand or the answer drifts.
      const wasKnown = known.has(norm(ing.name));
      const item = await onCreateCatalogItem(ing.name, ing.category);
      if (!item) continue;   // resolver failed and said why; keep the rest
      // Whole numbers only — the stepper cannot hold anything else. The Edge
      // Function already rounds up; this is the client-side floor, not a repeat.
      const qty = Math.max(1, Math.ceil(Number(ing.quantity) || 1));
      const unit = typeof ing.unit === "string" && ing.unit.trim() ? ing.unit.trim().toLowerCase() : "each";
      // "Check this" (spec: Filled): the reader flagged the line — advisory, never
      // blocks Save, cleared by editing the row.
      const check = ing.uncertain === true ? (typeof ing.note === "string" && ing.note.trim() ? ing.note.trim() : "Check this line") : null;
      // MERGE ON LANDING (ruling 2026-10-09; set4 listed olive oil twice): two rows
      // resolving to the same catalog item — or the same normalised NEW name, which
      // is the same pending id — become one. Counts add when both are `each`;
      // otherwise the larger stands (a "2 tbsp" and a "¼ cup" of the same thing is
      // not 2 + 1). A flag on either row survives the merge. Mirrors the sheet's
      // own already-staged behaviour (tap again → bump, never a second row).
      const dup = staged.find((r) => r.catalog_item_id === item.id);
      if (dup) {
        dup.quantity_per_serving = (dup.unit === "each" && unit === "each")
          ? dup.quantity_per_serving + qty
          : Math.max(dup.quantity_per_serving, qty);
        if (check && !dup.check) dup.check = check;
        continue;
      }
      staged.push({
        catalog_item_id: item.id,
        name: item.name,
        quantity_per_serving: qty,
        unit,
        category: item.category || ing.category || null,
        isNew: !wasKnown,
        check,
      });
    }
    if (controller.signal.aborted) return;
    setRows(staged);
    setDraftSource(source);
    setGalleyCollapsed(true);
    setStepsMode("read");
  };

  // A GIFT lands once, on mount (Commit 3): the same landing as the Galley and the import, so the
  // ingredients resolve against THIS place's catalog and the From field follows C6.
  const giftLandedRef = useRef(false);
  useEffect(() => {
    if (!gift || !gift.draft || giftLandedRef.current) return;
    giftLandedRef.current = true;
    landDraft(gift.draft, "gift", { signal: { aborted: false } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gift]);

  // Invent (Create a meal): one request, one meal, straight into the fields the manual path uses.
  const handleAskAI = async () => {
    const text = aiText.trim();
    if (!text || aiBusy || !onRequestSuggestion) return;
    if (inventTooLong(text)) return;   // the echo has said so and the button is inert; never truncate
    if (!isSignedIn) return;   // belt-and-braces: the button is already inert below
    // Sending is an unambiguous "I'm done talking" — leaving the mic live would keep
    // appending words to a field whose contents have already been sent.
    if (listening) stopListening();
    const controller = new AbortController();
    aiAbortRef.current = controller;
    aiSnapshotRef.current = { name, instructions, rows, occasion, attribution, fromVisible };
    setGalleyHint("");
    setAiBusy(true);
    try {
      const result = await onRequestSuggestion(text, { signal: controller.signal });
      // Never mind already restored the fields and cleared busy; do not overwrite
      // that with a draft that arrived a beat after the person gave up on it.
      if (controller.signal.aborted) return;
      if (!result) return;   // Never mind — silent
      // ONE error location (walk defect 16): the card's hint line, never a toast.
      // The request text is kept for the retry.
      if (result.reason) {
        setGalleyHint(GALLEY_HINTS[result.reason] || GALLEY_HINTS.failed);
        return;
      }
      await landDraft(result.draft, "galley", controller);
    } finally {
      if (aiAbortRef.current === controller) {
        aiAbortRef.current = null;
        setAiBusy(false);
      }
    }
  };

  // Never mind: abort the fetch, put the fields back exactly as they were, return
  // to idle with the request text intact. No toast — the hook swallows AbortError.
  const cancelAskAI = () => {
    const controller = aiAbortRef.current;
    if (!controller) return;
    controller.abort();
    aiAbortRef.current = null;
    const snap = aiSnapshotRef.current;
    if (snap) {
      setName(snap.name); setInstructions(snap.instructions); setRows(snap.rows); setOccasion(snap.occasion);
      setAttribution(snap.attribution); setFromVisible(snap.fromVisible);
    }
    aiSnapshotRef.current = null;
    setDraftSource(null);
    setServingsAssumed(false);
    setAiBusy(false);
  };

  // Read (photos or a pasted recipe): the same shape as handleAskAI with the staged input in place of
  // the request text. The snapshot holds the fields AND the staged input, so Never
  // mind puts both back and the person can try again without re-pasting or
  // re-picking. A decline or a clean failure sets the row's hint and keeps the
  // staged input; only a landed draft clears it.
  const runImport = async (input, source) => {
    if (importBusy || aiBusy || !onRequestImport || !isSignedIn || saving) return;
    const controller = new AbortController();
    importAbortRef.current = controller;
    importSnapshotRef.current = { name, instructions, rows, occasion, attribution, fromVisible, aiText, photos };
    setGalleyHint("");
    setImportBusy(source === "paste" ? { kind: "paste" } : { kind: "photo", count: input.images.length });
    try {
      const result = await onRequestImport(input, { signal: controller.signal });
      // Never mind already restored the fields and cleared busy; a response that
      // arrives a beat later lands nothing.
      if (controller.signal.aborted) return;
      if (!result) return;
      if (result.reason) {
        setGalleyHint(IMPORT_HINTS[result.reason] || IMPORT_HINTS.failed);
        return;   // staged input kept for the retry
      }
      await landDraft(result.draft, source, controller);
      if (controller.signal.aborted) return;
      // Landed: keep the pre-import fields for Start over. The staged input has
      // done its job — screenshots are read and discarded (spec D5), nothing is
      // kept, not even in memory.
      const snap = importSnapshotRef.current;
      startOverRef.current = snap
        ? { name: snap.name, instructions: snap.instructions, rows: snap.rows, occasion: snap.occasion,
            attribution: snap.attribution, fromVisible: snap.fromVisible }
        : null;
      setAiText("");
      aiTextBaseRef.current = "";
      revokePhotos(photos);
      setPhotos([]);
    } finally {
      if (importAbortRef.current === controller) {
        importAbortRef.current = null;
        importSnapshotRef.current = null;
        setImportBusy(null);
      }
    }
  };

  // Never mind for an import: abort, put the fields and the staged input back.
  const cancelImport = () => {
    const controller = importAbortRef.current;
    if (!controller) return;
    controller.abort();
    importAbortRef.current = null;
    const snap = importSnapshotRef.current;
    if (snap) {
      setName(snap.name); setInstructions(snap.instructions); setRows(snap.rows);
      setOccasion(snap.occasion); setAttribution(snap.attribution); setFromVisible(snap.fromVisible);
      setAiText(snap.aiText); setPhotos(snap.photos);
    }
    importSnapshotRef.current = null;
    setDraftSource(null);
    setServingsAssumed(false);
    setImportBusy(null);
  };

  // Start over (the banner): back to the pre-import snapshot — normally the empty
  // form — with both doors returned. Nothing was written, so this leaves zero
  // rows (D3): pending placeholders simply go out of state.
  const startOver = () => {
    const snap = startOverRef.current;
    startOverRef.current = null;
    if (snap) {
      setName(snap.name); setInstructions(snap.instructions); setRows(snap.rows);
      setOccasion(snap.occasion); setAttribution(snap.attribution); setFromVisible(snap.fromVisible);
    } else {
      setName(""); setInstructions(""); setRows([]); setOccasion([]); setAttribution(""); setFromVisible(false);
    }
    setDraftSource(null);
    setServingsAssumed(false);
    setGalleyCollapsed(false);
    setStepsMode("read");
    setGalleyHint("");
  };

  const revokePhotos = (list) => {
    for (const p of list || []) { try { URL.revokeObjectURL(p.url); } catch { /* already gone */ } }
  };

  // Clear (photo state): the staged photos go, the text box and whatever was typed
  // before the pick come back (G7).
  const clearPhotos = () => {
    revokePhotos(photos);
    setPhotos([]);
    setGalleyHint("");
  };

  // Photo: the picker's files (already snapshotted — see filesFromPicker), capped
  // at 4 with the spec's hint, each downscaled before it is staged. An image the
  // browser cannot decode is dropped with its own hint and the others are kept.
  // src/lib/stagePhotos.js owns the rules and never throws; the try/catch here is
  // the last line of defence so that NOTHING on this path can fail silently
  // (2026-10-11: a pick staged nothing and said nothing — the live FileList had
  // been emptied by the value reset before it was read).
  const addPhotos = async (files) => {
    if (!files || files.length === 0) return;   // the picker was dismissed
    try {
      const { made, hints } = await stagePhotos(files, {
        room: PHOTO_CAP - photosRef.current.length,
        downscale: downscaleToJpeg,
        createUrl: (blob) => URL.createObjectURL(blob),
        nextId: () => ++photoIdRef.current,
      });
      for (const m of made) console.info(`[recipe import] screenshot ${Math.round(m.originalBytes / 1024)} KB → ${Math.round(m.bytes / 1024)} KB`);
      if (made.length > 0) setPhotos((prev) => [...prev, ...made].slice(0, PHOTO_CAP));
      setGalleyHint(hints.join(" "));
    } catch (err) {
      console.warn("[recipe import] staging threw:", err && (err.message || err));
      setGalleyHint("Couldn't open that image");
    }
  };

  const removePhoto = (id) => {
    const gone = photos.find((p) => p.id === id);
    if (gone) revokePhotos([gone]);
    const next = photos.filter((p) => p.id !== id);
    setPhotos(next);
    setGalleyHint("");   // the last one gone brings the text box back on its own (photosStaged)
  };

  // Read these: base64 what is staged and send it as one request. A blob that
  // will not read (revoked, out of memory) is a hint, not a silent no-op.
  const readPhotos = async () => {
    if (photos.length === 0 || importBusy) return;
    let images;
    try {
      images = [];
      for (const p of photos) images.push({ media_type: "image/jpeg", data_base64: await blobToBase64(p.blob) });
    } catch (err) {
      console.warn("[recipe import] could not read a staged image:", err && (err.message || err));
      setGalleyHint(HINT_UNREADABLE);
      return;
    }
    await runImport({ kind: "images", images }, "photo");
  };

  // Read this recipe: the box's text, as one request to the reader. Sending is
  // "I'm done talking" for the mic here too.
  const readPaste = async () => {
    const text = aiText.trim();
    if (!text || importBusy) return;
    if (listening) stopListening();
    await runImport({ kind: "text", text }, "paste");
  };

  // Mirror for the unmount cleanup and the picker (both read outside render).
  useEffect(() => { photosRef.current = photos; }, [photos]);
  useEffect(() => () => {
    for (const p of photosRef.current) { try { URL.revokeObjectURL(p.url); } catch { /* already gone */ } }
  }, []);

  // "When the user clears all fields" — with nothing left in the sheet there is
  // nothing for the eyebrow to be the provenance of.
  useEffect(() => {
    if (draftSource && !name.trim() && !instructions.trim() && rows.length === 0) setDraftSource(null);
  }, [draftSource, name, instructions, rows]);

  // On Ask, bring the thinking card into view so the ember and Never mind are both
  // on screen while the draft is built. The ask block sits below the (now skeleton)
  // manual fields, and the Ask button is at its bottom — on a phone the card lands
  // under the keyboard's former footprint or off the bottom of the sheet. `nearest`
  // moves the overlay only as far as it must; reduced motion jumps instead.
  useEffect(() => {
    if (!busy) return;
    const el = aiCardRef.current;
    if (!el || typeof el.scrollIntoView !== "function") return;
    const reduced = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ block: "nearest", behavior: reduced ? "auto" : "smooth" });
  }, [busy]);

  // Screen 3 of the mockup: the manual fields dim and stop accepting input while a
  // suggestion is in flight, because they are about to be replaced by the draft.
  const aiDim = busy ? { opacity: 0.45, pointerEvents: "none" } : undefined;
  // The ask block dims while the steps are being hand-edited: a draft would
  // replace what is being typed. Same shape as aiDim, different trigger.
  const galleyDim = stepsMode === "edit" ? { opacity: 0.55, pointerEvents: "none" } : undefined;
  // THE GALLEY HIDES ON MANUAL BUILDING (Dan, 2026-10-03). The ask block shows
  // only while the draft has no ingredients and no steps, or the draft came
  // from the Galley (its "Not quite it? Ask the galley again" handle is
  // unchanged). It leaves in the same render as the first hand-added
  // ingredient or typed step, and returns if every one is removed; editing an
  // existing meal with ingredients opens without it. Reason: a leftover Ask
  // control would draft over hand-entered work.
  const galleyAvailable = draftSource !== null || (rows.length === 0 && !instructions.trim());
  // ONE door now (SPEC_galley_composer G1, G10): the card shows on that predicate
  // while nothing is in flight and the Galley is not folded away behind its
  // handle, and it hides when an ingredient is added by hand, returning when it is
  // removed. Photos never show in Edit Meal (recipe import: Visibility) — the card
  // there is words only.
  const showGalley = galleyAvailable && !busy && !galleyCollapsed;
  const canPhoto = !isEdit;
  const photosStaged = photos.length > 0;
  // Asking needs a real Clerk identity: both Edge Functions verify the token
  // against Clerk JWKS, so signed out they can only ever fail. Gate the control
  // rather than letting the attempt through to a generic error.
  const aiInert = !isSignedIn || !aiText.trim() || aiBusy || saving;
  // The card's one hint line, only when there is something to say. A read's decline
  // or failure first (it is the reason the staged input is still here), then the
  // sign-in gate, the mic, the cap. The cap-on-staging notice is advisory; every
  // other read hint and every mic hint is an error.
  const galleyHintLine = galleyHint
    ? galleyHint
    : !isSignedIn
      ? "Sign in to use Ask the Galley — it builds the meal against your account."
      : micHint
        ? micHint
        : listening
          ? "Listening… tap the mic again to stop, or just keep talking."
          : aiText.length >= TEXT_CAP
            ? "That's the limit — 20,000 characters."
            : "";
  const galleyHintIsError = galleyHint ? !galleyHint.startsWith("Up to 4 at a time.") : (!!micHint && isSignedIn);
  // THE MODE (G3, G4, G6 — src/lib/galleyMode.js): read from what the card holds.
  // Photos → Read these; recipe-shaped text → Read this recipe, unless "Want ideas
  // instead?" flipped it; anything else → Create a meal. The button label says
  // which before anyone taps, the echo above it says what will happen, and the
  // button's data-galley-mode carries the fixed enum for RUM, never text.
  // Edit Meal is words only (ruling 2026-10-12): no camera, and detection forced to
  // Invent so a long paste there never reads as a recipe it cannot bring.
  const galleyModeNow = galleyMode(aiText, photos.length, (isEdit || ideasOverride) ? MODES.invent : null);
  const galleyHasInput = photosStaged || aiText.trim().length > 0;
  // ASKING THE GALLEY HIDES THE HAND-BUILD FIELDS (SPEC_galley_hides_manual + its amendments of
  // 2026-10-25, Step 0 and b/walk) — the mirror of the 10-03 rule above. The name, From, Good for,
  // Ingredients, Steps and the "or" step aside, so the card sits under the title and its button above
  // the footer. Render-only: everything typed stays in state (a landed draft replaces it anyway). New
  // Meal only (Edit is the same sheet, so the flag is explicit), and never over a landed draft — "Ask
  // the Galley again" re-shows the card with the old request still in it.
  // ON FOCUS, not on the first character (amendment b, Dan's iPhone walk): iOS scrolls for the keyboard
  // once, on focus, against the layout as it is then — a hide on the first keystroke came after that
  // scroll and put the box above the screen. So the hide happens in the textarea's own focus handler
  // (a discrete event: React commits it before iOS measures), and the dialog is anchored at the top
  // while hidden (.op-anchor-top) so .modal-overlay's margin: auto (b62422e) never re-centres it.
  // galleyFocused ends only when the box blurs to somewhere outside the Galley card — a tap on its
  // camera / mic / chips is not a blur (iOS does not focus a tapped <button>, so a pointerdown inside
  // the card just before the blur counts as inside) — or on a tap outside the card, or when ✕ empties
  // the box (it never refocuses it), or when an ask starts. Then the fields return if the Galley is
  // empty; with input (text or photos) they stay hidden, through thinking, until the draft lands.
  const [galleyFocused, setGalleyFocused] = useState(false);
  const galleyTapAtRef = useRef(0);   // when a pointerdown last landed inside the Galley card
  const fieldsHidden = !isEdit && draftSource === null && (galleyFocused || galleyHasInput);
  useEffect(() => { if (busy) setGalleyFocused(false); }, [busy]);
  useEffect(() => {
    if (!galleyFocused) return undefined;
    const onDown = (e) => {
      const card = galleyCardRef.current;
      if (card && card.contains(e.target)) { galleyTapAtRef.current = Date.now(); return; }
      setGalleyFocused(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [galleyFocused]);
  const onGalleyFocus = () => { if (!isEdit) setGalleyFocused(true); };
  const onGalleyBlur = (e) => {
    const card = galleyCardRef.current;
    const to = e.relatedTarget;
    if (card && to && card.contains(to)) return;                         // focus stayed in the Galley
    if (!to && Date.now() - galleyTapAtRef.current < 600) return;        // an iOS tap on a Galley button
    setGalleyFocused(false);
  };
  // Invent past meal-suggestion's 600-character ceiling: said in the echo, button
  // inert, nothing cut. The override can steer a long paste here, so this is live.
  const galleyTooLong = galleyModeNow === MODES.invent && inventTooLong(aiText);
  const galleyEcho = galleyTooLong ? INVENT_TOO_LONG_ECHO : MODE_ECHO[galleyModeNow];
  const galleyGo = galleyModeNow === MODES.readPhotos ? readPhotos : galleyModeNow === MODES.readPaste ? readPaste : handleAskAI;
  const galleyGoInert = galleyModeNow === MODES.invent
    ? (aiInert || galleyTooLong)
    : (!isSignedIn || saving || busy || (galleyModeNow === MODES.readPaste && !aiText.trim()));

  const parsedSteps = useMemo(() => parseSteps(instructions), [instructions]);
  // What ACTUALLY renders in Steps while a draft is in the fields: logged on landing
  // and on every change to the instructions after it (the 2026-09-02 theory — a lone
  // "x" written client-side AFTER a good draft — would show here as a second line
  // with steps=1 chars=1 following a healthy first one). Counts only, no recipe text.
  useEffect(() => {
    if (!draftSource) return;
    console.info(`[galley] steps rendering: source=${draftSource} steps=${parsedSteps.steps.length} preamble=${parsedSteps.preamble.length} chars=${instructions.trim().length}`);
  }, [draftSource, parsedSteps, instructions]);
  const hasSteps = parsedSteps.steps.length > 0;
  const onHandCount = rows.filter((r) => r.on_hand).length;
  const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

  const canSave = name.trim().length > 0;

  // Unsaved changes: anything that differs from what the sheet opened with, or a
  // draft from either door in the fields (draftSource), or a request still in flight. Rows
  // compare by identity + quantity + on-hand, not by object reference.
  const rowsKey = (rs) => rs.map((r) => `${r.catalog_item_id}|${r.quantity_per_serving}|${r.on_hand ? 1 : 0}`).join(",");
  const isDirty = name !== openedWith.name
    || instructions !== openedWith.instructions
    || rowsKey(rows) !== rowsKey(openedWith.rows)
    || occasion.join(",") !== openedWith.occasion.join(",")   // a changed tag is unsaved work (spec)
    || attribution !== openedWith.attribution
    || draftSource !== null
    || busy;   // either door's request in flight (09-09 rule, extended to import)

  // Escape closes a CLEAN sheet only. A dirty one ignores the key rather than
  // asking "discard?" — with no tap-outside path left, the only way to lose work
  // is to press Cancel, and that button says what it does.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" || isDirty || saving || deleting) return;
      onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDirty, saving, deleting, onCancel]);

  return (
    // No backdrop onClick — this sheet is dismissed by Cancel, Save or Delete, never
    // by a stray tap. A drag-select in the Steps textarea that ends outside the modal
    // lands on the overlay as a click, and one of those used to throw away the whole
    // draft with no confirmation. The overlay is scenery now.
    <div className="modal-overlay">
      <div className={fieldsHidden ? "modal op-anchor-top" : "modal"} role="dialog" aria-modal="true" aria-label={isEdit ? "Edit meal" : "New meal"} style={{ maxHeight: "88vh", overflowY: "auto" }}>
        <SheetClose onClose={onCancel} disabled={saving || deleting} />
        {/* The fixed VERB halves of the ingredient rows' aria-labelledby names, rendered once,
            outside every button (2026-10-19, the library's 10-16 recipe). The item's half is the
            row's masked name, under an INDEX id — never catalog_item_id, which is
            "pending:{name}" for an item this sheet just created. */}
        <span id="ing-verb-need" className="sr-only">Need</span>
        <span id="ing-verb-again" className="sr-only">again</span>
        <span id="ing-verb-decrease" className="sr-only">Decrease</span>
        <span id="ing-verb-increase" className="sr-only">Increase</span>
        <span id="ing-verb-remove" className="sr-only">Remove</span>
        <span id="ing-verb-have" className="sr-only">I already have</span>
        <span id="ing-verb-have-tail" className="sr-only">— keep it in the meal but do not add it</span>
        <span id="ing-verb-added" className="sr-only">already added — add another</span>
        <h2 style={{ marginBottom: "20px" }}>{isEdit ? "Edit Meal" : "New Meal"}</h2>

        {/* A received meal (Commit 3; 10-07 frame 6 with the SPEC's words): the banner, "Adding to {place}" when
            the person has more than one, and the giver's note as a quote — their words, not a field.
            C10 (Dan 2026-10-30): the gift photo first, read-only — the view rendition signed from gift-photos
            as the card signs it; no menu, not editable; copied into meal-photos on Add (acceptGiftPhoto), Not for
            us discards it; no photo → no slot. .op-photo + .op-replay-block; no name in any attribute. */}
        {gift && gift.photoUrl && (
          <figure className="op-gift-photo">
            <img className="op-gift-photo-img op-photo op-replay-block" src={gift.photoUrl} alt="" draggable="false" />
            <figcaption className="op-gift-photo-cap">Comes with the meal.</figcaption>
          </figure>
        )}
        {gift && (
          <div className="op-gift-banner" role="status">
            <b>{gift.banner}</b>{gift.placeLine ? <span className="op-gift-place">{gift.placeLine}</span> : null}
          </div>
        )}
        {gift && gift.note && (
          <blockquote className="op-gift-note"><i>{gift.note.quote}</i>{gift.note.by && <small>{gift.note.by}</small>}</blockquote>
        )}

        {/* The import banner (spec: Filled). One line, card-tinted, NO teal — nothing
            here is a completion. Start over is the import's one way back; the
            Galley keeps its own "Ask the galley again" handle below. The second
            sentence counts the rows still wearing a "Check this" chip (2026-10-11):
            live, so it falls as the person edits them; "Check it over." at zero.
            Ingredient rows only — the servings chip is not counted. Advisory. */}
        {(draftSource === "photo" || draftSource === "paste") && !busy && (
          <div className="op-import-banner" role="status">
            <span>
              <b>{draftSource === "photo" ? "Filled from your screenshots." : "Filled from what you pasted."}</b>{" "}
              {bannerCheckLine(countFlaggedRows(rows))}
            </span>
            <button type="button" className="op-start-over" onClick={startOver} disabled={saving}>Start over</button>
          </div>
        )}

        {/* The hand-build fields — hidden while the Galley has input (fieldsHidden). Not re-indented. */}
        {!fieldsHidden && (<>
        <div className="modal-field" style={aiDim}>
          <label className="modal-label">Meal Name</label>
          {/* NO autoFocus. On iOS an autofocused field opens the keyboard the instant
              the sheet mounts, which shoves the modal up and buries the fields it was
              meant to help with (Dan, iPhone walk 2026-09-02). Every field in this sheet
              — name, ingredient search, AI request — focuses on touch and only on touch. */}
          <input
            className="modal-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Taco Night"
          />
        </div>

        {/* From (D6). Only when a recipe arrived with a source, or the meal already
            carries one. Free text; clearing it saves null. */}
        {fromVisible && (
          <div className="modal-field" style={aiDim}>
            <label className="modal-label">From</label>
            <input
              className="modal-input"
              value={attribution}
              onChange={(e) => setAttribution(e.target.value)}
              placeholder="NYT Cooking · @handle on Instagram · Grandma's card"
              aria-label="Where this recipe came from"
            />
          </div>
        )}

        <GoodForChips value={occasion} onChange={setOccasion} style={aiDim} />

        <div className="modal-field" style={busy ? undefined : aiDim}>
          <label className="modal-label">Ingredients</label>

          {/* The reader found no serving count and the function assumed 4 — said
              plainly, in the same advisory chip the flagged rows use. */}
          {servingsAssumed && !busy && (
            <div className="op-check-line">
              <span className="op-check-chip">Check this</span>
              <span className="op-check-note">The recipe didn't say how many it serves</span>
            </div>
          )}

          {/* Thinking: skeleton rows sit where the ingredients will land, so arrival
              is a fill rather than a layout jump. Search and results step aside —
              nothing typed now would survive the replace anyway. */}
          {busy && (
            <div aria-busy="true" aria-label="Building ingredients">
              {[60, 45, 55].map((w) => (
                <div key={w} className="op-skel-step op-skel-row">
                  <div className="op-skel-badge square" />
                  <div className="op-skel-lines"><div className="op-skel" style={{ width: `${w}%` }} /></div>
                </div>
              ))}
            </div>
          )}

          {!busy && rows.length > 0 && (
            <div style={{ marginBottom: "10px" }}>
              {/* Row styling matches .item-row.has-qty. Applied unconditionally:
                  every row in this list carries quantity ≥ 1 by definition, so a
                  qty-check would be a branch with one reachable outcome. */}
              {rows.map((r, ri) => (
                <div key={r.catalog_item_id} style={{
                  padding: "8px 10px",
                  // Dashed + faded says "still part of the recipe, just not being bought".
                  // Deleting is the solid absence; this is a softer state and reads as one.
                  border: r.on_hand ? "1.5px dashed #C9A97A" : "1.5px solid #c8973a",
                  borderRadius: "8px", marginBottom: "6px",
                  background: r.on_hand ? "rgba(250,244,236,0.55)" : "#FAF4EC",
                }}>
                  {/* flexWrap (SPEC_global_text_size decision 9): at XL / XXL the control drops below the name instead of squeezing it. */}
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px" }}>
                  <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center",
                    gap: "7px", flexWrap: "wrap" }}>
                    <span id={`ing-name-${ri}`} style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(15),
                      color: r.on_hand ? "#8a7a60" : "#2C1A0E" }}>
                      {r.name}
                    </span>
                    {r.on_hand && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px",
                        fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), fontWeight: 700,
                        letterSpacing: "0.04em", textTransform: "uppercase", color: "#6B4E1F",
                        background: "rgba(201,169,122,0.22)", borderRadius: "999px", padding: "2px 8px" }}>
                        On hand · {r.quantity_per_serving}
                      </span>
                    )}
                    {/* Only on items this sheet just created — it answers "where did
                        that go?", which is only a live question for a brand-new item. */}
                    {r.isNew && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "3px",
                        fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#8a7a60",
                        background: "rgba(160,114,74,0.12)", borderRadius: "999px", padding: "2px 7px" }}>
                        <span aria-hidden="true">{categoryGlyph(r.category)}</span>
                        new in {CATEGORY_LABEL[r.category] || r.category || "Household"}
                      </span>
                    )}
                    {/* The reader flagged this line. Advisory: Save is never blocked;
                        the chip and its reason clear on the row's next edit. */}
                    {r.check && <span className="op-check-chip">Check this</span>}
                  </span>
                  {/* Full Browse stepper: .qty-controls is the wrapper that gives
                      the group its geometry and containment, not just a border.
                      Reproducing only .qty-btn/.qty-display inside a hand-rolled
                      flex div left the dividers unbounded — .qty-display has no
                      height, so it sits a few px taller than the 34px buttons and
                      the pill is what reconciles them. Reuse the class; do not
                      rebuild its rules inline. */}
                  {r.on_hand ? (
                    // Cosmetic treatment was left OPEN by the spec; decided here.
                    // Muted, not hidden, and the row keeps its slot and height so
                    // shelving an ingredient never reflows the list under the thumb.
                    // "Need it" rather than "+" because + reads as "more of it" next
                    // to a stepper, and this is a state change, not an increment.
                    <button
                      onClick={() => needAgain(r.catalog_item_id)}
                      aria-labelledby={`ing-verb-need ing-name-${ri} ing-verb-again`}
                      style={{
                        background: "none", border: "1.5px solid #C9A97A", borderRadius: "999px",
                        padding: "6px 13px", fontFamily: "'Lato', sans-serif", fontSize: ts(14, 0.8),
                        fontWeight: 700, color: "#8A5F3A", cursor: "pointer", flexShrink: 0,
                        whiteSpace: "nowrap",
                      }}
                    >Need it</button>
                  ) : (
                    <div className="qty-controls">
                      <button
                        className="qty-btn"
                        onClick={() => setQty(r.catalog_item_id, r.quantity_per_serving - 1)}
                        aria-labelledby={`ing-verb-decrease ing-name-${ri}`}
                      >−</button>
                      <span className="qty-display">{r.quantity_per_serving}</span>
                      <button
                        className="qty-btn"
                        onClick={() => setQty(r.catalog_item_id, r.quantity_per_serving + 1)}
                        aria-labelledby={`ing-verb-increase ing-name-${ri}`}
                      >+</button>
                    </div>
                  )}
                  <button
                    onClick={() => removeRow(r.catalog_item_id)}
                    aria-labelledby={`ing-verb-remove ing-name-${ri}`}
                    style={{ background: "none", border: "none", color: "#8A5F3A", fontSize: "18px",
                      cursor: "pointer", padding: "0 2px", flexShrink: 0, lineHeight: 1 }}
                  >×</button>
                  </div>
                  {/* The flag's reason, on its own full-width line under the row: inside
                      the name column it shared the width with the stepper and broke
                      mid-word at XXL on 320 px (harness 2026-10-11). */}
                  {r.check && <div className="op-check-note" style={{ marginTop: "4px" }}>{r.check}</div>}
                  {/* Its own control, deliberately NOT folded into the stepper. Sits on
                      its own line so the stepper keeps its geometry and the shelve action
                      is reachable at ANY quantity without first editing the number —
                      which is the entire bug this replaced. Quiet by default: shelving is
                      occasional, editing quantity is not. */}
                  {!r.on_hand && (
                    // 1px here, not 6: the link belongs to the row above it and should
                    // read as attached to it. The card-to-card gap is the row's own
                    // marginBottom (6px) and is deliberately left alone — that contrast
                    // is what makes the pairing legible rather than just tight.
                    // The button keeps its 2px vertical padding, so the visible gap is
                    // ~3px and the tap target is unchanged.
                    <div style={{ marginTop: "1px" }}>
                      <button
                        onClick={() => shelve(r.catalog_item_id)}
                        aria-labelledby={`ing-verb-have ing-name-${ri} ing-verb-have-tail`}
                        style={{
                          background: "none", border: "none", padding: "2px 0", cursor: "pointer",
                          fontFamily: "'Lato', sans-serif", fontSize: ts(14, 0.8), fontWeight: 400,
                          color: "#9a8a78", textDecoration: "underline", textUnderlineOffset: "2px",
                        }}
                      >I have this</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!busy && (
            <input
              className="modal-input"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPickerOpen(false); }}
              placeholder="Search your catalog…"
            />
          )}

          {!busy && !trimmedQuery && (
            <div style={{ padding: "10px 2px 0", fontFamily: "'Lato', sans-serif",
              fontSize: ts(14), color: "#8A5F3A" }}>
              Start typing to find an ingredient.
            </div>
          )}

          {!busy && results.length > 0 && (
            <div style={{ marginTop: "8px" }}>
              {results.map((it, ii) => {
                const stagedRow = rows.find((r) => r.catalog_item_id === it.id);
                return (
                /* Same shape as Shop Add's .add-result row (the reference pattern, pass 2):
                   the name wins the width and WRAPS — never ellipsises — and the category
                   pill sits below it; the Add button keeps its size. The old row put the
                   pill beside a nowrap name, and at XXL the pill and button took the width
                   and the name went to one letter ("G…" for Ground beef). */
                <div key={it.id} style={{
                  display: "flex", alignItems: "center", gap: "10px", padding: "8px 2px",
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div id={`ing-result-${ii}`} style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(15), color: "#2C1A0E",
                      overflowWrap: "anywhere" }}>
                      {it.name}
                    </div>
                    <span style={{ display: "inline-block", marginTop: "3px",
                      fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#8A5F3A",
                      background: "rgba(201,169,122,0.16)", borderRadius: "999px", padding: "2px 8px",
                      // inline-block, not inline-flex: the label wraps at its spaces first. A
                      // one-word category ("Uncategorised") at XXL on 320 is wider than the
                      // column, so the pill may break the word rather than run under Add.
                      maxWidth: "100%", boxSizing: "border-box", overflowWrap: "anywhere" }}>
                      <span aria-hidden="true">{categoryGlyph(it.category)}</span>{" "}
                      {CATEGORY_LABEL[it.category] || it.category || "Uncategorised"}
                    </span>
                  </div>
                  {stagedRow ? (
                    <button
                      className="add-btn"
                      style={{ flexShrink: 0, fontSize: ts(14, 0.8), padding: "6px 14px",
                        background: "#E8D5B7", color: "#6b5a45" }}
                      onClick={() => stageItem(it)}
                      aria-labelledby={`ing-result-${ii} ing-verb-added`}
                    >Added ×{stagedRow.quantity_per_serving}</button>
                  ) : (
                    <button className="add-btn" style={{ flexShrink: 0, fontSize: ts(14, 0.8), padding: "6px 16px" }}
                      onClick={() => stageItem(it)}>+ Add</button>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {/* No match → inline create, matching Browse's live pattern. */}
          {!busy && showNoResults && (
            <div style={{ marginTop: "8px" }}>
              <div style={{ padding: "0 0 8px", fontFamily: "'Lato', sans-serif", fontSize: ts(12.5),
                letterSpacing: "1.5px", textTransform: "uppercase", color: "#8A5F3A" }}>
                No results for "{trimmedQuery}"
              </div>
              <div style={{ borderRadius: "10px", border: "1.5px dashed #C9A97A",
                background: "rgba(201,169,122,0.06)", overflow: "hidden" }}>
                {!pickerOpen ? (
                  <div onClick={() => setPickerOpen(true)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "12px 14px", cursor: "pointer" }}>
                    <div>
                      <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(15), color: "#8A5F3A" }}>
                        Add <strong>"{trimmedQuery}"</strong> to {name.trim() || "this meal"}
                      </div>
                      <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#8A5F3A", marginTop: "2px" }}>
                        Tap to choose a category
                      </div>
                    </div>
                    <button className="add-btn" style={{ flexShrink: 0, fontSize: ts(14, 0.8), padding: "6px 16px" }}>Add</button>
                  </div>
                ) : (
                  <div>
                    <div className="picker-sub" style={{ padding: "12px 14px 0" }}>
                      {adding ? "Adding…" : "Pick a category and we'll file it for next time."}
                    </div>
                    <div style={{ padding: "10px 14px 12px" }}>
                      <CategoryPickerGrid
                        categories={categories.filter((cat) => cat.rawName !== "⭐ My Custom Items")}
                        selected={null}
                        onSelect={createAndStage}
                        onNewCategory={() => setNewCatOpen(true)}
                      />
                    </div>
                    {/* ── New category inline input — revealed by the tile ── */}
                    {newCatOpen && (
                      <div style={{ padding: "0 14px 14px" }}>
                        <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), letterSpacing: "1.5px", textTransform: "uppercase", color: "#8A5F3A", marginBottom: "7px" }}>
                          Name the new category
                        </div>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                          {/* No autoFocus — same iOS keyboard problem, and this one is
                              nested two panels deep where the jump is worse. */}
                          <input
                            type="text"
                            value={newCatInput}
                            onChange={(e) => setNewCatInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") commitNewCategory(); }}
                            placeholder="e.g. Pet Supplies"
                            style={{
                              flex: 1, border: "1.5px solid #E8D5B7", borderRadius: "20px",
                              padding: "6px 14px", fontFamily: "'Lato', sans-serif", fontSize: ts(15),
                              color: "#2C1A0E", background: "#F5EDE0", outline: "none",
                            }}
                          />
                          <button
                            onClick={commitNewCategory}
                            disabled={!newCatInput.trim() || adding}
                            aria-label="Create category and add ingredient"
                            style={{
                              width: "30px", height: "30px", borderRadius: "50%",
                              background: newCatInput.trim() ? "#A0724A" : "#E8D5B7",
                              border: "none", color: "white",
                              fontSize: "18px", display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: newCatInput.trim() ? "pointer" : "default",
                              flexShrink: 0, transition: "background 0.15s",
                            }}
                          >+</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Steps (column: `instructions`, migration 043). Always present; a
            manually-created meal can carry steps too — this is not an AI-only
            surface, it is just usually filled by one. Read mode is the recipe card,
            edit mode is the same textarea + Done, thinking is skeletons. ── */}
        <div className="modal-field">
          <label className="modal-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Steps</span>
            {stepsMode === "read" && hasSteps && !busy && (
              <button type="button" className="op-steps-action" onClick={() => setStepsMode("edit")}>Edit steps</button>
            )}
          </label>

          {busy ? (
            <div aria-busy="true" aria-label="Building steps">
              {[[100, 80], [100, 65], [90]].map((ws, i) => (
                <div key={i} className="op-skel-step">
                  <div className="op-skel-badge" />
                  <div className="op-skel-lines">
                    {ws.map((w) => <div key={w} className="op-skel" style={{ width: `${w}%` }} />)}
                  </div>
                </div>
              ))}
            </div>
          ) : stepsMode === "edit" ? (
            <div>
              <textarea
                className="modal-input"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                rows={hasSteps ? 8 : 4}
                placeholder={"1. Whisk the dry ingredients.\n2. Fold in the wet."}
                style={{ resize: "vertical", lineHeight: 1.55, fontFamily: "'Lato', sans-serif" }}
              />
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#8a7968",
                marginTop: "7px", lineHeight: 1.5 }}>
                One step per line, numbered. Blank lines are ignored.
              </div>
              <button type="button" className="op-steps-done" onClick={() => setStepsMode("read")}>Done</button>
            </div>
          ) : hasSteps ? (
            <div className={`op-recipe${draftSource ? " galley" : ""}`}>
              {draftSource === "galley" && (
                <div className="op-recipe-eyebrow"><GalleyGlyph />From the Galley</div>
              )}
              {/* "photos", not "screenshots": the eyebrow's uppercase 900 at XXL on
                  320 px cannot hold SCREENSHOTS without growing the modal (harness
                  2026-10-10). */}
              {draftSource === "photo" && <div className="op-recipe-eyebrow">From your photos</div>}
              {draftSource === "paste" && <div className="op-recipe-eyebrow">From what you pasted</div>}
              {/* Mirrors the Meal Name field above; absent rather than "Untitled" when
                  the name is still empty, because the field is right there. Display
                  casing only (displayTitle, visual v2 D11) — the field and the row
                  keep what was typed. */}
              {name.trim() && <div className="op-recipe-title">{displayTitle(name.trim())}</div>}
              <div className="op-recipe-meta">
                {plural(rows.length, "ingredient")} · {onHandCount} on hand · {plural(parsedSteps.steps.length, "step")}
              </div>
              {/* The preamble: unnumbered lines before the first "1." (src/lib/parseSteps.js).
                  Today that is the import's verbatim "As written" block; rendered above
                  the steps as plain lines, never badged, never counted in the meta. */}
              {parsedSteps.preamble.length > 0 && (
                <div className="op-recipe-preamble">
                  {parsedSteps.preamble.map((line, i) => <p key={i}>{line}</p>)}
                </div>
              )}
              <ol className="op-steps">
                {parsedSteps.steps.map((s, i) => (
                  <li key={i}>
                    {parsedSteps.numbered && <span className="op-step-badge">{i + 1}</span>}
                    <p>{s}</p>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            // Mockup state 1: the empty state points at both paths, and the Galley
            // below stays live — which is why empty is not edit mode.
            <button type="button" className="op-steps-empty" onClick={() => setStepsMode("edit")}>
              {galleyAvailable ? "No steps yet — write them, or ask the Galley." : "No steps yet — write them."}
            </button>
          )}

          {/* Once a Galley draft has landed the ask block is folded away; this is its
              handle. An imported draft has no handle here — Start over is its one
              way back (spec, Open: "Start over is enough"). */}
          {galleyCollapsed && !busy && draftSource !== "photo" && draftSource !== "paste" && draftSource !== "gift" && (
            <button type="button" className="op-steps-action op-ask-again"
              onClick={() => setGalleyCollapsed(false)}>
              Not quite it? Ask the Galley again
            </button>
          )}
        </div>
        </>)}

        {/* Thinking. Single ember, no spinner; the request is echoed back so the wait
            has a subject. Never mind aborts and puts everything back. */}
        {busy && (
          <div ref={aiCardRef} style={{ background: "rgba(160,114,74,0.06)", border: "1.5px solid rgba(160,114,74,0.35)",
            borderRadius: "14px", padding: "16px", marginBottom: "16px" }} aria-live="polite">
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
              {/* The ember is the one thing that moves while the galley works: a pulsing
                  core plus a ring that expands and fades (both pseudo-elements, so the
                  ring is not scaled by the core's own pulse). Sized by ts() so it follows
                  the Text size knob with the headline beside it. Decorative — the live
                  region above carries the words. */}
              <div className="op-ember" style={{ width: ts(14), height: ts(14) }} aria-hidden="true" />
              <div style={{ minWidth: 0, flex: 1 }}>
                {/* Fixed headline: the quoted request below already echoes what was asked,
                    so the subject of the wait is never repeated up here. */}
                <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: ts(15), color: "#2C1A0E" }}>
                  {aiBusy
                    ? "The Galley's working on it…"
                    : importBusy.kind === "paste"
                      ? "Reading your recipe…"
                      : `Reading ${importBusy.count} screenshot${importBusy.count === 1 ? "" : "s"}…`}
                </div>
                {/* Measured on dev 2026-10-05 from the function's edge logs: eight real
                    runs 7.6–16.9 s, median ~11 s (one Opus call at low effort behind a
                    JWKS verify). "A few seconds" was a guess and read as a stall. */}
                <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#8a7968", marginTop: "2px" }}>
                  Usually 10 to 20 seconds.
                </div>
              </div>
            </div>
            {/* The request is echoed back for the Galley only — an import's subject is
                the staged input, already named in the headline. */}
            {aiBusy && (
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#6f5a45", fontStyle: "italic",
                background: "#FFFDF9", border: "1.5px solid rgba(44,26,14,0.10)", borderRadius: "9px",
                padding: "9px 11px", marginBottom: "14px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                "{aiText.trim()}"
              </div>
            )}
            <button type="button" onClick={aiBusy ? cancelAskAI : cancelImport} className="op-never-mind">Never mind</button>
          </div>
        )}

        {/* ── Ask the Galley (SPEC_galley_composer). ONE card under ONE "or", BELOW
            the hand-built form: two ways to build a meal — by hand, or with the
            Galley — and no third door. What the card is given is the instruction
            (words, photos, a pasted recipe) and the button says what will happen.
            The card and its divider show on the one predicate (showGalley): gone
            while the Galley is thinking (the manual fields are skeletons), once a
            draft has collapsed the card to its handle, and whenever an ingredient
            or step is hand-added — it returns when every one is removed. ── */}
        {showGalley && !fieldsHidden && (
          <div className="op-or" aria-hidden="true"><span>or</span></div>
        )}
        {showGalley && (
          <div className="op-galley" ref={galleyCardRef} style={galleyDim}>
            <div className="op-galley-h">
              <svg viewBox="0 0 24 24" fill="none" stroke="#A0724A" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
              </svg>
              Ask the Galley
            </div>
            {/* Edit Meal has no reading (no camera, Invent forced — walk step 18): the
                sub-line and the placeholder promise nothing about bringing a recipe. */}
            <div className="op-galley-sub">
              {canPhoto ? "Tell it what you're in the mood for, or bring a recipe you already have." : "Tell it what you're in the mood for."}
            </div>

            {/* The picker. Hidden and driven by Photo and the + tile; the value is
                reset after every pick so the same file can be chosen again after a
                remove — AFTER the list is copied: e.target.files is live and the
                reset empties it (the 2026-10-11 silent failure). Not rendered in
                Edit Meal: nothing there points at photos. */}
            {canPhoto && (
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                aria-hidden="true"
                tabIndex={-1}
                onChange={(e) => addPhotos(filesFromPicker(e.target))}
              />
            )}

            <div className="op-galley-box">
              {photosStaged ? (
                <>
                  {/* G5 / G7: the staged photos live INSIDE the box, numbered for
                      reference only — there is no reorder control (recipe import D7):
                      the reader is told the images may arrive in any order and
                      stitches them. The text box and the mic are hidden while photos
                      are staged: photo + words is not a mode yet, and a note we
                      ignored would be dishonest. Whatever was typed stays in state and
                      comes back on Clear; it is never sent with the photos. */}
                  <div className="op-galley-thumbs">
                    {/* The × names itself by aria-labelledby — the fixed verb (one hidden span, outside every
                        button) + the thumbnail's visible number — so no attribute carries the count (replay
                        copies attributes verbatim; it masks only text). alt is in the recorder's skip-set. */}
                    <span id="op-verb-remove-shot" className="sr-only">Remove screenshot</span>
                    {photos.map((p, i) => (
                      <div key={p.id} className="op-galley-th">
                        <img className="op-replay-block op-photo" src={p.url} alt={`Screenshot ${i + 1}`} draggable="false" />
                        <span id={`op-shot-${p.id}`} className="op-galley-n" aria-hidden="true">{i + 1}</span>
                        <button type="button" className="op-galley-x" aria-labelledby={`op-verb-remove-shot op-shot-${p.id}`}
                          onClick={() => removePhoto(p.id)} disabled={saving}>×</button>
                      </div>
                    ))}
                    {photos.length < PHOTO_CAP && (
                      <button type="button" className="op-galley-add" aria-label="Add another screenshot"
                        onClick={() => photoInputRef.current && photoInputRef.current.click()} disabled={saving}>+</button>
                    )}
                  </div>
                  <div className="op-galley-foot">
                    <button type="button" className="op-galley-clear" onClick={clearPhotos} disabled={saving}>Clear</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="op-galley-tawrap">
                    <textarea
                      className="op-galley-ta"
                      value={aiText}
                      onChange={(e) => {
                        // Hard cap, trimmed on the way in (recipe-import rejects > 20,000).
                        setAiText(e.target.value.slice(0, TEXT_CAP));
                        // Typing is "I've moved on" — clear a stale "didn't catch that" and a
                        // stale read hint. The BLOCKED mic message deliberately survives: it
                        // explains why the mic button is greyed out, and that stays true for
                        // the life of the sheet.
                        if (micHint && !micBlocked) setMicHint("");
                        if (galleyHint) setGalleyHint("");
                      }}
                      ref={taRef}
                      onFocus={onGalleyFocus}
                      onBlur={onGalleyBlur}
                      disabled={aiBusy || saving}
                      rows={taRows}
                      placeholder={canPhoto ? "Something cozy with chicken… or paste a recipe" : "Something cozy with chicken…"}
                      aria-label="Ask the Galley"
                    />
                    {/* Clear. Append-on-mic is right for building a request up in pieces, but
                        it has no way to express "scrap that, start again" — Dan typed pancakes,
                        spoke cheeseburger and got a meal made of both (iPhone walk 2026-09-02).
                        This is that missing intent, in one tap.

                        Clearing STOPS listening rather than just emptying the box. Mid-burst,
                        event.results still holds every word said since the tap, so the next
                        result would re-emit the pre-clear words and undo the clear. Ending the
                        burst is what makes "start again" actually start again — the next tap
                        gets a fresh results list.

                        Focus is deliberately NOT returned to the textarea: on iOS that reopens
                        the keyboard, which is the thing this whole pass is removing. */}
                    {aiText.length > 0 && !aiBusy && !saving && (
                      <button
                        type="button"
                        className="op-galley-tx"
                        onClick={() => {
                          setAiText("");
                          aiTextBaseRef.current = "";
                          setGalleyFocused(false);   // ✕ never refocuses the box: it is blurred and empty
                          if (listening) stopListening();
                          if (micHint && !micBlocked) setMicHint("");
                          if (galleyHint) setGalleyHint("");
                        }}
                        aria-label="Clear request"
                      >×</button>
                    )}
                  </div>
                  <div className="op-galley-foot">
                    {/* The tool row inside the box (G5): camera and mic as ghost icon
                        buttons at rest — no border, no fill, a secondary-brown glyph sized
                        in em so it follows the text knob, each on a 44 × 44 hit area.
                        Colour means active: the mic fills while listening (with its pulse),
                        the camera takes a brief pressed fill, then the picker. The pair stays
                        whenever the box shows — a craving can still become a photo read.
                        Edit Meal has no camera: nothing there points at photos. */}
                    {canPhoto && (
                      <button type="button" className="op-galley-photo" disabled={saving || aiBusy} aria-label="Add a photo"
                        onClick={() => { setGalleyHint(""); if (photoInputRef.current) photoInputRef.current.click(); }}>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="6" width="18" height="14" rx="3" /><circle cx="12" cy="13" r="3.5" /><path d="M8 6l1.5-2h5L16 6" /></svg>
                      </button>
                    )}
                    {/* Rendered ONLY where the browser actually supports it. On Safari/iOS the
                        button is absent entirely and this section is exactly the typed path. */}
                    {SPEECH_RECOGNITION_CTOR && (
                      <button
                        type="button"
                        className={`op-mic-btn${listening ? " listening" : ""}`}
                        onClick={() => { if (galleyHint) setGalleyHint(""); toggleMic(); }}
                        disabled={micBlocked || aiBusy || saving}
                        aria-pressed={listening}
                        aria-label={listening ? "Stop listening" : "Speak instead"}
                      >
                        <svg viewBox="0 0 24 24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="9" y="2" width="6" height="11" rx="3" />
                          <path d="M5 10v1a7 7 0 0 0 14 0v-1M12 18v4M8 22h8" />
                        </svg>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* One hint line, only when there is something to say: a read's decline or
                clean failure (kept with the staged input for the retry), the sign-in
                gate, the mic, the cap. Errors in red; the cap notice is advisory. */}
            {galleyHintLine && (
              <div className={`op-galley-hint${galleyHintIsError ? " err" : ""}`}>{galleyHintLine}</div>
            )}

            {/* The echo (G4): one line that says what will happen, so the two contracts
                stay visible and the person can see the Galley understood before it
                acts. Invent wears the sparkle; a read wears the page. Under a paste,
                "Want ideas instead?" is the one-tap override for the misses. */}
            {galleyHasInput && (
              <div className="op-galley-echo-wrap">
                <div className="op-galley-echo">
                  {galleyModeNow === MODES.invent ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="#8A5F3A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="#8A5F3A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 3h9l5 5v13H5z" /><path d="M14 3v5h5M8.5 12h7M8.5 16h5" />
                    </svg>
                  )}
                  <span>{galleyEcho}</span>
                </div>
                {/* Hidden past meal-suggestion's 600 characters: the override would only
                    lead to a dead button. Still offered for short multi-line text misread
                    as a recipe. */}
                {galleyModeNow === MODES.readPaste && !inventTooLong(aiText) && (
                  <button type="button" className="op-galley-switch" onClick={() => setIdeasOverride(true)} disabled={saving}>
                    Want ideas instead?
                  </button>
                )}
              </div>
            )}

            {/* Starter chips (G8): only on the EMPTY card. Two fill the box; "Read a
                recipe card" opens the picker (never in Edit Meal, where nothing points
                at photos). They teach the range of asks without a tutorial and leave
                the moment there is any input. "Use what's on hand" is held back until
                the Galley gets pantry context. */}
            {!galleyHasInput && (
              <div className="op-galley-try">
                <span className="op-galley-try-l">Try one:</span>
                <div className="op-galley-chips">
                  {STARTER_PROMPTS.map((p) => (
                    <button key={p} type="button" className="op-galley-chip" disabled={aiBusy || saving}
                      onClick={() => {
                        setAiText(p);
                        aiTextBaseRef.current = p;   // the mic appends to this, like typed text
                        if (micHint && !micBlocked) setMicHint("");
                        if (galleyHint) setGalleyHint("");
                      }}>{p}</button>
                  ))}
                  {canPhoto && (
                    <button type="button" className="op-galley-chip" disabled={saving}
                      onClick={() => { setGalleyHint(""); if (photoInputRef.current) photoInputRef.current.click(); }}>
                      Read a recipe card
                    </button>
                  )}
                </div>
              </div>
            )}

            <button
              type="button"
              className="op-galley-go"
              data-galley-mode={MODE_RUM[galleyModeNow]}
              onClick={galleyGo}
              disabled={galleyGoInert}
            >{MODE_LABEL[galleyModeNow]}</button>
            <div className="op-galley-fine">
              You'll get one meal, filled into the fields above to review and edit before saving. This replaces what's there now.
            </div>
            <button type="button" className="op-galley-about" aria-expanded={aboutOpen}
              onClick={() => setAboutOpen((v) => !v)}>About the Galley</button>
            {aboutOpen && (
              <div className="op-galley-about-line">The Galley is your AI meal-planning assistant. It drafts, you decide.</div>
            )}
          </div>
        )}

        {/* States the locked decision plainly rather than leaving it inferred. */}
        {isEdit && (
          <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#8a7a60",
            fontStyle: "italic", marginBottom: "14px" }}>
            Changes here won't update items already on your list.
          </div>
        )}

        {/* Delete danger zone — EDIT mode only. Deliberately NOT a bare
            one-tap and NOT a third swipe action: deleting a recipe is
            destructive and, unlike Hide, no lens toggle brings it back.
            Two-stage confirm, mirroring the household delete zone. */}
        {isEdit && onDelete && (
          <div style={{ marginTop: "4px", marginBottom: "16px", paddingTop: "14px", borderTop: "1px solid rgba(44,26,14,0.10)" }}>
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={saving || deleting}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "9px", width: "100%",
                  background: "none", border: "1.5px solid rgba(179,38,30,0.4)", borderRadius: "14px", padding: "13px",
                  color: "#c0392b", fontFamily: "'Lato', sans-serif", fontSize: ts(14, 0.8), fontWeight: 700,
                  cursor: (saving || deleting) ? "default" : "pointer", opacity: (saving || deleting) ? 0.5 : 1,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="1.8" strokeLinecap="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/></svg>
                Delete meal
              </button>
            ) : (
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  style={{ flex: 1, background: "#E8D5B7", border: "none", borderRadius: "12px", padding: "13px", fontFamily: "'Lato', sans-serif", fontSize: ts(14, 0.8), color: "#2C1A0E", cursor: deleting ? "default" : "pointer" }}
                >Keep</button>
                <button
                  onClick={() => { if (!deleting) onDelete(); }}
                  disabled={deleting}
                  style={{ flex: 2, background: "#c0392b", border: "none", borderRadius: "12px", padding: "13px", fontFamily: "'Lato', sans-serif", fontSize: ts(14, 0.8), fontWeight: 700, color: "#fff", cursor: deleting ? "default" : "pointer", opacity: deleting ? 0.6 : 1 }}
                >{deleting ? "Deleting…" : "Yes, delete meal"}</button>
              </div>
            )}
            <div style={{ fontSize: ts(14), color: "#9a8a78", textAlign: "center", marginTop: "8px", lineHeight: 1.4 }}>
              Removes this meal for everyone. Anything already bought stays on your list.
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button className="modal-cancel" onClick={onCancel} disabled={saving || deleting}>{gift ? "Not for us" : "Cancel"}</button>
          <button
            className="modal-confirm"
            disabled={!canSave || saving}
            style={{ opacity: (!canSave || saving) ? 0.5 : 1, cursor: (!canSave || saving) ? "default" : "pointer" }}
            onClick={() => onCommit({ name, instructions, ingredients: rows, occasion, attribution })}
          >{saving ? "Saving…" : gift ? "Add to our meals" : "Save Meal"}</button>
        </div>
      </div>
    </div>
  );
}

// PLACE ROW — one household in the Places list, moved verbatim out of ProvisionsApp
// (2026-10-19) so a test can render it. The active row names itself by aria-labelledby:
// the fixed verb span "hh-verb-edit" (rendered once by the list, outside every button)
// plus this row's masked name (id hh-name-{uuid}). The household name is text only,
// never an attribute the session recorder copies.
export function PlaceRow({ hh, isActive, onEdit, onSwitch }) {
  if (isActive) {
    // Active row: espresso fill + clay ring carry selection; a bare pencil
    // at the right edge opens Edit. Tapping the row also opens Edit.
    return (
      <button
        onClick={onEdit}
        aria-labelledby={`hh-verb-edit hh-name-${hh.id}`}
        style={{
          width: "100%", background: "#2C1A0E", border: "2px solid #c8973a",
          borderRadius: "8px", padding: "11px 14px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: "pointer", marginBottom: "6px", boxSizing: "border-box", textAlign: "left",
        }}
      >
        <span id={`hh-name-${hh.id}`} style={{
          fontFamily: "'Lato', sans-serif", fontSize: ts(15),
          color: "#FAF4EC", fontWeight: 700, flex: 1, minWidth: 0, overflowWrap: "anywhere",
        }}>{hh.name}</span>
        {/* Bare pencil + "Edit" label (no container/plate), per FINAL3 */}
        <span style={{
          display: "flex", alignItems: "center", gap: "6px", flexShrink: 0, marginLeft: "10px",
          fontFamily: "'Lato', sans-serif", fontSize: ts(14, 0.8), fontWeight: 700,
          color: "#FAF4EC", opacity: 0.82,
        }}>
          <svg style={{ width: ts(15, 0.8), height: ts(15, 0.8) }} viewBox="0 0 24 24" fill="none" stroke="#FAF4EC"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
          </svg>
          Edit
        </span>
      </button>
    );
  }
  return (
    <button
      onClick={onSwitch}
      style={{
        width: "100%", background: "#E8D5B7", border: "2px solid transparent",
        borderRadius: "8px", padding: "11px 14px",
        display: "flex", alignItems: "center",
        cursor: "pointer", marginBottom: "6px", boxSizing: "border-box", textAlign: "left",
      }}
    >
      <span style={{
        fontFamily: "'Lato', sans-serif", fontSize: ts(15),
        color: "#2C1A0E", fontWeight: 400, minWidth: 0, overflowWrap: "anywhere",
      }}>{hh.name}</span>
    </button>
  );
}

// ON-HAND PROMPT (SPEC_all_on_hand D4, 2026-10-19) — Add to Shop on a meal with ingredients
// marked on hand asks ONE question per item, do you need to buy it?, with two answers:
// "Buy it ({qty})" (include this time) / "I have it" (the default). The old third path,
// "Remove from this meal", deleted the ingredient from the RECIPE — a trap inside a
// shopping question; changing a recipe belongs in Edit, so it is gone. When nothing is
// Buy it AND every live ingredient of the meal is in the prompt (prompt.allOnHand), the
// confirm reads "I have everything" — the all-on-hand path (064 stamps the placement
// ready). Otherwise "Add to Shop": a partly-flagged meal still adds its other ingredients.
// Item names and quantities are text only, never in an attribute.
export function OnHandPrompt({ prompt, onChoose, onCancel, onConfirm }) {
  const anyBuy = Object.values(prompt.choices || {}).some((v) => v === "include");
  const haveEverything = !!prompt.allOnHand && !anyBuy;
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="Already on hand" onClick={(e) => e.stopPropagation()}>
        <SheetClose onClose={onCancel} />
        <h2>Already on hand</h2>
        <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14),
          color: "#5c4a36", lineHeight: 1.5, marginBottom: "14px" }}>
          <strong>{prompt.mealName}</strong> has ingredients you marked as on hand.
          Need to buy any of them this time?
        </div>

        {prompt.items.map((it) => {
          const choice = prompt.choices[it.catalog_item_id];
          const opts = [
            { key: "include", label: `Buy it (${it.quantity_per_serving})` },
            { key: "skip", label: "I have it" },
          ];
          return (
            <div key={it.catalog_item_id} className="onhand-row" style={{ marginBottom: "16px" }}>
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(15),
                fontWeight: 700, color: "#2C1A0E", marginBottom: "6px" }}>{it.name}</div>
              <div style={{ display: "flex", gap: "6px" }}>
                {opts.map((o) => {
                  const on = choice === o.key;
                  return (
                    <button
                      key={o.key}
                      onClick={() => onChoose(it.catalog_item_id, o.key)}
                      aria-pressed={on}
                      style={{
                        flex: 1, padding: "8px 4px", borderRadius: "999px", cursor: "pointer",
                        fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), fontWeight: 700,
                        border: on ? "1.5px solid #A0724A" : "1.5px solid #E8D5B7",
                        background: on ? "#A0724A" : "transparent",
                        color: on ? "#FAF4EC" : "#8a7a60",
                        transition: "all 0.2s",
                      }}
                    >{o.label}</button>
                  );
                })}
              </div>
            </div>
          );
        })}

        <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14),
          color: "#8a7a60", fontStyle: "italic", marginBottom: "12px", lineHeight: 1.5 }}>
          Buy it is just for this time — the ingredient stays on hand for next time.
        </div>

        {/* .modal-actions / .modal-cancel / .modal-confirm — the same classes the
            Edit Meal sheet's own Cancel/Save footer uses. */}
        <div className="modal-actions">
          <button className="modal-cancel" onClick={onCancel}>Cancel</button>
          <button className="modal-confirm" onClick={onConfirm}>{haveEverything ? "I have everything" : "Add to Shop"}</button>
        </div>
      </div>
    </div>
  );
}

function ProvisionsApp() {
  const { user, isSignedIn, isLoaded } = useUser();
  // D2 (SPEC_auth_state_ui_gating.md) as amended 2026-09-24: the session is LIVE
  // when Clerk is loaded and signed in — Clerk's word only. Token trouble and
  // offline (authHealth, fed by the Supabase fetch wrapper) are a HOLD on what
  // polls, never the end of a session. Every household-scoped input below keys
  // on this; the hold rides in authPhase.
  const { nullStreak, clerkFailStreak, lastFailClass, lastTokenAt, online, rejectedAt } = useAuthHealth();
  const sessionLive = useSessionLive({ isLoaded, isSignedIn });
  // Sign-up pre-fill (read once on mount). Outbound links may carry
  // ?email_address=&first_name=&last_name=; map them to Clerk's initialValues keys
  // so the sign-up modal opens with the form filled. Missing params fall back to
  // undefined so Clerk treats them as unset. No router here (CRA), so this is a
  // plain URLSearchParams read, same as the ?invite= / ?ref= bridge in index.js.
  const signUpInitialValues = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return {
        emailAddress: params.get("email_address") || undefined,
        firstName: params.get("first_name") || undefined,
        lastName: params.get("last_name") || undefined,
      };
    } catch (e) {
      return {};
    }
  }, []);
  // A GIFT LINK (/g/{CODE}; SPEC_meal_sharing_phase_a_client, Commit 1; S33): the code was held
  // under GIFT_KEY and the path cleaned by src/lib/preRum.js before Splunk started — this is the
  // only reader of that key on the signed-out side, and it never reads the URL. Read once at
  // mount: while the session is not live the gift card stands where the Home welcome would; the
  // key is NOT cleared here, because the same tab comes back through Clerk's sign-up round trip
  // and the signed-in arrival (Commit 3) consumes it after bootstrap. "Add to our meals" is the
  // 042 referral sign-up itself (the ?ref= that rode the link is already held under REF_KEY and
  // bootstrap passes it) — one mechanism, no second bridge. Clerk's modal redirect is the current
  // URL, already clean, so no redirect_url is set.
  const [pendingGiftCode] = useState(() => {
    try { return sessionStorage.getItem(GIFT_KEY) || null; } catch (e) { return null; }
  });
  // D5 (SPEC_auth_state_ui_gating §PII scrub 1): the pre-fill params leave the
  // URL the moment they are read. The memo above already holds the values, so
  // openSignUp / SignUpButton still pre-fill; what changes is that the email no
  // longer rides in location.href for the life of the tab — which is what the
  // RUM agent stamps on every span and what session replay records as the page
  // URL. replaceState, so no history entry and no navigation. The path is
  // normalised to "/" (Clerk's redirect had left us on /sign-up, a path with no
  // component behind it); the hash — the app's route — is kept. Declared BEFORE
  // useProvisions and the hash mirror so it runs first in this component's
  // effect order: it must precede the first routeChange the mirror can emit.
  //
  // Deliberately narrower than "strip the whole query": only the three pre-fill
  // keys go. ?invite= and ?ref= never reach this effect any more — src/lib/preRum.js
  // held them in sessionStorage and removed them from the URL before Splunk started
  // (S29 / S33; Amendment 2026-10-26 R2–R3). Nothing in the app reads a code from
  // the URL: bootstrap reads the held keys, the join banner keys on
  // just_joined_household, the welcome sheet on just_joined_household_id.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const had = ["email_address", "first_name", "last_name"].filter((k) => url.searchParams.has(k));
      const offPath = url.pathname !== "/";
      if (had.length === 0 && !offPath) return;
      had.forEach((k) => url.searchParams.delete(k));
      const search = url.searchParams.toString();
      window.history.replaceState(null, "", "/" + (search ? `?${search}` : "") + (url.hash || ""));
    } catch (e) { /* URL parsing or history blocked — nothing to scrub, nothing to break */ }
  }, []);
  // Auto-open the sign-up modal when the URL carries at least one pre-fill param
  // (the landing page at ourprovisions.app links here with all three). The header
  // SignUpButton stays as the manual fallback with the same initialValues. Gated on
  // Clerk being loaded and the visitor being signed out; fires once per load.
  const clerk = useClerk();
  const { openSignUp, openSignIn } = clerk;
  const autoSignUpFiredRef = useRef(false);
  useEffect(() => {
    if (autoSignUpFiredRef.current || !isLoaded || isSignedIn) return;
    const hasParam = Object.values(signUpInitialValues).some(Boolean);
    if (!hasParam) return;
    autoSignUpFiredRef.current = true;
    openSignUp({ initialValues: signUpInitialValues });
  }, [isLoaded, isSignedIn, signUpInitialValues, openSignUp]);
  const { getToken, sessionId } = useAuth();
  const { activeHouseholdId, myHouseholds, switchHousehold, refreshHouseholds, resolveAfterHouseholdLoss, beginDeliberateLoss, endDeliberateLoss, loadingHouseholds, householdsReadFailed, householdsLoaded, retryHouseholds } = useActiveHousehold();

  const {
    quantities,
    checked,
    prices: supabasePrices,
    categoryAvgPrices,
    contributorsMap,
    household,
    householdMembers,
    catalogMap,
    setCatalogMap,
    listRows,
    loading,
    householdReady,
    bootstrapped,
    membersLoaded,
    cooksLoaded,
    catalogLoaded,
    error,
    dismissError,
    updateQty,
    updatePrice,
    toggleChecked,
    updateBudgetGoal,
    hideItem,
    deleteItem,
    removeFromList,
    hiddenCatalogItems,
    restoreHiddenByCategory,
    unhideItem,
    createInvite,
    toggleStaple,
    renameItem,
    refreshCatalog,
    fetchMeals,
    createMeal,
    updateMeal,
    setMealOccasion,
    requestMealSuggestion,
    requestRecipeImport,
    deleteMeal,
    onListChangedRef,
    createCatalogItem,
    materializePendingIngredients,
    addMealToList,
    fetchMealProvenance,
    placements,
    refreshPlacements,
    reorderBoard,
    markCooked,
    skipMeal,
    planMeal,
    lockInAll,
    planNoShop,
    madeBefore,
    cooks,
    _listRows,
    updateFullName,
    activeCycle,
    activeSession,
    partnerSession,
    storeSuggestions,
    checkedByMap,
    refreshSessions,
    setSessionStore,
    recordListEvent,
    wrapUpTrip,
    fetchTripReality, answerTripReality,
    createHousehold,
    refreshMembers,
    renameHousehold,
    referralCode,
    joinHouseholdByCode,
    discardUnclaimedHousehold,
    uploadHouseholdPhoto,
    updateHouseholdBanner,
    addMealPhoto, uploadMealPhoto, saveMealPhoto, setPhotoKind, removeMealPhoto, fetchMealPhotos, rateCook, fetchCookRatings, cookIdsByMeal,
    giveMeal, copyGiftPhoto, revokeMealGifts, fetchMealGifts, previewMealGift, recordMealGiftReceipt, acceptGiftPhoto,
    supabase,
    _supabase,
    _household,
    _clerkId,
    _internalUserId,
  } = useProvisions({
    getToken,
    userId: sessionLive ? user?.id : undefined,
    clerkId: sessionLive ? user?.id : undefined,
    email: user?.primaryEmailAddress?.emailAddress,
    fullName: user?.fullName || null,
    activeHouseholdId,
    myHouseholds,
    sessionId,
    sessionLive,
  });

  // ── authPhase (SPEC_auth_state_ui_gating.md D2, D3; Amendment 2026-09-24 —
  // offline is not auth loss) ──
  // One derived state drives what renders and what polls. The truth table:
  //   booting             Clerk not loaded
  //   signed_out          loaded, not signed in (a deliberate Sign out lands here)
  //   signed_in_no_token  Clerk holds a session but a token cannot be fetched, the
  //                       device is offline, or a poller was just refused a token
  //                       — HOLD, indefinitely. No reset, no sheet; pollers skip;
  //                       the pill reads "Reconnecting…". A user in a store with
  //                       no signal stays signed in with their list on screen.
  //   bootstrapping       live, bootstrap_new_user not yet resolved
  //   ready               live and bootstrapped — the full app
  //   session_lost        Clerk ITSELF reports no session after we were live, with
  //                       no reload and no deliberate sign-out — the ONE signal.
  //                       Never from token-fetch failures alone (V5, 2026-09-24:
  //                       20s of DevTools Offline tripped the old three-null-
  //                       tokens rule and put the sheet over a live session).
  // A new Clerk session (sessionId changes) wipes the token streaks.
  const [lostSession, setLostSession] = useState(false);
  const wasLiveRef = useRef(false);
  const liveSinceRef = useRef(null);
  const prevSessionIdRef = useRef(sessionId);
  useEffect(() => {
    if (sessionId && sessionId !== prevSessionIdRef.current) resetAuthHealth();
    prevSessionIdRef.current = sessionId;
  }, [sessionId]);
  useEffect(() => {
    if (sessionLive) {
      if (!wasLiveRef.current) liveSinceRef.current = Date.now();
      wasLiveRef.current = true;
      setLostSession(false);
      return;
    }
    if (!isLoaded || !wasLiveRef.current) return;
    wasLiveRef.current = false;
    // The profile sheet's Sign out raised the flag before calling Clerk: a
    // choice, not a loss — plain signed_out, no sheet, no span.
    if (consumeDeliberateSignOut()) return;
    setLostSession(true);
    // §Instrumentation — once per transition, so the next occurrence explains
    // itself instead of being reconstructed from 27 console errors.
    try {
      const span = tracer.startSpan("auth.session-lost");
      span.setAttributes({
        "clerk.client_status": clerk?.status ?? "unknown",
        "clerk.session_status": clerk?.session?.status ?? "none",
        "auth.signed_in_age_seconds": liveSinceRef.current ? Math.round((Date.now() - liveSinceRef.current) / 1000) : -1,
        "auth.last_token_age_seconds": lastTokenAt ? Math.round((Date.now() - lastTokenAt) / 1000) : -1,
        "auth.null_token_streak": nullStreak,
        "auth.clerk_fail_streak": clerkFailStreak,
        "auth.last_fail_class": lastFailClass ?? "none",
        "net.online": !!online,
        "auth.clerk_signed_in": !!isSignedIn,
        "view": view,
        "household.id": activeHouseholdId ?? "none",
      });
      span.end();
    } catch (e) { /* telemetry never reaches the user */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionLive, isLoaded]);
  // The hold: offline, a token that could not be fetched (either class), or a
  // poller refused within the last REJECTED_HOLD_MS. It lifts by itself — the
  // next tick that gets a token clears the streak; the `online` event runs one
  // tick immediately; the refusal hold lapses inside isPollingOpen.
  const rejectedHold = !!rejectedAt && Date.now() - rejectedAt < REJECTED_HOLD_MS;
  const holding = !online || nullStreak > 0 || rejectedHold;
  const authPhase = !isLoaded ? "booting"
    : lostSession ? "session_lost"
    : !isSignedIn ? "signed_out"
    : holding ? "signed_in_no_token"
    : bootstrapped ? "ready" : "bootstrapping";
  const prevPhaseRef = useRef(null);
  useEffect(() => {
    const from = prevPhaseRef.current;
    if (from === authPhase) return;
    prevPhaseRef.current = authPhase;
    try {
      const span = tracer.startSpan("auth.phase-change");
      span.setAttributes({ "auth.from": from ?? "none", "auth.to": authPhase });
      span.end();
    } catch (e) { /* telemetry never reaches the user */ }
  }, [authPhase]);
  // signed_in_no_token holds the last good UI behind the quiet "Reconnecting…"
  // pill: ONE report per hold episode (not per tick — three reports would flip
  // the pill to "Offline — showing last saved", and the hold is indefinite). The
  // pill clears itself when a poller's read succeeds (reportSuccess at the
  // read sites). session_lost clears it too — the sheet is the surface then and
  // the network is not the story.
  const { reportTransientFailure, reportSuccess } = useConnectivity();
  const holdEpisodeRef = useRef(false);
  useEffect(() => {
    if (authPhase === "signed_in_no_token") {
      if (!holdEpisodeRef.current) { holdEpisodeRef.current = true; reportTransientFailure(); }
      return;
    }
    holdEpisodeRef.current = false;
    if (authPhase === "session_lost") reportSuccess();
  }, [authPhase, reportTransientFailure, reportSuccess]);

  // ── The session-lost sheet's one button (V5 follow-up, 2026-09-24) ──
  // The sheet must never present a dead button. openSignIn is a no-op while
  // clerk-js still holds an active session (Clerk closes the modal for a
  // signed-in client) — which is exactly the state the false V5 session_lost
  // left us in: sheet up, Sign In inert, a reload restored everything with no
  // password. So the button checks clerk-js FIRST: a session exists → re-activate
  // it (setActive emits, React catches up, the sheet unmounts); if React is
  // still not live 1200ms later → reload once (the session cookie carries it).
  // Only when Clerk genuinely has no session does the modal open.
  const sessionLiveRef = useRef(sessionLive);
  sessionLiveRef.current = sessionLive;
  const sheetReactivatingRef = useRef(false);
  const onSheetSignIn = useCallback(async () => {
    const c = clerk;
    const lastId = c?.client?.lastActiveSessionId || null;
    const clientSession = lastId ? (c?.client?.sessions || []).find((x) => x.id === lastId && x.status === "active") : null;
    const existing = c?.session || clientSession || null;
    if (!existing) { openSignIn(); return; }
    if (sheetReactivatingRef.current) return;
    sheetReactivatingRef.current = true;
    try {
      const span = tracer.startSpan("auth.sheet-reactivate");
      span.setAttributes({ "heal.method": "setActive", "clerk.session_status": existing.status ?? "unknown", "view": view });
      span.end();
    } catch (e) { /* telemetry never reaches the user */ }
    try { await Promise.resolve(c.setActive({ session: existing.id })); } catch (e) { /* fall through to the reload check */ }
    setTimeout(() => {
      sheetReactivatingRef.current = false;
      if (sessionLiveRef.current) return;          // React caught up — the sheet is already gone
      try {
        const span = tracer.startSpan("auth.sheet-reactivate");
        span.setAttributes({ "heal.method": "reload", "view": view });
        span.end();
      } catch (e) { /* telemetry never reaches the user */ }
      window.location.reload();
    }, 1200);
    // `view` is read inside the closures only (it is declared later in this
    // component); listing it here would be a use-before-define at render time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerk, openSignIn]);

  // ── 3h — activation watchdog (SPEC_auth_state_ui_gating, safety net) ──
  // The stuck state, seen live on the 2eafad0 preview: clerk-js finished a
  // modal sign-in on the server (client.lastActiveSessionId set, that client
  // session "active") but clerk.session stayed null because setActive returned
  // before its emit (the beforeunload-tracker race; root cause fixed by the
  // router functions in index.js). React never hears about the session, so a
  // hook can't react to it — this is a 500ms poll of the clerk-js instance,
  // the one place that does know. After 1500ms stuck: setActive({session})
  // once (no navigate → it emits). If still stuck 1500ms later: reload once,
  // sessionStorage-guarded so it can never loop. auth.activation-stuck before,
  // auth.activation-healed after (heal.method setActive | reload — the reload
  // one is emitted on the next load, when the session is actually live, so the
  // page tear-down cannot eat it).
  const ACTIVATION_RELOAD_KEY = "op_activation_reload";
  const healRef = useRef({ stuckSince: null, spanSent: false, setActiveAt: null });
  useEffect(() => {
    const tick = () => {
      const c = clerk;
      if (!c?.loaded) return;
      const h = healRef.current;
      const lastId = c.client?.lastActiveSessionId || null;
      const clientSession = lastId ? (c.client?.sessions || []).find((x) => x.id === lastId) : null;
      const stuck = !!clientSession && clientSession.status === "active" && !c.session;
      if (!stuck) {
        if (h.stuckSince && c.session && h.setActiveAt) {
          try {
            const span = tracer.startSpan("auth.activation-healed");
            span.setAttributes({ "heal.method": "setActive", "heal.stuck_ms": Date.now() - h.stuckSince, "view": view });
            span.end();
          } catch (e) { /* telemetry never reaches the user */ }
        }
        healRef.current = { stuckSince: null, spanSent: false, setActiveAt: null };
        return;
      }
      if (!h.stuckSince) { h.stuckSince = Date.now(); return; }
      const stuckMs = Date.now() - h.stuckSince;
      if (stuckMs < 1500) return;
      if (!h.spanSent) {
        h.spanSent = true;
        try {
          const span = tracer.startSpan("auth.activation-stuck");
          span.setAttributes({
            "auth.stuck_ms": stuckMs,
            "clerk.client_status": c.status ?? "unknown",
            "clerk.client_session_status": clientSession.status,
            "clerk.has_last_active_session": true,
            "auth.clerk_signed_in_react": !!isSignedIn,
            "auth.clerk_loaded_react": !!isLoaded,
            "view": view,
          });
          span.end();
        } catch (e) { /* telemetry never reaches the user */ }
      }
      if (!h.setActiveAt) {
        h.setActiveAt = Date.now();
        try { Promise.resolve(c.setActive({ session: lastId })).catch(() => {}); } catch (e) { /* fall through to the reload */ }
        return;
      }
      if (Date.now() - h.setActiveAt < 1500) return;
      // Still stuck after setActive: reload ONCE. The flag is set before the
      // reload and cleared only once a live session is observed (effect below),
      // so a page that reloads into the same stuck state stops here.
      try {
        if (sessionStorage.getItem(ACTIVATION_RELOAD_KEY)) return;
        sessionStorage.setItem(ACTIVATION_RELOAD_KEY, String(Date.now()));
      } catch (e) { return; }
      window.location.reload();
    };
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerk]);
  // The reload leg of the healed span, and the loop guard's release: a live
  // session after a guarded reload is the heal; a live session at any time
  // clears the guard so a future stuck state may reload again.
  useEffect(() => {
    if (!sessionLive) return;
    let stamp = null;
    try { stamp = sessionStorage.getItem(ACTIVATION_RELOAD_KEY); } catch (e) { return; }
    if (!stamp) return;
    try { sessionStorage.removeItem(ACTIVATION_RELOAD_KEY); } catch (e) { /* storage blocked */ }
    try {
      const span = tracer.startSpan("auth.activation-healed");
      span.setAttributes({ "heal.method": "reload", "heal.reload_age_ms": Date.now() - Number(stamp), "view": view });
      span.end();
    } catch (e) { /* telemetry never reaches the user */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionLive]);

  const [showSplash, setShowSplash] = useState(true);
  const handleSplashDone = useCallback(() => setShowSplash(false), []);
  const [localPrices, setLocalPrices] = useState({});
  // Merge: supabase prices override local defaults when available
  const prices = useMemo(() => ({ ...localPrices, ...supabasePrices }), [localPrices, supabasePrices]);
  // D3 (SPEC_rum_dxa_exposure.md): a door hash in the URL at load wins — reload
  // and Back land on the right door. No hash → Home until the landing rule has
  // spoken (SPEC_auth_state_ui_gating D4/D4a, §Landing — the effect sits below
  // the list counts it reads).
  const [view, setView] = useState(() => viewForHash(window.location.hash) || "home");
  // §Landing — has THIS load decided where it lands? True from the start when the
  // URL named a door (deep link, invite, bookmark: that hash wins, and while signed
  // out the kept `view` IS the pendingRoute — the Home welcome renders over it and
  // signing in lands there). Otherwise true once the rule below has run, or the
  // moment the user taps a door / uses Back. Until then no hash is written, so a
  // reload of an undecided load is still an undecided load, not a bookmark of Home.
  const [landingDecided, setLandingDecided] = useState(() => !!viewForHash(window.location.hash));
  // D9′ (amended 2026-09-12): compact exactly when the current door's control
  // row is off-screen. Each door hands its row (or a sentinel at the block's
  // bottom) to `controlRowRef`; only the active door renders one, so at most one
  // element is live. Home passes nothing and never compacts (D12).
  const [controlRow, setControlRow] = useState(null);
  const controlRowRef = useCallback((el) => setControlRow(el), []);
  const scrollCompact = useScrollCompact(controlRow);
  // The pre-Home landing effect (Shop if the list had items, else Browse) was
  // retired 2026-09-24 with D4. Its successor is the §Landing rule further down
  // (after totalItems / checkedCount, which it reads).
  const [meals, setMeals] = useState([]);
  // Plan has two screens since v2: the board (default, the tab's landing) and
  // the library, one tap away behind "+ Add a meal" and back behind the chevron.
  // Leaving the tab resets to the board so Plan always opens on the week.
  const [planScreen, setPlanScreen] = useState("board");
  useEffect(() => { if (view !== "plan") setPlanScreen("board"); }, [view]);
  const [mealsLoading, setMealsLoading] = useState(false);
  // The household whose meals AND placements the navigation load has read at
  // least once. The board's welcome gate reads this, not mealsLoading: before
  // the first read both are "not loading" and empty, which is exactly the
  // flash the welcome must never show. Reset by household change (the id no
  // longer matches), never by the 2s poll.
  const [mealsLoadedFor, setMealsLoadedFor] = useState(null);
  // Photos v1: the meals poll (declared above the photo loader) calls it through this ref.
  const loadMealPhotosRef = useRef(() => {});
  const [addingMealId, setAddingMealId] = useState(null);
  // Provenance for the teal meal facet: catalog_item_id → [{mealId,name,createdBy}].
  const [mealProvenance, setMealProvenance] = useState({});
  // The household whose provenance the navigation load has read at least once
  // (SPEC_home_v1_essentials D10) — the same shape as mealsLoadedFor. Home's
  // on-deck card derives To buy from mealRowCounts, which derives from
  // provenance; until this matches, a To-buy meal would read as Planned for a
  // beat on every cold open. Never show state the data doesn't support.
  const [provenanceLoadedFor, setProvenanceLoadedFor] = useState(null);

  // The library set (055): kind === 'meal' only. `meals` itself carries every
  // kind because the board renders no-shop rows; the library must never see
  // one (a Leftovers row is not a recipe). Legacy rows without a kind read as
  // meals — the column is NOT NULL DEFAULT 'meal', so this is belt-and-braces.
  const libraryMeals = useMemo(() => (meals || []).filter((m) => !isNoShop(m)), [meals]);
  const mealById = useMemo(() => {
    const map = {};
    (meals || []).forEach((m) => { map[m.id] = m; });
    return map;
  }, [meals]);

  // The Plan board (050, SPEC_meal_planning_v1_board_queue.md): THE QUEUE OF
  // OPEN PLACEMENTS — a meal is on the board iff its placement has neither
  // cooked_at nor skipped_at, in sort_order (position 0 is up next). The list
  // no longer decides membership: wrap-up, decrements and other meals' adds
  // cannot remove a card. A soft-deleted meal is not in `meals`, so it cannot
  // render even before its placement closes.
  const boardMeals = useMemo(() => {
    const byCreated = (a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0);
    return (meals || [])
      .filter((m) => { const p = placements[m.id]; return !!p && !p.cookedAt && !p.skippedAt; })
      .sort((a, b) => (placements[a.id].sortOrder - placements[b.id].sortOrder) || byCreated(a, b));
  }, [meals, placements]);
  // Cards cooked THIS board load. Cooked it closes the placement (cooked_at)
  // exactly as before, and boardMeals — the queue: upNext, counts, numbering,
  // the header — drops the meal at once. Only the RENDERED list keeps the card,
  // muted in place, until the next board load clears this set. The board is
  // what's left to cook; the week's record is a Home/history feature (ROADMAP
  // NEXT), not the queue.
  const [cookedHere, setCookedHere] = useState(() => new Set());
  // AN OPEN PLACEMENT ALWAYS WINS OVER THE AFTERGLOW (walk 8b, 2026-10-03).
  // cookedHere records "cooked on this device this load"; afterglowIds is the
  // set the board and Home actually DRAW, keeping an id only while its
  // placement is still closed as cooked. Re-plan a meal straight after Cooked
  // it (the library's Plan, or a partner's arriving by poll) and the placement
  // reopens (cookedAt null), so the id drops out here and the card is a
  // planned card again — no reload. A plain cook keeps its ✓ Cooked, as before:
  // the placements map holds closed rows with their stamp (loadPlacements has
  // no cooked/skipped filter; markCooked's optimistic write keeps the entry).
  const afterglowIds = useMemo(() => {
    if (cookedHere.size === 0) return cookedHere;
    const next = new Set();
    cookedHere.forEach((id) => { if (placements[id]?.cookedAt) next.add(id); });
    return next;
  }, [cookedHere, placements]);
  const boardCards = useMemo(() => {
    if (afterglowIds.size === 0) return boardMeals;
    const byCreated = (a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0);
    return (meals || [])
      .filter((m) => { const p = placements[m.id]; return !!p && !p.skippedAt && (!p.cookedAt || afterglowIds.has(m.id)); })
      .sort((a, b) => (placements[a.id].sortOrder - placements[b.id].sortOrder) || byCreated(a, b));
  }, [meals, placements, boardMeals, afterglowIds]);

  // Per-meal live list rows { total, bought }, from the same provenance map as
  // the ×n count — no new query. This is the card's STATE while it is to-buy
  // ("2 of 4 in cart", "4 to buy"); readiness itself is placement.readyAt.
  // (src/lib/mealLinks.js — the same counts, lifted so the release rule is proven through them.)
  const mealRowCounts = useMemo(() => rowCountsFromProvenance(mealProvenance), [mealProvenance]);

  // Board header / banner facts (v2, spec "The board"). `cards` = the meal
  // cards (no-shop cards never count toward either banner or toward M).
  //   stillToAdd — Planned meal cards WITH ingredients, queue order: the N in
  //                "N meals to add to Shop" and the ids Add all runs. A planned meal
  //                with no ingredients has nothing to add and is left out.
  //   allReady   — every meal card Ready → "stocked" / teal banner.
  //   nonePlanned — every meal card on the list (To buy or Ready), none
  //                Planned → "Everything's in Shop ✓" / sand banner.
  const boardStats = useMemo(() => {
    const cards = boardMeals.filter((m) => !isNoShop(m));
    const isReady = (m) => !!placements[m.id]?.readyAt;
    const isPlanned = (m) => !isReady(m) && (mealRowCounts[m.id]?.total || 0) === 0;
    const stillToAdd = cards.filter((m) => isPlanned(m) && (m.meal_ingredients || []).length > 0).map((m) => m.id);
    return {
      cards,
      anyNoShop: boardMeals.length > cards.length,
      stillToAdd,
      allReady: cards.length > 0 && cards.every(isReady),
      nonePlanned: cards.length > 0 && !cards.some(isPlanned),
    };
  }, [boardMeals, placements, mealRowCounts]);
  // Header subtitle — "{n} nights" plus the batch action, as a ladder (N =
  // Planned meal cards with ingredients, the ids Add all runs):
  //   N ≥ 2            · "{N} meals to add to Shop →"   "add to Shop →" is the
  //                      one tap target (lockInAll); 44px invisible padding
  //   N = 1            · "1 meal to add to Shop"        plain — the card's own
  //                      button is the affordance
  //   N = 0, any To buy · "Everything's in Shop ✓"
  //   all meals Ready  · "ready"
  //   no meals at all  · nothing after the count
  //   ONE open card    · nothing after the count, whatever its state — "1 meal"
  //                      / "1 night". The card and the banner say the rest.
  // Unit: "meals" while every open placement is a meal, "nights" once any
  // no-shop card exists (spec). Returned as parts because the link is JSX.
  const boardSubtitle = useMemo(() => {
    const n = boardMeals.length;
    if (n === 0) return { head: "Pick meals from the library to fill the week", tail: "", link: 0 };
    const unit = boardStats.anyNoShop ? "night" : "meal";
    const head = `${n} ${unit}${n === 1 ? "" : "s"}`;
    if (n === 1) return { head, tail: "", link: 0 };   // one card: just "1 meal" / "1 night"
    const N = boardStats.stillToAdd.length;
    if (boardStats.cards.length === 0) return { head, tail: "", link: 0 };
    if (N >= 2) return { head, tail: ` · ${N} meals to `, link: N };
    if (N === 1) return { head, tail: " · 1 meal to add to Shop", link: 0 };
    if (boardStats.allReady) return { head, tail: " · ready", link: 0 };
    if (boardStats.nonePlanned) return { head, tail: " · Everything's in Shop ✓", link: 0 };
    return { head, tail: "", link: 0 };
  }, [boardMeals.length, boardStats]);
  const boardBanner = boardStats.allReady ? "stocked" : boardStats.nonePlanned ? "set" : null;
  // The welcome gate (PATCH_plan_tab_welcome). boardReady: this household's
  // meals and placements have loaded — until then the board renders its title
  // alone, never the welcome (no flash on a slow load). showWelcome: loaded and
  // nothing RENDERED on the board — boardCards, not boardMeals, because a card
  // cooked this board load stays muted in place until the next load, and the
  // welcome waits for that load too rather than replacing "✓ Cooked" under the
  // finger. everCooked: any placement carrying cooked_at (madeBefore — data the
  // hook already loads, no new query) picks "Add a meal" over "Add your first
  // meal".
  const boardReady = !!household?.id && mealsLoadedFor === household.id;
  // Home renders everything under the date behind ONE gate: the board's (the
  // same load now runs on Home too), householdReady (the list has arrived) AND
  // this household's provenance (D10 — the on-deck card's To buy state reads
  // it). The card, the list line and the budget line appear together on a cold
  // open, never one popping in above another, and never a state the data then
  // contradicts.
  const homeReady = boardReady && householdReady && provenanceLoadedFor === household?.id;
  const showWelcome = boardReady && boardCards.length === 0;
  const everCooked = madeBefore.size > 0;
  // The drag hint earns its line only once there is an order to change: two
  // or more open cards, and no banner in its place. The head's bottom margin
  // follows it (8px to the hint, else 20px to the first card / banner).
  const showDragHint = boardReady && !showWelcome && !boardBanner && boardMeals.length >= 2;
  const [editingPrice, setEditingPrice] = useState(null);
  const [priceInput, setPriceInput] = useState("");
  const [editModalItem, setEditModalItem] = useState(null);

  const [removeConfirmItem, setRemoveConfirmItem] = useState(null);

  // SHOP swipe-to-remove. Own item (sole contributor) → remove immediately.
  // Shared item (anyone else contributed) → confirm first. A row picked through
  // an Ask always confirms (R3, 2026-11-03) — the rule lives in lib/askPicks.
  const handleSwipeRemove = (item) => {
    const confirm = swipeRemoveConfirm(item, user?.id);
    if (confirm) {
      setRemoveConfirmItem({
        name: item.name,
        catalogItemId: item.catalogItemId,
        verb: confirm.verb,
        byName: confirm.byName,
      });
    } else {
      removeFromList(item.name, item.catalogItemId);
    }
  };

  // ── Meals (add-path, migration 025) ──────────────────────────
  // Placements ride along with both reads below. Both are gated on the doors
  // that render them (the navigation effect and the 2s poll run on PLAN and,
  // since HOME v1's on-deck card, on HOME), so the board's rows cost nothing
  // on Browse or Shop.
  const loadMeals = useCallback(async () => {
    setMealsLoading(true);
    try {
      const [ms] = await Promise.all([fetchMeals(), refreshPlacements()]);
      setMeals(ms);
    }
    finally { setMealsLoading(false); }
  }, [fetchMeals, refreshPlacements]);

  // Same read, but deliberately NEVER touches mealsLoading. MealsLens renders
  // "Loading meals…" whenever loading is true AND the list is empty, so a
  // spinner-toggling poll would strobe that empty state every 2s for any
  // household that hasn't made its first meal yet. The navigation load above
  // keeps the spinner; the poll stays invisible.
  const refreshMeals = useCallback(async () => {
    const [ms] = await Promise.all([fetchMeals(), refreshPlacements()]);
    setMeals(ms);
  }, [fetchMeals, refreshPlacements]);

  // Provenance powers the SHOP provenance line's teal meal facet, so it's
  // needed on the list surface too — not only the Meals lens. Cheap read; the
  // poll refreshes it only when the list actually changed (Part 3).
  const refreshProvenance = useCallback(async () => {
    setMealProvenance(await fetchMealProvenance());
  }, [fetchMealProvenance]);

  // Part 3 — the second trigger for provenance. The effect below covers a
  // client that ARRIVES on a badge-showing surface; this covers one already
  // sitting there while somebody else moves the list. Without it a passive
  // viewer renders fresh quantities over a stale provenance map until they
  // navigate (observed 2026-08-20: DH's "from Test1" badge never appeared
  // until a tab-away) — the same defect family as the June Hide-poll bug.
  //
  // The poll calls this ONLY on a tick where the list actually changed, so an
  // idle client stays quiet. Gated on the badge-showing surfaces for the same
  // reason the navigation effect is: provenance nobody is looking at is a
  // wasted query. Assigned during render, mirroring how the hook assigns
  // refreshCatalogRef — a ref write is idempotent, so the double render in
  // StrictMode is harmless.
  onListChangedRef.current = () => {
    if (MEALS_ENABLED && (view === "list" || view === "plan" || view === "home")) refreshProvenance();
  };

  // Load the meal cards when the Plan tab opens — or Home, whose on-deck card
  // is the board's first card and must not decide "empty" before the data is in.
  useEffect(() => {
    if (MEALS_ENABLED && (view === "plan" || view === "home") && household?.id && authPhase === "ready") {
      const hhId = household.id;
      setCookedHere(new Set());
      loadMeals().then(() => setMealsLoadedFor(hhId));
    }
  }, [view, household?.id, loadMeals, authPhase]);

  // ...and keep them live while PLAN is the visible tab. Navigation-only meant
  // a client sitting on PLAN while another member created, renamed or
  // re-ingredient-ed a meal stayed frozen until they navigated away and back —
  // the same gap Part 3 (1e81774) closed for SHOP provenance, one surface over.
  //
  // Scoped to the VISIBLE tab, and that scoping is the whole cost control:
  // only PLAN and HOME (the on-deck card) render meal data, so polling on any
  // other door would be a pure wasted query — the multiplier Part 3 was
  // careful to avoid. Leaving the tab changes the view, which runs this
  // effect's cleanup and stops the interval.
  //
  // A second interval rather than piggybacking the SHOP list-change signal,
  // because that signal cannot see this: onListChangedRef fires on list_items
  // movement, and editing a meal's name or ingredients touches meals /
  // meal_ingredients WITHOUT touching the list at all. The ingredient-count
  // case this fixes would never have fired it.
  useEffect(() => {
    // §Polling discipline: `ready` only — signed_in_no_token holds the last good
    // UI without a fetch, and session_lost has already torn household down.
    if (!MEALS_ENABLED || !(view === "plan" || view === "home") || !household?.id || authPhase !== "ready") return;
    // Amendment 2026-09-24: the tick passes isPollingOpen (closed offline and
    // for the hold after a 401/403); back online runs one tick immediately.
    // Photos v1: every 5th tick (10 s) also re-reads the household's photo rows — a
    // partner's photo touches no meal, placement or list row, so nothing else would
    // carry it; the fingerprint drops an unchanged read without a render.
    let n = 0;
    const tick = () => {
      if (!isPollingOpen()) return;
      refreshMeals();
      n += 1;
      if (n % 5 === 0) loadMealPhotosRef.current();
    };
    const mealsPoll = setInterval(tick, 2000);
    window.addEventListener("online", tick);
    return () => { clearInterval(mealsPoll); window.removeEventListener("online", tick); };
  }, [view, household?.id, refreshMeals, authPhase]);

  // Load provenance when a surface that shows the badge is visible: SHOP renders the
  // teal meal facet, PLAN is where the meal cards live, and HOME's on-deck card
  // mirrors the board's first card (its To buy line is mealRowCounts, which is
  // provenance). The first resolved read for a household marks it loaded for
  // Home's gate; the 2s list poll keeps it fresh via onListChangedRef above.
  useEffect(() => {
    if (MEALS_ENABLED && household?.id && (view === "list" || view === "plan" || view === "home")) {
      const hhId = household.id;
      refreshProvenance().then(() => setProvenanceLoadedFor(hhId));
    }
  }, [household?.id, view, refreshProvenance]);

  // Toast. `action` = { label, onClick } renders a tap target on the pill
  // (VIEW WEEK after Plan, SHOP after Add all) and holds it a little longer.
  // `opts.line` = the one-line variant (Plan's "Added to your week ✓"): a
  // 44px pill, 16px side padding, 14px text, the action as plain uppercase
  // letter-spaced text with no border. Same anchor, same timing; every other
  // toast keeps the default pill.
  // Declared here, above the Plan handlers that need it — CI=true turns
  // no-use-before-define into a build failure.
  const [toastMessage, setToastMessage] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = useCallback((message, action = null, opts = {}) => {
    setToastMessage({ text: message, action, line: !!opts.line });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMessage(null), action ? 4000 : 2500);
  }, []);
  // SPEC_list_counts D4: "{Meal} added · N to get" / "N meals added · M to get", N/M = `open`
  // AFTER the add. The number is read from the hook's row ref, not from this render's
  // `totalItems - checkedCount` (a closure over pre-add state): addMealToList awaits
  // loadListItems before returning, and a successful load always assigns the ref a NEW
  // array. So `rowsBefore` (the array captured before the add) !== the ref now proves the
  // list was refreshed; if it is the same array, loadListItems skipped its tick (another
  // load in flight, or a wrap-up) and the count is unknowable here — the copy drops it:
  // "{Meal} added to your list." A missing number beats a wrong one. Same copy when
  // `open` is 0 (everything was already on the list). D2: listRows.length — every row the
  // RPC returned, bought and quantity-zero ones included — never reaches a person.
  const openAfterAdd = useCallback((rowsBefore) => {
    const rows = _listRows?.current;
    if (!rows || rows === rowsBefore) return null;
    return rows.filter((r) => (r.quantity || 0) > 0 && r.status !== "bought").length;
  }, [_listRows]);
  const addedToast = useCallback((what, rowsBefore) => {
    const open = openAfterAdd(rowsBefore);
    return open ? `${what} added · ${open} to get` : `${what} added to your list.`;
  }, [openAfterAdd]);

  // The two exits (050). Skip on a to-buy card zeroes the meal's pending rows
  // first (removeMealFromList: never un-buys, leaves shared ingredients to the
  // extent another meal needs them) and then closes the placement; on a Ready
  // card it only closes. Cooked it closes as cooked. The card leaves because
  // its placement closed — the provenance refresh keeps the library honest.
  const [busyMealId, setBusyMealId] = useState(null);
  const handleSkipMeal = useCallback(async (mealId, zeroList) => {
    setBusyMealId(mealId);
    try {
      await skipMeal(mealId, { zeroList });
      await refreshProvenance();
    } finally {
      setBusyMealId(null);
    }
  }, [skipMeal, refreshProvenance]);
  const handleCookedMeal = useCallback(async (mealId) => {
    setBusyMealId(mealId);
    setCookedHere((prev) => new Set(prev).add(mealId));   // mute in place, this load only
    try {
      const ok = await markCooked(mealId);
      if (!ok) setCookedHere((prev) => { const next = new Set(prev); next.delete(mealId); return next; });
      // markCooked released the meal's links (replanning starts over): re-read provenance
      // so a re-plan from this load reads the board's Planned, not last trip's cart.
      else await refreshProvenance();
    } finally { setBusyMealId(null); }
  }, [markCooked, refreshProvenance]);
  // Home's Switch (SPEC_home_v1_essentials D8): cards 1 and 2 trade places
  // through the board's own reorder — the full queue in order with the head
  // pair swapped, and the hook renumbers 0..n-1. Optimistic like a drag; the
  // flag only disables the deck's buttons while the write is in flight.
  const [deckSwitching, setDeckSwitching] = useState(false);
  const handleSwitchDeck = useCallback(async () => {
    if (boardMeals.length < 2 || deckSwitching) return;
    const ids = boardMeals.map((m) => m.id);
    [ids[0], ids[1]] = [ids[1], ids[0]];
    setDeckSwitching(true);
    try { await reorderBoard(ids); } finally { setDeckSwitching(false); }
  }, [boardMeals, reorderBoard, deckSwitching]);

  // Plan (library): the placement only — the one door out of the library. The
  // toast names the meal and carries no action (v1.1 A7: the week line is the
  // persistent route to This Week); showToast replaces, never stacks, so a
  // second Plan inside the window swaps the text. Add to Shop (board):
  // the SAME add handler as before (so a meal with on-hand ingredients gets
  // the on-hand prompt). Add all: the hook's batch over the Planned cards in
  // queue order, default quantity 1, on-hand skipped — one toast at the end.
  const [planningMealId, setPlanningMealId] = useState(null);
  // Every Plan tap confirms itself: v1.1 A7's "{Meal} added to your week ✓"
  // names the meal and teaches what "+" means. (A once-per-session "Added to
  // This Week" toast was tried in the 2026-10-16 polish and reverted the same
  // day — the design chat hadn't known A7 existed.)
  const handlePlanMeal = useCallback(async (mealId) => {
    setPlanningMealId(mealId);
    try {
      const ok = await planMeal(mealId);
      if (ok) showToast(`${mealById[mealId]?.name || "Meal"} added to your week ✓`);
    } finally {
      setPlanningMealId(null);
    }
  }, [planMeal, showToast, mealById]);
  const onBoardIds = useMemo(() => new Set(boardMeals.map((m) => m.id)), [boardMeals]);
  // The library's stage per meal (SPEC_meal_library_unplan D4): the board's own
  // derivation, so the pill can never disagree with This Week or Home. A meal
  // outside onBoardIds — never planned, removed, or cooked (afterglow included:
  // afterglowIds is not an open placement) — is "none" and offers Plan.
  const libraryStages = useMemo(() => {
    const map = {};
    libraryMeals.forEach((m) => {
      if (!onBoardIds.has(m.id)) { map[m.id] = "none"; return; }
      const st = boardCardState(m, { placements, rows: mealRowCounts, mealById, cookedIds: afterglowIds });
      map[m.id] = st.ready ? "ready" : st.toBuy ? "toBuy" : "planned";
    });
    return map;
  }, [libraryMeals, onBoardIds, placements, mealRowCounts, mealById, afterglowIds]);
  // Un-plan (the library's one step back): the board's Remove path for a
  // PLANNED card, unchanged — skipMeal with zeroList false, because in the
  // un-plannable window nothing is on the list to zero (Step 0, 2026-10-04:
  // every reader of skipped_at is an "is it open?" predicate; none reads a skip
  // as a preference; the stamp is cleared by the next re-plan). closePlacement
  // is optimistic, so the pill, the week line and the queue move in the same
  // render as the tap; a failure reloads the rows and the card comes back.
  const handleUnplanMeal = useCallback(async (mealId) => {
    setPlanningMealId(mealId);
    try {
      const ok = await skipMeal(mealId, { zeroList: false });
      if (ok) showToast(`${mealById[mealId]?.name || "Meal"} removed from your week`);
    } finally {
      setPlanningMealId(null);
    }
  }, [skipMeal, showToast, mealById]);
  // "Set meal type" (visual v2 Amendment A5): OPTIMISTIC — the card shows the
  // new tint, icon and meta word at once; the narrow write follows; on failure
  // the one meal reverts and the quiet toast says so. On success the quiet
  // refresh (never the spinner) reconciles with the row the server holds.
  const handleSetOccasion = useCallback(async (mealId, occasion) => {
    const prev = mealById[mealId]?.occasion || [];
    setMeals((ms) => ms.map((m) => (m.id === mealId ? { ...m, occasion } : m)));
    const ok = await setMealOccasion(mealId, occasion);
    if (ok) {
      await refreshMeals();
    } else {
      setMeals((ms) => ms.map((m) => (m.id === mealId ? { ...m, occasion: prev } : m)));
      showToast("Couldn't save the meal type");
    }
    return ok;
  }, [mealById, setMealOccasion, refreshMeals, showToast]);

  // No-shop cards (055/056/057): Leftovers / Eating out / Something else. The
  // foot buttons open a one-field sheet — leftovers: a multi-select (no
  // minimum) over the board's open meals + meals cooked in the last
  // LEFTOVER_WINDOW_DAYS (058, D4 — the cook log, not the placement, so a
  // re-planned meal still counts); eating out: optional place name; something
  // else: a free name (soccer, Mom's, takeout, no idea yet) — then planNoShop
  // inserts the meals row and places it in one action.
  //   noShopSheet: null | { kind: 'leftovers' | 'out' | 'other', name, fromMealIds: [] }
  //   leftoverSince: ISO — now − N days, recorded when the sheet opens so the
  //   memo is stable while it is open (no clock drift mid-pick).
  const [noShopSheet, setNoShopSheet] = useState(null);
  const [noShopBusy, setNoShopBusy] = useState(false);
  const [leftoverSince, setLeftoverSince] = useState(null);
  const openLeftoversSheet = useCallback(() => {
    setLeftoverSince(new Date(Date.now() - LEFTOVER_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString());
    setNoShopSheet({ kind: "leftovers", name: "", fromMealIds: [] });
  }, []);
  const leftoverSources = useMemo(() => {
    const open = boardMeals.filter((m) => !isNoShop(m));
    const cooked = libraryMeals
      .filter((m) => {
        const at = cooks[m.id]?.lastCookedAt;
        if (!at || !leftoverSince || open.some((o) => o.id === m.id)) return false;
        return at >= leftoverSince;
      })
      .sort((a, b) => (cooks[b.id].lastCookedAt > cooks[a.id].lastCookedAt ? 1 : -1));
    return [...open, ...cooked];
  }, [boardMeals, libraryMeals, cooks, leftoverSince]);
  const commitNoShop = useCallback(async () => {
    if (!noShopSheet || noShopBusy) return;
    setNoShopBusy(true);
    try {
      const id = await planNoShop(noShopSheet.kind, noShopSheet.name, noShopSheet.fromMealIds);
      if (id) {
        setNoShopSheet(null);
        await refreshMeals();   // the card renders from `meals`, so pull the new row now
      }
    } finally {
      setNoShopBusy(false);
    }
  }, [noShopSheet, noShopBusy, planNoShop, refreshMeals]);

  // On-hand prompt state. null = closed. Only ever set for a meal that actually
  // HAS on-hand ingredients, so the common case never sees it.
  //   { mealId, mealName, items: [...], choices: { [catalogItemId]: 'include'|'skip'|'remove' } }
  const [onHandPrompt, setOnHandPrompt] = useState(null);

  const handleAddMealToList = useCallback(async (mealId) => {
    // The ingredients are already in memory from loadMeals — no fetch, so the
    // no-on-hand path costs nothing and behaves exactly as it did before.
    const meal = (meals || []).find((m) => m.id === mealId);
    const onHand = (meal?.meal_ingredients || []).filter((mi) => mi.on_hand);

    if (onHand.length === 0) {
      setAddingMealId(mealId);
      try {
        // flat: servings = 1 (dial deferred). The hook places the card (050):
        // a closed placement reopens at the end, an open one keeps its slot.
        const rowsBefore = _listRows?.current;
        const count = await addMealToList(mealId, 1);
        await refreshProvenance();        // reflect the card state immediately
        if (count) showToast(addedToast(meal?.name || "Meal", rowsBefore));
      } finally {
        setAddingMealId(null);
      }
      return;
    }

    // Default every choice to 'skip' — on-hand already MEANS "skip by default",
    // so the prompt opens showing what would happen if you just confirmed.
    setOnHandPrompt({
      mealId,
      mealName: meal?.name || "this meal",
      // Every LIVE ingredient is in the prompt (all flagged on hand; meals carry live rows only):
      // only then can "I have it" for all mean nothing to buy — the "I have everything" confirm.
      allOnHand: onHand.length === (meal?.meal_ingredients || []).length,
      items: onHand.map((mi) => ({
        catalog_item_id: mi.catalog_item_id,
        name: mi.catalog_items?.name || "Unknown item",
        quantity_per_serving: Number(mi.quantity_per_serving) || 1,
      })),
      choices: Object.fromEntries(onHand.map((mi) => [mi.catalog_item_id, "skip"])),
    });
  }, [meals, addMealToList, refreshProvenance, showToast, addedToast, _listRows]);

  // Create/edit sheet. `mealSheet` is null when closed, otherwise
  // { mode: 'create' | 'edit', meal } — one piece of state drives both modes.
  const [mealSheet, setMealSheet] = useState(null);
  // A received gift (Commit 3): what stands in the form's place when it cannot open, and the one-time thank-you.
  const [giftArrival, setGiftArrival] = useState(null);   // null | { kind: "error" | "already" | "giving" | "giving_deleted", line, mealId? }
  const [thankYou, setThankYou] = useState(null);         // null | { mealId, ask, title, text }

  // THE RECIPE VIEW (SPEC_recipe_view D1–D4): { mealId, landing } while open. The view
  // reads the LIVE row from mealById, so Edit → Save shows through at once. It renders
  // only while no Edit sheet is open — the sheet's .modal-overlay (z 100) would sit
  // UNDER the view's scrim (z 1000) — and comes back on Cancel / Save with the row the
  // server now holds. A deleted meal leaves mealById and the view simply stops
  // rendering. "Your cooks" is a PER-MEAL meal_cooks read at open (Step 0 ruling,
  // 2026-10-16: loadCooks keeps only count + last date per meal), re-read whenever the
  // household's count for this meal moves — this device's Cooked it, or a partner's
  // arriving by the placements poll. SELECT only; rows arrive through cook_meal.
  const [recipeView, setRecipeView] = useState(null);
  const [recipeCooks, setRecipeCooks] = useState(null);   // { mealId, rows: [{ id, cooked_at }] }
  const openRecipe = useCallback((m, landing = "top") => {
    if (m && !isNoShop(m)) setRecipeView({ mealId: m.id, landing });
  }, []);
  const rvMealId = recipeView?.mealId || null;
  const rvCookCount = rvMealId ? (cooks[rvMealId]?.count || 0) : 0;
  const rvLastCookedAt = rvMealId ? (cooks[rvMealId]?.lastCookedAt || null) : null;
  // THE ANCHOR (A2-R correction, 063): the open placement's planned_at — stamped by the plan
  // upsert on every plan or re-plan; the meal's last cook is the backstop for rows older than 063.
  const rvPlannedAt = rvMealId ? (placements[rvMealId]?.plannedAt || null) : null;
  const rvAnchor = planAnchor(rvPlannedAt, rvLastCookedAt);
  useEffect(() => {
    if (!rvMealId) { setRecipeCooks(null); return undefined; }
    let cancelled = false;
    (async () => {
      const db = _supabase.current;
      const hh = _household.current;
      if (!db || !hh) return;
      const { data, error: err } = await db
        .from("meal_cooks")
        .select("id, cooked_at")
        .eq("household_id", hh.id)
        .eq("meal_id", rvMealId)
        .order("cooked_at", { ascending: false });
      if (cancelled) return;
      if (err) { console.warn("recipe cooks (non-fatal):", err.message); return; }
      setRecipeCooks({ mealId: rvMealId, rows: data || [] });
    })();
    return () => { cancelled = true; };
  }, [rvMealId, rvCookCount, _supabase, _household]);
  // Switching households closes the view: the id belongs to the old place.
  useEffect(() => { setRecipeView(null); }, [household?.id]);

  // ── Meal photos v1 (065; slice 1) ──────────────────────────────────────
  // The household's live photo rows, newest first, and the household they were read
  // for — the no-flash gate (the provenanceLoadedFor pattern): a hero or a card shows
  // the tint until the read for THIS household resolves, then the photo fades in once.
  // Re-read on entering Plan / Home, on opening a recipe, every 5th tick of the 2 s
  // meals poll (a partner's photo has no other signal — it touches no meal, placement
  // or list row), and after this device's own add / remove. A poll that reads the
  // same set is dropped by fingerprint, so an idle library never re-renders.
  const [mealPhotos, setMealPhotos] = useState({ hhId: null, rows: [], print: "" });
  const photoUrlCacheRef = useRef(null);
  if (!photoUrlCacheRef.current) photoUrlCacheRef.current = createSignedUrlCache({ bucket: "meal-photos" });
  const [photoUrls, setPhotoUrls] = useState({});
  const [photoBusyMealId, setPhotoBusyMealId] = useState(null);
  const loadMealPhotos = useCallback(async () => {
    const hhId = _household.current?.id;
    if (!hhId) return;
    const rows = await fetchMealPhotos();
    if (rows == null || _household.current?.id !== hhId) return;   // a failed read keeps what we had
    const print = photosFingerprint(rows);
    setMealPhotos((prev) => (prev.hhId === hhId && prev.print === print ? prev : { hhId, rows, print }));
  }, [fetchMealPhotos, _household]);
  loadMealPhotosRef.current = loadMealPhotos;
  useEffect(() => {
    if (MEALS_ENABLED && household?.id && authPhase === "ready" && (view === "plan" || view === "home" || rvMealId)) loadMealPhotos();
  }, [household?.id, view, rvMealId, authPhase, loadMealPhotos]);
  const photosReady = !!household?.id && mealPhotos.hhId === household.id;
  const photosByMealId = useMemo(() => (photosReady ? photosByMeal(mealPhotos.rows) : {}), [photosReady, mealPhotos]);
  // Signed URLs (R9): only for what is on screen — the library's card THUMBNAILS (the
  // newest photo per meal, D2 — the library never loads a view copy, A3) and the open
  // recipe's view copies (up to 3). Minted in one batch, reused for the session by path,
  // re-minted only near expiry; the <img> is loading="lazy", so a card below the fold
  // costs its URL, not its bytes, until it scrolls in.
  const libraryOnScreen = view === "plan" && planScreen === "library";
  const wantedPhotoPaths = useMemo(() => {
    const paths = [];
    if (libraryOnScreen) libraryMeals.forEach((m) => { const p = (photosByMealId[m.id] || [])[0]; if (p) paths.push(p.thumb_path); });
    // The board afterglow's "Photo added" thumbnail (S4): the cook-attached photo of a cook this device logged.
    if (view === "plan" && planScreen !== "library") {
      Object.entries(cookIdsByMeal || {}).forEach(([mealId, cookId]) => {
        const p = cookId && (mealPhotos.rows || []).find((r) => r.meal_id === mealId && r.meal_cook_id === cookId);
        if (p) paths.push(p.thumb_path);
      });
    }
    if (rvMealId) (photosByMealId[rvMealId] || []).forEach((p) => paths.push(p.view_path));
    return paths;
  }, [libraryOnScreen, libraryMeals, rvMealId, photosByMealId, view, planScreen, cookIdsByMeal, mealPhotos]);
  // The library's cards carry the photo seam (LibrarySlot): hasCardPhoto as soon as the read
  // says one exists (the slot takes its 132 px shape at once), cardPhotoUrl once minted.
  const libraryCards = useMemo(() => libraryMeals.map((m) => {
    const p = (photosByMealId[m.id] || [])[0];
    return p ? { ...m, hasCardPhoto: true, cardPhotoUrl: photoUrls[p.thumb_path] || null } : m;
  }), [libraryMeals, photosByMealId, photoUrls]);
  useEffect(() => {
    if (wantedPhotoPaths.length === 0) return undefined;
    let cancelled = false;
    photoUrlCacheRef.current.resolve(_supabase.current, wantedPhotoPaths).then((got) => {
      if (cancelled) return;
      setPhotoUrls((prev) => (Object.keys(got).every((k) => prev[k] === got[k]) ? prev : { ...prev, ...got }));
    });
    return () => { cancelled = true; };
  }, [wantedPhotoPaths, _supabase]);
  // Add / remove (R2): quiet on failure — a toast, never the error banner, and the hero
  // is unchanged. The read-back after success is what shows the photo (the server's cap
  // may have aged one out), not an optimistic insert.
  const handleAddMealPhoto = useCallback(async (mealId, file, cookId = null) => {
    if (photoBusyMealId) return;
    setPhotoBusyMealId(mealId);
    try {
      const r = await addMealPhoto(mealId, file, cookId);
      if (!r.ok) showToast(r.reason === "decode" ? "Couldn't read that photo" : "Couldn't add the photo. Try again.");
      await loadMealPhotos();
    } finally { setPhotoBusyMealId(null); }
  }, [photoBusyMealId, addMealPhoto, loadMealPhotos, showToast]);
  // The flip (073; SPEC_meal_sharing_v2 S46): the photo's ⋯ → "Make showcase" (after the S13 prompt)
  // or "Make it a meal photo", author-only. Quiet on failure — the one plain line when the meal's
  // cook photos are full (the server refuses rather than ages one out), a toast otherwise; the
  // read-back shows the result (the chip moves, a freed slot brings "Add a meal photo" back).
  const handleSetPhotoKind = useCallback(async (mealId, photoId, kind) => {
    if (photoBusyMealId) return;
    setPhotoBusyMealId(mealId);
    try {
      const r = await setPhotoKind(photoId, kind);
      if (!r.ok) showToast(r.line);   // the RPC's message or its plain line (setKindFailure) — never swallowed
      await loadMealPhotos();
    } finally { setPhotoBusyMealId(null); }
  }, [photoBusyMealId, setPhotoKind, loadMealPhotos, showToast]);
  const handleRemoveMealPhoto = useCallback(async (photoId) => {
    const ok = await removeMealPhoto(photoId);
    if (!ok) showToast("Couldn't remove the photo. Try again.");
    await loadMealPhotos();
  }, [removeMealPhoto, loadMealPhotos, showToast]);
  // Replace (SPEC_photo_menu DAN-1, ruled 2026-10-25): no replace RPC, and the cap of 3 AGES OUT
  // the oldest rather than refusing, so the order is replaceSteps(old, live count) — jest-proven in
  // mealPhotos.test.js. A cook photo goes through add_meal_photo with the old photo's cook (P1's
  // per-cook replace, atomic, "Cooked {date}" kept); a meal photo under 3 is upload → add → remove;
  // at 3, upload → remove → add, and if that final add fails the old row is already soft-deleted
  // (recoverable by hand) and the error toast says so. A failed upload changes nothing. The new photo
  // is the newest (slot 1, the library card). No success toast. The count is the live set as read
  // (photosByMealId), not the strip's.
  const handleReplaceMealPhoto = useCallback(async (mealId, old, file) => {
    if (photoBusyMealId || !old) return;
    setPhotoBusyMealId(mealId);
    try {
      let upload = null;
      let removed = false;
      let saved = false;
      for (const step of replaceSteps(old, liveCookCount(photosByMealId[mealId]))) {
        if (step === "upload") {
          const up = await uploadMealPhoto(mealId, file);
          if (!up.ok) { showToast(up.reason === "decode" ? "Couldn't read that photo" : "Couldn't add the photo. Try again."); return; }
          upload = up.upload;
        } else if (step === "saveCook" || step === "save") {
          const r = await saveMealPhoto(upload, step === "saveCook" ? old.meal_cook_id : null);
          if (!r.ok) { showToast(removed ? "Couldn't add the new photo. The old one was removed." : "Couldn't replace the photo. Try again."); return; }
          saved = true;
        } else if (step === "removeOld") {
          if (!(await removeMealPhoto(old.id))) {
            showToast(saved ? "Added the new photo, but couldn't remove the old one." : "Couldn't replace the photo. Try again.");
            return;
          }
          removed = true;
        }
      }
    } finally {
      await loadMealPhotos();
      setPhotoBusyMealId(null);
    }
  }, [photoBusyMealId, uploadMealPhoto, saveMealPhoto, removeMealPhoto, photosByMealId, loadMealPhotos, showToast]);
  // ── Cook ratings (066; slice 2, S1) ─────────────────────────────────────
  // cook id → every member's rating rows for that cook, as read (RLS: membership). The afterglow's
  // cook starts empty (it was just logged); the recipe view reads its meal's cooks at open (S5).
  // Writes go through rate_cook only, and only ever for the viewer — the optimistic row below is
  // the viewer's own, replaced by the server's rows on the re-read.
  const [ratingsByCook, setRatingsByCook] = useState({});
  useEffect(() => { setRatingsByCook({}); }, [household?.id]);
  const mergeRatings = useCallback((cookIds, rows) => {
    setRatingsByCook((prev) => {
      const next = { ...prev };
      cookIds.forEach((id) => { next[id] = []; });
      (rows || []).forEach((r) => { (next[r.meal_cook_id] = next[r.meal_cook_id] || []).push(r); });
      return next;
    });
  }, []);
  const myRatingFor = useCallback((cookId) => {
    const me = _internalUserId.current;
    return (ratingsByCook[cookId] || []).find((r) => r.user_id === me) || null;
  }, [ratingsByCook, _internalUserId]);
  const handleRateCook = useCallback(async (cookId, rating, note) => {
    const me = _internalUserId.current;
    if (!cookId || !me) return;
    const before = ratingsByCook[cookId] || [];
    const others = before.filter((r) => r.user_id !== me);
    const cleared = rating == null && !note;
    setRatingsByCook((prev) => ({ ...prev, [cookId]: cleared ? others : [...others, { meal_cook_id: cookId, user_id: me, rating, note: note || null }] }));
    const ok = await rateCook(cookId, rating, note);
    if (!ok) {
      setRatingsByCook((prev) => ({ ...prev, [cookId]: before }));
      showToast("Couldn't save that. Try again.");
      return;
    }
    const rows = await fetchCookRatings([cookId]);
    if (rows) mergeRatings([cookId], rows);
  }, [ratingsByCook, rateCook, fetchCookRatings, mergeRatings, showToast, _internalUserId]);
  // The board's afterglow (S4): the device's cook id (S3), the viewer's own rating, the cook's
  // photo (a "Photo added" thumbnail), and the cap — "Add a meal photo" hides at 3 live photos (R1).
  const afterglowCookPhoto = useCallback((mealId, cookId) => {
    // S46: a cook photo flipped to showcase keeps its meal_cook_id; the afterglow's "Photo added"
    // thumbnail is cook-only, so it skips showcase rows (and the cook is free to add a new cook photo).
    const p = (mealPhotos.rows || []).find((r) => r.meal_id === mealId && r.meal_cook_id === cookId && !isShowcase(r));
    return p ? { id: p.id, url: photoUrls[p.thumb_path] || null } : null;
  }, [mealPhotos, photoUrls]);
  // The open recipe's cooks' ratings (S5): one read when its cook rows arrive (and again whenever they
  // change — a cook from this device, or a partner's arriving by the placements poll).
  const rvCookIds = (recipeCooks?.rows || []).map((r) => r.id).join(",");
  useEffect(() => {
    if (!rvCookIds) return undefined;
    let cancelled = false;
    const ids = rvCookIds.split(",");
    fetchCookRatings(ids).then((rows) => { if (!cancelled && rows) mergeRatings(ids, rows); });
    return () => { cancelled = true; };
  }, [rvCookIds, fetchCookRatings, mergeRatings]);
  const boardAfterglow = useMemo(() => (!isSignedIn || !photosReady ? null : {
    cookIdFor: (mealId) => (cookIdsByMeal || {})[mealId] || null,
    myRating: myRatingFor,
    onRate: handleRateCook,
    photoFor: afterglowCookPhoto,
    atCap: (mealId) => atPhotoCap(photosByMealId[mealId]),   // cook photos only (S26)
    onAddPhoto: handleAddMealPhoto,
    photoBusyMealId,
  }), [isSignedIn, photosReady, cookIdsByMeal, myRatingFor, handleRateCook, afterglowCookPhoto, photosByMealId, handleAddMealPhoto, photoBusyMealId]);
  // A2 / A2-R (2026-10-18): the view's ingredient tags — this meal's list_item_meals
  // links, INCLUDING rows archived as bought at wrap-up (deleted_at set, status
  // bought; list_items_select is membership only, so RLS allows the read — Step
  // 0b), plus the checked / unchecked events on those rows so the buyer can be
  // named. Row 1's window starts at the ANCHOR — the placement's planned_at (063),
  // the meal's last cook as the backstop — and runs to now (A2-R, corrected
  // 2026-10-18: the Add-to-Shop link was the wrong anchor because a re-plan
  // without a cook keeps the old links). One read per open + whenever the
  // anchor or the provenance map moves. Keyed by catalog item: the live link
  // wins over an archived one, then the newest.
  const [recipeLinks, setRecipeLinks] = useState(null);   // { mealId, byCatalog: { [catalog_item_id]: link }, addedThisPlan }
  useEffect(() => {
    // Not before the household's cook reduce has loaded (2026-10-18 correction): the window's
    // anchor is the meal's last cook, and "no entry" only means "never cooked" once it is read.
    if (!rvMealId || !cooksLoaded) { setRecipeLinks(null); return undefined; }
    let cancelled = false;
    (async () => {
      const db = _supabase.current;
      const hh = _household.current;
      if (!db || !hh) return;
      const { data: links, error: lErr } = await db
        .from("list_item_meals")
        .select("list_item_id, created_at, list_items!inner(id, catalog_item_id, status, quantity, deleted_at, household_id)")
        .eq("meal_id", rvMealId)
        .eq("list_items.household_id", hh.id);
      if (cancelled) return;
      if (lErr) { console.warn("recipe links (non-fatal):", lErr.message); return; }
      const ids = (links || []).map((l) => l.list_item_id);
      let events = [];
      if (ids.length > 0) {
        const { data: ev, error: eErr } = await db
          .from("list_item_events")
          .select("list_item_id, user_id, event_type, created_at, sequence")
          .eq("household_id", hh.id)
          .in("list_item_id", ids)
          .in("event_type", ["checked", "unchecked"])
          .order("created_at", { ascending: false })
          .order("sequence", { ascending: false })
          .limit(500);
        if (cancelled) return;
        if (eErr) console.warn("recipe link events (non-fatal):", eErr.message);   // nameless "Bought" then
        events = ev || [];
      }
      const byItem = {};
      events.forEach((e) => { (byItem[e.list_item_id] = byItem[e.list_item_id] || []).push(e); });
      const byCatalog = {};
      (links || []).forEach((l) => {
        const li = l.list_items;
        if (!li?.catalog_item_id) return;
        // Row 1's window starts at the later of the link and the meal's last cook (the
        // re-plan edge): a purchase for the previous cook never reads as bought for this one.
        const link = {
          status: li.status, deletedAt: li.deleted_at ?? null, quantity: Number(li.quantity) || 0,
          linkCreatedAt: l.created_at,
          ...boughtForThisMeal({ status: li.status, linkCreatedAt: l.created_at }, byItem[l.list_item_id], rvAnchor),
        };
        const cur = byCatalog[li.catalog_item_id];
        if (!cur || (cur.deletedAt && !link.deletedAt) || (!!cur.deletedAt === !!link.deletedAt && link.linkCreatedAt > cur.linkCreatedAt)) byCatalog[li.catalog_item_id] = link;
      });
      // Was THIS plan added to Shop? One link at or after the last cook (2026-10-18 correction).
      setRecipeLinks({ mealId: rvMealId, byCatalog, addedThisPlan: wasAddedThisPlan((links || []).map((l) => ({ linkCreatedAt: l.created_at })), rvAnchor) });
    })();
    return () => { cancelled = true; };
  }, [rvMealId, rvAnchor, cooksLoaded, mealProvenance, _supabase, _household]);
  const rvMeal = rvMealId ? mealById[rvMealId] : null;
  // GIVE THIS MEAL (SPEC_meal_sharing_phase_a_client Commit 2). The open meal's live gifts decide whether
  // the give sheet's top line offers "Stop giving"; re-read after Give and after Stop. The code never enters state here —
  // the sheet holds it for the share payload and lets it go.
  const [rvGifts, setRvGifts] = useState({ mealId: null, count: 0 });
  const loadRvGifts = useCallback(async (mealId) => {
    if (!mealId) return;
    const rows = await fetchMealGifts(mealId);
    if (rows) setRvGifts({ mealId, count: rows.length });
  }, [fetchMealGifts]);
  useEffect(() => { if (MEALS_ENABLED && rvMealId && authPhase === "ready") loadRvGifts(rvMealId); }, [rvMealId, authPhase, loadRvGifts]);
  // Send it (C3): navigator.share is called INSIDE the tap — the first await here IS the share. No
  // share API (desktop) → the text and the link go to the clipboard, as the invite does.
  const handleShareGift = useCallback(async (payload) => {
    if (!payload) return;
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share(payload); } catch (e) { if (e && e.name !== "AbortError") console.error("share failed:", e); }
    } else if (typeof navigator !== "undefined" && navigator.clipboard) {
      // The thank-you (S24) has no url: the message alone is copied.
      try { await navigator.clipboard.writeText(payload.url ? `${payload.text} ${payload.url}` : payload.text); showToast(payload.url ? "Link copied" : "Message copied"); } catch (e) { showToast("Couldn’t copy."); }
    }
  }, [showToast]);
  const handleCopyGiftLink = useCallback(async (url) => {
    try { await navigator.clipboard.writeText(url); showToast("Link copied"); } catch (e) { showToast("Couldn’t copy the link."); }
  }, [showToast]);
  const handleStopGiving = useCallback(async (mealId) => {
    const r = await revokeMealGifts(mealId);
    showToast(r.ok ? "Stopped giving this meal." : "Couldn’t stop giving. Try again.");
    loadRvGifts(mealId);
  }, [revokeMealGifts, showToast, loadRvGifts]);
  const giveProps = useMemo(() => (!isSignedIn ? null : {
    openGifts: rvGifts.mealId === rvMealId ? rvGifts.count : 0,
    give: giveMeal, copyPhoto: copyGiftPhoto, stopGiving: handleStopGiving,
    share: handleShareGift, copyLink: handleCopyGiftLink, referralCode, onGiven: loadRvGifts,
  }), [isSignedIn, rvGifts, rvMealId, giveMeal, copyGiftPhoto, handleStopGiving, handleShareGift, handleCopyGiftLink, referralCode, loadRvGifts]);
  // The plan action follows the board's own derivation (D9); null = not on This Week.
  // boardCards (not boardMeals) so a card cooked this load still reads "✓ Cooked".
  const rvState = rvMeal && boardCards.some((m) => m.id === rvMeal.id)
    ? boardCardState(rvMeal, { placements, rows: mealRowCounts, mealById, cookedIds: afterglowIds })
    : null;
  // iOS standalone status bar — the MEASURED inset (fourth cut, 2026-10-17; src/lib/standaloneInset.js).
  // env(safe-area-inset-top) reads 0 on Dan's installed app while the WebView is full height under a
  // transparent bar, so the inset is measured: standalone only, screen.height − innerHeight must be a
  // known bar height (47 / 54 / 59 / 62), latched through keyboard resizes, 0 in landscape. Written
  // once per change onto :root. The viewport itself already starts BELOW the bar (proven on the phone
  // 2026-10-17), so the header carries no offset; the value sizes the .safe-top band that covers the
  // bar's region above the viewport. A Safari tab never qualifies, so a tab stays pixel-identical.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const root = document.documentElement;
    let current = -1;
    const apply = () => {
      const next = nextInsetTop(Math.max(current, 0), {
        standalone: window.navigator.standalone,
        screenHeight: window.screen.height,
        innerHeight: window.innerHeight,
        innerWidth: window.innerWidth,
      });
      if (next === current) return;
      current = next;
      root.style.setProperty("--op-inset-top", `${next}px`);
    };
    apply();
    const later = window.setTimeout(apply, 300);   // iOS settles innerHeight after the first paint
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
    const so = window.screen && window.screen.orientation;
    if (so && so.addEventListener) so.addEventListener("change", apply);
    return () => {
      window.clearTimeout(later);
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      if (so && so.removeEventListener) so.removeEventListener("change", apply);
    };
  }, []);
  const [mealSaving, setMealSaving] = useState(false);
  const [mealDeleting, setMealDeleting] = useState(false);

  // After "Add to our meals" saved the copy (Commit 3): the receipt (one silent retry inside), then the photo
  // copy when the gift has one (quiet beyond one toast — the meal is already theirs, S40), then the S24
  // thank-you, once. The toast is the 10-07 line.
  const finishGiftArrival = useCallback(async (gift, mealId) => {
    const rec = await recordMealGiftReceipt(gift.code, mealId);
    showToast("Added to your meals.");
    if (!rec.ok) console.warn("[gift] the receipt did not save; the meal is theirs regardless (10-07 residual)");
    if (gift.hasPhoto && rec.ok) {
      const p = await acceptGiftPhoto(gift.giftId, mealId, gift.photoExt);   // S47: the gift's own format
      if (!p.ok) showToast("Added, but the photo couldn’t come along.");
      await loadMealPhotos();
    }
    setThankYou({ mealId, ...thankYouPayload(gift.mealName, gift.giverFirst) });
  }, [recordMealGiftReceipt, acceptGiftPhoto, loadMealPhotos, showToast]);

  const commitMealSheet = useCallback(async ({ name, instructions, ingredients, occasion, attribution }) => {
    if (!mealSheet) return;
    setMealSaving(true);
    try {
      // The deferred catalog write. Pending placeholders become real rows HERE,
      // and only here — a draft that is cancelled never reaches this line, so it
      // never mints anything. null means an insert failed and the hook has said
      // why; leave the sheet open so the person can retry rather than commit a
      // meal that references ids which do not exist.
      const resolved = await materializePendingIngredients(ingredients);
      if (!resolved) return;
      const payload = {
        name,
        instructions,
        occasion,   // 061 — ordered Good-for list; the hook normalises it
        attribution,   // 062 — the From field; the hook saves '' as null
        // flat: the servings dial is deferred. NOTE the AI draft also reports its own
        // baseServings and it is deliberately DISCARDED here — quantity_per_serving only
        // reads as a flat quantity while base_servings is 1 (migration 025). Storing the
        // model's 4 would silently reinterpret the column as a ratio and double-scale it
        // the day the dial ships.
        baseServings: 1,
        ingredients: resolved.map((r) => ({
          catalog_item_id: r.catalog_item_id,
          quantity_per_serving: r.quantity_per_serving,
          on_hand: !!r.on_hand,   // 044
        })),
      };
      const ok = mealSheet.mode === "edit"
        ? await updateMeal(mealSheet.meal.id, payload)
        : await createMeal(payload);
      if (ok) {
        const gift = mealSheet.mode === "create" ? mealSheet.gift || null : null;
        setMealSheet(null);
        await loadMeals();   // reflect the new name/count on PLAN immediately
        // An edit can take dropped ingredients off Shop (updateMeal): re-read provenance so
        // the card's "{n} left" and the board's counts move in the same beat.
        if (mealSheet.mode === "edit") await refreshProvenance();
        // A received meal: createMeal returned the new row's id — the receipt and the photo follow.
        if (gift && typeof ok === "string") await finishGiftArrival(gift, ok);
      }
    } finally {
      setMealSaving(false);
    }
  }, [mealSheet, materializePendingIngredients, updateMeal, createMeal, loadMeals, refreshProvenance, finishGiftArrival]);
  // Delete the recipe itself. Zeroing of its still-pending list items
  // happens inside deleteMeal (bought items untouched, shared ingredients
  // left for the meals that still need them) — the sheet only drives it.
  const commitMealDelete = useCallback(async () => {
    if (!mealSheet || mealSheet.mode !== "edit" || !mealSheet.meal?.id) return;
    setMealDeleting(true);
    try {
      const ok = await deleteMeal(mealSheet.meal.id);
      if (ok) {
        setMealSheet(null);
        await loadMeals();   // the deleted meal drops off PLAN immediately
      }
    } finally {
      setMealDeleting(false);
    }
  }, [mealSheet, deleteMeal, loadMeals]);




  // ── Shared-list provenance line (SPEC_provenance_line_display.md) ──
  // Up to TWO facets, always in this order, each rendered only when it applies:
  //   1. clay  — "Added by …"  the list_item_contributors ledger (a direct add)
  //   2. teal  — "For … · …"   the list_item_meals ledger (a meal pulled it in)
  // Single-origin rows stay one line; only genuinely dual-origin rows spend two.
  //
  // TEAL IS THE MEAL SIGNAL and nothing else in the row uses it, so its mere
  // presence tells the eye a meal is in play. Keep it pure — never render teal
  // for a catalog-only row.
  //
  // Each facet owns its own name, which is the point: on one line, "Dan & Taco
  // Night (Elly)" lets a name bleed onto the wrong origin and reads as though
  // Dan added the ingredient. Two lines make that misparse structurally
  // impossible. Do not merge them.
  const joinNames = (names) => {
    const n = [...new Set(names.filter(Boolean))];
    if (n.length === 0) return null;
    if (n.length === 1) return n[0];
    return `${n.slice(0, -1).join(", ")} & ${n[n.length - 1]}`;
  };

  const provenanceLines = useCallback((item) => {
    // Facet 1 — who put it on the list directly.
    const contributorNames = (item.contributors || [])
      .map((c) => (c.clerkId && c.clerkId === user?.id ? "you" : c.fullName))
      .filter(Boolean);
    const catalogLine = contributorNames.length > 0
      ? `Added by ${joinNames(contributorNames)}`
      : null;

    // Facet 2 — which meal(s) pulled it in. Names the meals rather than
    // collapsing to a "Multiple meals" count; that string is deliberately
    // retired, not lost.
    //
    // NO NAME CLAUSE. This briefly read "For Pizza · Dan Holmes", naming the
    // meal's AUTHOR — but the shared-list question is who put this on THIS
    // WEEK's list, and that is not recorded anywhere: list_item_meals has no
    // user column. Naming the author answers a question nobody asked and
    // misattributes on a shared list, so the clause is off until real
    // attribution lands (post-Vegas). mealProvenance still carries createdBy,
    // ready for it.
    const links = mealProvenance[item.catalogItemId] || [];
    const mealNames = joinNames(links.map((l) => l.name));
    const mealLine = mealNames ? `For ${mealNames}` : null;

    if (!catalogLine && !mealLine) return null;
    // Text size pass 2: both sub-lines are meta 14 at k 1 (they sat fixed at 12.5 px, the same
    // size at Default and XXL). The "For {meal}" line is teal TEXT at body size, so it takes the
    // text teal #0A776D (4.96:1 on cream; brand #0D9488 read 3.43:1) — ruling 2026-10-07: brand
    // teal stays on fills, strokes and large text only.
    const base = { fontFamily: "'Lato', sans-serif", fontSize: ts(14), letterSpacing: "0.3px", marginTop: "3px" };
    return (
      <>
        {catalogLine && <div style={{ ...base, color: "#8A5F3A" }}>{catalogLine}</div>}
        {mealLine && <div style={{ ...base, color: "#0A776D" }}>{mealLine}</div>}
      </>
    );
  }, [mealProvenance, user?.id]);

  const [editModalName, setEditModalName] = useState("");
  const [editModalPrice, setEditModalPrice] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");
  const [newItemName, setNewItemName] = useState("");
  const [newItemPrice, setNewItemPrice] = useState("");
  const [newItemCategory, setNewItemCategory] = useState(CATEGORY_ORDER[0]);
  const [addError, setAddError] = useState("");
  const [addModalResetDone, setAddModalResetDone] = useState(false);
  const [invitePreparing, setInvitePreparing] = useState(false); // share hand-off link generation in flight
  const [joinBanner, setJoinBanner] = useState(null); // household name after accepting
  const [pendingJoinId, setPendingJoinId] = useState(null); // joined household id awaiting the lens switch (reactive; see join effect)
  const [showVelayoMenu, setShowVelayoMenu] = useState(false);
  const [showHouseholdModal, setShowHouseholdModal] = useState(false);
  // ── Edit household sheet (OurBanner) — draft state, committed on Save ──
  const [showEditHousehold, setShowEditHousehold] = useState(false);
  const [edName, setEdName] = useState("");
  const [edPhotoPath, setEdPhotoPath] = useState(null); // existing stored path; null = none/removed
  const [edFile, setEdFile] = useState(null);           // newly chosen File, not yet uploaded
  const [edLocalUrl, setEdLocalUrl] = useState(null);   // object URL preview for edFile
  const [edX, setEdX] = useState(50);
  const [edY, setEdY] = useState(50);
  const [edZoom, setEdZoom] = useState(100);
  const [edWordmark, setEdWordmark] = useState("large");
  const [edSaving, setEdSaving] = useState(false);
  const [edDeleteConfirm, setEdDeleteConfirm] = useState(false);
  const edFileInputRef = useRef(null);
  const edDragRef = useRef(null); // { startX, startY, baseX, baseY }
  const [creating, setCreating] = useState(false);
  const [newHouseholdName, setNewHouseholdName] = useState("");
  const [creatingInFlight, setCreatingInFlight] = useState(false);
  const [showManageCategoriesModal, setShowManageCategoriesModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [renamingCategory, setRenamingCategory] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [deleteMoveTarget, setDeleteMoveTarget] = useState("");
  const [householdCategories, setHouseholdCategories] = useState(new Set());
  const [stapleFilter, setStapleFilter] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [searchPickerOpen, setSearchPickerOpen] = useState(false);
  // The `+ New category` tile reveals the name input, rather than the input
  // sitting open as a second, competing answer to "where does this go?".
  const [searchNewCatOpen, setSearchNewCatOpen] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState("");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showWrapUpModal, setShowWrapUpModal] = useState(false);
  const [wrapUpRollItems, setWrapUpRollItems] = useState(new Set()); // item names to roll forward
  // Two pieces, deliberately separate:
  //   tripSnapshotRef — the facts, captured the moment Wrap up is invoked,
  //     while the trip still exists. It has to be a capture: wrapUpTrip nulls
  //     activeSession, closes the cycle and empties listRows, so every live
  //     source the All done card reads (totalItems, checkedCount,
  //     activeSession.started_at) is gone by the time the summary renders.
  //     `minutes` is frozen at capture, never recomputed. A ref, not state,
  //     because the direct path writes it and reads it back in the same tick.
  //   tripSummary — the receipt actually on screen, or null. Set once, on a
  //     wrap-up that closed AND bought something.
  // Facts only — no learning line, no "faster than last time". Those are
  // claims, and claims need qualification: this will later be backed by the
  // get_wrap_up_summary RPC (SPEC_wrapup_share.md), pending qualification v2.
  const tripSnapshotRef = useRef(null);
  const [tripSummary, setTripSummary] = useState(null);
  // Trip reality (SPEC_trip_qualification_v2): the server's verdict for the
  // wrapped session, fetched once the summary is up. The client displays it and
  // never decides it (D8). realityAnswer is the local record of the last tap:
  // "real" collapses to "Counted."; "testing" collapses to the EXCLUDED state's
  // own line, where Count it stays available — a testing answer is never final
  // on the summary (D7). Only a same-answer double tap is ignored.
  const [tripReality, setTripReality] = useState(null);
  const [realityAnswer, setRealityAnswer] = useState(null);

  // ── D3: hash routes (SPEC_rum_dxa_exposure.md) — ADDITIVE. The hash mirrors
  // `view`; `view` values are untouched and every view === "…" branch still
  // keys on them. Three pieces:
  //   goToDoor  — a nav tap sets view AND pushes the door hash (a history entry,
  //               so Back walks doors). Passed to <Helm />.
  //   hashchange — Back/forward/typed hash maps back to view; leaving
  //               #/shop/wrap-up closes the Wrap up modal.
  //   mirror    — programmatic view changes (the landing effect) get their hash
  //               by replaceState: a route-change span, no history entry.
  // The RUM agent emits routeChange on both hashchange and replaceState, so
  // every door change is a page view. Sheets and other modals write no hash.
  // The two scroll roots: on phones the window scrolls; inside the desktop phone
  // frame the .phone-scroll column scrolls and the document does not. Scroll
  // both — the one that isn't scrolling is a no-op.
  const scrollToTop = () => {
    window.scrollTo(0, 0);
    const col = document.querySelector(".phone-scroll");
    if (col) col.scrollTop = 0;
  };
  const goToDoor = useCallback((v) => {
    // D4 (SPEC_auth_state_ui_gating): without a live session, every door but Home
    // opens the sign-in modal instead of switching view — no household-shaped
    // shell renders behind a SIGN IN header. (Browse's signed-out preview went
    // with D4a: the anon catalog fetch behind it is retired, so there is nothing
    // to show.) A tap is a landing decision (§Landing).
    if (isLoaded && !sessionLive && v !== "home") { openSignIn(); return; }
    setLandingDecided(true);
    // D12 (SPEC_plan_week_meals_switch): tapping PLAN while already on Plan
    // returns to This Week, scrolled to the top — the way back when the switch
    // row is off-screen and the Helm is compact (the iOS re-tap convention). A
    // SAME-door tap only: coming from another door is left as it was — leaving
    // Plan already resets planScreen to the board (the view effect), and Home's
    // "Add a meal" deliberately sets the library before calling goToDoor("plan"),
    // a route a cross-door reset here would break. Before this, a same-door tap
    // did nothing (same view, hash already equal).
    if (v === "plan" && view === "plan") { setPlanScreen("board"); scrollToTop(); }
    setView(v);
    const h = hashForView(v);
    if (h && window.location.hash !== h) window.location.hash = h;
  }, [isLoaded, sessionLive, openSignIn, view]);
  // Not live — signed_out, session_lost, AND booting (Clerk still loading) —
  // every door shows the Home welcome variant, never the signed-in shell
  // (Signed-out entry cleanup, 2026-10-02: past the splash's 5 s failsafe a
  // slow Clerk used to show the household-shaped shell under a header with two
  // disabled buttons). While booting the welcome's own pair is disabled; it goes
  // live when Clerk loads, or the whole welcome hands off to the signed-in
  // shell in one render. The view is KEPT (only what renders changes) — it is
  // the pendingRoute: signing in from a #/plan deep link lands on Plan.
  const signedOutWelcome = !sessionLive;
  useEffect(() => {
    const onHashChange = () => {
      const v = viewForHash(window.location.hash);
      if (v) { setLandingDecided(true); setView(v); }   // Back/forward/typed: the user's route
      if (window.location.hash !== WRAP_UP_HASH) setShowWrapUpModal(false);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  useEffect(() => {
    if (!landingDecided) return;   // §Landing: hold — no hash until this load has decided
    const h = hashForView(view);
    if (!h || viewForHash(window.location.hash) === view) return;
    window.history.replaceState(null, "", h);
  }, [view, landingDecided]);
  // Wrap up is the one modal with a hash (D3, decided 2026-09-16): the funnel's
  // last step is a page view. replaceState both ways — no extra history entry,
  // and Back from the open modal leaves Shop, which closes it (hashchange above).
  const closeWrapUp = useCallback(() => {
    setShowWrapUpModal(false);
    if (window.location.hash === WRAP_UP_HASH) window.history.replaceState(null, "", hashForView("list"));
  }, []);
  const [wrappingUp, setWrappingUp] = useState(false);
  // Shop lens (SPEC_shop_lens_instore_capture.md): "aisles" (grouped) or "az"
  // (flat). Replaces the three-phase cycle on Shop — checked items are no longer
  // a filter state, they live in the In cart tray. Per-device, survives a reload
  // (the old phase index never persisted, so this is a fresh localStorage slot).
  const [shopLens, setShopLens] = useState(() => {
    try { return localStorage.getItem("op_shop_lens") === "az" ? "az" : "aisles"; } catch { return "aisles"; }
  });
  useEffect(() => {
    try { localStorage.setItem("op_shop_lens", shopLens); } catch { /* storage unavailable — lens stays in memory */ }
  }, [shopLens]);
  // Per-trip Shop state (component state, not DB): the tray's expanded flag, the
  // catalog ids added from the aisle this trip ("added here" tag — the visible
  // face of the added_in_store event, not a permanent badge), the Add sheet, and
  // the store prompt (explicitly re-opened from the eyebrow / skipped for a
  // session id). Reset on household switch and on Wrap up.
  const [trayOpen, setTrayOpen] = useState(false);
  const [addedHereIds, setAddedHereIds] = useState(() => new Set());
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  // Check motion (presentation only — the database write happens on the tap).
  // list_item.id → { dir: "out" (aisle → tray) | "in" (tray → aisle), phase: "mark" | "collapse" }.
  // Keyed by the ROW's id, not its name: names are not unique (a custom "Milk"
  // beside the catalog "Milk", a reused cycle row), and two rows must never
  // share a phase.
  // out: beat 1 marks the row (teal check, name dims + strikes, 120ms), hold
  // ~250ms; beat 2 collapses it (200ms) while the tray count picks it up; then
  // the row unmounts. in (uncheck from the tray) is the reverse with no hold —
  // it's a correction. A row in motion ignores further taps, so a fast double
  // check of two adjacent rows can't stack or jump.
  const [rowMotion, setRowMotion] = useState({});
  const rowMotionTimers = useRef({});
  useEffect(() => () => { Object.values(rowMotionTimers.current).forEach(clearTimeout); }, []);
  const [storePromptOpen, setStorePromptOpen] = useState(false);
  const [storePromptSkippedFor, setStorePromptSkippedFor] = useState(null);
  const [storeSaving, setStoreSaving] = useState(false);
  const addSheetInputRef = useRef(null);
  // Browse declutter cycle: 0 default (pills shown, grouped) · 1 tidied (pills hidden, grouped) · 2 flat (A–Z).
  const [browsePhase, setBrowsePhase] = useState(0);
  // Declutter phase + Browse filters are per-view/per-household ephemeral state:
  // reset on tab or household switch so a stale filter can't shrink the new
  // household's list (phase 1 hides the pills that would otherwise explain it).
  useEffect(() => {
    setBrowsePhase(0);
    setSelectedCategories(new Set());
    setStapleFilter(false);
  }, [view, activeHouseholdId]);
  // eslint-disable-next-line no-unused-vars
  const [categoryError, setCategoryError] = useState(null);

  const { signOut } = useClerk();
  const [showProfileSheet, setShowProfileSheet] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [profileName, setProfileName] = useState(() => user?.fullName || "");
  const [showPrices, setShowPrices] = useState(() => {
    return localStorage.getItem('op_showPrices') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('op_showPrices', showPrices);
  }, [showPrices]);

  // Device-local list text size — persists the index (0–4), not the scale.
  const [textSizeIdx, setTextSizeIdx] = useState(() => {
    const saved = parseInt(localStorage.getItem("op_list_text_size"), 10);
    return Number.isInteger(saved) && saved >= 0 && saved < TEXT_STEPS.length ? saved : 1;
  });

  useEffect(() => {
    document.documentElement.style.setProperty("--op-text-scale", TEXT_STEPS[textSizeIdx]);
    localStorage.setItem("op_list_text_size", String(textSizeIdx));
  }, [textSizeIdx]);

  // Re-fetch the member roster each time the manage-households sheet opens,
  // so name changes by other members appear without a full page reload.
  useEffect(() => {
    if (showHouseholdModal) {
      refreshMembers();
    }
  }, [showHouseholdModal, refreshMembers]);

  // Add all ("+ Add M to Shop"): the hook's batch over the Planned meal cards
  // in queue order — boardStats.stillToAdd, which already excludes no-shop
  // cards and ingredient-less meals — one toast at the end, with a SHOP action.
  const [lockingAll, setLockingAll] = useState(false);
  const handleLockInAll = useCallback(async () => {
    const ids = boardStats.stillToAdd;
    if (!ids.length) return;
    setLockingAll(true);
    try {
      const rowsBefore = _listRows?.current;
      // { added, readyOnHand }: readyOnHand = the meals 064 stamped ready (every ingredient
      // on hand), counted from the placement reload inside the hook (SPEC_all_on_hand).
      const { added: n, readyOnHand: m } = await lockInAll(ids);
      await refreshProvenance();
      const line = m > 0 ? addAllOnHandLine(n, m) : addedToast(`${n} meal${n === 1 ? "" : "s"}`, rowsBefore);
      showToast(line, { label: "Shop", onClick: () => goToDoor("list") });
    } finally {
      setLockingAll(false);
    }
  }, [boardStats, lockInAll, refreshProvenance, showToast, addedToast, _listRows, goToDoor]);

  // Defined here, below showToast, rather than beside handleAddMealToList where it
  // logically belongs: it needs showToast, and CI=true turns no-use-before-define
  // into a build failure. Only the confirm half moved — opening the prompt still
  // lives with the add handler.
  const confirmOnHandPrompt = useCallback(async () => {
    if (!onHandPrompt) return;
    const { mealId, mealName, choices } = onHandPrompt;
    const includeIds = Object.entries(choices).filter(([, v]) => v === "include").map(([k]) => k);

    setOnHandPrompt(null);
    setAddingMealId(mealId);
    try {
      const rowsBefore = _listRows?.current;
      const count = await addMealToList(mealId, 1, includeIds);
      await refreshProvenance();
      // All on hand (SPEC_all_on_hand D1/D3): nothing to add, so no trip — 064 stamped the
      // placement ready and addMealToList reloaded placements, so every surface already
      // reads "Everything's in" with Cooked it. Mark the moment.
      if (!count) showToast("Everything's on hand — no trip needed.");
      else showToast(addedToast(mealName, rowsBefore));
    } finally {
      setAddingMealId(null);
    }
  }, [onHandPrompt, addMealToList, refreshProvenance, showToast, addedToast, _listRows]);


  const budgetNum = household?.budget_goal ? parseFloat(household.budget_goal) : null;

  // Build categories from live Supabase catalog
  const categories = useMemo(() => {
    // Reverse lookup: display name → raw name (handles legacy emoji-named catalog rows)
    const displayToRaw = {};
    Object.entries(CATEGORY_DISPLAY).forEach(([raw, display]) => {
      displayToRaw[display] = raw;
    });

    const catMap = {};
    Object.values(catalogMap).forEach(item => {
      let cat = item.category || "Household";
      // Normalize: if the stored category is an emoji display name, map it back to raw
      if (displayToRaw[cat]) cat = displayToRaw[cat];
      if (!catMap[cat]) catMap[cat] = [];
      catMap[cat].push({ name: item.name });
    });

    // Sort categories: known order first, then custom
    const sorted = [];
    CATEGORY_ORDER.forEach(catKey => {
      if (catMap[catKey]) {
        sorted.push({
          name: CATEGORY_DISPLAY[catKey] || catKey,
          rawName: catKey,
          items: catMap[catKey].sort((a, b) => a.name.localeCompare(b.name)),
        });
      }
    });
    // Append any unknown categories (custom items etc)
    Object.keys(catMap).forEach(catKey => {
      if (!CATEGORY_ORDER.includes(catKey)) {
        sorted.push({
          name: CATEGORY_DISPLAY[catKey] || catKey,
          rawName: catKey,
          items: catMap[catKey].sort((a, b) => a.name.localeCompare(b.name)),
        });
      }
    });
    // Append empty household categories (created but no items yet)
    householdCategories.forEach(catKey => {
      if (!sorted.find(c => c.rawName === catKey)) {
        sorted.push({ name: catKey, rawName: catKey, items: [] });
      }
    });
    return sorted;
  }, [catalogMap, householdCategories]);

  const displayCategories = useMemo(() => {
    // Layer 1 — Staples is a cross-cutting filter applied first
    let result = stapleFilter
      ? categories
          .map(cat => ({
            ...cat,
            items: cat.items.filter(item => catalogMap[item.name]?.is_staple),
          }))
          .filter(cat => cat.items.length > 0)
      : categories;

    // Layer 2 — Category chips narrow within whatever Layer 1 produced.
    // Only narrow by categories that still exist, so a stale filter id
    // (e.g. a category deleted here or on another device) can never blank the view.
    const liveNames = new Set(result.map(cat => cat.rawName));
    const activeSelected = [...selectedCategories].filter(n => liveNames.has(n));
    if (activeSelected.length > 0) {
      result = result.filter(cat => activeSelected.includes(cat.rawName));
    }

    return result;
  }, [categories, stapleFilter, selectedCategories, catalogMap]);

  // Browse declutter phase 2 — flat A–Z across the filtered catalog (rawCategory attached for price fallback).
  const browseFlatItems = useMemo(() =>
    displayCategories
      .flatMap(cat => cat.items.map(item => ({ ...item, rawCategory: cat.rawName })))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [displayCategories]);
  // Count of active filters for the declutter descriptor line.
  const browseFilterCount = (stapleFilter ? 1 : 0) + selectedCategories.size;

  // Filter descriptor — names the active FILTER SET, not the active categories.
  // The rail carries two kinds of filter: category values, and Staples, which is
  // NOT a category — it is per-household row-presence in `household_staples`
  // (migration 016), applied as a predicate in displayCategories Layer 1. Naming
  // only the categories while counting a staple-narrowed set made the line
  // describe a set its own sentence didn't name.
  //
  // The count always comes from the same rows the list is rendering, so the
  // number and the names can never disagree.
  const browseFilterDescriptor = useMemo(() => {
    // Only categories that survive Layer 1 count as active — a selection that has
    // gone stale (category deleted here or on another device) must not name every
    // category, and must not suppress a Staples-only line.
    const shownCats = displayCategories.filter(cat => selectedCategories.has(cat.rawName));
    const categoriesActive = shownCats.length > 0;
    if (!stapleFilter && !categoriesActive) return null;

    const shown = categoriesActive ? shownCats : displayCategories;
    return {
      // Staples leads: it is the broader predicate, and it isn't a category.
      names: [
        ...(stapleFilter ? ["Staples"] : []),
        ...shown.map(cat => categoryRailLabel(cat.rawName)),
      ],
      count: shown.reduce((total, cat) => total + cat.items.length, 0),
    };
  }, [stapleFilter, selectedCategories, displayCategories]);

  const catRailNode = useRef(null);
  // Which edges have more content past them — drives the fade masks, so a mask
  // never dims content that isn't overflowing. A rail that doesn't overflow is
  // at BOTH edges at once, which turns both masks off with no separate
  // "overflows" flag to keep in sync.
  const [railEdges, setRailEdges] = useState({ atStart: true, atEnd: true });

  const syncRailEdges = useCallback(() => {
    const rail = catRailNode.current;
    if (!rail) return;
    const max = rail.scrollWidth - rail.clientWidth;
    const atStart = rail.scrollLeft <= 1;
    const atEnd = rail.scrollLeft >= max - 1;
    setRailEdges(prev =>
      prev.atStart === atStart && prev.atEnd === atEnd
        ? prev
        : { atStart, atEnd });
  }, []);

  // Resting position. An ACTIVE pill is scrolled into view; with no active pill the
  // rail is pinned to 0 EXPLICITLY rather than just left alone — the pills arrive
  // asynchronously (catalog load, emoji metrics), and a scroll-snap container that
  // gains children re-runs snap selection, which is what was parking the rail
  // mid-list with Staples off-screen. Layout effect so it lands before paint, and
  // keyed off the pill set rather than the selection so it never yanks a rail the
  // user is mid-scroll.
  useLayoutEffect(() => {
    const rail = catRailNode.current;
    if (!rail) return;
    const firstActive = rail.querySelector('[data-active="1"]');
    rail.scrollLeft = firstActive
      ? Math.max(0, firstActive.offsetLeft - rail.offsetLeft)
      : 0;
    syncRailEdges();
    // Emoji metrics can settle after this effect and change scrollWidth without
    // changing the pill set, so re-measure once fonts are ready.
    if (document.fonts?.ready) document.fonts.ready.then(syncRailEdges).catch(() => {});
  }, [categories, browsePhase, syncRailEdges]);

  useEffect(() => {
    const rail = catRailNode.current;
    if (!rail) return;
    rail.addEventListener("scroll", syncRailEdges, { passive: true });
    window.addEventListener("resize", syncRailEdges);
    return () => {
      rail.removeEventListener("scroll", syncRailEdges);
      window.removeEventListener("resize", syncRailEdges);
    };
  }, [syncRailEdges, browsePhase]);

  // No first-run scroll hint here by design — see SPEC_browse_category_rail.md.
  // The styled scrollbar advertises that the rail scrolls, permanently and
  // without motion, so a one-off animation has nothing left to teach.

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.trim().toLowerCase();
    const matches = [];
    categories.forEach(cat => {
      cat.items.forEach(item => {
        if (item.name.toLowerCase().includes(q)) {
          matches.push({ name: item.name, category: cat.name, rawCategory: cat.rawName });
        }
      });
    });
    return matches;
  }, [searchQuery, categories]);

  // Exact-normalized name key (F1b): lowercase + trim + collapse internal whitespace.
  // NEVER fuzzy — this gates a household-wide un-hide, so it must match exactly.
  const normalizeName = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");

  // A HIDDEN item whose name exactly matches the current query, live on the list
  // or not. `searchResults` is built from the catalog map, which EXCLUDES hidden
  // items — so such an item would show as "No results" and fall through to the
  // false "add as new" that forks the catalog row. The shared results component
  // renders it as a reveal card instead (F1b Layer 1, the honest UX on top of
  // the floor): live → un-hide only; not live → un-hide and add at 1, the same
  // branch addSearchedItem takes.
  const hiddenMatch = useMemo(() => {
    const typed = searchQuery.trim();
    if (!typed) return null;
    const norm = normalizeName(typed);
    const hidden = hiddenCatalogItems.find(h => normalizeName(h.name) === norm);
    if (!hidden) return null;
    const liveRow = listRows.find(r => r.catalogItemId === hidden.id && (r.quantity || 0) > 0);
    return { item: hidden, qty: liveRow ? liveRow.quantity : 0, live: !!liveRow };
  }, [searchQuery, hiddenCatalogItems, listRows]);

  // Search "add to your list" handler (F1b Layer 1 — the floor). Re-adding a HIDDEN
  // item must never write quantity across the person boundary: hide is a lens, not an
  // edit. Resolve the typed name against the hidden set first.
  // Resolves to updateQty's `{ listItemId, catalogItemId }` when a quantity was
  // written, null otherwise (reveal-only, or nothing typed) — the Shop Add sheet
  // records its added_in_store event from that. Browse ignores the value.
  const addSearchedItem = async (rawCategory) => {
    const typed = searchQuery.trim();
    setSearchQuery("");
    setSearchPickerOpen(false);
    setSearchNewCatOpen(false);
    setNewCategoryInput("");
    if (!typed) return null;
    const norm = normalizeName(typed);
    const hidden = hiddenCatalogItems.find(h => normalizeName(h.name) === norm);
    if (hidden) {
      const liveRow = listRows.find(r => r.catalogItemId === hidden.id && (r.quantity || 0) > 0);
      if (liveRow) {
        // Live shared row exists → un-hide ONLY. No quantity write; reveal it as-is.
        unhideItem(hidden.id);
        return null;
      }
      // Hidden while at qty 0 (not on the list) → un-hide, then add at typed qty.
      unhideItem(hidden.id);
      return updateQty(typed, 1, rawCategory);
    }
    // No hidden match → today's behavior: add as a (possibly new) item.
    return updateQty(typed, 1, rawCategory);
  };

  // Show the join banner once the lens has landed on the joined household.
  // Banner DISPLAY ONLY — the switch itself is driven by the durable-intent effect
  // below, decoupled from this guard. (Previously the switch was wired here, gated
  // on `household.name !== "My Household"`. That guard is exactly false for an
  // existing user still active in "My Household" whose lens hasn't moved yet, so on
  // slower prod loads the join flag was never consumed and the switch never fired.
  // See docs/SPEC_join_activates_household ADDENDUM_reopen.) We clear only the
  // banner-NAME flag here; the `_id` flag is consumed solely on a confirmed switch.
  // No URL read here (R3 ruling (a), 2026-10-26): the URL carries no code once
  // preRum has run, and just_joined_household — set only when the join actually
  // happened — is the whole gate.
  useEffect(() => {
    if (loading || !household) return;
    if (household?.name && household.name !== "My Household" && !joinBanner) {
      const justJoined = sessionStorage.getItem("just_joined_household");
      if (justJoined) {
        setJoinBanner(justJoined); // always fires — sole feedback in the silent-join case
        sessionStorage.removeItem("just_joined_household");
      }
    }
  }, [loading, household]); // eslint-disable-line react-hooks/exhaustive-deps

  // Durable, retriable completion of the invite-join switch (ADDENDUM_reopen).
  // The sessionStorage flag `just_joined_household_id` — written by useProvisions
  // Effect 1 on joined_via_invite — is the source of truth for "unfinished join
  // intent": it survives reload, React state does not. We derive the switch from it
  // on EVERY relevant render (not once), so a slow prod membership propagation
  // (the joined household populating into myHouseholds a beat after load) can't
  // strand the join. The lens (switchHousehold) remains the single writer.
  const joinRefreshTriesRef = useRef(0);
  useEffect(() => {
    if (loading) return;
    const joinedId = sessionStorage.getItem("just_joined_household_id");
    if (!joinedId) return; // no unfinished join intent

    // Switch confirmed — the lens now points at the joined household. Consume the
    // durable flag and clear the intent ONLY here, after activeHouseholdId agrees.
    // Clearing before this is what stranded the intent on prod.
    if (activeHouseholdId === joinedId) {
      sessionStorage.removeItem("just_joined_household_id");
      if (pendingJoinId) setPendingJoinId(null);
      joinRefreshTriesRef.current = 0;
      return;
    }

    // Intent alive but not yet landed — keep pendingJoinId in sync with the flag.
    if (pendingJoinId !== joinedId) setPendingJoinId(joinedId);

    // Membership resolved: route the switch through the lens (the single writer).
    if (myHouseholds.some((h) => h.id === joinedId)) {
      switchHousehold(joinedId);
      return;
    }

    // Membership not yet propagated (prod latency): nudge get_my_households a bounded
    // number of times so a slow propagation can't strand the join indefinitely.
    if (joinRefreshTriesRef.current < 4) {
      joinRefreshTriesRef.current += 1;
      refreshHouseholds();
    }
  }, [loading, activeHouseholdId, myHouseholds, pendingJoinId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hand the bootstrap-made household to the lens (prod, 2026-09-24). A brand-new
  // user's first get_my_households (ActiveHouseholdContext) can resolve BEFORE
  // bootstrap_new_user has minted their place, so the context settles on
  // activeHouseholdId = null while useProvisions Effect 2 loads the place from
  // bootstrap's fallback id. Nothing then told the context the place existed — its
  // next word was the 30s watchdog, which read "list no longer contains null" as a
  // removal and posted "No longer a member of…" at someone who had just named their
  // first place. The welcome sheet only RENAMES that place, so the hand-off belongs
  // here, at the point the place becomes known, not in the sheet's handlers. Same
  // shape as the join effect above: derived on every relevant render, a bounded
  // number of refresh nudges, and switchHousehold stays the single writer. The
  // null guard in checkPresence is the safety net if this never lands.
  //
  // A7 (Amendment 2026-09-27): this hand-off is for a lens that RESOLVED to null
  // (a successful empty read), never for one whose read errored. On the 2026-09-27
  // cold start the context's first get_my_households failed, the lens sat null
  // with loadingHouseholds false, and this effect persisted bootstrap's own pick
  // (most-recently-joined: o11y Test House) over the person's remembered place.
  // The context now holds and retries a transient failure (loadingHouseholds
  // stays true) and flags a real one as householdsReadFailed; both keep this
  // effect out. The persisted place changes only by the person's own switch or
  // by a real, successful membership read.
  const adoptTriesRef = useRef(0);
  useEffect(() => {
    if (loading || loadingHouseholds || householdsReadFailed || activeHouseholdId) return;
    const hhId = household?.id;
    if (!hhId) return;
    if (myHouseholds.some((h) => h.id === hhId)) {
      switchHousehold(hhId);
      return;
    }
    if (adoptTriesRef.current < 4) {
      adoptTriesRef.current += 1;
      refreshHouseholds();
    }
  }, [loading, loadingHouseholds, householdsReadFailed, activeHouseholdId, myHouseholds, household?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss the join banner: on a timer (success confirmations self-clear),
  // and immediately if the user switches away from the joined household (the
  // banner would otherwise contradict the header).
  //
  // Subtlety: the explicit-accept flow sets the banner BEFORE the async
  // switchHousehold(joinedId) lands, so for an existing user there is a brief
  // window where the banner shows the joined name while `household` is still the
  // PRIOR household. A naive "name !== joinBanner → clear" would fire in that
  // window and kill the banner on its own arrival. So we only treat a name
  // mismatch as "switched away" once the joined household has actually been
  // active at least once (bannerSeenRef). The 5s timer runs regardless and is
  // the safety net if arrival never happens.
  const bannerSeenRef = useRef(false);
  useEffect(() => {
    if (!joinBanner) { bannerSeenRef.current = false; return; }
    if (household?.name === joinBanner) {
      bannerSeenRef.current = true; // arrived at the joined household
    } else if (bannerSeenRef.current) {
      setJoinBanner(null); // arrived earlier, now switched away → stale, clear
      return;
    }
    const t = setTimeout(() => setJoinBanner(null), 5000);
    return () => clearTimeout(t);
  }, [joinBanner, household?.name]);

  // ─────────────────────────────────────────────────────────────
  // First-run welcome sheet (SPEC_solo_start_experience.md, D1–D5, D11)
  // ─────────────────────────────────────────────────────────────
  //
  // THE TRIGGER IS A CONJUNCTION, NOT A FLAG. All three must hold:
  //   1. sessionStorage.just_signed_up — bootstrap CREATED a place this session
  //      (server-authoritative via created_household; migration 042)
  //   2. no ?invite= in the URL — an invited arrival is already where it belongs
  //   3. the active place is still on the "My Household" DB sentinel
  //
  // Condition 3 is the safety net, and it is the whole reason the flag alone is not
  // enough: a stray surviving flag on an already-named place stays silent. Behaviour
  // before label, applied to a trigger — we never interrupt someone whose data says
  // they are already settled.
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeName, setWelcomeName] = useState("");
  const [welcomeCodeOpen, setWelcomeCodeOpen] = useState(false);
  const [welcomeCode, setWelcomeCode] = useState("");
  const [welcomeError, setWelcomeError] = useState(null);
  const [welcomeBusy, setWelcomeBusy] = useState(false);
  // An invite that bootstrap's join_household retry could not resolve: one fixed line
  // on the sheet (R3 ruling (a), 2026-10-26). Read from sessionStorage.invite_failed
  // when the sheet opens, burned on read. Never the code itself.
  const [welcomeInviteFailed, setWelcomeInviteFailed] = useState(false);
  // The place bootstrap made this session — the D11 discard candidate. Captured when the
  // sheet opens, because by the time Join resolves the lens has begun moving elsewhere.
  const welcomeOrphanIdRef = useRef(null);

  // Condition 2 is no longer a URL read (preRum cleaned it before Splunk started):
  // an invited arrival is one whose join HAPPENED — just_joined_household_id, set by
  // bootstrap only on a successful join and consumed only once the lens agrees (the
  // effect above, which runs first in this pass). A failed invite leaves no such
  // flag, so the sheet's other two conditions decide, and the invite_failed line
  // tells them why they are here (R3 ruling (a), 2026-10-26).
  useEffect(() => {
    if (loading || !household || welcomeOpen) return;
    let flag = null;
    let joinedId = null;
    let failed = null;
    try {
      flag = sessionStorage.getItem("just_signed_up");
      joinedId = sessionStorage.getItem("just_joined_household_id");
      failed = sessionStorage.getItem("invite_failed");
    } catch (e) { /* storage blocked */ }
    if (!flag) return;
    if (joinedId) return;
    if (household.name !== "My Household") return;
    if (failed) { try { sessionStorage.removeItem("invite_failed"); } catch (e) { /* storage blocked */ } }
    setWelcomeInviteFailed(!!failed);
    welcomeOrphanIdRef.current = household.id;
    setWelcomeOpen(true);
  }, [loading, household, welcomeOpen]);

  // The flag burns on ANY exit — Continue, Skip, or a successful code-join (D3).
  // Burn FIRST, then act: if the rename or the join throws, the sheet must still not
  // re-fire on the next load. Nagging is the failure mode this spec is avoiding, and a
  // half-finished naming attempt is not a reason to ask again.
  const closeWelcome = useCallback(() => {
    try { sessionStorage.removeItem("just_signed_up"); } catch (e) { /* storage blocked */ }
    setWelcomeOpen(false);
    setWelcomeInviteFailed(false);
    setWelcomeError(null);
    setWelcomeCodeOpen(false);
    setWelcomeCode("");
  }, []);

  // THE SIGNED-IN ARRIVAL (SPEC_meal_sharing_phase_a_client Commit 3; S33). The code preRum held under GIFT_KEY
  // is consumed once: bootstrap ready, the catalog loaded (the form resolves ingredient names against it), no
  // meal sheet open, and the welcome sheet — a brand-new user's first stop — closed (ruled 2026-10-29: welcome
  // first, then the form). The key is removed at read time, so a bad code can never loop; the answer stands in
  // the form's place when it cannot open (unknown, revoked, not a member, already added). The ingredient names
  // are matched against THIS place's catalog in landDraft; the giver's catalog ids never cross (V3).
  const giftArrivalRanRef = useRef(false);
  useEffect(() => {
    if (giftArrivalRanRef.current) return;
    if (!MEALS_ENABLED || authPhase !== "ready" || !household?.id || !catalogLoaded || loading || welcomeOpen || mealSheet) return;
    let code = null;
    try { code = sessionStorage.getItem(GIFT_KEY); } catch (e) { return; }
    if (!code) return;
    giftArrivalRanRef.current = true;
    try { sessionStorage.removeItem(GIFT_KEY); } catch (e) { /* storage blocked */ }
    const placeName = household?.name || null;
    const placeCount = (myHouseholds || []).length;
    const existingNames = (meals || []).filter((m) => m && m.kind === "meal" && !m.deleted_at).map((m) => m.name);
    (async () => {
      const r = await previewMealGift(code);
      if (!r.ok) { setGiftArrival({ kind: "error", line: r.line }); return; }
      // C11 first (075): this place gave it — the source meal is theirs already, no copy. Then a copy
      // already made here (V11). A same-place copy made before C11 reads as the giving place.
      // G (b): 075 returns source_meal_id only while the source meal is live — null = deleted.
      if (r.isGivingPlace && !r.sourceMealId) { setGiftArrival({ kind: "giving_deleted", line: givingDeletedLine(placeName) }); return; }
      if (r.isGivingPlace) { setGiftArrival({ kind: "giving", mealId: r.sourceMealId && mealById[r.sourceMealId] ? r.sourceMealId : null, line: givingPlaceLine(placeName) }); return; }
      if (r.alreadyAdded) { setGiftArrival({ kind: "already", mealId: r.alreadyAdded, line: alreadyAddedLine(placeName) }); return; }
      const giverFirst = (r.snapshot && typeof r.snapshot.giver_first_name === "string" ? r.snapshot.giver_first_name : "").trim();
      const draft = giftDraft(r.snapshot, { existingNames, giverFirst });
      setMealSheet({
        mode: "create", meal: null,
        gift: {
          code, giftId: r.giftId, hasPhoto: r.hasPhoto, photoExt: r.photoExt, photoUrl: r.photoUrl || null, giverFirst, mealName: draft.name, draft,
          banner: giftBanner(giverFirst), note: giftNoteQuote(r.note, giverFirst), placeLine: placeLine(placeName, placeCount),
        },
      });
    })();
  }, [authPhase, household, catalogLoaded, loading, welcomeOpen, mealSheet, previewMealGift, meals, myHouseholds, mealById]);

  const handleWelcomeContinue = async () => {
    if (welcomeBusy) return;
    const trimmed = welcomeName.trim();
    if (!trimmed) return;                       // D4 — Continue is inert while empty
    setWelcomeBusy(true);
    try {
      closeWelcome();
      const ok = await renameHousehold(trimmed);
      if (ok) await refreshHouseholds();        // propagate the name to the switcher
    } finally {
      setWelcomeBusy(false);
    }
  };

  // Skip leaves the sentinel in place (D3). No nag, no re-fire — the pencil-edit
  // affordance in Edit place is the standing fallback, and earned-Our carries the
  // later nudge on its own.
  const handleWelcomeSkip = () => {
    if (welcomeBusy) return;
    closeWelcome();
  };

  // The recovery path (D5). This is the Prem incident's exit door: someone who meant to
  // join a household but landed solo gets handed the door back, inline, with no second
  // screen to abandon.
  const handleWelcomeJoin = async () => {
    if (welcomeBusy) return;
    const code = welcomeCode.trim();
    if (!code) { setWelcomeError("Enter an invite code."); return; }
    setWelcomeBusy(true);
    setWelcomeError(null);

    // The watchdog gap is real on this path: the D11 discard soft-deletes the household
    // that is STILL ACTIVE for another beat, and checkPresence would read that as an
    // external removal and post "No longer a member of…" — the precise scary-but-wrong
    // message this spec exists to design away. Raise the guard BEFORE the RPCs.
    beginDeliberateLoss();
    try {
      const result = await joinHouseholdByCode(code);
      if (!result.ok) {
        // Server-authored message ("Invite not found." / "…already used." / "…expired.")
        // rendered inline. The sheet STAYS OPEN — a mistyped code must not cost the user
        // the recovery surface.
        setWelcomeError(result.message || "Invite not found.");
        return;
      }

      // Joined. Discard the machine-made place they never claimed (D11). Best-effort by
      // design — the server owns the four-condition guard, and a refusal is a normal
      // answer, not an error. The join has already succeeded either way.
      const orphanId = welcomeOrphanIdRef.current;
      if (orphanId && orphanId !== result.householdId) {
        await discardUnclaimedHousehold(orphanId);
      }
      await refreshHouseholds();
      closeWelcome();
      // The switch itself is owned by the durable-intent effect, which is already
      // watching just_joined_household_id. Nothing to do here.
    } finally {
      endDeliberateLoss();
      setWelcomeBusy(false);
    }
  };

  // Invite → OS share sheet hand-off (spec D5). Generate a fresh link for the
  // active household, then hand the pre-filled "come aboard" message to
  // navigator.share(). No in-app share UI is rendered, so nothing can linger
  // after sending (the old banner bug is deleted by construction). On a platform
  // with no share sheet (desktop) we copy the message so the invite still lands.
  const handleInviteShare = async () => {
    if (invitePreparing) return;
    setInvitePreparing(true);
    try {
      const url = await createInvite();
      if (!url) { showToast("Couldn't prepare an invite link. Try again."); return; }
      const name = household?.name || "my place";
      // The link rides the `url` field, not the prose, so platforms that render a
      // preview card get a clean target instead of parsing it out of the sentence.
      // It is therefore ABSENT from `text` — including both would print it twice.
      const text = `Come aboard my OurProvisions list — join ${name} and it gets smarter as we go.`;
      if (typeof navigator !== "undefined" && navigator.share) {
        try {
          await navigator.share({ title: "Come aboard my OurProvisions list", text, url });
        } catch (e) {
          if (e && e.name !== "AbortError") console.error("share failed:", e);
        }
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        // The clipboard has no `url` field to carry the link, so it must be
        // reattached here. Copying bare `text` would put a linkless invitation on
        // the desktop path — an invite that cannot be accepted.
        await navigator.clipboard.writeText(`${text} ${url}`);
        showToast("Invite copied");
      }
    } finally {
      setInvitePreparing(false);
    }
  };

  // Share the app → OS share sheet (spec D6–D8). The SECOND verb, and structurally
  // different from the first in every way that matters:
  //   * carries ?ref=CODE, never ?invite= — it attributes, it does not enroll
  //   * NEVER names the place, so the message itself cannot be mistaken for an
  //     invitation to a shared list. That ambiguity is what produced the Prem incident.
  //   * no RPC and no spinner — the referral code is stable and already in hand
  //   * NO DB WRITE AT SEND (D10). Attribution is written when a signup arrives
  //     carrying the code. Arrivals are facts; sends are noise.
  // No interstitial or confirm on either verb: labels and visual hierarchy carry the
  // distinction, and a confirm punishes the common case.
  const handleAppShare = async () => {
    // D8 forbids emitting a naked URL, and a missing code is the only way that could
    // happen here. The verb hides itself in that state (see the render guard), so this
    // is the belt to that braces — reachable only if the code vanishes between render
    // and tap.
    if (!referralCode) return;
    const url = `${window.location.origin}?ref=${referralCode}`;
    // As with Invite aboard: the link rides the `url` field so the OS can render a
    // preview card, and is deliberately absent from `text` to avoid printing twice.
    const text = `I've been using OurProvisions to keep our shopping list straight — worth a look.`;
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: "OurProvisions", text, url });
      } catch (e) {
        if (e && e.name !== "AbortError") console.error("share failed:", e);
      }
    } else if (typeof navigator !== "undefined" && navigator.clipboard) {
      // Reattach the link for the clipboard path — there is no `url` field here, and
      // a copied recommendation with no link is D8's naked-share failure in reverse:
      // not an ambiguous link, but no link at all.
      await navigator.clipboard.writeText(`${text} ${url}`);
      showToast("Link copied");
    }
  };


  // eslint-disable-next-line no-unused-vars
  const startEditPrice = (itemName) => {
    setEditingPrice(itemName);
    const existing = prices[itemName];
    setPriceInput(existing ? String(Math.round(existing * 100)) : "");
  };

  const openEditModal = (itemName) => {
    const catalogEntry = catalogMap[itemName];
    const isCustom = catalogEntry && catalogEntry.is_global === false;
    // Catalog (non-custom) items can only have their price edited; the name is
    // locked. With pricing off there's nothing to edit, so don't open an empty
    // modal. (The swipe Edit affordance is also suppressed for this case.)
    if (!isCustom && !showPrices) return;
    setEditModalItem({ name: itemName, isCustom });
    setEditModalName(itemName);
    const existing = prices[itemName];
    setEditModalPrice(existing ? String(Math.round(existing * 100)) : "");
  };

  const commitEditModal = async () => {
    if (!editModalItem) return;
    const oldName = editModalItem.name;
    const newName = editModalItem.isCustom ? editModalName.trim() : oldName;
    const priceVal = parseFloat(centsToDisplay(editModalPrice));

    if (!isNaN(priceVal) && priceVal >= 0) {
      setLocalPrices(prev => {
        const updated = { ...prev };
        if (newName !== oldName) delete updated[oldName];
        return { ...updated, [newName]: priceVal };
      });
      updatePrice(newName !== oldName ? newName : oldName, priceVal);
    }

    if (editModalItem.isCustom && newName && newName !== oldName) {
      await renameItem(oldName, newName);
    }

    setEditModalItem(null);
  };

  const handlePriceInput = (raw) => {
    // Strip non-digits, remove leading zeros
    const digits = raw.replace(/\D/g, "").replace(/^0+/, "") || "";
    setPriceInput(digits);
  };

  const centsToDisplay = (cents) => {
    if (!cents) return "0.00";
    const num = parseInt(cents, 10);
    return (num / 100).toFixed(2);
  };

  const commitPrice = (itemName) => {
    const val = parseFloat(centsToDisplay(priceInput));
    if (!isNaN(val) && val >= 0) {
      setLocalPrices(prev => ({ ...prev, [itemName]: val }));
      updatePrice(itemName, val);
    }
    setEditingPrice(null);
  };

  const handleAddItem = () => {
    const name = newItemName.trim();
    if (!name) { setAddError("Please enter an item name."); return; }
    const allItems = Object.keys(catalogMap).map(k => k.toLowerCase());
    if (allItems.includes(name.toLowerCase())) { setAddError("That item already exists."); return; }
    const price = parseFloat(centsToDisplay(newItemPrice)) || 0;
    if (price > 0) setLocalPrices(prev => ({ ...prev, [name]: price }));
    // updateQty with qty=1 will auto-create the catalog item in Supabase if it doesn't exist
    updateQty(name, 1, newItemCategory, price > 0 ? price : undefined);
    setNewItemName(""); setNewItemPrice(""); setAddError(""); setShowAddModal(false);
  };

  const createCategory = (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setHouseholdCategories(prev => new Set([...prev, trimmed]));
    setNewCategoryName("");
  };

  const renameCategory = async (oldRawName, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldRawName) return;
    const db = _supabase.current;
    const hh = _household.current;
    if (!db || !hh) return;

    const itemsInCat = Object.values(catalogMap).filter(i => i.category === oldRawName);
    const globalItems = itemsInCat.filter(i => i.is_global);
    const householdItems = itemsInCat.filter(i => !i.is_global);

    for (const item of globalItems) {
      const { error: insertErr } = await db
        .from("catalog_items")
        .insert({
          name: item.name,
          category: trimmed,
          is_global: false,
          household_id: hh.id,
          created_by: _internalUserId.current,
        })
        .select().single();
      if (insertErr) { setCategoryError(`Could not copy item: ${insertErr.message}`); return; }
      await db.from("user_hidden_items").insert({
        clerk_id: _clerkId.current,
        catalog_item_id: item.id,
      }).select();
    }

    if (householdItems.length > 0) {
      const { error: updateErr } = await db
        .from("catalog_items")
        .update({ category: trimmed })
        .eq("household_id", hh.id)
        .eq("category", oldRawName)
        .is("deleted_at", null);
      if (updateErr) { setCategoryError(`Could not rename: ${updateErr.message}`); return; }
    }

    await refreshCatalog();
    setShowManageCategoriesModal(false);
  };

  const handleResetCategories = async () => {
    const db = _supabase.current;
    const hh = _household.current;
    if (!db || !hh) return;
    const { error: delErr } = await db
      .from("catalog_items")
      .update({ deleted_at: new Date().toISOString() })
      .eq("household_id", hh.id)
      .eq("is_global", false)
      .is("deleted_at", null);
    if (delErr) { setCategoryError(`Could not reset: ${delErr.message}`); return; }
    await db
      .from("user_hidden_items")
      .delete()
      .eq("clerk_id", user.id);
    setShowResetConfirm(false);
    setShowManageCategoriesModal(false);
    await refreshCatalog();
  };

  const deleteCategory = async (rawName, moveTo) => {
    if (!supabase || !household) return;
    if (moveTo) {
      const { error: moveErr } = await supabase
        .from("catalog_items")
        .update({ category: moveTo })
        .eq("household_id", household.id)
        .eq("category", rawName)
        .is("deleted_at", null);
      if (moveErr) { return; }
      setCatalogMap(prev => {
        const updated = {};
        Object.entries(prev).forEach(([k, v]) => {
          updated[k] = v.category === rawName ? { ...v, category: moveTo } : v;
        });
        return updated;
      });
    } else {
      const itemsInCat = Object.values(catalogMap).filter(i => i.category === rawName);
      for (const item of itemsInCat) {
        await deleteItem(item.name);
      }
    }
    setHouseholdCategories(prev => { const s = new Set(prev); s.delete(rawName); return s; });
    setSelectedCategories(prev => { const s = new Set(prev); s.delete(rawName); return s; });
    setDeletingCategory(null);
    setDeleteMoveTarget("");
  };

  const openBudgetModal = () => {
    setBudgetInput(budgetNum !== null ? String(budgetNum) : "");
    setShowBudgetModal(true);
  };

  const saveBudget = () => {
    const val = parseFloat(budgetInput);
    if (!isNaN(val) && val > 0) {
      updateBudgetGoal(parseFloat(val.toFixed(2)));
    } else if (budgetInput === "" || budgetInput === "0") {
      updateBudgetGoal(null);
    }
    setShowBudgetModal(false);
  };

  const clearBudget = () => {
    updateBudgetGoal(null);
    setShowBudgetModal(false);
  };

  // The one wrap-up routine. Both entries reach it: the modal's confirm, and
  // the no-choice direct path in openWrapUp. `rollNames` is the CONFIRMED
  // roll-forward set (empty on the direct path, where nothing is pending), and
  // `snapshot` is passed in rather than read from state so the direct path —
  // which captures and wraps up in one tick — cannot race a pending setState.
  const runWrapUp = async (rollNames, snapshot) => {
    setWrappingUp(true);
    const closed = await wrapUpTrip(rollNames);
    setWrappingUp(false);
    closeWrapUp();
    setWrapUpRollItems(new Set());
    // Only a trip that actually closed earns a summary, and only one that
    // bought something: nothing in the cart is not a trip to celebrate, so the
    // wrap-up closes straight to the list. On failure the hook has already
    // surfaced the error and the list is untouched — show nothing and leave
    // the user where they were. carriedCount is the set actually handed to
    // wrapUpTrip, never openWrapUp's pre-selection.
    setTripSummary(
      closed && snapshot && snapshot.itemCount > 0
        ? { ...snapshot, carriedCount: rollNames.length }
        : null
    );
    // The trip is over: the tray, the "added here" tags and the prompt state
    // were all scoped to it.
    setTrayOpen(false);
    setAddedHereIds(new Set());
    setStorePromptOpen(false);
    setStorePromptSkippedFor(null);
  };

  // The modal's confirm. wrapUpRollItems is the user's edited selection, which
  // is why carriedCount is settled here and not at openWrapUp.
  const handleWrapUp = () => runWrapUp(Array.from(wrapUpRollItems), tripSnapshotRef.current);

  // The summary stays until it is dismissed — it is the receipt for the trip,
  // not a toast. A cancelled Wrap up leaves nothing behind: the snapshot sits
  // in the ref unread and is overwritten by the next openWrapUp, and only a
  // completed wrap-up ever writes tripSummary.
  const dismissTripSummary = () => setTripSummary(null);

  // Fetch the verdict once per summary, keyed on the wrapped session; cleared
  // with it so a stale verdict can never attach to the next trip. Null row
  // (no session, RPC failure, non-member) renders state 1 — the plain summary.
  const tripSessionId = tripSummary?.sessionId || null;
  useEffect(() => {
    let live = true;
    setTripReality(null);
    setRealityAnswer(null);
    if (!tripSessionId) return undefined;
    fetchTripReality(tripSessionId).then((row) => { if (live) setTripReality(row); });
    return () => { live = false; };
  }, [tripSessionId, fetchTripReality]);

  // One handler for all three controls: Real trip / Just testing write the
  // answer; Count it writes `real`. The collapse happens only after the server
  // confirms the write — a failed write leaves the control in place rather
  // than showing a confirmation the data doesn't support. A testing answer is
  // never final here: Count it stays available after Just testing and simply
  // overwrites it (answer_trip_reality is last-answer-wins). Only a same-answer
  // double tap is ignored.
  const handleRealityAnswer = async (answer) => {
    if (!tripSessionId || realityAnswer === answer) return;
    const row = await answerTripReality(tripSessionId, answer);
    if (!row) return;
    setTripReality(row);
    setRealityAnswer(answer);
  };

  // ── Shop tab: in-store actions (SPEC_shop_lens_instore_capture.md) ──
  // Every handler here does the PRIMARY write first through the existing path,
  // then records the event best-effort (D12) — never awaited by the tap, never
  // a toast. The first event of a trip starts the session (and so the store
  // prompt) as a side effect inside recordListEvent.
  const handleShopToggle = async (item) => {
    const name = item.name;
    const id = item.listItemId;
    if (rowMotion[id]) return;                         // already in motion — ignore the tap
    const reduced = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reduced) {
      const dir = checked[id] ? "in" : "out";
      const markMs = dir === "out" ? 120 + 250 : 120; // beat 1 (+ hold on check only)
      const collapseMs = 200;                          // beat 2
      // Mark BEFORE the write: toggleChecked flips `checked` synchronously and
      // the row would otherwise leave the aisle on this very render.
      setRowMotion(prev => ({ ...prev, [id]: { dir, phase: "mark" } }));
      rowMotionTimers.current[id] = setTimeout(() => {
        setRowMotion(prev => prev[id] ? { ...prev, [id]: { dir, phase: "collapse" } } : prev);
        rowMotionTimers.current[id] = setTimeout(() => {
          setRowMotion(prev => { const n = { ...prev }; delete n[id]; return n; });
          delete rowMotionTimers.current[id];
        }, collapseMs);
      }, markMs);
    }
    // `status` is the server-confirmed transition, or null when the write
    // failed OR changed nothing (a re-tap on an already-committed row —
    // SPEC_checked_event_duplicate_emission). Only a real transition is an event.
    const status = await toggleChecked(name, item.listItemId);
    if (!status) return;
    recordListEvent(status === "bought" ? "checked" : "unchecked", { listItemId: item.listItemId, catalogItemId: item.catalogItemId });
  };
  const rowMotionClass = (listItemId) => {
    const m = rowMotion[listItemId];
    if (!m) return "";
    if (m.phase === "collapse") return " collapsing";
    return m.dir === "out" ? " checking" : " unchecking";
  };
  // Wrap up (D4): one home for the pre-select logic — called by the helm's
  // Wrap up chip and by the all-done "Wrap Up Trip →" button. Pending (unchecked)
  // items are pre-selected to roll forward; at 100% that set is simply empty.
  // Both entries into Wrap up come through here — the helm's muted chip and the
  // All done card's "Wrap up trip →" — so capturing the snapshot here covers
  // both paths with one capture, including the chip path that never renders the
  // All done card at all.
  const openWrapUp = () => {
    // The direct path has no modal to disable its confirm, so the re-entry
    // guard lives here, at the one entry both paths share: a second tap while
    // a wrap-up is in flight would run close_cycle twice.
    if (wrappingUp) return;
    const pending = new Set(
      shoppingList.flatMap(cat =>
        cat.items.filter(i => !checked[i.listItemId]).map(i => i.name)
      )
    );
    const startedAt = activeSession?.started_at ? new Date(activeSession.started_at).getTime() : null;
    const snapshot = {
      // What went in the cart, not what was on the list — the chip opens Wrap up
      // at any percentage, and a summary that counts unbought rows is a lie.
      itemCount: boughtItems.length,
      // Frozen now. Omitted with no open session, exactly as the All done card
      // omits it (a delivery-style check-off reads "N items" alone).
      minutes: startedAt ? Math.max(0, Math.round((Date.now() - startedAt) / 60000)) : null,
      storeName: activeSession?.store_name_raw || null,
      // The session the verdict is about. Captured here for the same reason as
      // everything else: wrapUpTrip nulls activeSession before the summary mounts.
      sessionId: activeSession?.id || null,
      // Settled at confirm, from the set actually handed to wrapUpTrip — this
      // is the moment the roll-forward set is PRE-selected, not the moment it
      // is confirmed, and the modal exists precisely so it can be edited
      // (per-row toggles, Select all, Clear all). A count taken here would
      // claim "3 carried forward" after a Clear all carried nothing.
      carriedCount: 0,
    };
    tripSnapshotRef.current = snapshot;
    // A confirm appears only when it carries a choice. Everything bought and
    // nothing pending means the modal's only content — which items to roll
    // forward — is empty, so it would ask nothing and offer one button. Go
    // straight to the wrap-up and the summary. The empty list (nothing bought,
    // nothing pending) is NOT this case: there is no trip to close out, so it
    // keeps the modal rather than acting on a tap with no stated intent.
    if (pending.size === 0 && boughtItems.length > 0) {
      runWrapUp([], snapshot);
      return;
    }
    setWrapUpRollItems(pending);
    setShowWrapUpModal(true);
    window.history.replaceState(null, "", WRAP_UP_HASH);
  };

  const openAddSheet = () => {
    setSearchQuery("");
    setSearchPickerOpen(false);
    setAddSheetOpen(true);
  };
  const closeAddSheet = () => {
    setAddSheetOpen(false);
    setSearchQuery("");
  };
  // D11 — the + does the DOOR's add. Shop → the Add sheet (add-from-the-aisle,
  // "added here"); Browse → the same sheet, plain; Plan → what the switch row's
  // own button does on the view you are on (SPEC_plan_week_meals_switch D11:
  // one rule everywhere — the pill's + does what the header button above it
  // did): on This Week, "+ Add" → Meals; on Meals, "+ New" → the New Meal sheet
  // (signed in only, like the button). The labels are the buttons' aria-labels.
  // Home → absent, so no + ever renders there (D12). The + never becomes a menu.
  const doorAdd = {
    list:  { label: "Add something", run: openAddSheet },
    input: { label: "Add to your list", run: openAddSheet },
    ...(MEALS_ENABLED
      ? (planScreen === "library"
        ? (isSignedIn ? { plan: { label: "Create a new meal", run: () => setMealSheet({ mode: "create", meal: null }) } } : {})
        : { plan: { label: "Add a meal to this week", run: () => setPlanScreen("library") } })
      : {}),
  };
  // "added here" and the added_in_store event are Shop semantics: the same sheet
  // opened from Browse's + adds plainly (no tag, no event, no session start).
  const noteAddedHere = (res) => {
    if (!res || view !== "list") return;
    setAddedHereIds(prev => { const n = new Set(prev); n.add(res.catalogItemId); return n; });
    recordListEvent("added_in_store", res);
  };
  // A catalog hit — the same write Browse's row makes (+1 through updateQty,
  // which sets status back to pending: D6, adds land unchecked).
  const handleShopAddResult = async (item) => {
    closeAddSheet();
    const res = await updateQty(item.name, qtyFor(item) + 1, item.rawCategory);
    noteAddedHere(res);
  };
  // Create / reveal-add — Browse's addSearchedItem, unchanged, with the category
  // fixed to `Other` for a genuinely new item (D7). It resolves hidden items
  // before anything else (F1b), which is also the reveal card's not-live path.
  const handleShopAddNew = async (category = "Other") => {
    const res = await addSearchedItem(category);
    setAddSheetOpen(false);
    noteAddedHere(res);
  };
  const handleStorePick = async (name) => {
    setStoreSaving(true);
    const ok = await setSessionStore(name);
    setStoreSaving(false);
    if (ok) setStorePromptOpen(false);
  };
  const handleStoreSkip = () => {
    setStorePromptSkippedFor(activeSession?.id || null);
    setStorePromptOpen(false);
  };
  // The tray's initial: who last CHECKED the row, from the event log — only for
  // rows the current user did not check themselves.
  const initialFor = (listItemId) => {
    const uid = checkedByMap[listItemId];
    if (!uid || uid === _internalUserId?.current) return null;
    const member = householdMembers.find(m => m.user_id === uid);
    const parts = (member?.users?.full_name || "").trim().split(/\s+/).filter(Boolean);
    return parts.length > 0 ? parts.slice(0, 2).map(p => p[0].toUpperCase()).join("") : "?";
  };
  const storeChips = useMemo(() => {
    const seen = new Set();
    const out = [];
    [partnerSession?.store_name_raw, ...storeSuggestions].forEach((n) => {
      const clean = (n || "").trim();
      if (clean && !seen.has(clean.toLowerCase())) { seen.add(clean.toLowerCase()); out.push(clean); }
    });
    return out;
  }, [partnerSession, storeSuggestions]);
  const storePromptVisible = view === "list" && !!activeSession && (
    storePromptOpen || (!activeSession.store_name_raw && storePromptSkippedFor !== activeSession.id)
  );

  // Shop mount: re-read open sessions (D11 expiry check, partner's store).
  useEffect(() => {
    if (view === "list") refreshSessions();
  }, [view, refreshSessions]);
  // Per-trip Shop state does not survive a household switch.
  useEffect(() => {
    setTrayOpen(false);
    setAddedHereIds(new Set());
    setAddSheetOpen(false);
    setStorePromptOpen(false);
    setStorePromptSkippedFor(null);
  }, [activeHouseholdId]);
  useEffect(() => {
    if (addSheetOpen) addSheetInputRef.current?.focus();
  }, [addSheetOpen]);
  // The Add sheet rides on top of the on-screen keyboard (2026-11-04): iOS keeps the
  // layout viewport full height when the keyboard opens, so the fixed sheet sat under
  // it. Lifted by the covered strip, capped to the visible height (see the sheet).
  const addSheetKeyboard = useKeyboardInset(addSheetOpen);
  // While the sheet is open the page behind the scrim never scrolls — otherwise iOS
  // scrolls the list when the input takes focus. The splash's lock, same shape:
  // restore whatever was there before.
  useEffect(() => {
    if (!addSheetOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [addSheetOpen]);

  const handleRemoveMember = async (m) => {
    const name = m.users?.full_name || (m.users?.email ? m.users.email.split("@")[0] : "this member");
    if (!window.confirm(`Remove ${name} from this place? Anything they added to the current list stays.`)) return;
    try {
      const { error } = await supabase.rpc("remove_member", {
        p_household_id: household.id,
        p_user_id: m.user_id,
      });
      if (error) throw error;
      await refreshMembers();
      await refreshHouseholds();
      showToast(`${name} removed`);
    } catch (err) {
      showToast(err.message || "Could not remove member");
    }
  };

  const handleLeaveHousehold = async () => {
    if (!window.confirm("Leave this place? Anything you added stays behind for the others.")) return;
    beginDeliberateLoss();                     // BEFORE the RPC — closes the watchdog gap
    try {
      const leftId = household.id;
      const { error } = await supabase.rpc("leave_household", {
        p_household_id: leftId,
      });
      if (error) throw error;
      setShowHouseholdModal(false);
      await refreshHouseholds();
      const remaining = myHouseholds.filter(h => h.id !== leftId);
      if (remaining.length > 0) switchHousehold(remaining[0].id);
      showToast("You left the place");
    } catch (err) {
      showToast(err.message || "Could not leave place");
    } finally {
      endDeliberateLoss();                     // always clears, even on error
    }
  };

  const handleDeleteHousehold = async () => {
    const deletedId = activeHouseholdId;
    beginDeliberateLoss();                     // BEFORE the RPC — closes the watchdog gap
    try {
      const { error } = await supabase.rpc('delete_household', { p_household_id: deletedId });
      if (error) throw error;
      setShowEditHousehold(false);
      setEdDeleteConfirm(false);
      setShowHouseholdModal(false);
      showToast("Place deleted");
      await resolveAfterHouseholdLoss(deletedId, false);
    } catch (err) {
      showToast(err.message || "Could not delete place");
    } finally {
      endDeliberateLoss();                     // always clears, even on error
    }
  };

  // ── Edit household sheet (OurBanner) ──
  // Am I the creator? Creator-only Delete gate (spec D4). The switcher already
  // proved the owner-role identity works; reuse it.
  // A5 (Amendment 2026-09-27): never derived from an EMPTY list — until this
  // place's members have been read, nobody is the creator and nobody may Leave
  // (with members unloaded the owner used to see "Leave place").
  const isHouseholdCreator = membersLoaded && householdMembers.some(m => m.users?.clerk_id === user?.id && m.role === 'owner');

  // ── Earned "Our" (SPEC_wordmark_earned_our.md) ──
  // The single source of truth for the header wordmark. `householdMembers` is already
  // reloaded per active household, so switching re-evaluates this for free — no new
  // state, no new query. Signed out or still loading both land on false, which is the
  // point: "Provisions" is the default in every unknown state (A5: neutral until the
  // members are actually loaded, not merely absent).
  const hasEarnedOur = isSignedIn && membersLoaded && householdMembers.length > 1;

  // Draft has a photo when a new file is staged OR an existing stored path survives.
  const edHasPhoto = !!edFile || !!edPhotoPath;
  // Preview source: staged file wins; else the existing signed URL.
  const edPreviewUrl = edLocalUrl || (edPhotoPath ? household?.photoUrl : null);

  const openEditHousehold = () => {
    // Seed drafts from the active household. banner_wordmark is read raw (not the
    // photo-gated header value) so the segment reflects the persisted choice.
    setEdName(household?.name || "");
    setEdPhotoPath(household?.photo_path || null);
    setEdFile(null); setEdLocalUrl(null);
    setEdX(household?.photo_position_x ?? 50);
    setEdY(household?.photo_position_y ?? 50);
    setEdZoom(household?.photo_zoom ?? 100);
    setEdWordmark(household?.banner_wordmark || "large");
    setEdDeleteConfirm(false);
    setShowEditHousehold(true);
  };

  const closeEditHousehold = () => {
    if (edLocalUrl) URL.revokeObjectURL(edLocalUrl);
    setEdFile(null); setEdLocalUrl(null);
    setEdDeleteConfirm(false);
    setShowEditHousehold(false);
  };

  const onEdPickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-picking the same file
    // Confirms the change handler actually fired (rules out a detached input /
    // unwired onChange). The upload itself happens on Save, not here.
    console.log("[photo pick] onChange fired; file:", file ? `${file.name} (${file.type}, ${file.size}b)` : "none");
    if (!file) return;
    if (edLocalUrl) URL.revokeObjectURL(edLocalUrl);
    setEdFile(file);
    setEdLocalUrl(URL.createObjectURL(file));
    setEdPhotoPath(null);           // a new file supersedes any stored path
    // A fresh photo starts at a sensible frame; the user tunes from here.
    setEdX(50); setEdY(46); setEdZoom(165);
  };

  const onEdRemovePhoto = () => {
    if (edLocalUrl) URL.revokeObjectURL(edLocalUrl);
    setEdFile(null); setEdLocalUrl(null);
    setEdPhotoPath(null);
    setEdX(50); setEdY(50); setEdZoom(100);
  };

  // Drag-to-reposition on the preview. Panning the finger right pans the image
  // right (shows more of its left) → background-position % decreases. Zoom scales
  // sensitivity so a big zoom doesn't feel sluggish.
  const onEdDragStart = (e) => {
    if (!edHasPhoto) return;
    const rect = e.currentTarget.getBoundingClientRect();
    edDragRef.current = {
      w: rect.width, h: rect.height,
      startX: e.clientX, startY: e.clientY, baseX: edX, baseY: edY,
    };
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onEdDragMove = (e) => {
    const d = edDragRef.current;
    if (!d) return;
    const k = edZoom / 100; // more zoom → same finger move covers less of the image
    const dxPct = ((e.clientX - d.startX) / d.w) * 100 / k;
    const dyPct = ((e.clientY - d.startY) / d.h) * 100 / k;
    const clamp = (v) => Math.max(0, Math.min(100, v));
    setEdX(Math.round(clamp(d.baseX - dxPct)));
    setEdY(Math.round(clamp(d.baseY - dyPct)));
  };
  const onEdDragEnd = (e) => {
    edDragRef.current = null;
    if (e.currentTarget.releasePointerCapture) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_e) {}
    }
  };

  const saveEditHousehold = async () => {
    if (edSaving) return;
    if (!edName.trim()) { showToast("Place needs a name"); return; }
    setEdSaving(true);
    try {
      const patch = { name: edName, banner_wordmark: edWordmark };
      let removedPath = null;
      if (edFile) {
        // New/replacement photo: normalize + upload, then commit path + framing.
        const path = await uploadHouseholdPhoto(edFile);
        if (!path) {
          // The hook already logged the exact failing stage; make it visible.
          showToast("Couldn't save the photo. Please try again.");
          setEdSaving(false); return;
        }
        patch.photo_path = path;
        patch.photo_position_x = edX; patch.photo_position_y = edY; patch.photo_zoom = edZoom;
      } else if (!edPhotoPath && household?.photo_path) {
        // Photo removed in this session: null the row + reset framing, delete object.
        patch.photo_path = null;
        patch.photo_position_x = 50; patch.photo_position_y = 50; patch.photo_zoom = 100;
        removedPath = household.photo_path;
      } else if (edPhotoPath) {
        // Existing photo kept: persist any reframing.
        patch.photo_position_x = edX; patch.photo_position_y = edY; patch.photo_zoom = edZoom;
      }
      const ok = await updateHouseholdBanner(patch);
      if (!ok) { setEdSaving(false); return; }
      if (removedPath) {
        try { await supabase.storage.from("household-photos").remove([removedPath]); } catch (_e) {}
      }
      await refreshHouseholds();   // name change propagates to the switcher list
      showToast("Saved");
      closeEditHousehold();
    } finally {
      setEdSaving(false);
    }
  };

  const shoppingList = useMemo(() => {
    // Group directly from the RPC rows (listRows) — the source of truth that is
    // identical on every client. catalogMap is no longer in the display path,
    // so a stale/incomplete local catalog can never drop a synced item.
    const groups = {}; // rawCategory -> items[]
    for (const row of listRows) {
      if ((row.quantity || 0) <= 0) continue;
      const rawCat = row.category || "Household";
      const realPrice = prices[row.name] && supabasePrices[row.name]
        ? prices[row.name]
        : (localPrices[row.name] || 0);
      // added_by is a stale scalar — set once at row creation and never updated, so a
      // remove→re-add can leave it naming a user who is no longer the contributor.
      // Derive the name-line from the contributor ledger instead (the source of truth).
      const contributors = contributorsMap?.[row.name] || [];
      const soleContributor = contributors.length === 1 ? contributors[0] : null;
      const isOwnItem = !soleContributor || soleContributor.clerkId === user?.id;
      if (!groups[rawCat]) groups[rawCat] = [];
      groups[rawCat].push({
        name: row.name, catalogItemId: row.catalogItemId, listItemId: row.id, qty: row.quantity,
        price: realPrice,
        subtotal: (row.quantity || 0) * realPrice,
        category: CATEGORY_DISPLAY[rawCat] || rawCat,
        isOwnItem,
        contributors,
        pickedByLabels: row.pickedByLabels || null,
      });
    }

    const orderedRaw = [
      ...CATEGORY_ORDER.filter(c => groups[c]),
      ...Object.keys(groups).filter(c => !CATEGORY_ORDER.includes(c)),
    ];

    return orderedRaw.map(rawCat => ({
      category: CATEGORY_DISPLAY[rawCat] || rawCat,
      items: groups[rawCat].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [listRows, prices, supabasePrices, localPrices, contributorsMap, user?.id]);

  // INVARIANT: list-row state (`quantities`, `checked`) is keyed by list_item.id;
  // the name is a label, never an identity. Browse rows are CATALOG items, so a
  // stepper resolves catalog id → live list row → quantity; a row still being
  // inserted reads the hook's pre:<catalog id> placeholder.
  const qtyFor = (item) => {
    const cid = item.catalogItemId || item.id || catalogMap[item.name]?.id;
    if (!cid) return 0;
    const row = listRows.find(r => r.catalogItemId === cid);
    if (row && quantities[row.id] != null) return quantities[row.id];
    const pre = quantities[`pre:${cid}`];
    return pre != null ? pre : 0;
  };
  const pendingItems = shoppingList.flatMap(cat =>
    cat.items.filter(item => !checked[item.listItemId])
  );
  const boughtItems = shoppingList.flatMap(cat =>
    cat.items.filter(item => checked[item.listItemId])
  );

  // Loading state for catalog — only true while fetch is in flight, not based on result size.
  // A5 (Amendment 2026-09-27): and until this place's catalog has actually been read —
  // an unloaded catalog is the quiet placeholder, never an empty Browse with one chip.
  const catalogLoading = loading || (sessionLive && !catalogLoaded);

  const totalItems = shoppingList.reduce((acc, c) => acc + c.items.length, 0);
  const totalCost = shoppingList.reduce((acc, c) => acc + c.items.reduce((a, i) => a + i.subtotal, 0), 0);
  const hasEstimatedPrices = shoppingList.some(c => c.items.some(i => !prices[i.name]));
  // Counted from the SAME set as totalItems, never from the raw `checked` map.
  // `checked` is keyed by list_item.id for every row the server returned,
  // including rows shoppingList drops (quantity <= 0). Counting the map could
  // make checkedCount exceed totalItems, and the All done card's
  // `checkedCount === totalItems` gate would then never fire at 100%.
  const checkedCount = boughtItems.length;
  // ── §Landing (SPEC_auth_state_ui_gating.md D4a) — decided ONCE per load ──
  // No hash at load and the session is `ready`:
  //   list has arrived (householdReady) → unbought items ≥ 1 → Shop, else Home;
  //   rendered without list data (splash gone, loading overlay gone) → Home, and
  //   STAY — never bounce a user off a tab after it has rendered.
  // signed_out / bootstrapping → hold (Home welcome or the splash covers it), so
  // signing in from the welcome runs this same rule once. "Unbought" is the Shop
  // badge's own count. After the decision the user's taps own the route.
  useEffect(() => {
    if (landingDecided || authPhase !== "ready") return;
    if (householdReady) {
      setLandingDecided(true);
      if (totalItems - checkedCount > 0) setView("list");
      return;
    }
    if (!showSplash && !loading) setLandingDecided(true);
  }, [landingDecided, authPhase, householdReady, showSplash, loading, totalItems, checkedCount]);
  const checkedCost = shoppingList.reduce((acc, c) =>
    acc + c.items.reduce((a, i) => a + (checked[i.listItemId] ? i.subtotal : 0), 0), 0);

  // A row stays in its aisle while it is leaving ("out"), and stays out of it
  // while it is returning from the tray ("in"), so the motion can play where
  // the person is looking. The write already happened; this is only where the
  // row is drawn.
  const inAisle = (i) => {
    const m = rowMotion[i.listItemId];
    if (m) return m.dir === "out";
    return !checked[i.listItemId];
  };
  const inTray = (i) => {
    const m = rowMotion[i.listItemId];
    if (m) return m.dir === "in";
    return !!checked[i.listItemId];
  };
  // Shop A–Z lens — flat, alphabetical, unchecked only (checked items live in the In cart tray).
  const shopFlatItems = useMemo(() =>
    shoppingList
      .flatMap(c => c.items)
      .filter(inAisle)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [shoppingList, checked, rowMotion]); // eslint-disable-line react-hooks/exhaustive-deps
  // Shop Aisles lens — today's grouping minus the checked rows; an aisle that
  // empties out disappears (its items are all in the tray).
  const shopAisles = useMemo(() =>
    shoppingList
      .map(cat => ({ ...cat, items: cat.items.filter(inAisle) }))
      .filter(cat => cat.items.length > 0),
    [shoppingList, checked, rowMotion]); // eslint-disable-line react-hooks/exhaustive-deps
  // Tray rows and the tray COUNT move at different beats: a checked row is
  // counted once it starts collapsing out of the aisle; an unchecked row stops
  // being counted once it starts collapsing out of the tray.
  const trayItems = shoppingList.flatMap(cat => cat.items.filter(inTray));
  const trayCount = shoppingList.flatMap(cat => cat.items).filter(i => {
    const m = rowMotion[i.listItemId];
    if (m?.dir === "out") return m.phase === "collapse";
    if (m?.dir === "in") return m.phase === "mark";
    return !!checked[i.listItemId];
  }).length;

 
  const budgetRemaining = budgetNum !== null ? budgetNum - totalCost : null;
  const budgetPct = budgetNum !== null ? Math.min((totalCost / budgetNum) * 100, 100) : null;
  const overBudget = budgetNum !== null && totalCost > budgetNum;

  // ── OurBanner header state (migration 024) ──
  // Photo-gated by construction: no photoUrl → today's espresso header and no
  // banner control (spec D3). photoUrl is a signed URL resolved on switch, so it
  // swaps the instant activeHouseholdId changes — no stale frame (spec: swap).
  const bannerPhotoUrl = isSignedIn ? (household?.photoUrl || null) : null;

  // Desktop column background (2026-09-20, treatment 3): the same household
  // photo, full-bleed behind the column, blur 7px, a 58% scrim (raised from
  // the mockup's 44% — judged against a BRIGHT photo, the case that needs the
  // darkening) and a radial vignette on top: clear at the centre behind the
  // column, darker toward the viewport edges, so focus comes from contrast
  // rather than uniform darkness. All on the one layer that lives OUTSIDE the
  // column (ShoppingListApp's .desk-bg), handed the whole background value
  // through a CSS variable set on that layer. No photo → the variable is absent and
  // the layer falls back to the cream field, which is every new household. The
  // photo appearing twice on desktop — cropped as the banner inside the column,
  // blurred behind it — is intentional: an echo.
  //
  // The variable lives on .desk-bg ITSELF, never on <html> (2026-10-19): the
  // session recorder copies every element's style attribute, and <html> cannot
  // be excluded, so a URL there reached every replay snapshot. .desk-bg carries
  // .op-replay-block, so its style — and the URL in it — is never serialized.
  useEffect(() => {
    const root = document.querySelector(".desk-bg");
    if (!root) return undefined;
    if (bannerPhotoUrl) {
      root.style.setProperty(
        "--op-desk-bg",
        "radial-gradient(ellipse 62% 70% at 50% 50%, rgba(26,14,6,0) 0%, rgba(26,14,6,0.18) 60%, rgba(26,14,6,0.42) 100%), " +
        "linear-gradient(rgba(26,14,6,0.58), rgba(26,14,6,0.58)), " +
        `url("${bannerPhotoUrl}") center / cover no-repeat #FAF4EC`
      );
    } else {
      root.style.removeProperty("--op-desk-bg");
    }
    return () => root.style.removeProperty("--op-desk-bg");
  }, [bannerPhotoUrl]);
  // HOME-ONLY BANNER (ARCHITECTURE "Home v2 — forward model", decided 2026-09-27,
  // built 2026-10-01). The OurBanner block — the photo layers, the dissolve and
  // the Row 2 wordmark band — renders only on HOME. Plan, Browse and Shop get
  // Row 1 alone (avatar · ⚓ household ⚓ · Velayo dots) on #1a0e06, so every
  // photo-dependent treatment in the header keys off bannerHasPhoto, which is
  // now "a photo exists AND we are on Home". Storage, framing and the earned
  // Our are unchanged; the ⚓ household button stays in Row 1 on every tab (it
  // is the household sheet's trigger off Home). The desktop blurred backdrop
  // (--op-desk-bg, outside the column) is not the banner and keeps the photo
  // on every tab. Signed out still lands on Home via signedOutWelcome.
  const bannerOnHome = view === "home";
  const bannerHasPhoto = !!bannerPhotoUrl && bannerOnHome;
  // Dormancy (spec): wordmark choice persists even with no photo, but only takes
  // effect when a photo exists; with no photo the wordmark always renders large.
  const bannerWordmark = bannerHasPhoto ? (household?.banner_wordmark || "large") : "large";
  const bannerX = household?.photo_position_x ?? 50;
  const bannerY = household?.photo_position_y ?? 50;
  const bannerZoom = household?.photo_zoom ?? 100;
  // Band scrim: darkens the two horizontal strips that are always type (top bar,
  // wordmark base) and leaves the middle clear — assumes nothing about the photo.
  const BAND_SCRIM = "linear-gradient(to bottom," +
    "rgba(0,0,0,0.86) 0%,rgba(0,0,0,0.52) 16%,rgba(0,0,0,0.04) 34%," +
    "rgba(0,0,0,0.04) 62%,rgba(0,0,0,0.58) 84%,rgba(0,0,0,0.88) 100%)";
  // Full-region gradient for the live header: spans the header AND the nav strip
  // as one continuous background so the photo dissolves into the tabs with no
  // hard seam. Top is the abyss band for the wordmark; the bottom ramps into
  // solid espresso (#2C1A0E) so the tabs keep full contrast where it's opaque.
  const BANNER_DISSOLVE = "linear-gradient(to bottom," +
    "rgba(2,15,26,0.55) 0%,rgba(2,15,26,0) 22%,rgba(2,15,26,0) 46%," +
    "rgba(44,26,14,0.72) 74%,#2C1A0E 100%)";
  const CHROME_SHADOW = bannerHasPhoto ? "0 1px 6px rgba(0,0,0,0.9)" : "none";
  const WORDMARK_SHADOW = bannerHasPhoto ? "0 2px 14px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.7)" : "none";

  return (
      <div className="app-root" style={{ fontFamily: "'Georgia', serif", background: "#FAF4EC", color: "#2C1A0E" }}>
      {/* ready (§5): Clerk auth resolved, and — if signed in — household/provisions
          loaded. Signed-out has nothing to load, so it's ready once auth resolves. */}
      {/* Gated on householdReady for the same reason as the landing effect — the list must have actually arrived, not merely stopped loading — so the landing tab is settled before the splash dissolves and there is no flash of Browse before Shop; the 5s failsafe still bounds it. */}
      {showSplash && <SplashScreen onDone={handleSplashDone} ready={isLoaded && (!sessionLive || householdReady)} />}

      {/* Loading overlay — shown while Supabase bootstraps after sign-in */}
      {sessionLive && loading && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(250,244,236,0.88)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 50, backdropFilter: "blur(2px)",
        }}>
          <div style={{
            fontFamily: "'Lato', sans-serif", fontSize: "0.8rem", letterSpacing: "2px",
            textTransform: "uppercase", color: "#8a7a60",
          }}>Loading your provisions…</div>
        </div>
      )}

      {/* ── Bottom status stack ──────────────────────────────────────────────
          One anchor, one order, for every bottom-centred status surface.

          Before this, three elements anchored themselves independently to the
          same place: the connectivity pill and the success toast were BOTH at
          `bottom: 28px, z-index: 2000` (an exact overlap), and the error toast
          sat 4px off at 24px. Any two active at once produced illegible stacked
          text — two accurate messages, neither readable, which is the same
          "surfaces must self-identify state" failure as a missing message.

          Deliberately NOT coupled: no element knows whether the others are
          showing. They stay independently owned and independently conditional;
          the flex column does the arranging. Order is fixed and top-to-bottom:
          persistent status first, then transient notifications, with the one
          carrying an action (Dismiss) nearest the thumb.

          Adding another bottom-centred surface? Put it in here. Do not give it
          its own `position: fixed; bottom: …` — that is exactly how this bug
          got in. */}
      <div className="bottom-stack" style={{
        position: "fixed", left: "50%", transform: "translateX(-50%)",
        zIndex: 2000, display: "flex", flexDirection: "column", alignItems: "center",
        gap: "10px", maxWidth: "90vw",
        // The stack itself must never eat taps meant for the app behind it;
        // children that need interaction opt back in individually.
        pointerEvents: "none",
      }}>
        {/* A5b: "Offline — showing last saved" only once this household's list has
            actually arrived (householdReady — the same signal the splash and the
            landing rule treat as "the data is on screen"); before that, a fresh
            sign-in with nothing saved reads "Reconnecting…". */}
        <ConnectivityPill hasSavedData={householdReady} />

        {/* Error toast — carries a Dismiss button, so it opts into pointer events */}
        {error && (
          <div style={{
            background: "#2C1A0E", color: "#FAF4EC", borderRadius: "8px",
            padding: "12px 20px", display: "flex", alignItems: "center", gap: "14px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
            fontFamily: "'Lato', sans-serif", fontSize: "0.82rem", maxWidth: "90vw",
            pointerEvents: "auto",
          }}>
            <span>⚠ {error}</span>
            <button className="op-chrome" onClick={dismissError} style={{
              background: "none", border: "1px solid rgba(255,255,255,0.3)", color: "#FAF4EC",
              borderRadius: "4px", padding: "3px 10px", cursor: "pointer", fontSize: "0.75rem",
            }}>Dismiss</button>
          </div>
        )}

        {/* Success toast — relocated here from further down the tree; it was the
            element sharing the pill's exact anchor. Purely presentational, so it
            stays pointer-transparent. */}
        {toastMessage && (
          <div style={{
            background: "rgba(44,26,14,0.92)", color: "#FAF4EC",
            fontFamily: "'Lato', sans-serif", fontSize: toastMessage.line ? "14px" : "0.85rem",
            // The one-line variant is a fixed 44px pill with 16px sides; the
            // default pill keeps its padding-driven height.
            ...(toastMessage.line
              ? { height: "44px", padding: "0 16px", whiteSpace: "nowrap" }
              : { padding: toastMessage.action ? "8px 10px 8px 20px" : "10px 22px" }),
            borderRadius: "999px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
            maxWidth: "90vw", display: "flex", alignItems: "center", gap: toastMessage.line ? "16px" : "12px",
            animation: "fadeIn 0.18s ease",
            // A toast with an action is the one bottom-stack child that opts
            // back into pointer events; a plain one stays transparent.
            pointerEvents: toastMessage.action ? "auto" : "none",
          }}>
            <span>{toastMessage.text}</span>
            {toastMessage.action && (
              <button
                type="button"
                onClick={() => { const a = toastMessage.action; setToastMessage(null); a.onClick(); }}
                style={toastMessage.line ? {
                  // Plain text action: no border, no fill; the 44px pill is the hit box.
                  flex: "none", background: "none", border: "none", color: "#FAF4EC",
                  padding: "0", cursor: "pointer", alignSelf: "stretch",
                  fontFamily: "'Lato', sans-serif", fontSize: "12px", fontWeight: 900, letterSpacing: "1.4px", textTransform: "uppercase",
                } : {
                  flex: "none", background: "none", border: "1px solid rgba(250,244,236,0.4)", color: "#FAF4EC",
                  borderRadius: "999px", padding: "5px 12px", cursor: "pointer",
                  fontFamily: "'Lato', sans-serif", fontSize: "0.7rem", fontWeight: 900, letterSpacing: "1px", textTransform: "uppercase",
                }}
              >{toastMessage.action.label}</button>
            )}
          </div>
        )}
      </div>

      {/* The Helm — floating nav pill (SPEC_nav_helm.md). Fixed, outside the
          scrolling content, z-index 900: over content, under sheets (1000) and
          the bottom status stack (2000). Not a bottom-centred STATUS surface, so
          it is deliberately not inside the stack; the stack sits above it.
          The ONLY nav renderer at every width (rail retired 2026-09-20): on
          desktop the pill anchors to the phone column, because the column's
          transform makes it the containing block for every fixed descendant. */}
      {/* Not on the signed-out gift card or its A3 answer (Dan 2026-10-30): the pill's doors lead
          nowhere without an account, and the mockup has no tab bar there. Back once signed in. */}
      {!(signedOutWelcome && pendingGiftCode) && <Helm
        view={signedOutWelcome ? "home" : view}
        onChange={goToDoor}
        badgeCount={totalItems - checkedCount}
        compact={scrollCompact && view !== "home"}
        onPlus={doorAdd[view] || null}
      />}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=Lato:wght@300;400;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        /* --op-add: deep sand = adding things (both "Add a meal" buttons). Dark brown stays the Helm's; teal = the household finished something. */
        :root { --op-text-scale: 1; --op-add: #D9BC8C; --op-add-ink: #2B1E16; }
        /* SAFE-AREA INSETS, gated to the installed app (2026-10-16). The viewport meta carries viewport-fit=cover so iOS
           reports the real insets; every inset use in the app reads these variables, which are 0 in a browser tab and
           the real env() values only under display-mode: standalone — so a Safari tab is pixel-identical to before, and
           the home-screen app pads the header, the Helm, the sheets and the .safe-top band by what the device overlays. */
        /* TOP is MEASURED, not read (fourth cut, 2026-10-17): on Dan's installed app env(safe-area-inset-top) is 0 while
           the WebView is full height under a transparent bar, so no CSS could ever see the inset. The variable defaults
           to 0px here and ProvisionsApp's useEffect writes the measured value onto :root — only when navigator.standalone
           is true and screen.height − innerHeight is a known bar height (src/lib/standaloneInset.js). A Safari tab keeps
           0px and is pixel-identical. BOTTOM stays gated to display-mode: standalone: a tab's bottom inset becomes ~34px
           when Safari's toolbar collapses, which would move the Helm. */
        :root { --op-inset-top: 0px; --op-inset-bottom: 0px; }
        @media (display-mode: standalone) {
          :root { --op-inset-bottom: env(safe-area-inset-bottom, 0px); }
        }
        /* Landscape with a notch, in EVERY mode: viewport-fit=cover lets a page extend under the notch, so the side insets come
           back as body padding — 0 in portrait, so a portrait tab is untouched; in landscape it restores exactly what iOS
           used to inset on its own. Fixed elements (the Helm, the Wrap Up bar) sit at the bottom edge, clear of the notch. */
        body { padding-left: env(safe-area-inset-left, 0px); padding-right: env(safe-area-inset-right, 0px); }
        body { background: #FAF4EC; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .header { background: #2C1A0E; color: #FAF4EC; position: relative; }
/* iOS standalone: the status bar overlays the page, so once the header scrolls away content passes under the clock. This
   fixed band is the status bar's own background — PAGE CREAM, because the clock on Dan's iPhone is BLACK (the 2026-10-16
   first cut assumed a white clock and painted it #1a0e06). Height follows the MEASURED inset (0 in a browser tab, so the
   band is 0 tall and top 0 there — nothing changes). It sits ABOVE the layout viewport, at −inset…0: Dan's phone
   (2026-10-17, the fourth cut's first check) showed a double-height strip when the band also covered 0…+inset, which
   proves a fixed top:0 sits at the viewport's top edge, one bar-height BELOW the clock — the WebView lays the page out
   below the bar at rest and only PAINTS scrolled content up into the bar's region. So the band covers exactly that
   region and nothing in the viewport; the header needs no offset. Below every scrim (1000). */
.safe-top { position: fixed; top: calc(-1 * var(--op-inset-top)); left: 0; right: 0; height: var(--op-inset-top); background: #FAF4EC; z-index: 950; pointer-events: none; }
/* Visually hidden, read by assistive tech. Used for the fixed VERB half of an aria-labelledby accessible name (2026-10-16): a
   button names itself from this span plus the masked title element, so no attribute ever carries a meal name or a count —
   the session recorder copies every attribute except title / alt / on* into replay snapshots, and masks only text nodes. */
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
        .header h1 { font-size: 42px; letter-spacing: 0.02em; }
        .badge { display: inline-block; background: #E8A838; color: white; font-weight: 700; border-radius: 10px; padding: 1px 7px; font-size: 0.7rem; margin-left: 6px; font-family: 'Lato', sans-serif; }
        /* ── The Helm — floating nav pill (SPEC_nav_helm.md v2; mockup_nav_helm_v2.html is the visual authority) ── */
        .helm { position: fixed; left: 24px; right: 24px; bottom: calc(18px + var(--op-inset-bottom)); height: 56px; border-radius: 28px; z-index: 900;
                background: #2C1A0E; display: flex; align-items: stretch; padding: 0 6px;
                box-shadow: 0 8px 24px rgba(44,26,14,0.32), inset 0 0 0 1px rgba(201,169,122,0.16);
                transition: height .2s ease, left .2s ease, right .2s ease, border-radius .2s ease; }
        .helm.compact { left: 52px; right: 52px; height: 44px; border-radius: 22px; }
        @supports ((backdrop-filter: blur(10px)) or (-webkit-backdrop-filter: blur(10px))) {
          .helm { background: rgba(44,26,14,0.94); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); }
        }
        .helm-door { position: relative; flex: 1; min-width: 0; margin: 5px 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px;
                     background: none; border: none; padding: 0; cursor: pointer; color: #C9A97A; border-radius: 22px;
                     font-family: 'Lato', sans-serif; font-size: 12px; letter-spacing: 1.2px; text-transform: uppercase; /* chrome: fixed at 12px, never follows the knob (SPEC_global_text_size) */
                     transition: color .2s ease, background .2s ease, margin .2s ease; -webkit-tap-highlight-color: transparent; }
        .helm-door svg { width: 18px; height: 18px; display: block; transition: width .2s ease, height .2s ease; }
        .helm.compact .helm-door { margin: 4px 0; }
        .helm.compact .helm-door svg { width: 17px; height: 17px; }
        .helm-door.active { color: #FAF4EC; background: rgba(201,169,122,0.10); }
        .helm-label { line-height: 1; white-space: nowrap; transition: opacity .2s ease; }
        .helm-badge { position: absolute; top: 1px; left: calc(50% + 5px); margin: 0; font-size: 0.58rem; padding: 0 4px; line-height: 15px; }
        .control-row-end { height: 0; margin: 0; padding: 0; }
        /* ── Plan (SPEC_meal_planning_v2_pick_commit_cook.md): Meals → Board → List → Cook. TEAL = the household finished something —
           on this surface only Cooked it (then ✓ Cooked) and the stocked banner. Everything else is espresso (#6f5a45) or outline. ── */
        /* ── Plan's title switch (SPEC_plan_week_meals_switch, D1–D3, D10): "This Week" · "Meals" ARE the page title, both always visible;
           active = ink + 3px clay underline the width of the word, inactive = muted. The row is the Helm's compact sentinel on both views.
           Spacing: banner → row 30px (the container's 24 + 6 here), title → subtitle 8px, then 18px to the first card / 20px to the search.
           The add button is top-aligned with the title line, 44px tall. ── */
        .plan-ctl { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin: 6px 0 20px; }
        .plan-ctl.board { margin-bottom: 18px; }
        .plan-ctl.board.with-hint { margin-bottom: 8px; }
        .plan-ctl.meals { margin-bottom: 20px; }
        .plan-ctl-text { flex: 1; min-width: 0; }
        .plan-switch { display: flex; gap: 18px; }
        /* 8px top + 5px bottom padding + the line + the underline ≈ 46px tap target; the negative top margin keeps the title's
           visual top on the row's top edge so the add button aligns with the title line, not the padded box. */
        .plan-view { border: none; background: none; padding: 8px 0 5px; margin: -8px 0 0; cursor: pointer; white-space: nowrap;
                     font-family: 'Playfair Display', serif; font-size: calc(26px * (1 + (var(--op-text-scale) - 1) * 0.4)); font-weight: 400; line-height: 1.15; color: #8C7660;
                     border-bottom: 3px solid transparent; }
        /* The active view reads by WEIGHT, not only by colour (visual v2 D12): the inactive link is regular, the active one bold + underlined. */
        .plan-view.on { color: #2A170C; font-weight: 700; border-bottom-color: #A0714A; cursor: default; }
        .plan-sub { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; margin-top: 8px; }
        /* .hdr-action — THE header action pill (src/components/HeaderAction.js): one style on Shop, This Week and Meals.
           "+ Add" brings an existing thing into the view; "+ New" creates one. Deep sand (--op-add) = adding things. */
        .hdr-action { flex: none; border: none; background: var(--op-add); color: var(--op-add-ink); border-radius: 999px; height: 44px; padding: 0 18px; cursor: pointer;
                      font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; white-space: nowrap; }
        .hdr-action:disabled { opacity: 0.6; cursor: default; }
        /* "add to Shop →" — the subtitle's tap target at N ≥ 2. Prose-sized, underlined, espresso. 15px vertical padding gives a ≥44px
           hit box; the matching negative margins keep the line box exactly where it was. Not a bar, not teal. */
        .plan-addall { display: inline-block; vertical-align: baseline; border: none; background: none; color: #6f5a45; cursor: pointer;
                       padding: 15px 4px; margin: -15px -4px; line-height: inherit;
                       font: inherit; font-weight: 700; text-decoration: underline; text-underline-offset: 3px; text-decoration-color: rgba(111,90,69,0.4); }
        .plan-addall:disabled { opacity: 0.6; cursor: default; }
        /* Board head → first card: 20px. With the drag hint (2+ cards, no banner): 8px to the hint, 20px from the hint to the first card.
           The hint sits flush with "This Week" (no side margin). */
        .plan-prompt { margin: 0 0 20px; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; }
        .plan-banner { display: flex; align-items: center; gap: 12px; border-radius: 12px; padding: 12px 14px; margin-bottom: 12px; }
        .plan-banner.set { background: rgba(201,169,122,0.22); }
        .plan-banner.stocked { background: rgba(13,148,136,0.10); }
        .plan-banner-check { flex: none; width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.85rem; font-weight: 900; }
        .plan-banner.set .plan-banner-check { background: #C9A97A; color: #2C1A0E; }
        .plan-banner.stocked .plan-banner-check { background: #0D9488; color: #fff; }
        .plan-banner-title { font-family: 'Playfair Display', serif; font-weight: 700; font-size: calc(0.98rem * var(--op-text-scale)); color: #2C1A0E; }
        .plan-banner-sub { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #6f5a45; margin-top: 1px; }
        .board { position: relative; margin-bottom: 14px; }
        .board-empty { height: 52px; display: flex; align-items: center; justify-content: center; border: 1.5px dashed #C9A97A; border-radius: 14px;
                       font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; }
        /* Two columns: the numbered tile, then everything else. No third column, so nothing wraps at 390–430px (the ROADMAP bug). */
        .board-card { display: flex; align-items: stretch; background: #fff; border-radius: 12px; box-shadow: 0 3px 10px rgba(44,26,14,0.13);
                      cursor: pointer; user-select: none; -webkit-user-select: none; -webkit-touch-callout: none; touch-action: pan-y; position: relative;
                      transition: transform .15s ease, box-shadow .15s ease; }
        .board-card.noshop { background: transparent; box-shadow: none; cursor: grab; outline: 1.5px dashed #C9A97A; outline-offset: -1.5px; }
        .board-card.noshop.lifted { cursor: grabbing; }
        /* Lifted: amber hairline (an outline, so the card's size never changes), deeper shadow. The lifted card follows the pointer with no transition. */
        .board-card.lifted { z-index: 3; outline: 1.5px solid #c8973a; outline-offset: -1.5px; box-shadow: 0 14px 26px rgba(44,26,14,0.3); transition: none; }
        @media (prefers-reduced-motion: reduce) { .board-card { transition: none; } }
        .board-card + .board-card { margin-top: 8px; }
        .board-tile { flex: none; width: 62px; border-radius: 12px 0 0 12px; display: flex; flex-direction: column; align-items: flex-start; padding: 10px 6px; gap: 3px; overflow: hidden; }
        .board-num { font-family: 'Playfair Display', serif; font-size: 1.35rem; font-weight: 700; line-height: 1; }
        /* Rail word: 50px of content box. Tight tracking (.08em) and 0.5rem so nine capitals (LEFTOVERS, SOMETHING) fit; a space still wraps (EATING OUT). Anything longer clips at the tile edge, never bleeds. */
        .board-rail { font-family: 'Lato', sans-serif; font-size: 0.5rem; font-weight: 900; letter-spacing: 0.08em; text-transform: uppercase; line-height: 1.2; opacity: 0.92; max-width: 100%; }
        /* One line of rhythm between name / chip / line / button. Grip and × share one grey. */
        .board-main { flex: 1; min-width: 0; padding: 8px 8px 8px 12px; display: flex; flex-direction: column; gap: 3px; position: relative; }
        .board-top { display: flex; align-items: center; gap: 6px; min-width: 0; min-height: 28px; }
        .board-grip { flex: none; fill: #b5a58f; margin-left: 2px; cursor: grab; }
        .board-card.lifted .board-grip { cursor: grabbing; }
        .board-card-title { flex: 1; min-width: 0; font-family: 'Playfair Display', serif; font-size: calc(0.98rem * (1 + (var(--op-text-scale) - 1) * 0.5)); font-weight: 700; color: #2C1A0E; line-height: 1.15;
                            overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .board-chip { flex: none; font-family: 'Lato', sans-serif; font-size: 0.56rem; font-weight: 900; letter-spacing: 1px; text-transform: uppercase; color: #8a7a60;
                      border: 1px solid #E8D5B7; border-radius: 999px; padding: 2px 7px; }
        .board-line { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; line-height: 1.25; }
        .board-card.ready .board-line { color: #6f5a45; }
        .board-actions { display: flex; align-items: center; gap: 2px; margin-top: 3px; }
        .board-actions-gap { flex: 1; }
        /* One primary per state. Add to Shop / See on list: espresso outline. Cooked it: teal fill — the household finished something. */
        .board-lock, .board-see, .board-cook { flex: none; border-radius: 999px; padding: 6px 12px; cursor: pointer; white-space: nowrap;
                      font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 900; letter-spacing: 0.5px; }
        .board-lock { border: 1.5px solid #6f5a45; background: transparent; color: #6f5a45; }
        .board-see { border: 1.5px solid #C9A97A; background: transparent; color: #6f5a45; }
        .board-cook { border: 1.5px solid #0D9488; background: #0D9488; color: #fff; }
        /* Cooked this load: muted in place (tile + text at 55%), ✓ Cooked as a teal outline, disabled at full weight. */
        .board-card.cooked { cursor: default; }
        .board-card.cooked .board-tile, .board-card.cooked .board-top, .board-card.cooked .board-line { opacity: 0.55; }
        /* THE AFTERGLOW (Photos slice 2, S4): "How was it?" + stars, the note once a star is tapped, and
           "Add a meal photo" — full strength (outside the 55% rule), wrapping at XXL on 320. */
        .board-glow { margin-top: 4px; display: flex; flex-direction: column; gap: 2px; cursor: default; }
        .board-glow-photo { display: flex; align-items: center; flex-wrap: wrap; gap: 2px 10px; }
        .board-photo-add { display: inline-flex; align-items: center; gap: 6px; min-height: 44px; padding: 0 4px; border: none; background: transparent; color: #8A5F3A;
          font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; cursor: pointer; white-space: nowrap; }
        .board-photo-add:disabled { opacity: .55; cursor: default; }
        .board-photo-add:focus-visible { outline: 2px solid #A0724A; outline-offset: 2px; border-radius: 8px; }
        .board-photo-done { display: inline-flex; align-items: center; gap: 8px; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); color: #0A776D; }
        .board-photo-thumb { width: 32px; height: 32px; border-radius: 6px; object-fit: cover; display: block; }
        /* The stars (S4/S5) — shared by the afterglow and "Your cooks". 44 px targets, no averages anywhere. */
        .rate-row { display: flex; align-items: center; flex-wrap: wrap; gap: 0 6px; }
        .rate-label { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #5C4636; }
        .rate-stars { display: inline-flex; align-items: center; }
        .rate-star { width: 44px; height: 44px; padding: 0; border: none; background: transparent; color: #C9A97A; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
        .rate-star.on { color: #A0724A; }
        .rate-star:disabled { opacity: .55; cursor: default; }
        .rate-star:focus-visible { outline: 2px solid #A0724A; outline-offset: -4px; border-radius: 10px; }
        .rate-note { flex: 1 1 100%; min-width: 0; min-height: 40px; margin: 2px 0 4px; padding: 6px 10px; border: 1px solid #E5D7C1; border-radius: 10px; background: #FFFDF8;
          font-family: 'Lato', sans-serif; font-size: max(16px, calc(14px * var(--op-text-scale))); color: #2C1A0E; }
        .rate-note:focus { outline: 2px solid #A0724A; outline-offset: 0; }
        /* Teal text sweep (ruling 2026-10-07): text at body size and below is #0A776D; the border is a
           stroke and keeps brand #0D9488. Same split on .deck-primary.done, .added-here-tag, .wrapbar-a. */
        .board-cook.done, .board-cook.done:disabled { background: transparent; color: #0A776D; border-color: #0D9488; opacity: 1; cursor: default; }
        .board-lock:disabled, .board-see:disabled, .board-cook:disabled { opacity: 0.5; cursor: default; }
        .board-more { flex: none; width: 30px; height: 30px; border-radius: 50%; border: none; background: transparent; color: #8a7a60; cursor: pointer;
                      font-family: 'Lato', sans-serif; font-size: 1.1rem; font-weight: 700; line-height: 1; padding: 0 0 6px; }
        .board-more:hover { color: #6f5a45; }
        .board-x { flex: none; width: 28px; height: 28px; border-radius: 50%; border: none; background: transparent; color: #b5a58f; cursor: pointer;
                   font-family: 'Lato', sans-serif; font-size: 1.15rem; font-weight: 300; line-height: 1; padding: 0 0 2px; }
        .board-x:hover { color: #6f5a45; }
        .board-x:disabled { cursor: default; }
        .board-menu-backdrop { position: fixed; inset: 0; z-index: 4; }
        .board-menu { position: absolute; right: 6px; bottom: 40px; z-index: 5; background: #fff; border: 1px solid #E8D5B7; border-radius: 10px;
                      box-shadow: 0 8px 24px rgba(44,26,14,0.18); padding: 4px; min-width: 150px; }
        .board-menu button { display: block; width: 100%; text-align: left; border: none; background: transparent; padding: 9px 12px; border-radius: 7px;
                             font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); color: #2C1A0E; cursor: pointer; }
        .board-menu button:hover { background: #F5EDE0; }
        .board-menu button:disabled { opacity: 0.5; cursor: default; }
        .plan-foot { margin: 10px 0 18px; }
        /* Hold a night — ruled label over three solid-sand buttons (icon + label, 64px). Dashed borders belong to the no-shop cards, not these. */
        .hold-night { width: 100%; }
        .hold-night-rule { display: flex; align-items: center; gap: 12px; }
        .hold-night-rule > span:not(.hold-night-label) { flex: 1 1 auto; height: 1px; background: #E2D3BD; }
        .hold-night-label { flex: none; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); font-weight: 700; letter-spacing: 0.14em; color: #7A6656; }
        /* A wrapping flex row, no breakpoint: each button's basis is its own min-content (the
           width of its longest word), so three sit in a row when they fit and the third drops
           to a full-width line when they don't (320 XXL). Labels never break mid-word; items
           stretch, so heights match within a row. */
        .hold-night-btns { margin-top: 14px; display: flex; flex-wrap: wrap; gap: 8px; }
        /* min-height, not height: the label grows with the knob and the three buttons share
           the tallest row (grid items stretch). Vertical padding keeps a two-line label off the border. */
        .plan-noshop { flex: 1 1 min-content; min-height: 64px; border: 1.5px solid #D8C3A5; background: transparent; color: #4A3226; border-radius: 16px; padding: 8px 4px; cursor: pointer;
                       display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 5px;
                       font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; line-height: 1.1; text-align: center; }
        .plan-noshop svg { width: 20px; height: 20px; flex: none; }
        /* The empty-week welcome (mockup_plan_tab_welcome.html). Ghost cards fade 1 → .7 → .4; generous vertical air on purpose. */
        .plan-welcome { display: flex; flex-direction: column; align-items: center; padding: 0 4px; }
        .plan-ghosts { margin-top: 4px; width: 100%; display: flex; flex-direction: column; gap: 8px; }
        .plan-ghost { height: 56px; border-radius: 14px; border: 1.5px dashed #C9AE88; background: #fff; display: flex; align-items: center; gap: 12px; padding: 0 8px; }
        .plan-ghost:nth-child(2) { border-color: #D8C3A5; background: transparent; opacity: 0.7; }
        .plan-ghost:nth-child(3) { border-color: #E2D3BD; background: transparent; opacity: 0.4; }
        .plan-ghost-tile { flex: none; width: 46px; height: 46px; border-radius: 10px; background: #EADCC6; color: #6E5A4A; display: flex; flex-direction: column; align-items: center; justify-content: center; }
        .plan-ghost:nth-child(2) .plan-ghost-tile { background: #EFE4D3; color: #7A6656; }
        .plan-ghost:nth-child(3) .plan-ghost-tile { background: #F1E7D8; color: #7A6656; }
        .plan-ghost-num { font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 700; line-height: 18px; }
        .plan-ghost-word { font-family: 'Lato', sans-serif; font-size: 7px; font-weight: 700; letter-spacing: 0.1em; }
        .plan-ghost-lines { flex: 1 1 auto; display: flex; flex-direction: column; gap: 7px; }
        .plan-ghost-lines span { display: block; height: 10px; border-radius: 5px; background: #E8DBC8; }
        .plan-ghost-lines span + span { height: 8px; border-radius: 4px; background: #F1E7D8; }
        .plan-ghost:nth-child(n+2) .plan-ghost-lines span { background: #EBDFCD; }
        .plan-ghost:nth-child(n+2) .plan-ghost-lines span + span { background: #F3EADD; }
        .plan-welcome-title { margin: 32px 0 0; font-family: 'Playfair Display', serif; font-size: calc(28px * (1 + (var(--op-text-scale) - 1) * 0.4)); font-weight: 700; line-height: 1.15; color: #2C1A0E; text-align: center; }
        /* Unitless line-height so the leading follows the knob (a fixed 22px stacked 26 px lines on
           top of each other at XXL); 1.47 keeps Default at the same 22 px. One flowing paragraph,
           balanced, instead of a hard break between the two sentences. */
        .plan-welcome-sub { margin: 10px 0 0; font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); line-height: 1.47; color: #6E5A4A; text-align: center; text-wrap: balance; }
        .plan-welcome-add { margin-top: 28px; width: 100%; height: 54px; border-radius: 27px; border: none; background: var(--op-add); color: var(--op-add-ink); cursor: pointer;
                            display: flex; align-items: center; justify-content: center; gap: 10px; font-family: 'Lato', sans-serif; font-size: calc(16px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; }
        .plan-welcome-add svg { width: 18px; height: 18px; flex: none; }
        .plan-welcome .hold-night { margin-top: 36px; }
        /* Library — "What sounds good?" (SPEC_meal_library_v1 + v1.1). Two-column grid, 16px row / 12px column gap, one column
           below 340px. Card = coloured top (first occasion word + 20px serif name; tone = mealTone, the board's rule) over a
           white strip (count + the Plan pill). ZERO TEAL: Plan is espresso outline, ✓ Planned is sand/espresso, the rail and
           chips use the filled-espresso on-state. Clay's 11.5px word is the known AA miss (tracked in LATER). */
        .lib-create { flex: none; height: 44px; padding: 0 18px; border-radius: 22px; cursor: pointer; white-space: nowrap;
                      font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; display: inline-flex; align-items: center; gap: 6px; }
        .lib-create.outline { border: 1.5px solid #6f5a45; background: transparent; color: #6f5a45; }
        .lib-search-row { display: flex; gap: 8px; margin: 0 0 12px; }
        .lib-search { flex: 1; min-width: 0; height: 44px; display: flex; align-items: center; gap: 10px; padding: 0 14px; border-radius: 12px;
                      border: 1.5px solid #E8D5B7; background: #F5EDE0; color: #8a7a60; }
        .lib-search input { flex: 1; min-width: 0; border: none; background: transparent; outline: none; font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); color: #2C1A0E; }
        .lib-search input::-webkit-search-cancel-button { -webkit-appearance: none; }
        .lib-fbtn, .lib-vtog { flex: none; width: 44px; height: 44px; border-radius: 12px; border: 1.5px solid #E8D5B7; background: #FFFDF9; color: #6f5a45; cursor: pointer;
                    display: flex; align-items: center; justify-content: center; position: relative; padding: 0; }
        .lib-fdot { position: absolute; top: 8px; right: 8px; width: 8px; height: 8px; border-radius: 50%; background: #A0724A; border: 1.5px solid #FFFDF9; }
        .lib-active { display: flex; flex-wrap: wrap; gap: 8px; margin: 0 0 12px; }
        .lib-chip-x { height: 32px; padding: 0 10px 0 6px; border-radius: 16px; border: 1.5px solid #6f5a45; background: #FFFDF9; color: #2C1A0E; cursor: pointer;
                      font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; display: inline-flex; align-items: center; gap: 7px; }
        .lib-chip-x > span:last-child { color: #8a7a60; font-weight: 400; }
        .lib-chip-x .lib-mini { width: 22px; height: 22px; font-size: 0.62rem; }
        .lib-rail { margin: 0 0 14px; }
        /* The library's rail hides its scrollbar (polish 2026-10-16 — it showed on desktop); Browse's rail keeps its styled
           one. scrollbar-width: none covers Chromium 121+ / Firefox / Safari 18.2+; the ::-webkit rule covers older WebKit. */
        .lib-rail .cat-rail { scrollbar-width: none; }
        .lib-rail .cat-rail::-webkit-scrollbar { display: none; }
        .lib-occ { flex: none; height: 36px; padding: 0 16px; border-radius: 18px; border: 1.5px solid #E8D5B7; background: #FFFDF9; color: #6f5a45; cursor: pointer;
                   font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; white-space: nowrap; }
        .lib-occ.on { background: #6f5a45; border-color: #6f5a45; color: #FAF4EC; }
        .lib-hint { margin: 0 0 14px; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; }
        .lib-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 12px; align-items: stretch; }
        @media (max-width: 339px) { .lib-grid { grid-template-columns: minmax(0, 1fr); } }
        /* List view (D8): full-width rows, 10px apart — a 60×60 rounded thumbnail (photo, or tint + icon at 30), a 2-line
           title at the Card title role, the meta line, and the "+" at 48px. Grid is for browsing by picture; list is for
           scanning by name. */
        .lib-list { display: flex; flex-direction: column; gap: 10px; }
        /* Amendment A4 restructure: a 3-column grid — thumb (rows 1–2) · name (row 1) over meta (row 2) · action (rows 1–2).
           The name is the open button; its ::before covers the thumbnail column (the row is the positioned ancestor), so the
           picture still opens the meal while the meta row stays a sibling and can carry the "+ Set meal type" button. */
        .lib-row { position: relative; display: grid; grid-template-columns: 60px minmax(0, 1fr) auto; grid-template-rows: auto auto;
                   column-gap: 12px; row-gap: 3px; align-items: center; border-radius: 14px; border: 1px solid #E8D5B7; background: #FFFDF9; padding: 10px; }
        .lib-thumb { grid-column: 1; grid-row: 1 / 3; width: 60px; height: 60px; border-radius: 12px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .lib-rbody { grid-column: 2; grid-row: 1; align-self: end; min-width: 0; display: block; text-align: left; padding: 0; border: none;
                     background: none; color: #2C1A0E; cursor: pointer; font: inherit; }
        .lib-rbody::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 82px; }
        .lib-rmeta { grid-column: 2; grid-row: 2; align-self: start; min-width: 0; }
        .lib-rname { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
                     font-family: 'Playfair Display', serif; font-size: calc(17px * (1 + (var(--op-text-scale) - 1) * 0.5)); font-weight: 600; line-height: 1.2; overflow-wrap: anywhere; }
        .lib-rmeta { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); line-height: 1.3; color: #8A5F3A; }
        .lib-row .lib-plan { grid-column: 3; grid-row: 1 / 3; width: 48px; height: 48px; margin-left: 0; }
        .lib-row .lib-plan::before { inset: 0; }
        .lib-row .lib-stage { grid-column: 3; grid-row: 1 / 3; }
        /* The row's status text follows the Button role (k 0.8, the pill it replaced): at XXL on 320px a full-scale
           "✓ Ready" squeezed the title column until a word broke mid-word (harness 2026-10-16). */
        .lib-row .lib-stage { height: 48px; margin-left: 0; padding: 0; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); }
        /* VISUAL v2 (SPEC_meal_library_visual_v2): a LIGHT card — cream, 1px warm border, dark text (D1). Flex column:
           slot → body (name) → strip (meta + the round "+"). */
        .lib-gcard { display: flex; flex-direction: column; border-radius: 14px; border: 1px solid #E8D5B7; background: #FFFDF9; overflow: hidden; }
        .lib-gbody { flex: 1; display: flex; flex-direction: column; align-items: stretch; text-align: left; width: 100%; padding: 0; border: none;
                     background: none; color: #2C1A0E; cursor: pointer; font: inherit; }
        /* The slot (D3): a short tinted band, the occasion mark centred — 56px without a photo; a photo takes a fixed 132px.
           ROW MATCHING IN PURE CSS (D4, build scope 2) — the base rules below are the FALLBACK (fixed 56px bands, titles
           may sit at different heights); the @supports block further down is the real thing: CSS SUBGRID (polish
           2026-10-16, replacing the flex: 1 slot trick whose bands came out ragged when names differed in line count).
           No row math in JS, at any column count. */
        .lib-slot { position: relative; flex: none; height: 56px; display: flex; align-items: center; justify-content: center; background: #F4EADC; }
        .lib-slot.photo { height: 132px; }
        /* "✓ Ready" as a chip in the band's top-right (grid card only, 2026-10-16): Eyebrow role (12.5px, k 1), cream on a
           1px warm border, pill radius — so the strip's meta line stays on one line. The list row keeps its text label. */
        .lib-ready { position: absolute; top: 6px; right: 6px; display: inline-flex; align-items: center; gap: 3px; padding: 4px 8px 4px 6px;
                     border-radius: 999px; border: 1px solid #EADBC4; background: #FFFDF9; color: #3A2A20;
                     font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); font-weight: 700; line-height: 1; white-space: nowrap; }
        .lib-ready svg { width: 0.8em; height: 0.8em; }
        /* With a chip in the band, the icon centres in the space LEFT of it: the band reserves the chip's width in the chip's own
           em (so it tracks every text step) — no overlap at Default through XXL, no JS. */
        .lib-slot.has-chip { font-size: calc(12.5px * var(--op-text-scale)); padding-right: calc(5.6em + 12px); }
        /* AMENDMENT A4 — the untagged meal's invitation, in place of the occasion word: a real button in the Meta role, accent
           #8A5F3A, no underline, a ≥44px hit area through the pseudo-element. It is never inside the open button (grid: the
           strip is the open button's sibling; list: the meta row is a sibling cell), so it can never open the meal. */
        .lib-settype { position: relative; display: inline; background: none; border: none; padding: 6px 0; margin: -6px 0; font: inherit;
                       font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); font-weight: 700; color: #8A5F3A; cursor: pointer; }
        .lib-settype::before { content: ""; position: absolute; inset: -7px -6px; }
        /* AMENDMENT A5 — the "Set meal type" picker: a small bottom sheet (SheetPanel) with the seven occasions as icon chips. */
        .lib-type-scrim { position: fixed; inset: 0; background: rgba(2,15,26,0.6); z-index: 1000; display: flex; align-items: flex-end; }
        .lib-type-sheet { position: relative; width: 100%; background: #FDF8F2; border-radius: 20px 20px 0 0; padding: 0 20px calc(24px + var(--op-inset-bottom)); }
        .lib-type-handle { width: 36px; height: 4px; background: #c8b89a; border-radius: 2px; margin: 10px auto 0; }
        .lib-type-h { margin: 14px 0 2px; font-family: 'Playfair Display', serif; font-size: calc(22.4px * (1 + (var(--op-text-scale) - 1) * 0.5)); font-weight: 700; color: #2C1A0E; }
        .lib-type-name { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8A5F3A; margin: 0 0 14px; overflow-wrap: anywhere; }
        .lib-type-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .lib-type-chip { display: inline-flex; align-items: center; gap: 7px; height: 44px; padding: 0 14px 0 10px; border-radius: 22px; border: 1.5px solid #E8D5B7; background: #FFFDF9; color: #6f5a45; cursor: pointer;
                         font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; }
        .lib-type-chip svg { width: 22px; height: 22px; }
        .lib-type-chip.on { background: #6f5a45; border-color: #6f5a45; color: #FAF4EC; }
        .lib-type-chip .gf-oncard { margin-left: 2px; }
        .lib-type-help { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; margin-top: 10px; }
        .lib-type-btns { display: flex; gap: 10px; justify-content: flex-end; margin-top: 18px; }
        .lib-type-cancel { min-height: 44px; padding: 0 18px; border-radius: 12px; border: 1.5px solid #6f5a45; background: transparent; color: #6f5a45; cursor: pointer;
                           font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; }
        .lib-type-save { min-height: 44px; padding: 0 20px; border-radius: 12px; border: none; background: #6f5a45; color: #FAF4EC; cursor: pointer;
                         font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; }
        .lib-type-save:disabled { opacity: 0.5; cursor: default; }
        .lib-slot-img { display: block; width: 100%; height: 100%; object-fit: cover; opacity: 0; transition: opacity .25s ease; }
        .lib-slot-img.in { opacity: 1; }
        @media (prefers-reduced-motion: reduce) { .lib-slot-img { transition: none; } }
        /* ── The recipe view (SPEC_recipe_view, 2026-10-18). A full-height SheetPanel: the scrim is the
           library's type-sheet scrim (z 1000, above the Helm); the panel keeps SheetPanel's cap and
           scrolls inside itself; min-height makes it a page, not a strip, for a short recipe. Close
           moves to the LEFT here (the mockup: Close ✕ left, Edit right); every other sheet keeps it
           right. Text follows the 10-04 role table, not the mockup's pixels (re-mapped 2026-10-18 after
           the phone check read a size too large): title = sheet title 22.4 at k 0.5; the section
           headings = the Edit Meal sheet's section labels (.modal-label: 12.5 uppercase at k 1);
           byline / Added by / meta / proof = meta 14 at k 1; ingredient rows, step text and cook rows
           = body 16 at k 1 on 44 px rows; the step number circle 28 at k 1 with its numeral at the
           12.5 floor; Edit, the plan pill and Show all = button 14 at k 0.8; tags and the seam 12.5.
           Cook mode (slice c) steps the step text up one size from here. Teal only on Cooked it /
           ✓ Cooked (the household finished something); "On hand" is text teal #0A776D. ── */
        .rv-scrim { position: fixed; inset: 0; background: rgba(2,15,26,0.6); z-index: 1000; display: flex; align-items: flex-end; }
        /* Sized against the SCRIM (100% of it, less SheetPanel's 24 px), not the window: on the desktop the fixed,
           transformed .phone-frame is the containing block and sits 48 px shorter than 100dvh, so a dvh-sized sheet
           overran the frame's top by 24 px (✕ / Edit crowded, the grabber hidden, the corner clipped — 2026-10-18).
           On the phone the scrim IS the layout viewport, so 100% there equals 100dvh. The cap is passed to
           SheetPanel the same way (its inline style wins over this rule). */
        .rv-sheet { position: relative; width: 100%; min-height: calc(100% - 24px); background: #FAF4EC; border-radius: 20px 20px 0 0; padding: 0 0 calc(24px + var(--op-inset-bottom)); }
        .rv-sheet .sheet-close { left: 8px; right: auto; }
        .rv-edit { position: absolute; top: 8px; right: 12px; min-height: 44px; padding: 0 8px; border: none; background: transparent; color: #8A5F3A; cursor: pointer;
                   font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; }
        .rv-edit:focus-visible { outline: 2px solid #A0724A; outline-offset: 2px; border-radius: 8px; }
        /* GIVE THIS MEAL (2026-10-30): the share icon beside Edit — same row, same quiet clay, the same 44 px hit area. */
        .rv-give { position: absolute; top: 8px; right: 60px; z-index: 3; display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; padding: 0; border: none; border-radius: 50%; background: transparent; color: #8A5F3A; cursor: pointer; }
        .rv-give:disabled { opacity: .5; cursor: default; }
        .rv-give:focus-visible { outline: 2px solid #A0724A; outline-offset: 2px; }
        /* Another member's photo ⋯ opens with their name (inert, greyed) so the shorter menu reads as "not yours". */
        .rv-photo-owner { display: block; padding: 8px 14px 4px; font-family: 'Lato', sans-serif; font-size: calc(12px * var(--op-text-scale)); color: #8a7a60; white-space: nowrap; }
        /* THE GIVE SHEET (B1–B5, mockup_gift_preview): kicker, name, the photo row, the note, the teal Give; then the mini card and Send it. */
        .give-giving { display: flex; flex-wrap: wrap; align-items: center; gap: 0 5px; margin: 0 0 12px; padding: 8px 40px 8px 12px; border-radius: 10px; background: #E6F4F2; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); line-height: 1.4; color: #0F5F58; }
        .give-giving button { background: none; border: 0; padding: 0; min-height: 28px; font: inherit; font-weight: 900; color: #0D9488; cursor: pointer; }
        .give-giving button:disabled { opacity: .5; cursor: default; }
        .give-giving button:focus-visible { outline: 2px solid #0D9488; outline-offset: 2px; border-radius: 4px; }
        .give-kicker { font-family: 'Lato', sans-serif; font-size: 10.5px; font-weight: 900; letter-spacing: .18em; text-transform: uppercase; color: #A0724A; padding-right: 36px; }
        .give-name { margin-top: 6px; font-family: 'Playfair Display', serif; font-weight: 700; font-size: calc(20px * (1 + (var(--op-text-scale) - 1) * 0.5)); line-height: 1.2; color: #2C1A0E; overflow-wrap: anywhere; }
        .give-prow { display: flex; gap: 12px; align-items: center; margin-top: 14px; padding: 10px; border: 1px solid #E3D4BC; border-radius: 12px; background: #fff; }
        .give-thumb-wrap { position: relative; flex: none; }
        .give-thumb { display: block; width: 54px; height: 54px; border-radius: 9px; object-fit: cover; background: #E8D5B7; }
        .give-thumb-empty { background: #FAF4EC; border: 1.5px dashed #C9A97A; }
        .give-sc { position: absolute; left: -4px; top: -6px; font-family: 'Lato', sans-serif; font-size: 8.5px; font-weight: 900; letter-spacing: .06em; text-transform: uppercase; background: #0D9488; color: #fff; padding: 2px 5px; border-radius: 6px; }
        .give-ptext { font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); line-height: 1.45; color: #6f5a45; }
        .give-ptext b { display: block; color: #2C1A0E; font-size: calc(13px * var(--op-text-scale)); margin-bottom: 1px; }
        .give-nudge { background: none; border: 0; padding: 0; font: inherit; font-weight: 700; color: #0D9488; cursor: pointer; text-align: left; }
        .give-nudge:disabled { opacity: .5; cursor: default; }
        .give-flabel { display: block; margin: 16px 2px 6px; font-family: 'Lato', sans-serif; font-size: calc(12px * var(--op-text-scale)); font-weight: 700; color: #2C1A0E; }
        .give-flabel span { font-weight: 400; color: #8a7a60; }
        .give-ta { display: block; width: 100%; min-height: 74px; border: 1px solid #E3D4BC; border-radius: 10px; background: #fff; padding: 10px 12px; font-family: 'Playfair Display', serif; font-style: italic; font-size: calc(14px * var(--op-text-scale)); color: #2C1A0E; resize: vertical; }
        .give-ta::placeholder { color: #b7a589; }
        .give-ta-filled { margin-top: 14px; min-height: 0; }
        .give-count { text-align: right; font-family: 'Lato', sans-serif; font-size: 10.5px; color: #8a7a60; margin-top: 4px; }
        .give-count.over { color: #9a4a1e; font-weight: 700; }
        .give-go { display: flex; align-items: center; justify-content: center; width: 100%; margin-top: 18px; min-height: 48px; background: #0D9488; color: #fff; font-family: 'Lato', sans-serif; font-weight: 900; font-size: calc(15px * (1 + (var(--op-text-scale) - 1) * 0.8)); letter-spacing: .02em; border: 0; border-radius: 12px; padding: 14px; cursor: pointer; }
        .give-go:disabled { background: #9cc9c4; cursor: default; }
        .give-spin { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.5); border-top-color: #fff; border-radius: 50%; margin-right: 8px; animation: give-spin .9s linear infinite; }
        @keyframes give-spin { to { transform: rotate(360deg); } }
        .give-fine { font-family: 'Lato', sans-serif; font-size: calc(11.5px * var(--op-text-scale)); color: #8a7a60; text-align: center; margin-top: 10px; line-height: 1.45; }
        .give-steps { margin-top: 14px; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); color: #6f5a45; line-height: 2; }
        .give-steps .on { color: #2C1A0E; font-weight: 700; }
        .give-steps .ok { color: #0D9488; font-weight: 900; margin-right: 6px; }
        .give-steps .dot { display: inline-block; width: 12px; margin-right: 6px; color: #C9A97A; }
        .give-warn { margin-top: 12px; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); color: #9a4a1e; background: #fbeee3; border: 1px solid #efd2bc; border-radius: 10px; padding: 9px 11px; }
        .give-mini-cap { font-family: 'Lato', sans-serif; font-size: 10.5px; font-weight: 900; letter-spacing: .14em; text-transform: uppercase; color: #8a7a60; margin-top: 14px; }
        .give-mini { display: flex; gap: 11px; align-items: center; margin-top: 8px; border: 1px solid #E3D4BC; border-radius: 12px; background: #fff; padding: 10px; }
        .give-mini-t { font-family: 'Playfair Display', serif; font-weight: 700; font-size: calc(14.5px * (1 + (var(--op-text-scale) - 1) * 0.5)); line-height: 1.2; color: #2C1A0E; overflow-wrap: anywhere; }
        .give-mini-m { font-family: 'Lato', sans-serif; font-size: calc(11.5px * var(--op-text-scale)); color: #6f5a45; margin-top: 3px; }
        .give-copy { display: block; width: 100%; margin-top: 12px; min-height: 44px; background: none; border: 0; font-family: 'Lato', sans-serif; font-size: calc(13px * var(--op-text-scale)); font-weight: 700; color: #0D9488; cursor: pointer; }
        .stop-giving-yes, .stop-giving-no { flex: 1 1 auto; min-height: 48px; padding: 10px 18px; border-radius: 24px; white-space: nowrap; cursor: pointer;
          font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; line-height: 1.2; }
        .stop-giving-yes { background: #FFFFFF; border: 1.5px solid #E5D7C1; color: #A33A2A; }
        .stop-giving-no { background: #D9BC8C; border: 1.5px solid #D9BC8C; color: #2C1A0E; }
        .give-go:focus-visible, .give-copy:focus-visible, .give-nudge:focus-visible, .stop-giving-yes:focus-visible, .stop-giving-no:focus-visible { outline: 2px solid #A0724A; outline-offset: 2px; }
        .rv-hero { margin: 44px 16px 0; height: 132px; border-radius: 14px; display: flex; align-items: center; justify-content: center; }
        /* Photos v1 (R2): the hero becomes the strip at its own 132 px — native horizontal scroll-snap, one photo
           per slide, object-fit cover, the tint behind each photo until it decodes (it fades in once, .in). */
        .rv-strip { display: flex; overflow-x: auto; overflow-y: hidden; scroll-snap-type: x mandatory; overscroll-behavior-x: contain; scrollbar-width: none; justify-content: flex-start; overflow-clip-margin: 0; }
        .rv-strip::-webkit-scrollbar { display: none; }
        .rv-slide { position: relative; flex: 0 0 100%; height: 100%; scroll-snap-align: start; scroll-snap-stop: always; }
        /* S30 / R6 (2026-10-26): every photo <img> people put in the app — .rv-photo-img, .rv-viewer-img,
           .rv-psheet-thumb, .lib-slot-img, .board-photo-thumb, .op-galley-th img — carries .op-photo (plus
           draggable="false" in the markup): no iOS long-press save/share callout, no drag-out. One rule, not six.
           The Clerk avatar is not a household photo and is out; background-image divs don't get the callout.
           S14's honest limit (screenshots) still holds. */
        .op-photo { -webkit-user-drag: none; -webkit-touch-callout: none; }
        .rv-photo-img { display: block; width: 100%; height: 100%; object-fit: cover; opacity: 0; transition: opacity .25s ease; user-select: none; }
        .rv-photo-img.in { opacity: 1; }
        @media (prefers-reduced-motion: reduce) { .rv-photo-img { transition: none; } }
        /* PHOTO HERO (Dan, 2026-10-21): with a photo the hero is 4:3, capped at 420 px tall on wide screens (the
           width then stays full and the photo crops a little more — object-fit: cover). The tint hero (0 photos)
           keeps its 132 px. Each photo is a full-bleed <button> that opens the viewer; the expand mark is a
           decoration pinned to the hero's corner (it does not scroll with the strip). */
        .rv-hero-wrap { position: relative; margin: 44px 16px 0; }
        .rv-hero-wrap .rv-hero.rv-strip { margin: 0; width: 100%; height: auto; aspect-ratio: 4 / 3; max-height: 420px; overflow-y: hidden; }   /* width: 100% — without it the 420 cap shrank the width to keep 4:3 (560 px in a 768 px sheet, the expand mark off the photo) */
        .rv-photo-open { display: block; width: 100%; height: 100%; padding: 0; border: 0; border-radius: 0; background: transparent; cursor: zoom-in; }
        .rv-photo-open:focus-visible { outline: 3px solid #A0724A; outline-offset: -3px; }
        .rv-hero-expand { position: absolute; right: 10px; bottom: 10px; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
          background: rgba(20,12,6,.55); color: #FFFDF8; pointer-events: none; }
        /* THE VIEWER: a full-screen dark ground over everything (the recipe view's scrim is z 1000). */
        .rv-viewer { position: fixed; inset: 0; z-index: 1100; display: flex; flex-direction: column; background: #120D09; color: #F4EADC;
          padding: 0 0 var(--op-inset-bottom, 0px); animation: rv-viewer-in .18s ease-out; }
        @keyframes rv-viewer-in { from { opacity: 0; } to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .rv-viewer { animation: none; } .rv-viewer-strip { scroll-behavior: auto; } }
        .rv-viewer-top { flex: none; display: flex; align-items: center; justify-content: space-between; padding: 8px 4px 4px 4px; }
        .rv-viewer-close { min-width: 44px; min-height: 44px; border: none; border-radius: 22px; background: transparent; color: #FFFDF8; font-size: 30px; line-height: 1; cursor: pointer; }
        .rv-viewer-close:focus-visible { outline: 2px solid #E8D5B7; outline-offset: 2px; }
        .rv-viewer-spacer { flex: none; width: 44px; }
        .rv-viewer-top .rv-photo-pop { right: 0; }
        .rv-viewer-count { flex: 1 1 auto; text-align: center; font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); color: #E8D5B7; white-space: nowrap; }
        .rv-viewer-stage { position: relative; flex: 1 1 auto; min-height: 0; display: flex; }
        .rv-viewer-strip { flex: 1 1 auto; min-height: 0; display: flex; overflow-x: auto; overflow-y: hidden; scroll-snap-type: x mandatory; overscroll-behavior: contain; scrollbar-width: none; }
        .rv-viewer-strip::-webkit-scrollbar { display: none; }
        .rv-viewer-slide { flex: 0 0 100%; height: 100%; display: flex; align-items: center; justify-content: center; scroll-snap-align: start; scroll-snap-stop: always; }
        .rv-viewer-img { display: block; max-width: 100%; max-height: 100%; width: 100%; height: 100%; object-fit: contain; user-select: none; }
        .rv-viewer-foot { flex: none; padding: 10px 16px 18px; }
        .rv-viewer-cap { text-align: center; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); color: #E8D5B7; overflow-wrap: anywhere; }
        /* The dots are buttons; on a phone they look and lay out exactly as the old spans (6 px dot, 6 px gap) and
           take no pointer — the swipe is the control. The dot itself is ::before so the target can grow on desktop. */
        .rv-dots { display: flex; justify-content: center; gap: 6px; margin: 8px 0 0; }
        .rv-dot { flex: none; width: 6px; height: 6px; padding: 0; border: 0; background: transparent; display: inline-flex; align-items: center; justify-content: center; pointer-events: none; }
        .rv-dot::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: #D9C9B0; }
        .rv-dot.on::before { background: #8A5F3A; }
        .rv-dots.rv-viewer-dots { margin: 0 0 8px; }
        .rv-viewer-dots .rv-dot::before { background: rgba(244,234,220,.35); }
        .rv-viewer-dots .rv-dot.on::before { background: #F4EADC; }
        /* ‹ › — desktop only. 44 px round targets over the photo's left / right middle. */
        .rv-photo-prev, .rv-photo-next { display: none; }
        @media (hover: hover) and (pointer: fine) {
          .rv-photo-prev, .rv-photo-next { display: flex; position: absolute; top: 50%; transform: translateY(-50%); z-index: 1; width: 44px; height: 44px; padding: 0; border: 0;
            border-radius: 50%; align-items: center; justify-content: center; background: rgba(20,12,6,.55); color: #FFFDF8; cursor: pointer; }
          .rv-photo-prev { left: 10px; }
          .rv-photo-next { right: 10px; }
          .rv-photo-prev:hover, .rv-photo-next:hover { background: rgba(20,12,6,.75); }
          .rv-viewer .rv-photo-prev { left: 16px; }
          .rv-viewer .rv-photo-next { right: 16px; }
          .rv-viewer .rv-photo-prev, .rv-viewer .rv-photo-next { background: rgba(244,234,220,.14); }
          .rv-viewer .rv-photo-prev:hover, .rv-viewer .rv-photo-next:hover { background: rgba(244,234,220,.26); }
          .rv-dots { gap: 0; }
          .rv-dot { width: 24px; height: 44px; pointer-events: auto; cursor: pointer; border-radius: 8px; }
          .rv-dots.rv-viewer-dots { margin: 0 0 2px; }
        }
        .rv-photo-prev:focus-visible, .rv-photo-next:focus-visible { outline: 2px solid #E8D5B7; outline-offset: 2px; }
        .rv-dot:focus-visible { outline: 2px solid #A0724A; outline-offset: -2px; }
        .rv-viewer-dots .rv-dot:focus-visible { outline-color: #E8D5B7; }
        /* The row under the hero keeps only "Add a meal photo" (SPEC_photo_menu P1); a photo becomes a showcase
           from its own ⋯ (S46), never from a second button. */
        .rv-photo-ctl { display: flex; align-items: center; justify-content: center; margin: 4px 16px 0; }
        .rv-photo-add { display: inline-flex; align-items: center; gap: 6px; min-height: 44px; padding: 0 10px; border: none; background: transparent; color: #8A5F3A;
          font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; cursor: pointer; white-space: nowrap; }
        .rv-photo-add:disabled { opacity: .55; cursor: default; }
        /* S28: a showcase slide is marked — a small sand chip, bottom-left, out of the way of ⋯ and the expand mark;
           a sibling of the photo button (never inside it), inert to taps. */
        .rv-showcase-tag { position: absolute; left: 10px; bottom: 10px; z-index: 2; pointer-events: none; padding: 3px 8px; border-radius: 8px;
          background: rgba(44,26,14,0.62); color: #E8D5B7; font-family: 'Lato', sans-serif; font-size: 11px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
        .rv-psheet-sub { color: #8a7a60; }
        /* ⋯ ON THE PHOTO (P2): a 44 px target in the hero's top-right corner; its visible part is the expand mark's
           30 px dark translucent circle, inset the same 10 px from the corner (3 px + the 7 px of target around it). */
        .rv-photo-menu { position: relative; display: inline-flex; }
        .rv-photo-menu.hero { position: absolute; top: 3px; right: 3px; z-index: 2; }
        .rv-photo-more { width: 44px; height: 44px; padding: 0; border: none; border-radius: 50%; background: transparent; color: #FFFDF8; display: flex; align-items: center; justify-content: center; cursor: pointer; }
        .rv-photo-more-dot { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(20,12,6,.55); }
        .rv-viewer .rv-photo-more-dot { background: rgba(244,234,220,.14); }
        .rv-photo-more:disabled { opacity: .55; cursor: default; }
        .rv-photo-more:focus-visible { outline: none; }
        .rv-photo-more:focus-visible .rv-photo-more-dot { box-shadow: 0 0 0 2px #FFFDF8, 0 0 0 4px #A0724A; }
        /* The menu drops from ⋯, right-aligned to it (P3): Replace, Details, Save, a divider, Remove in red. */
        .rv-photo-pop { position: absolute; top: calc(100% + 2px); right: 4px; z-index: 3; display: flex; flex-direction: column; min-width: 200px; max-width: calc(100vw - 40px);
          background: #FFFDF8; border: 1px solid #E5D7C1; border-radius: 12px; box-shadow: 0 6px 18px rgba(44,26,14,.18); padding: 4px; }
        .rv-photo-item { display: block; width: 100%; min-height: 44px; padding: 0 14px; border: none; border-radius: 9px; background: transparent; color: #2C1A0E; text-align: left; white-space: nowrap;
          font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; cursor: pointer; }
        .rv-photo-item:hover { background: #F4EADC; }
        .rv-photo-item.rv-photo-remove { color: #A33A2A; }
        .rv-photo-div { display: block; height: 1px; margin: 4px 8px; background: #E5D7C1; }
        /* P7: the last photo removed — the hero keeps its height as a dashed placeholder until the sheet closes. */
        .rv-photo-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; width: 100%; aspect-ratio: 4 / 3; max-height: 420px;
          padding: 16px; border: 2px dashed #C9A97A; border-radius: 14px; background: #FFFDF8; color: #8A5F3A; cursor: pointer; }
        .rv-photo-placeholder:disabled { opacity: .55; cursor: default; }
        .rv-ph-title { display: inline-flex; align-items: center; gap: 6px; font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; }
        .rv-ph-sub { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; text-align: center; }
        /* The small sheets over the recipe view (P5 Remove confirm, Photo details): fixed inside its scrim. */
        .rv-psheet-scrim { position: fixed; inset: 0; z-index: 5; display: flex; align-items: flex-end; background: rgba(2,15,26,0.45); }
        .rv-psheet { position: relative; width: 100%; background: #FDF8F2; border-radius: 20px 20px 0 0; padding: 0 20px calc(24px + var(--op-inset-bottom)); }
        .rv-psheet-in { padding-top: 14px; }
        .rv-sheet .rv-psheet .sheet-close { left: auto; right: 8px; }   /* the recipe view moves ITS ✕ left; a sheet over it keeps the usual right */
        .rv-psheet-head { display: flex; align-items: center; gap: 12px; padding-right: 36px; }
        .rv-psheet-thumb { flex: none; width: 56px; height: 56px; border-radius: 10px; object-fit: cover; background: #E8D5B7; }
        .rv-psheet-h { margin: 0; padding-right: 36px; font-family: 'Playfair Display', serif; font-size: calc(22.4px * (1 + (var(--op-text-scale) - 1) * 0.5)); font-weight: 700; line-height: 1.2; color: #2C1A0E; }
        .rv-psheet-head .rv-psheet-h { padding-right: 0; }
        .rv-psheet-body { margin: 12px 0 0; font-family: 'Lato', sans-serif; font-size: calc(16px * var(--op-text-scale)); line-height: 1.4; color: #5C4636; }
        .rv-psheet-line { margin-top: 10px; font-family: 'Lato', sans-serif; font-size: calc(16px * var(--op-text-scale)); line-height: 1.4; color: #2C1A0E; overflow-wrap: anywhere; }
        .rv-psheet-btns { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 20px; }
        .rv-photo-confirm-remove, .rv-photo-confirm-keep, .rv-showcase-go, .rv-showcase-cancel, .rv-photo-full-ok { flex: 1 1 auto; min-height: 48px; padding: 10px 18px; border-radius: 24px; white-space: nowrap; cursor: pointer;
          font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; line-height: 1.2; }
        /* Never celebrate deleting: Keep is the gold primary, Remove is quiet — white with red text. */
        .rv-photo-confirm-remove { background: #FFFFFF; border: 1.5px solid #E5D7C1; color: #A33A2A; }
        .rv-photo-confirm-keep { background: #D9BC8C; border: 1.5px solid #D9BC8C; color: #2C1A0E; }
        /* The S13 prompt (S46 flip): Make showcase is the gold primary (the author's own deliberate act); Not now is quiet.
           The "full" sheet's OK is the same gold. */
        .rv-showcase-go, .rv-photo-full-ok { background: #D9BC8C; border: 1.5px solid #D9BC8C; color: #2C1A0E; }
        .rv-showcase-cancel { background: #FFFFFF; border: 1.5px solid #E5D7C1; color: #5C4636; }
        .rv-photo-add:focus-visible, .rv-photo-item:focus-visible, .rv-photo-placeholder:focus-visible, .rv-photo-confirm-remove:focus-visible, .rv-photo-confirm-keep:focus-visible, .rv-showcase-go:focus-visible, .rv-showcase-cancel:focus-visible, .rv-photo-full-ok:focus-visible { outline: 2px solid #A0724A; outline-offset: 2px; }
        .rv-head { padding: 16px 20px 0; }
        .rv-title { margin: 0; font-family: 'Playfair Display', serif; font-weight: 600; font-size: calc(22.4px * (1 + (var(--op-text-scale) - 1) * 0.5)); line-height: 1.15; color: #2C1A0E; overflow-wrap: anywhere; }
        .rv-byline { margin-top: 4px; font-family: 'Playfair Display', serif; font-style: italic; font-weight: 500; font-size: calc(14px * var(--op-text-scale)); line-height: 1.4; color: #8A5F3A; overflow-wrap: anywhere; }
        .rv-added { margin-top: 4px; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8A5F3A; }
        .rv-meta { margin-top: 12px; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); line-height: 1.4; color: #2C1A0E; }
        .rv-proof { margin-top: 8px; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); line-height: 1.45; color: #5C4636; }
        .rv-plan-row { padding: 16px 20px 0; }
        .rv-plan, .rv-see { display: flex; align-items: center; justify-content: center; width: 100%; min-height: 48px; padding: 10px 16px; border-radius: 24px; border: 1.5px solid transparent;
                   font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; line-height: 1.2; text-align: center; cursor: pointer; }
        .rv-plan.add, .rv-plan.wrap { background: #D9BC8C; border-color: #D9BC8C; color: #2C1A0E; }
        .rv-plan.teal { background: #0D9488; border-color: #0D9488; color: #fff; }
        /* A5 (2026-10-18): To buy offers the board card's "See on list" in the board's own outline. Its own class
           since Amendment B (R4): the count stays out of click text. */
        .rv-see { background: transparent; border-color: #C9A97A; color: #6f5a45; }
        .rv-plan:disabled, .rv-remove:disabled { opacity: 0.5; cursor: default; }
        /* Amendment B: the status line is text, never a control; 12.5 is the floor (the seam's role). */
        .rv-status { margin-bottom: 10px; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); letter-spacing: 0.14em; text-transform: uppercase; font-weight: 700; color: #8A5F3A; line-height: 1.4; }
        .rv-status-sep { opacity: 0.5; }
        .rv-status-soft { letter-spacing: 0.04em; text-transform: none; color: #5C4636; white-space: nowrap; }
        .rv-nw { white-space: nowrap; }
        /* The quiet remove link: a real button on a 44 px target, underlined text, never a pill. */
        .rv-remove { display: block; width: 100%; min-height: 44px; margin-top: 4px; padding: 8px 12px; background: none; border: 0; font-family: 'Lato', sans-serif;
                     font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); color: #5C4636; text-decoration: underline; text-underline-offset: 3px; text-decoration-color: #C9A97A; cursor: pointer; }
        .rv-seam { display: flex; align-items: center; gap: 10px; margin: 22px 20px 0; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); letter-spacing: 0.14em; text-transform: uppercase; font-weight: 700; color: #8A5F3A; }
        .rv-seam::before, .rv-seam::after { content: ""; flex: 1; height: 1px; background: #E8D5B7; }
        /* The section headings are the Edit Meal sheet's section labels (.modal-label) in size and shape, so the
           two sheets read as one — on the small-text brown #8A5F3A (the 10-04 floor colour; Dan, 2026-10-18). */
        .rv-sec { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: baseline; gap: 2px 10px; margin: 0; padding: 22px 20px 6px; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); font-weight: 400; letter-spacing: 1.5px; text-transform: uppercase; color: #8A5F3A; }
        /* The trailing span is a count or A2's summary sentence ("8 · 2 on the shopping list"): normal case, right-aligned,
           and it drops under the label as its own line when it cannot fit beside it (320 XXL wrapped it one word per line). */
        .rv-sec span { flex: 1 1 auto; min-width: 0; text-align: right; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); font-weight: 400; letter-spacing: 0; text-transform: none; color: #8A5F3A; }
        .rv-ing { list-style: none; margin: 0; padding: 0 20px; }
        .rv-ing li { display: flex; flex-wrap: wrap; align-items: baseline; align-content: center; gap: 2px 10px; box-sizing: border-box; min-height: 44px; padding: 6px 0; border-bottom: 1px solid #EFE6D9; font-family: 'Lato', sans-serif; font-size: calc(16px * var(--op-text-scale)); line-height: 1.4; color: #2C1A0E; }
        .rv-ing li:last-child { border-bottom: 0; }
        /* The name is laid out at its full (max-content) width: a short name keeps its tag beside it;
           a name that would not fit whole beside the tag takes the row and the tag drops under it,
           right-aligned. Not min-content: at 320 XXL that squeezed a long name to one word per line
           beside the tag (harness 2026-10-18). */
        .rv-ing-name { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
        .rv-tag { flex: none; margin-left: auto; white-space: nowrap; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); color: #8A5F3A; }
        /* A2: "Already have it", "{Name} bought it" / "Bought" and "In {Name}'s cart" / "In cart" in text teal
           (the household has it); "On the shopping list" brown. */
        .rv-tag.have, .rv-tag.bought, .rv-tag.cart { color: #0A776D; }
        .rv-preamble { margin: 0 20px 14px; padding: 0 0 12px; border-bottom: 1px solid #EFE6D9; }
        .rv-preamble p { margin: 0; font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); line-height: 1.5; color: #2C1A0E; white-space: pre-wrap; overflow-wrap: anywhere; }
        .rv-steps { list-style: none; margin: 0; padding: 0 20px; }
        .rv-steps li { display: flex; gap: 12px; align-items: flex-start; padding: 6px 0; }
        .rv-steps p { margin: 0; min-width: 0; font-family: 'Lato', sans-serif; font-size: calc(16px * var(--op-text-scale)); line-height: 1.5; color: #2C1A0E; overflow-wrap: anywhere; }
        /* The numeral's circle is sized to the step text (28 px beside a 24 px line at Default, k 1 so it keeps that ratio at every step). */
        .rv-step-n { flex: none; width: calc(28px * var(--op-text-scale)); height: calc(28px * var(--op-text-scale)); border-radius: 50%; background: #F1E7D8; color: #2C1A0E;
                     font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); font-weight: 700; display: flex; align-items: center; justify-content: center; margin-top: calc(-2px * var(--op-text-scale)); }
        .rv-cooks { list-style: none; margin: 0; padding: 0 20px; }
        .rv-cooks li { display: flex; align-items: center; box-sizing: border-box; min-height: 44px; padding: 6px 0; border-bottom: 1px solid #EFE6D9; font-family: 'Lato', sans-serif; font-size: calc(16px * var(--op-text-scale)); line-height: 1.4; color: #2C1A0E; }
        .rv-cooks li:last-child { border-bottom: 0; }
        /* S5: a cook row is the date, then one line per rater (name in a 2+ member household, stars, the note
           beneath), then the viewer's own CookRating ("How was it for you?" until they have rated). */
        .rv-cooks li.rv-cook { flex-direction: column; align-items: stretch; gap: 2px; padding: 8px 0; }
        .rv-cooks li.rv-cook > * { min-width: 0; }   /* the note input never sets the row's width (XXL on 320 ran 327 px before) */
        .rv-cook-date { min-height: 28px; display: flex; align-items: center; }
        .rv-rater-line { display: flex; align-items: center; flex-wrap: wrap; gap: 0 8px; min-height: 28px; }
        .rv-rater-name { font-size: calc(14px * var(--op-text-scale)); color: #5C4636; }
        .rv-stars { display: inline-flex; align-items: center; gap: 2px; color: #A0724A; }
        .rv-stars svg { width: 18px; height: 18px; }
        .rv-rater-note { font-size: calc(14px * var(--op-text-scale)); color: #6E5A4A; overflow-wrap: anywhere; padding: 0 0 2px; }
        .rv-showall { display: block; margin: 4px 20px 0; padding: 10px 0; border: none; background: transparent; color: #8A5F3A; cursor: pointer;
                      font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; }
        .rv-empty { margin: 14px 20px 0; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; }
        .rv-pad { height: 24px; }
        /* SUBGRID: the library grid's rows are the cards' rows. Each card spans 3 rows (slot / title / meta) and subgrids
           them; the open button spans the first 2 (slot / title) and subgrids them again, so the button keeps its
           semantics and still covers slot + name. The shared slot row sizes to the tallest slot in the row (a 132px photo
           stretches the neighbour's tint; 56px otherwise), the title row to the tallest clamped title, the meta row to the
           tallest strip — every edge aligns across the row. The card's margin is the row gap (the grid's own row-gap would
           also land between a card's rows). Chrome 117+, Safari 16+ (iOS 16+), Firefox 71+; older engines keep the
           fallback above. */
        @supports (grid-template-rows: subgrid) {
          .lib-grid { row-gap: 0; align-items: stretch; }
          .lib-gcard { display: grid; grid-template-rows: subgrid; grid-row: span 3; margin-bottom: 16px; }
          .lib-gbody { display: grid; grid-template-rows: subgrid; grid-row: span 2; }
          .lib-slot { height: auto; min-height: 56px; }
          .lib-slot.photo { height: 132px; }
          .lib-gstrip { align-self: end; }
        }
        /* The name in the body (D2): clamped to 3 lines with an ellipsis so a long imported title never sets the card height. */
        /* Card title role (D10): 17px at Default, k 0.5 — was 20. One serif moment per screen is the page title.
           Weight 600 (polish 2026-10-16; the 600 face is loaded in public/index.html — without it the browser would pick 700). */
        .lib-gname { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; padding: 10px 12px 0;
                     font-family: 'Playfair Display', serif; font-size: calc(17px * (1 + (var(--op-text-scale) - 1) * 0.5)); font-weight: 600; line-height: 1.2; overflow-wrap: anywhere; }
        /* The strip never pushes the "+" below the meta: the meta WRAPS INTERNALLY beside the button when the two no longer
           fit (Text Size decision 9 — wrap, never shrink or clip — applied to the text, so the count is never lost to an
           ellipsis and the action stays where the thumb expects it at every step). */
        .lib-gstrip { display: flex; align-items: center; justify-content: space-between; gap: 6px; padding: 6px 8px 8px 12px; }
        /* The meta line (D6): "{Occasion} · {n} items" at the Meta role — 14px (Text Size D4's floor; the spec's 13 was
           resolved to the role table, Dan 2026-10-16), muted #8A5F3A (Text Size D5, ≥ 4.5:1). The same expression ts(14) emits. */
        .lib-gmeta { flex: 1 1 0; min-width: 0; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); line-height: 1.3; color: #8A5F3A; overflow-wrap: anywhere; }
        /* The round "+" (D7): drawn at 40px, a 44×44 touch target through the pseudo-element; soft fill, dark glyph;
           planned = sand fill with a check (tapping un-plans). */
        .lib-plan { position: relative; flex: none; margin-left: auto; width: 40px; height: 40px; border-radius: 50%; border: none; background: #F1E6D6; color: #2C1A0E; cursor: pointer;
                    display: inline-flex; align-items: center; justify-content: center; padding: 0; }
        .lib-plan::before { content: ""; position: absolute; inset: -2px; border-radius: 50%; }
        .lib-plan svg { width: 18px; height: 18px; }
        .lib-plan-wait { font-family: 'Lato', sans-serif; font-size: 16px; font-weight: 700; line-height: 1; }
        .lib-plan:disabled { cursor: default; opacity: 0.6; }
        .lib-plan.planned { background: #EFE6D6; color: #3A2A20; opacity: 1; cursor: pointer; }
        .lib-plan.planned svg { stroke-width: 2.6; }
        .lib-plan.planned:disabled { cursor: default; opacity: 0.6; }
        /* Stage label (un-plan rows 4–5): text, not a control — no border, no fill, no hover, no focus ring; the button's height so the strip doesn't jump. */
        .lib-stage { flex: none; margin-left: auto; height: 44px; padding: 0 4px; display: inline-flex; align-items: center; gap: 4px; white-space: nowrap;
                     font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); font-weight: 600; color: #6f5a45; }
        @media (max-width: 399px) {
          .lib-gstrip { padding: 6px 6px 8px 10px; }
        }
        .lib-create-tile { margin-top: 16px; border: 1.5px dashed #C9A97A; border-radius: 14px; padding: 16px; display: flex; align-items: center; gap: 14px; }
        .lib-create-tile.gated { border-color: #C9A97A; opacity: 0.8; }
        .lib-create-text { flex: 1; min-width: 0; }
        .lib-create-t { font-family: 'Playfair Display', serif; font-weight: 700; font-size: calc(1rem * (1 + (var(--op-text-scale) - 1) * 0.5)); color: #2C1A0E; }
        .lib-create-s { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; margin-top: 2px; }
        /* Filter sheet (mockup screen 3) in the app's modal chrome. */
        .lib-sheet-lbl { font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); font-weight: 700; letter-spacing: 1.8px; text-transform: uppercase; color: #8A5F3A; margin: 20px 0 10px; }
        .lib-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .lib-chef { height: 40px; padding: 0 14px 0 6px; border-radius: 20px; border: 1.5px solid #E8D5B7; background: #FFFDF9; color: #2C1A0E; cursor: pointer;
                    font-family: 'Lato', sans-serif; font-size: calc(0.88rem * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; display: inline-flex; align-items: center; gap: 8px; }
        .lib-chef.on { background: #6f5a45; border-color: #6f5a45; color: #FAF4EC; }
        .lib-mini { flex: none; width: 28px; height: 28px; border-radius: 50%; background: #E8D5B7; color: #8a7a60; display: inline-flex; align-items: center; justify-content: center;
                    font-family: 'Lato', sans-serif; font-size: 0.72rem; font-weight: 700; }
        .lib-sheet-note { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; margin-top: 8px; }
        .lib-sw { width: 100%; display: flex; align-items: center; gap: 12px; padding: 12px 0; border: none; border-bottom: 1px solid #EFE6D6; background: transparent; text-align: left; cursor: pointer; }
        .lib-sw > span:first-child { flex: 1; min-width: 0; display: flex; flex-direction: column; }
        .lib-sw-l { font-family: 'Lato', sans-serif; font-size: calc(0.95rem * var(--op-text-scale)); font-weight: 700; color: #2C1A0E; }
        .lib-sw-d { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; margin-top: 2px; }
        .lib-track { flex: none; width: 44px; height: 26px; border-radius: 13px; background: #D9C9AE; position: relative; transition: background 0.15s; }
        .lib-track::after { content: ""; position: absolute; top: 3px; left: 3px; width: 20px; height: 20px; border-radius: 50%; background: #FFFDF9; transition: left 0.15s; }
        .lib-sw[aria-checked="true"] .lib-track { background: #6f5a45; }
        .lib-sw[aria-checked="true"] .lib-track::after { left: 21px; }
        .lib-sheet-btns { display: flex; gap: 10px; margin-top: 24px; }
        .lib-clear { flex: none; height: 48px; padding: 0 20px; border-radius: 24px; border: 1.5px solid #E8D5B7; background: transparent; color: #6f5a45; cursor: pointer;
                     font-family: 'Lato', sans-serif; font-size: calc(0.95rem * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; }
        .lib-show { flex: 1; height: 48px; border-radius: 24px; border: none; background: #6f5a45; color: #FAF4EC; cursor: pointer;
                    font-family: 'Lato', sans-serif; font-size: calc(0.95rem * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; }
        .lib-empty { padding: 24px 12px; text-align: center; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; font-style: italic; }
        /* Good for (061) — the meal sheet's occasion chips. Espresso on-state, zero teal; ON CARD marks the first pick. */
        .gf-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .gf-chip { height: 38px; padding: 0 14px; border-radius: 19px; border: 1.5px solid #E8D5B7; background: #FFFDF9; color: #6f5a45; cursor: pointer;
                   font-family: 'Lato', sans-serif; font-size: calc(0.88rem * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; display: inline-flex; align-items: center; gap: 7px; }
        .gf-chip.on { background: #6f5a45; border-color: #6f5a45; color: #FAF4EC; }
        .gf-oncard { font-size: 0.56rem; font-weight: 900; letter-spacing: 1.2px; text-transform: uppercase; color: #D9BC8C; }
        .gf-help { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; margin-top: 8px; }
        /* No-shop sheet */
        .noshop-sub { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #6f5a45; margin-bottom: 14px; }
        .noshop-label { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); font-weight: 700; color: #2C1A0E; margin-bottom: 8px; }
        .noshop-label span { font-weight: 400; color: #8a7a60; }
        .noshop-checks { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; max-height: 40vh; overflow-y: auto; }
        .noshop-check { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border: 1.5px solid #E8D5B7; border-radius: 10px; cursor: pointer;
                        font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); color: #2C1A0E; }
        .noshop-check.on { border-color: #6f5a45; background: #FAF4EC; }
        .noshop-check input { position: absolute; opacity: 0; width: 0; height: 0; }
        .noshop-check-box { flex: none; width: 18px; height: 18px; border-radius: 4px; border: 1.5px solid #C9A97A; display: flex; align-items: center; justify-content: center;
                            font-size: 0.7rem; font-weight: 900; color: #FAF4EC; background: transparent; }
        .noshop-check.on .noshop-check-box { background: #6f5a45; border-color: #6f5a45; }
        .noshop-check-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .noshop-check-meta { flex: none; font-size: 12.5px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; color: #8a7a60; }
        .noshop-none { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7a60; font-style: italic; margin-bottom: 14px; }
        .noshop-input { width: 100%; box-sizing: border-box; padding: 12px 13px; border-radius: 10px; border: 1.5px solid #E8D5B7; background: #FFFDF9;
                        font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); color: #2C1A0E; outline: none; margin-bottom: 14px; }
        .noshop-commit { width: 100%; border: none; background: #6f5a45; color: #FAF4EC; border-radius: 10px; padding: 12px; cursor: pointer;
                         font-family: 'Lato', sans-serif; font-size: calc(0.88rem * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 900; }
        .noshop-commit:disabled { opacity: 0.6; cursor: default; }
        /* Compact (D9′): icons only, pulled in from the sides; labels stay in the DOM at font-size 0. */
        .helm.compact .helm-door { gap: 0; }
        .helm.compact .helm-label { font-size: 0; opacity: 0; }
        .helm.compact .helm-badge { top: 0; }
        .helm-divider { flex: 0 0 1px; align-self: center; height: 18px; background: rgba(201,169,122,0.22); margin-left: 4px; }
        .helm-plus { flex: 0 0 auto; align-self: center; width: 32px; height: 32px; margin: 0 2px 0 6px; border-radius: 50%; border: none; background: #FAF4EC; color: #2C1A0E; cursor: pointer;
                     display: flex; align-items: center; justify-content: center; font-family: 'Lato', sans-serif; font-size: 1.35rem; font-weight: 300; line-height: 1; padding: 0 0 2px;
                     box-shadow: 0 2px 8px rgba(0,0,0,.25); animation: helmPlusIn .2s ease; }
        @keyframes helmPlusIn { from { opacity: 0; } to { opacity: 1; } }
        .helm.no-anim, .helm.no-anim * { transition: none !important; animation: none !important; }
        @media (prefers-reduced-motion: reduce) { .helm, .helm * { transition: none !important; } }
        /* HOME v1 — greeting, date, the Tonight card. */
        .home { padding: 12px 2px 0; }
        /* Home on the knob (pass 2): greeting = page-title rate k .4 at its 22.4; date = meta 14;
           deck eyebrow 12.5 #8A5F3A, title = card-title rate k .5 at its 24, status = meta at its 14.7,
           buttons = button 14 k .8 in a wrapping row; list line + link = body 15; budget caption = meta. */
        .home-greeting { font-family: 'Playfair Display', serif; font-size: calc(22.4px * (1 + (var(--op-text-scale) - 1) * 0.4)); line-height: 1.2; color: #2C1A0E; }
        .home-date { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8A5F3A; margin-top: 3px; }
        /* D4 — the signed-out welcome variant: the splash wordmark's face (Playfair italic, Our light / Provisions bold) in espresso on the page cream;
           one line in the Tonight card's body tone; the header's two buttons in the sheet's espresso and sand. */
        .home-welcome { padding-top: 64px; text-align: center; }
        .home-wm { font-family: 'Playfair Display', serif; font-style: italic; font-size: 2.1rem; line-height: 1; letter-spacing: 0.02em; color: #2C1A0E; white-space: nowrap; }
        .home-wm .o { font-weight: 400; } .home-wm .p { font-weight: 700; }
        .home-welcome-line { margin: 14px 0 0; font-family: 'Lato', sans-serif; font-size: 0.95rem; line-height: 1.45; color: #6E5A4A; }
        .home-welcome-actions { display: flex; gap: 10px; justify-content: center; margin-top: 28px; }
        .home-welcome-btn { font-family: 'Lato', sans-serif; font-size: 0.78rem; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; padding: 11px 22px; border-radius: 6px; cursor: pointer; }
        .home-welcome-btn.ghost { background: transparent; border: 1.5px solid #A0724A; color: #8A5F3A; }
        .home-welcome-btn.solid { background: #2C1A0E; border: 1.5px solid #2C1A0E; color: #FAF4EC; }
        .home-welcome-btn:disabled { opacity: 0.5; cursor: default; }
        /* THE PUBLIC GIFT CARD (/g/{CODE}, signed out — SPEC_meal_sharing_phase_a_client, mockup_gift_preview A1–A3).
           Paper page, one white card: a 4:3-ish photo (or the short sand band with the plate mark, C8), the title in
           Playfair, From in ink-soft, the count as a cream chip; the teal CTA; the two quiet lines; the footer sentence. */
        .gift-page { padding: 20px 18px 40px; max-width: 420px; margin: 0 auto; }
        .gift-eyebrow { margin: 4px 2px 12px; font-family: 'Lato', sans-serif; font-size: 10.5px; font-weight: 900; letter-spacing: .18em; text-transform: uppercase; color: #A0724A; }
        .gift-card { background: #fff; border: 1px solid #E3D4BC; border-radius: 16px; overflow: hidden; box-shadow: 0 6px 18px -10px rgba(44,26,14,.25); }
        .gift-card-loading { min-height: 150px; background: #FBF7F0; }
        .gift-photo { aspect-ratio: 4 / 3; max-height: 260px; background: #E8D5B7; }
        .gift-photo-img { display: block; width: 100%; height: 100%; object-fit: cover; }
        .gift-noph { height: 84px; background: #E8D5B7; display: flex; align-items: center; justify-content: center; }
        .gift-plate { display: flex; align-items: center; justify-content: center; width: 46px; height: 46px; border-radius: 50%; border: 2px solid #A0724A; }
        .gift-plate::after { content: ""; width: 26px; height: 26px; border-radius: 50%; border: 1.5px solid rgba(160,114,74,.55); }
        .gift-plate-big { width: 64px; height: 64px; margin: 0 auto 22px; opacity: .6; }
        .gift-plate-big::after { width: 36px; height: 36px; }
        .gift-body { padding: 14px 16px 16px; }
        .gift-title { margin: 0; font-family: 'Playfair Display', serif; font-weight: 700; font-size: calc(21px * (1 + (var(--op-text-scale) - 1) * 0.5)); line-height: 1.2; color: #2C1A0E; overflow-wrap: anywhere; }
        .gift-from { margin-top: 6px; font-family: 'Lato', sans-serif; font-size: calc(13px * var(--op-text-scale)); color: #6f5a45; overflow-wrap: anywhere; }
        .gift-meta { display: inline-block; margin-top: 10px; font-family: 'Lato', sans-serif; font-size: 11px; font-weight: 700; color: #A0724A; background: #FAF4EC; border: 1px solid #E3D4BC; padding: 4px 9px; border-radius: 12px; }
        .gift-add { display: block; width: 100%; margin-top: 18px; min-height: 48px; background: #0D9488; color: #fff; font-family: 'Lato', sans-serif; font-weight: 900; font-size: calc(15px * (1 + (var(--op-text-scale) - 1) * 0.8)); letter-spacing: .02em; border: 0; border-radius: 12px; padding: 14px; text-align: center; cursor: pointer; }
        .gift-add:disabled { background: #9cc9c4; cursor: default; }
        .gift-after, .gift-signin-line { margin: 9px 0 0; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); color: #6f5a45; text-align: center; line-height: 1.5; }
        .gift-signin-line { margin-top: 14px; }
        .gift-signin { background: none; border: 0; padding: 0 2px; min-height: 24px; font: inherit; font-weight: 700; color: #0D9488; cursor: pointer; }
        .gift-signin:disabled { opacity: .5; cursor: default; }
        .gift-foot { margin: 36px 0 0; padding-top: 14px; border-top: 1px solid #E3D4BC; font-family: 'Lato', sans-serif; font-size: 11.5px; color: #8a7a60; text-align: center; line-height: 1.5; }
        .gift-foot b { color: #2C1A0E; font-family: 'Playfair Display', serif; font-style: italic; font-weight: 600; }
        .gift-dead { padding: 50px 8px 0; text-align: center; }
        .gift-dead-h { margin: 0; font-family: 'Playfair Display', serif; font-size: calc(22px * (1 + (var(--op-text-scale) - 1) * 0.5)); color: #2C1A0E; }
        .gift-dead-p { margin: 8px 0 0; font-family: 'Lato', sans-serif; font-size: calc(13.5px * var(--op-text-scale)); color: #6f5a45; }
        .gift-what, .gift-retry { display: inline-block; margin-top: 24px; font-family: 'Lato', sans-serif; font-size: 13px; font-weight: 700; color: #0D9488; background: #fff; border: 1.5px solid #0D9488; border-radius: 12px; padding: 10px 18px; text-decoration: none; cursor: pointer; }
        .gift-add:focus-visible, .gift-signin:focus-visible, .gift-what:focus-visible, .gift-retry:focus-visible { outline: 2px solid #A0724A; outline-offset: 2px; }
        /* THE ON-DECK CARD in the board's language: the welcome's warm cream, no shadow, no photo (the household photo asserts
           context, never decorates). Eyebrow = the board chip's type; title = Playfair; status = the board line's tone. One primary
           per state as a pill (the board's buttons at Home's scale); Switch is a text button; × is the board's ×. */
        .deck { margin-top: 22px; background: #F1E7D8; border-radius: 16px; padding: 18px 20px 16px; }
        .deck.busy { opacity: 0.5; }
        .deck.noshop { background: transparent; outline: 1.5px dashed #C9A97A; outline-offset: -1.5px; }
        .deck-eyebrow { font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); font-weight: 900; letter-spacing: 1.5px; text-transform: uppercase; color: #8A5F3A; }
        .deck-body { margin: 6px -6px 0; padding: 0 6px; border-radius: 10px; }
        .deck-body.tap { cursor: pointer; -webkit-tap-highlight-color: transparent; }
        .deck-body.tap:focus-visible { outline: 2px solid #c8973a; outline-offset: 2px; }
        .deck-title { margin: 0; font-family: 'Playfair Display', serif; font-size: calc(24px * (1 + (var(--op-text-scale) - 1) * 0.5)); font-weight: 700; line-height: 1.15; color: #2C1A0E; overflow-wrap: anywhere; }
        .deck-status { margin: 6px 0 0; font-family: 'Lato', sans-serif; font-size: calc(14.7px * var(--op-text-scale)); line-height: 1.45; color: #6E5A4A; }
        .deck.cooked .deck-title { opacity: 0.55; }
        .deck-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 18px; margin-top: 16px; }
        /* Primary: espresso outline (Add to Shop); teal fill ONLY for Cooked it — the household finished something; deep sand (--op-add) for
           Add a meal, the same as both of Plan's add buttons. */
        .deck-primary { flex: none; min-height: 44px; padding: 0 22px; border-radius: 22px; cursor: pointer; white-space: nowrap;
                        border: 1.5px solid #6f5a45; background: transparent; color: #6f5a45;
                        font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 900; letter-spacing: 0.5px; }
        .deck-primary.teal { border-color: #0D9488; background: #0D9488; color: #fff; }
        .deck-primary.add { border-color: var(--op-add); background: var(--op-add); color: var(--op-add-ink); }
        .deck-primary.done, .deck-primary.done:disabled { border-color: #0D9488; background: transparent; color: #0A776D; opacity: 1; cursor: default; }
        .deck-primary:disabled { opacity: 0.5; cursor: default; }
        .deck-switch { flex: none; min-height: 44px; padding: 0 4px; border: none; background: none; cursor: pointer; color: #6f5a45;
                       font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; }
        .deck-switch:disabled { opacity: 0.5; cursor: default; }
        .deck-x { flex: none; width: 36px; height: 36px; border-radius: 50%; border: none; background: transparent; color: #b5a58f; cursor: pointer;
                  font-family: 'Lato', sans-serif; font-size: 1.4rem; font-weight: 300; line-height: 1; padding: 0 0 3px; }
        .deck-x:hover { color: #6f5a45; }
        .deck-x:disabled { cursor: default; opacity: 0.5; }
        .deck .hold-night { margin-top: 18px; }
        /* THE LIST LINE and THE BUDGET LINE — one ruled block under the card. The line reads as one sentence; only the link is a tap
           target (a leaf button with fixed copy — RUM). The budget bar is the budget banner's bar at 6px, on the page. */
        .home-line { display: flex; flex-wrap: wrap; align-items: baseline; column-gap: 6px; margin-top: 16px; padding: 8px 0 4px; border-top: 1px solid #EADCC6;
                     font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); line-height: 1.45; }
        .home-line-text { color: #2C1A0E; overflow-wrap: anywhere; }
        .home-line-tail { white-space: nowrap; color: #2C1A0E; }
        .home-line-link { padding: 10px 0; margin: -10px 0; border: none; background: none; cursor: pointer; white-space: nowrap;
                          font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); font-weight: 700; color: #6f5a45; text-decoration: underline; text-underline-offset: 3px; }
        .home-budget { margin-top: 10px; padding-top: 14px; border-top: 1px solid #EADCC6; }
        .home-budget-bar { height: 6px; background: #F1E6D6; border-radius: 3px; overflow: hidden; }
        .home-budget-fill { height: 100%; border-radius: 3px; transition: width 0.4s ease, background 0.3s; }
        .home-budget-label { margin-top: 8px; font-family: 'Lato', sans-serif; font-size: calc(14.4px * var(--op-text-scale)); color: #6E5A4A; }
        .home-budget-label.over { color: #e05c5c; }
        /* §6 — every scrolling root clears the pill. The pill is present at EVERY width (rail retired 2026-09-20);
           on phones the document is the scroll root, on desktop the phone column's inner scroller is. */
        .app-root { min-height: 100vh; padding-bottom: calc(96px + var(--op-inset-bottom)); }
        .helm-fade { position: fixed; left: 0; right: 0; bottom: 0; height: calc(90px + var(--op-inset-bottom)); pointer-events: none; z-index: 899;
                     background: linear-gradient(to bottom, rgba(250,244,236,0), rgba(250,244,236,0.85) 55%, #FAF4EC); }
        /* Bottom status stack rides above the pill — at every width now. */
        .bottom-stack { bottom: calc(92px + var(--op-inset-bottom)); }
        /* With the Wrap Up bar on the page the stack rides above IT (the error toast overlapped the bar on
           Shop — the 2026-10-28 LATER row, folded in 2026-10-30). The modal rule below still wins while a
           sheet is open (same specificity, later). */
        body:has(.wrapbar) .bottom-stack { bottom: calc(150px + var(--op-inset-bottom)); }
        /* While ANY modal sheet is open the stack anchors at the TOP of the viewport
           instead — just under the safe area, still centred, same look — so the
           pill and the toasts never sit over a sheet's pinned footer (Cancel / Save
           Meal; found offline on the phone 2026-10-11). Keyed off the document, not
           a flag: eleven surfaces own a .modal-overlay (ten in ProvisionsApp plus
           LibraryFilterSheet's own state) and the next one would miss a flag.
           Position stays with the stack; the pill carries none (ConnectivityPill.js).
           Engines without :has() (Safari < 15.4) keep today's bottom anchor. */
        body:has(.modal-overlay) .bottom-stack { bottom: auto; top: 10px; }   /* not + --op-inset-top: the viewport already starts below the bar (2026-10-17) */
        /* …and the Helm with its fade leave entirely. A sheet is modal, so the nav
           cannot be used anyway, and at z 900 / 899 over the overlay's 100 the pill
           and the fade drew across the sheet's pinned Cancel / Save Meal, which were
           partly covered and hard to hit (iPhone, the 2026-10-11 walk). Hidden, not
           lowered: a lowered Helm would still read as reachable. SAME SIGNAL as the
           stack rule above, so the two cannot disagree. Restored on close. */
        body:has(.modal-overlay, .rv-scrim, .rv-psheet-scrim, .lib-type-scrim, .add-sheet-scrim, .board-menu-backdrop) .helm,
        body:has(.modal-overlay, .rv-scrim, .rv-psheet-scrim, .lib-type-scrim, .add-sheet-scrim, .board-menu-backdrop) .helm-fade { display: none; }
        /* …under EVERY bottom sheet, not only the modal overlay (Dan 2026-10-31): the thank-you sheet's Send / Skip and
           the arrival sheet's Open it sat under the pill. The same six surfaces as the Wrap Up bar's rule below. */
        .container { max-width: 680px; margin: 0 auto; padding: 24px 16px; }

        /* Budget banner */
        .budget-banner { background: #2C1A0E; color: #FAF4EC; padding: 14px 20px; display: flex; justify-content: space-between; align-items: center; max-width: 100%; border-bottom: 2px solid #c8973a; gap: 12px; }
        .budget-banner.over { border-bottom-color: #e05c5c; }
        .budget-label { font-family: 'Lato', sans-serif; font-size: 0.75rem; letter-spacing: 2px; text-transform: uppercase; color: #c8b89a; }
        .budget-amount { font-family: 'Playfair Display', serif; font-size: 1.6rem; font-weight: 700; color: #c8973a; }
        .budget-amount.over { color: #e05c5c; }
        .budget-items { font-family: 'Lato', sans-serif; font-size: 0.8rem; color: #c8b89a; margin-top: 2px; }
        .budget-goal-section { text-align: right; }
        .budget-goal-label { font-family: 'Lato', sans-serif; font-size: 0.7rem; letter-spacing: 1.5px; text-transform: uppercase; color: #c8b89a; }
        .budget-goal-remaining { font-family: 'Playfair Display', serif; font-size: 1.1rem; font-weight: 700; margin-top: 2px; }
        .budget-goal-remaining.ok { color: #8A5F3A; }
        .budget-goal-remaining.over { color: #e05c5c; }
        .budget-bar-wrap { width: 100%; height: 4px; background: rgba(255,255,255,0.15); border-radius: 2px; margin-top: 6px; overflow: hidden; }
        .budget-bar-fill { height: 100%; border-radius: 2px; transition: width 0.4s ease, background 0.3s; }
        .set-budget-btn { font-family: 'Lato', sans-serif; font-size: 0.7rem; letter-spacing: 1px; text-transform: uppercase; padding: 5px 10px; border: 1px solid #c8b89a; background: transparent; color: #c8b89a; cursor: pointer; border-radius: 4px; transition: all 0.2s; white-space: nowrap; }
        .set-budget-btn:hover { border-color: #c8973a; color: #c8973a; }

        .toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
        .hint { font-family: 'Lato', sans-serif; font-size: 0.8rem; color: #8a7a60; letter-spacing: 0.5px; }
        .add-item-btn { font-family: 'Lato', sans-serif; font-size: 0.78rem; letter-spacing: 1px; text-transform: uppercase; padding: 8px 14px; background: #A0724A; color: #FAF4EC; border: none; border-radius: 5px; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 6px; }
        .add-item-btn:hover { background: #6B4423; }
        .category-block { margin-bottom: 28px; }
        .cat-title { font-family: 'Lato', sans-serif; font-size: 12.5px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: #8A5F3A; border-bottom: 2px solid #E8D5B7; padding-bottom: 8px; margin-bottom: 12px; }
        .items-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        /* One column on phones, and one column inside the desktop phone column (it is 430px wide, whatever the window is). */
        @media (max-width: 520px), (min-width: ${COLUMN_MIN_WIDTH}px) { .items-grid { grid-template-columns: 1fr; } }
        .item-row { display: flex; flex-direction: column; background: #F5EDE0; border: 1.5px solid #E8D5B7; border-radius: 8px; padding: 10px 12px; transition: border-color 0.2s, box-shadow 0.2s; gap: 8px; user-select: none; -webkit-tap-highlight-color: transparent; }
        @media (hover: hover) { .item-row:hover { border-color: #c8973a; box-shadow: 0 2px 8px rgba(200,151,58,0.15); } }
        .item-row.has-qty { border-color: #c8973a; background: #FAF4EC; }
        .item-top { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 6px; }
        .item-name { font-family: 'Lato', sans-serif; font-size: calc(0.88rem * var(--op-text-scale)); color: #2C1A0E; flex: 1; }
        .op-mic-btn { flex: none; align-self: flex-start; width: 44px; height: 44px; border-radius: 10px; background: #2C1A0E; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background .15s; }
        .op-mic-btn svg { width: 19px; height: 19px; stroke: #FFFDF9; fill: none; }
        .op-mic-btn:disabled { opacity: .45; cursor: default; }
        .op-mic-btn.listening { background: #c0392b; animation: opMicPulse 1.2s ease-in-out infinite; }
        @keyframes opMicPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(192,57,43,0.55); } 50% { box-shadow: 0 0 0 8px rgba(192,57,43,0); } }
        @media (prefers-reduced-motion: reduce) { .op-mic-btn.listening { animation: none; } }
        /* Galley recipe card — Phase A. Tokens from handoff/mockup_galley_recipe_card.html. */
        .op-skel { height: 10px; border-radius: 5px; background: linear-gradient(90deg, rgba(160,114,74,.10), rgba(160,114,74,.22), rgba(160,114,74,.10)); background-size: 200% 100%; animation: opShimmer 1.4s linear infinite; margin-bottom: 8px; }
        .op-skel:last-child { margin-bottom: 0; }
        @keyframes opShimmer { to { background-position: -200% 0; } }
        .op-skel-step { display: flex; gap: 10px; align-items: flex-start; margin-bottom: 12px; }
        .op-skel-row { align-items: center; min-height: 52px; padding: 8px 10px; border: 1.5px solid rgba(44,26,14,.08); border-radius: 8px; margin-bottom: 6px; }
        .op-skel-badge { width: 22px; height: 22px; border-radius: 50%; background: rgba(160,114,74,.15); flex: none; }
        .op-skel-badge.square { border-radius: 6px; }
        .op-skel-lines { flex: 1; padding-top: 4px; }
        .op-skel-row .op-skel-lines { padding-top: 0; }
        /* Ember: the wrapper is sized inline by ts(14) and never transforms; ::before is
           the core in #8A5F3A (opacity .6↔1, scale .8↔1.1), ::after the 2px ring in brand
           teal #0D9488 (0.6× → 2.4×, fading out). Both ride the same 1.6 s clock. The ring overshoots the box by ~10px
           on purpose — it lives inside the panel's 16px padding. */
        .op-ember { position: relative; flex: none; }
        .op-ember::before, .op-ember::after { content: ""; position: absolute; inset: 0; border-radius: 50%; }
        .op-ember::before { background: #8A5F3A; animation: opEmber 1.6s ease-in-out infinite; }
        .op-ember::after { border: 2px solid #0D9488; animation: opEmberRing 1.6s ease-out infinite; }
        @keyframes opEmber { 0%, 100% { opacity: .6; transform: scale(.8); } 50% { opacity: 1; transform: scale(1.1); } }
        @keyframes opEmberRing { 0% { opacity: .7; transform: scale(.6); } 100% { opacity: 0; transform: scale(2.4); } }
        @media (prefers-reduced-motion: reduce) {
          .op-skel, .op-ember::before { animation: none; }
          .op-ember::before { opacity: 1; transform: none; }
          .op-ember::after { display: none; }
        }
        .op-import-banner { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 6px 12px; background: rgba(160,114,74,.06); border: 1.5px solid rgba(160,114,74,.22); border-radius: 10px; padding: 9px 12px; margin: -6px 0 16px; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); line-height: 1.45; color: #6f5a45; }
        .op-import-banner b { color: #2C1A0E; font-weight: 700; }
        .op-import-banner > span { flex: 1 1 180px; min-width: 0; }
        /* A received meal (Commit 3): the banner shares the import banner's tint; the giver's note is a quote. */
        .op-gift-photo { margin: -6px 0 12px; border-radius: 12px; overflow: hidden; background: #E8D5B7; }
        .op-gift-photo-img { display: block; width: 100%; aspect-ratio: 4 / 3; max-height: 220px; object-fit: cover; }
        .op-gift-photo-cap { padding: 6px 10px; background: #FBF7F0; border: 1px solid #E3D4BC; border-top: 0; border-radius: 0 0 12px 12px; font-family: 'Lato', sans-serif; font-size: calc(12px * var(--op-text-scale)); color: #8a7a60; }
        .op-gift-banner { background: rgba(160,114,74,.06); border: 1.5px solid rgba(160,114,74,.22); border-radius: 10px; padding: 9px 12px; margin: -6px 0 12px; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); line-height: 1.45; color: #6f5a45; }
        .op-gift-banner b { color: #2C1A0E; font-weight: 700; }
        .op-gift-place { display: block; margin-top: 2px; color: #8a7a60; }   /* its own line, no separator (Dan 2026-11-02) */
        .op-gift-note { margin: 0 0 16px; padding: 10px 14px; border-left: 3px solid #D9BC8C; font-family: 'Playfair Display', serif; font-style: italic; font-size: calc(15px * var(--op-text-scale)); line-height: 1.45; color: #2C1A0E; overflow-wrap: anywhere; }
        .op-gift-note small { display: block; margin-top: 4px; font-family: 'Lato', sans-serif; font-style: normal; font-size: calc(12px * var(--op-text-scale)); color: #8a7a60; }
        .gift-open, .gift-ok, .thanks-send, .thanks-skip { flex: 1 1 auto; min-height: 48px; padding: 10px 18px; border-radius: 24px; white-space: nowrap; cursor: pointer;
          font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; line-height: 1.2; }
        .gift-open, .thanks-send { background: #D9BC8C; border: 1.5px solid #D9BC8C; color: #2C1A0E; }
        .gift-ok, .thanks-skip { background: #FFFFFF; border: 1.5px solid #E5D7C1; color: #5C4636; }
        .gift-open:focus-visible, .gift-ok:focus-visible, .thanks-send:focus-visible, .thanks-skip:focus-visible { outline: 2px solid #A0724A; outline-offset: 2px; }
        .op-start-over { background: none; border: none; padding: 4px 0; font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; color: #8A5F3A; text-decoration: underline; text-decoration-color: rgba(160,114,74,.4); text-underline-offset: 3px; cursor: pointer; white-space: nowrap; }
        .op-start-over:disabled { opacity: .5; cursor: default; }
        .op-check-line { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; margin: 0 0 10px; }
        .op-check-chip { display: inline-flex; align-items: center; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); font-weight: 700; letter-spacing: .02em; color: #7A4A1E; background: #F3DFC8; border-radius: 999px; padding: 2px 8px; white-space: nowrap; }
        .op-check-note { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #7A4A1E; line-height: 1.4; overflow-wrap: anywhere; }
        /* Ask the Galley — the one composer (SPEC_galley_composer; tokens from docs/mockups/mockup_galley_composer.html, sized by the text knob
           like the block it replaces). .op-or is the single divider above it. */
        .op-or { display: flex; align-items: center; gap: 10px; margin: 22px 0 14px; }
        .op-or::before, .op-or::after { content: ""; flex: 1; height: 1px; background: rgba(44,26,14,.12); }
        .op-or span { font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); font-weight: 900; letter-spacing: .1em; text-transform: uppercase; color: #b5a48d; }
        .op-galley { background: rgba(160,114,74,.06); border: 1.5px solid rgba(160,114,74,.22); border-radius: 14px; padding: 14px; margin-bottom: 16px; }
        .op-galley-h { display: flex; align-items: center; gap: 7px; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); font-weight: 900; letter-spacing: .1em; text-transform: uppercase; color: #8A5F3A; }
        .op-galley-h svg { width: 13px; height: 13px; flex: none; }
        .op-galley-sub { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7968; line-height: 1.4; margin: 4px 0 10px; }
        .op-galley-box { background: #FFFDF9; border: 1.5px solid #E8D5B7; border-radius: 12px; padding: 10px 10px 8px; }
        .op-galley-tawrap { position: relative; }
        /* Three line-heights plus a little, NOT a fixed px: at XXL the 15 px body is 26 px and a fixed band cut the placeholder's third line (2026-10-11). */
        .op-galley-ta { display: block; width: 100%; box-sizing: border-box; border: none; background: transparent; outline: none; resize: vertical; padding: 2px 26px 2px 3px;
                        font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); line-height: 1.45; min-height: calc(3 * 1.45 * 15px * var(--op-text-scale) + 4px); color: #2C1A0E; }
        .op-galley-ta:disabled { opacity: .6; }
        .op-galley-tx { position: absolute; top: 0; right: 0; width: 22px; height: 22px; border-radius: 50%; border: none; background: rgba(44,26,14,.10); color: #6b5a45; font-size: 14px; line-height: 1; cursor: pointer;
                        display: flex; align-items: center; justify-content: center; padding: 0; }
        /* The tool row: ghost icon buttons at rest (no border, no fill, secondary-brown glyph — #7A4E28 on #FFFDF9 is 7.6:1),
           glyphs in em so they follow the text knob, 44 × 44 hit areas. Colour means active. Scoped to the card: the Shop
           Add sheet's disabled "coming later" mic keeps the dark fill under its LATER badge. */
        .op-galley-foot { display: flex; align-items: center; justify-content: flex-end; gap: 4px; margin-top: 4px; font-size: calc(15px * var(--op-text-scale)); }
        .op-galley-photo, .op-galley-foot .op-mic-btn { flex: none; align-self: auto; width: 44px; height: 44px; min-width: 44px; padding: 0; border: none; border-radius: 10px; background: transparent;
                                                       display: inline-flex; align-items: center; justify-content: center; cursor: pointer; color: #7A4E28; transition: background .15s, color .15s;
                                                       font-size: inherit; }   /* a button does not inherit font-size on its own; the em glyph below needs the row's */
        .op-galley-photo svg, .op-galley-foot .op-mic-btn svg { width: 1.4em; height: 1.4em; max-width: 34px; max-height: 34px; stroke: currentColor; fill: none; stroke-width: 1.7; flex: none; }
        .op-galley-photo:active { background: rgba(44,26,14,.10); color: #2C1A0E; }   /* the brief pressed fill, then the picker */
        .op-galley-foot .op-mic-btn.listening { background: #2C1A0E; color: #FFFDF9; }   /* filled while listening — plus the pulse the rule above keeps */
        .op-galley-photo:disabled, .op-galley-foot .op-mic-btn:disabled { opacity: .45; cursor: default; }
        .op-galley-photo:focus-visible, .op-galley-foot .op-mic-btn:focus-visible { outline: 2px solid #A0724A; outline-offset: 2px; }
        /* Thumbnails (walk steps 8/20): four tiles — or three + the add tile — on ONE line wherever they fit: 48 × 64 tiles, an 11 px
           column gap that the number badge (hangs 5 px left) and the × (hangs 6 px right) share without touching. Four need 225 px;
           the box offers 225 at 375 and 240 at 390. At 320 it offers 170, so they wrap 2 + 2 there — they truly cannot fit. */
        .op-galley-thumbs { display: flex; flex-wrap: wrap; gap: 12px 11px; padding: 8px 4px 2px 6px; }
        .op-galley-th { position: relative; width: 48px; height: 64px; border-radius: 7px; border: 1px solid #E8D5B7; background: #fff; flex: none; }
        .op-galley-th img { display: block; width: 100%; height: 100%; object-fit: cover; border-radius: 7px; }
        .op-galley-n { position: absolute; top: -5px; left: -5px; width: 18px; height: 18px; border-radius: 50%; background: #2B1E16; color: #FAF4EC; font-family: 'Lato', sans-serif; font-size: 11.5px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
        .op-galley-x { position: absolute; top: -7px; right: -6px; width: 24px; height: 24px; border-radius: 50%; border: 1px solid #E8D5B7; background: #FAF4EC; color: #8A5F3A; font-size: 16px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; }
        .op-galley-add { width: 48px; height: 64px; border-radius: 7px; border: 1.5px dashed #C9A97A; background: #FFFDF9; color: #8A5F3A; font-size: 22px; line-height: 1; cursor: pointer; flex: none; padding: 0; }
        .op-galley-add:disabled, .op-galley-x:disabled { opacity: .5; cursor: default; }
        .op-galley-clear { background: none; border: none; min-height: 44px; padding: 8px 4px; font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; color: #8A5F3A; text-decoration: underline; text-decoration-color: rgba(160,114,74,.4); text-underline-offset: 3px; cursor: pointer; }
        .op-galley-clear:disabled { opacity: .5; cursor: default; }
        .op-galley-hint { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7968; margin-top: 8px; line-height: 1.5; }
        .op-galley-hint.err { color: #b3261e; }
        .op-galley-echo-wrap { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; margin-top: 12px; }
        .op-galley-echo { display: flex; align-items: flex-start; gap: 8px; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); line-height: 1.45; color: #4E3A28; }
        .op-galley-echo svg { width: 16px; height: 16px; flex: none; margin-top: 2px; }
        .op-galley-switch { margin-left: 24px; background: none; border: none; min-height: 40px; padding: 6px 0; font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; color: #8A5F3A;
                            text-decoration: underline; text-decoration-color: rgba(160,114,74,.4); text-underline-offset: 3px; cursor: pointer; text-align: left; }
        .op-galley-switch:disabled { opacity: .5; cursor: default; }
        .op-galley-try { display: flex; flex-direction: column; gap: 6px; margin-top: 10px; }
        .op-galley-try-l { font-family: 'Lato', sans-serif; font-size: calc(13px * var(--op-text-scale)); color: #8a7968; }
        .op-galley-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .op-galley-chip { position: relative; min-height: 36px; padding: 6px 12px; border: 1px solid #D9C4A4; border-radius: 999px; background: #FFFDF9; font-family: 'Lato', sans-serif; font-size: calc(13.5px * (1 + (var(--op-text-scale) - 1) * 0.8));
                          line-height: 1.3; color: #5E4630; cursor: pointer; text-align: left; max-width: 100%; }   /* wraps as whole words where a chip is wider than the card (320 px at XXL) */
        .op-galley-chip::after { content: ""; position: absolute; inset: -5px 0; }   /* the only pills on the card, at their visual size; the hit area is 44 px tall (the insets measure from the padding box, inside the 1 px borders: 34 + 5 + 5); neighbouring rows, 8 px apart, never overlap */
        .op-galley-chip:disabled { opacity: .5; cursor: default; }
        .op-galley-go { display: block; width: 100%; margin-top: 12px; border: none; border-radius: 12px; padding: 12px; min-height: 48px; background: #2B1E16; color: #FAF4EC; font-family: 'Lato', sans-serif;
                        font-size: calc(15px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; cursor: pointer; transition: background .15s; }
        .op-galley-go:disabled { background: #E8D5B7; color: #9a8a78; cursor: default; }
        .op-galley-fine { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7968; margin-top: 8px; line-height: 1.5; }
        .op-galley-about { background: none; border: none; padding: 6px 0; margin-top: 2px; font-family: 'Lato', sans-serif; font-size: calc(13px * (1 + (var(--op-text-scale) - 1) * 0.8)); color: #8a7968;
                           text-decoration: underline; text-decoration-color: rgba(138,121,104,.5); text-underline-offset: 3px; cursor: pointer; }
        .op-galley-about-line { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #4E3A28; line-height: 1.5; margin-top: 2px; }
        .op-never-mind { width: 100%; margin-top: 6px; padding: 10px; background: none; border: 1.5px solid rgba(44,26,14,.10); border-radius: 10px; font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; color: #6f5a45; cursor: pointer; }
        .op-recipe { background: #fff; border: 1.5px solid rgba(44,26,14,.10); border-radius: 14px; padding: 16px 16px 14px; }
        .op-recipe.galley { border-color: rgba(160,114,74,.3); }
        .op-recipe-eyebrow { display: flex; align-items: center; gap: 6px; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); font-weight: 900; letter-spacing: .1em; text-transform: uppercase; color: #8A5F3A; margin-bottom: 8px; }
        .op-recipe-title { font-family: 'Playfair Display', serif; font-weight: 700; font-size: calc(21px * (1 + (var(--op-text-scale) - 1) * 0.5)); line-height: 1.15; color: #2C1A0E; margin-bottom: 4px; }
        .op-recipe-meta { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7968; margin-bottom: 14px; }
        .op-recipe-preamble { margin: 0 0 14px; padding: 0 0 12px; border-bottom: 1px solid rgba(44,26,14,.10); }
        .op-recipe-preamble p { margin: 0; font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); line-height: 1.5; color: #2C1A0E; white-space: pre-wrap; overflow-wrap: anywhere; }
        .op-steps { list-style: none; margin: 0; padding: 0; }
        .op-steps li { display: flex; gap: 11px; align-items: flex-start; margin-bottom: 12px; }
        .op-steps li:last-child { margin-bottom: 0; }
        .op-step-badge { flex: none; width: 24px; height: 24px; border-radius: 50%; background: rgba(160,114,74,.13); color: #8A5F3A; font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); font-weight: 900; display: flex; align-items: center; justify-content: center; margin-top: 1px; }
        .op-steps p { margin: 0; font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); line-height: 1.5; color: #2C1A0E; white-space: pre-wrap; }
        .op-steps-action { background: none; border: none; padding: 0; font-family: 'Lato', sans-serif; font-weight: 700; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); letter-spacing: 0; text-transform: none; color: #8A5F3A; text-decoration: underline; text-decoration-color: rgba(160,114,74,.4); text-underline-offset: 3px; cursor: pointer; }
        .op-ask-again { display: block; width: 100%; text-align: center; margin-top: 12px; }
        .op-steps-done { width: 100%; margin-top: 10px; padding: 11px; background: #A0724A; border: none; border-radius: 10px; font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 900; color: #fff; cursor: pointer; }
        .op-steps-empty { width: 100%; padding: 12px 11px; background: none; border: 1.5px dashed #C9A97A; border-radius: 9px; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8a7968; cursor: pointer; text-align: center; }
        .qty-controls { display: inline-flex; align-items: center; background: transparent; border: 1px solid #C9A97A; border-radius: 999px; overflow: hidden; flex-shrink: 0; }
        .qty-btn { width: 38px; height: 34px; border: 0; background: transparent; color: #A0724A; font-size: 1.2rem; cursor: pointer; display: flex; align-items: center; justify-content: center; font-family: 'Lato', sans-serif; line-height: 1; transition: background 0.12s; }
        .qty-btn:active { background: #F5EDE0; }
        .qty-display { font-family: 'Playfair Display', serif; font-size: 1rem; font-weight: 700; min-width: 30px; text-align: center; color: #2C1A0E; border-left: 1px solid #C9A97A; border-right: 1px solid #C9A97A; padding: 8px 0; }
        .qty-display.zero { color: #c8b89a; font-weight: 400; }
        .add-btn { border: 1px solid #C9A97A; background: transparent; color: #8A5F3A; font-family: 'Lato', sans-serif; font-weight: 700; font-size: 0.9rem; letter-spacing: 0.02em; padding: 9px 22px; border-radius: 999px; cursor: pointer; transition: all 0.14s; }
        .add-btn:active { background: #A0724A; border-color: #8A5F3A; color: #fff; }
        @media (hover: hover) { .add-btn:hover { background: #A0724A; border-color: #8A5F3A; color: #fff; } }
        .price-row { display: flex; align-items: center; gap: 8px; }
        .price-display { font-family: 'Lato', sans-serif; font-size: calc(0.78rem * var(--op-text-scale)); color: #8a7a60; }
        .price-edit-wrap { display: flex; align-items: center; gap: 4px; width: 100%; }
        .price-input { font-family: 'Lato', sans-serif; font-size: 0.82rem; border: 1.5px solid #c8973a; border-radius: 4px; padding: 3px 6px; width: 70px; color: #2C1A0E; background: #F5EDE0; outline: none; }
        .price-save-btn { font-family: 'Lato', sans-serif; font-size: 0.7rem; background: #c8973a; color: white; border: none; border-radius: 3px; padding: 4px 8px; cursor: pointer; }
        .item-subtotal { font-family: 'Lato', sans-serif; font-size: calc(0.75rem * var(--op-text-scale)); color: #c8973a; font-weight: 700; text-align: right; }
        .list-empty { text-align: center; padding: 60px 20px; }
        /* Shop empty state on the knob (pass 2): heading = page-title rate k .4 at its 24, #8A5F3A (espresso tried 7b1e0b4, too heavy for an empty state);
           line = body 15; the shared .home-line-link scales on its own rule since Home joined. */
        .list-empty h2 { font-family: 'Playfair Display', serif; font-size: calc(24px * (1 + (var(--op-text-scale) - 1) * 0.4)); color: #8A5F3A; }
        .list-empty-line { display: flex; flex-wrap: wrap; justify-content: center; align-items: baseline; column-gap: 6px; margin-top: 12px;
                           font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); line-height: 1.45; color: #2C1A0E; }
        /* Wrap, never squeeze (SPEC_global_text_size decision 9): the count keeps its one line; when the three controls
           don't fit beside it (phone width, "+ Add" is wider than the old round +), they drop to a second line as ONE
           group, right-aligned — never split, never a count ellipsed to "4 …". */
        .list-header { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; margin-bottom: 16px; gap: 10px 8px; }
        .list-header.empty { justify-content: flex-end; }
        .list-progress { flex: 1 0 auto; white-space: nowrap; }
        .list-controls { flex: none; display: flex; align-items: center; gap: 8px; margin-left: auto; }
        /* ── The Wrap Up bar (src/components/WrapUpBar.js; docs/mockups/mockup_shop_wrapup_bar.html) ──
           Pinned above the Helm: bottom = the Helm's 18px + its 56px + a 10px gap (+ safe area); 16px sides = the container's
           content edge. z 900 = the Helm's (they never overlap) and above .helm-fade (899). Cream, hairline border, soft shadow.
           The fill is SOFT teal (#CFE8E3 → #B5DDD5, a 45% teal rule at its edge) and animates on every check — saturated
           #0D9488 is the All done card's alone. The text and the action sit above the fill (position: relative). */
        .wrapbar { position: fixed; left: 16px; right: 16px; bottom: calc(84px + var(--op-inset-bottom)); z-index: 900;
                   display: flex; align-items: center; justify-content: space-between; gap: 12px; overflow: hidden;
                   min-height: 46px; padding: 12px 16px; border-radius: 16px; border: 1.5px solid #E8DDCE; background: #FAF4EC;
                   box-shadow: 0 4px 14px rgba(0,0,0,0.08); cursor: pointer; text-align: left; }
        /* While ANY sheet or modal is open the bar hides and returns on close (Dan 2026-10-30: at z 900 it
           floated over every sheet and partly covered the arrival form's Not for us / Add to our meals).
           Keyed off the document like the Helm rule above: every surface that owns a scrim or overlay. */
        body:has(.modal-overlay, .rv-scrim, .rv-psheet-scrim, .lib-type-scrim, .add-sheet-scrim, .board-menu-backdrop) .wrapbar { display: none; }
        .wrapbar.p0 { border-style: dashed; }
        .wrapbar:disabled { cursor: default; opacity: 0.7; }
        /* No hard edge on the fill: its 2px right border was the line through the "W" of
           the label at XXL (pass 2, Shop). The soft tint alone marks progress. */
        .wrapbar-fill { position: absolute; left: 0; top: 0; bottom: 0; background: linear-gradient(90deg, #CFE8E3, #B5DDD5);
                        transition: width 0.2s ease; }
        .wrapbar-t { position: relative; min-width: 0; font-family: 'Lato', sans-serif; font-size: calc(13px * var(--op-text-scale)); color: #6E5A4A; }
        .wrapbar-t b { color: #2C1A0E; font-weight: 700; }
        .wrapbar-a { position: relative; flex: none; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); font-weight: 700; color: #0A776D; white-space: nowrap; }
        /* The list's last rows scroll clear of the bar: its height + the 10px gap, on top of .app-root's Helm padding. */
        .wrapbar-spacer { height: 58px; }
        .cat-toggle { background: none; border: none; cursor: pointer; padding: 4px 6px; border-radius: 4px; display: flex; align-items: center; gap: 5px; font-family: 'Lato', sans-serif; font-size: 0.68rem; letter-spacing: 1px; text-transform: uppercase; transition: opacity 0.2s; }
        .cat-toggle:hover { opacity: 0.7; }
        /* List header + In-cart tray on the knob (pass 2, Shop slice 2): progress = eyebrow k 1 at
           its 12.8; lens toggle = button 14 k .8 (the 46px segment keeps its height); aisle + A–Z
           eyebrows 11.2 → 12.5; tray title = body 15, tray count = meta 14. Muted → #8A5F3A. */
        .list-progress { font-family: 'Lato', sans-serif; font-size: calc(12.8px * var(--op-text-scale)); color: #8A5F3A; letter-spacing: 1px; text-transform: uppercase; }
        .cyc-ico { flex: none; width: 46px; height: 46px; border-radius: 11px; display: flex; align-items: center; justify-content: center; border: 1px solid #E8D5B7; background: #fff; color: #A0724A; cursor: pointer; padding: 0; transition: background 0.18s, border-color 0.18s, color 0.18s; }
        .cyc-ico.on { background: #A0724A; border-color: #8A5F3A; color: #fff; }
        .cyc-ico svg { width: 22px; height: 22px; display: block; }
        /* ── Shop tab: lens · In cart tray · in-store Add · store prompt (SPEC_shop_lens_instore_capture.md) ── */
        .shop-seg { flex: none; height: 46px; border-radius: 11px; border: 1px solid #E8D5B7; background: #fff; display: flex; overflow: hidden; }
        .shop-seg button { border: none; background: none; padding: 0 12px; font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; color: #8A5F3A; cursor: pointer; transition: background .15s, color .15s; }
        .shop-seg button.on { background: #A0724A; color: #fff; }
        /* Text size pass 2, slice 3: meta 14 at k 1 in #8A5F3A (the #8a7a60 it replaced read 3.82:1 on
           cream). flex-wrap lets a long store name drop below "Shopping at" at XXL instead of
           wrapping inside its own word-box; the chevron is a glyph at the 12.5 floor. */
        .store-line { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8A5F3A; margin: -6px 2px 12px; display: flex; flex-wrap: wrap; align-items: center; gap: 5px; cursor: pointer; }
        .store-line b { color: #2C1A0E; font-weight: 700; }
        .store-line .chev { font-size: calc(12.5px * var(--op-text-scale)); color: #8A5F3A; }
        .store-line .store-set { color: #8A5F3A; font-weight: 700; text-decoration: underline dotted; text-underline-offset: 3px; }
        .az-eyebrow { font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); letter-spacing: 2.5px; text-transform: uppercase; color: #8A5F3A; margin: 2px 0 6px; }
        /* A–Z is a different MODE, not the same rows minus headers: one flat list, small circle, no provenance, no prices.
           Padding is set so a single-line row lands at roughly an Aisles row's tap height. */
        .list-item.az { padding: 13px 4px; gap: 12px; border-bottom: 1px solid #F0E6D6; }
        .list-item.az .checkbox { width: 18px; height: 18px; }
        .list-item.az .li-name { font-size: calc(0.9rem * var(--op-text-scale)); }
        .added-here-tag { display: inline-block; margin-left: 6px; font-family: 'Lato', sans-serif; font-size: 0.62rem; font-weight: 700; color: #0A776D; border: 1px solid #0D9488; border-radius: 4px; padding: 1px 5px; vertical-align: middle; letter-spacing: .3px; }
        .in-cart-tray { margin-top: 26px; border-radius: 12px; background: #fff; border: 1px solid #E3D4BC; overflow: hidden; }
        .tray-head { display: flex; align-items: center; gap: 10px; padding: 13px 14px; cursor: pointer; background: none; border: none; width: 100%; text-align: left; }
        .tray-cb { width: 22px; height: 22px; border-radius: 50%; background: #c8973a; color: #fff; font-size: 0.78rem; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .tray-title { flex: 1; font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); font-weight: 700; color: #2C1A0E; }
        .tray-sub { font-size: calc(14px * var(--op-text-scale)); color: #8A5F3A; font-weight: 400; margin-left: 6px; }
        .tray-chev { color: #8a7a60; font-size: 0.8rem; }
        .tray-body { border-top: 1px solid #E3D4BC; padding: 0 14px 12px; }
        .in-cart-tray { margin-bottom: 8px; }
        .tray-body .list-item { padding: 11px 0; opacity: 0.55; }
        .tray-body .list-item:last-child { border-bottom: none; }
        .tray-body .li-name { text-decoration: line-through; color: #a89878; font-size: calc(0.88rem * var(--op-text-scale)); }
        .tray-initial { width: 20px; height: 20px; border-radius: 50%; background: #E8D5B7; color: #2C1A0E; font-family: 'Lato', sans-serif; font-size: 0.6rem; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        /* Rows animate INTO wherever they land — down into the tray, back up into an aisle — so the person sees where the item went. */
        @keyframes opRowIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
        @keyframes opRowInDown { from { opacity: 0; transform: translateY(6px); } to { opacity: 0.55; transform: none; } }
        .shop-row-in { animation: opRowIn .22s ease; }
        .tray-body .shop-row-in { animation-name: opRowInDown; }
        @media (prefers-reduced-motion: reduce) { .shop-row-in { animation: none; } }
        /* Check motion — presentation only, the write is on the tap.
           Beat 1 (.checking, 120ms): circle fills teal with the check, name dims + strikes. Hold ~250ms.
           Beat 2 (.collapsing, 200ms): height + opacity to zero, then the row unmounts.
           Uncheck from the tray (.unchecking) is the reverse with no hold. */
        .list-item .checkbox { transition: background .12s ease, border-color .12s ease; }
        .list-item .li-name { transition: color .12s ease; }
        .list-item.checking .checkbox { background: #0D9488; border-color: #0D9488; }
        .list-item.checking .checkbox::after { content: "✓"; color: #fff; font-size: 0.7rem; font-weight: 700; line-height: 1; }
        .list-item.checking .li-name { color: #a89878; text-decoration: line-through; }
        .list-item.checking, .list-item.unchecking, .list-item.collapsing { pointer-events: none; }
        .tray-body .list-item.unchecking .checkbox.checked { background: transparent; border-color: #c8b89a; }
        .tray-body .list-item.unchecking .checkmark { opacity: 0; transition: opacity .12s ease; }
        .tray-body .list-item.unchecking .li-name { text-decoration: none; color: #2C1A0E; }
        .tray-body .list-item.unchecking { opacity: 1; transition: opacity .12s ease; }
        @keyframes opRowOut { from { opacity: 1; max-height: 140px; } to { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; border-bottom-width: 0; } }
        @keyframes opRowOutTray { from { opacity: 0.55; max-height: 140px; } to { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; border-bottom-width: 0; } }
        .list-item.collapsing { overflow: hidden; animation: opRowOut .2s ease forwards; }
        .tray-body .list-item.collapsing { animation-name: opRowOutTray; }
        @media (prefers-reduced-motion: reduce) { .list-item.collapsing { animation: none; } }
        .store-prompt { margin: 0 0 22px; padding: 14px 14px 12px; background: #fff; border: 1px solid #E3D4BC; border-radius: 12px; }
        /* Store prompt card on the Text size knob (pass 2, Shop): question = card title k .5,
           chips = button k .8, Skip = meta, STORE NAME = eyebrow, input = body. Colours to #8A5F3A. */
        .store-prompt-q { font-family: 'Playfair Display', serif; font-size: calc(16.8px * (1 + (var(--op-text-scale) - 1) * 0.5)); color: #2C1A0E; margin-bottom: 10px; }
        .store-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .store-chip { font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); font-weight: 700; padding: 8px 13px; border-radius: 20px; border: 1px solid #E8D5B7; background: #FAF4EC; color: #2C1A0E; cursor: pointer; }
        .store-chip:disabled { opacity: .5; cursor: default; }
        .store-chip.other { color: #8A5F3A; border-style: dashed; font-weight: 400; }
        .store-skip { display: block; background: none; border: none; padding: 0; font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8A5F3A; margin-top: 10px; cursor: pointer; text-decoration: underline; text-align: left; }
        .store-field-label { font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); letter-spacing: 1.5px; text-transform: uppercase; color: #8A5F3A; margin-bottom: 7px; }
        .store-input { flex: 1; min-width: 0; border: 1.5px solid #E8D5B7; border-radius: 20px; padding: 7px 14px; font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); color: #2C1A0E; background: #F5EDE0; outline: none; }
        .add-sheet-scrim { position: fixed; inset: 0; background: rgba(44,26,14,0.42); z-index: 1000; display: flex; align-items: flex-end; }
        .add-sheet { background: #FAF4EC; border-radius: 20px 20px 0 0; width: 100%; max-width: 680px; margin: 0 auto; padding: 12px 18px 26px; box-shadow: 0 -10px 30px rgba(44,26,14,0.25); max-height: 80vh; display: flex; flex-direction: column; transition: transform 150ms ease-out; }
        @media (prefers-reduced-motion: reduce) { .add-sheet { transition: none; } }
        .add-sheet-grab { width: 36px; height: 4px; border-radius: 2px; background: #C9A97A; margin: 0 auto 14px; }
        /* Add sheet on the knob (pass 2, Shop slice 2): title = sheet-title rate k .5 at its
           current 18.4 (not promoted to 22.4 — not a .modal h2, same call as the Profile sheet);
           hint = meta 14 #8A5F3A. The search input is CatalogSearchBox (body 15, shared with Browse). */
        .add-sheet-title { font-family: 'Playfair Display', serif; font-size: calc(18.4px * (1 + (var(--op-text-scale) - 1) * 0.5)); color: #2C1A0E; margin-bottom: 12px; }
        .add-sheet-results { overflow-y: auto; margin-top: 10px; min-height: 0; }
        .add-result { display: flex; align-items: center; gap: 12px; padding: 12px 4px; border-bottom: 1px solid #E8D5B7; cursor: pointer; }
        .add-result .ar-main { flex: 1; min-width: 0; }
        .add-result .ar-name { font-family: 'Lato', sans-serif; font-size: calc(15px * var(--op-text-scale)); color: #2C1A0E; }
        .add-result .ar-cat { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8A5F3A; margin-top: 2px; }
        .add-result .ar-go { font-family: 'Lato', sans-serif; font-size: 0.78rem; font-weight: 700; color: #8A5F3A; }
        .add-result.create { border-bottom: none; }
        .add-result.create .ar-name { color: #8A5F3A; }
        .add-result.create .ar-name b { color: #2C1A0E; }
        .add-sheet-hint { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8A5F3A; margin-top: 12px; line-height: 1.45; }
        .op-later-badge { position: absolute; top: -8px; right: -8px; font-family: 'Lato', sans-serif; font-size: 0.56rem; font-weight: 700; letter-spacing: .5px; background: #0D9488; color: #fff; padding: 2px 6px; border-radius: 8px; }
        .declutter-desc { font-family: 'Lato', sans-serif; font-size: 0.72rem; color: #a9967c; font-style: italic; letter-spacing: 0.3px; margin: -8px 0 14px; }
        /* Names in the filter descriptor read as content, not as voice. */
        .declutter-desc b { font-style: normal; font-weight: 700; color: #8A5F3A; }
        /* ── Browse category rail ──
           Bleeds to the container edges (.container pads 16px) so pills scroll
           UNDER the fade rather than clipping at it, while resting flush with the
           section headers below. */
        /* The rail occupies the SAME box as the search bar and the item rows —
           .container's content column — so their left edges align at every width.
           It is not full-bleed: on a phone the column edge and the window edge are
           16px apart, and the rail respects the column. */
        .cat-rail-wrap { position: relative; margin: 0 0 4px; }
        .cat-rail-wrap::before, .cat-rail-wrap::after { content: ''; position: absolute; top: 0; bottom: 0; width: 24px; pointer-events: none; z-index: 3; opacity: 1; transition: opacity 0.15s; }
        .cat-rail-wrap::before { left: 0; background: linear-gradient(90deg, #FAF4EC, rgba(250,244,236,0)); }
        .cat-rail-wrap::after { right: 0; background: linear-gradient(270deg, #FAF4EC, rgba(250,244,236,0)); }
        /* A fade must never dim content that isn't overflowing — masks engage only
           when there is more rail past that edge. */
        .cat-rail-wrap.at-start::before { opacity: 0; }
        .cat-rail-wrap.at-end::after { opacity: 0; }
        /* touch-action: pan-x is load-bearing on Windows touch. Without it the
           element inherits auto, so Chromium waits to disambiguate tap-vs-pan
           against the <button> children before handing the gesture to the
           scroller — which is why a finger slide did nothing until a ~1s hold
           resolved the ambiguity. pan-x claims horizontal immediately and leaves
           vertical pans to bubble to the page. iOS arbitrates differently, which
           is why it never reproduced there.
           No horizontal padding: the rail's content box IS the content column, so
           the first pill at scrollLeft 0 aligns with the search bar. With
           padding-left 0 the first pill's snap offset is 0, so no scroll-padding
           is needed to keep snap from cancelling an inset. */
        .cat-rail { display: flex; align-items: center; gap: 8px; overflow-x: auto; padding: 4px 0; touch-action: pan-x; scroll-snap-type: x proximity; }
        /* The scrollbar IS the affordance — it shows position and affords dragging,
           which the pager buttons it replaces never did. No pointer media query
           gates it: on hybrid Windows hardware hover/pointer/any-hover/any-pointer
           all report false (device-confirmed on a Surface), so those queries would
           hide it from the exact mice that need it. No gate is needed anyway —
           mobile browsers use transient overlay scrollbars, so this renders on
           desktop and effectively nothing on touch. If a permanent track ever does
           show up on a touchscreen, gate by VIEWPORT WIDTH: a size question, which
           media queries answer honestly. */
        .cat-rail::-webkit-scrollbar { height: 4px; }
        .cat-rail::-webkit-scrollbar-track { background: transparent; }
        .cat-rail::-webkit-scrollbar-thumb { background: #E8D5B7; border-radius: 2px; }
        .cat-rail::-webkit-scrollbar-thumb:hover { background: #C9A97A; }
        /* Firefox only. Chromium IGNORES ::-webkit-scrollbar entirely once
           scrollbar-width/-color is set on the element, so this must stay behind a
           query only a non-webkit engine passes. */
        @supports not selector(::-webkit-scrollbar) {
          .cat-rail { scrollbar-width: thin; scrollbar-color: #E8D5B7 transparent; }
        }
        .cat-rail > * { scroll-snap-align: start; flex: 0 0 auto; white-space: nowrap; }
        .cat-pill { display: flex; align-items: center; gap: 7px; padding: 9px 16px 9px 13px; border-radius: 999px; border: 1px solid #E8D5B7; background: #F5EADA; color: #8A5F3A; font-family: 'Lato', sans-serif; font-size: 0.82rem; cursor: pointer; transition: background 0.15s, border-color 0.15s, color 0.15s; }
        /* The load-bearing detail: a fixed glyph box makes a wide emoji and a
           narrow one produce identical pill geometry. */
        .cat-pill .cat-em { font-size: 0.92rem; line-height: 1; width: 17px; text-align: center; flex: 0 0 auto; }
        .cat-pill.on { background: #2C1A0E; border-color: #2C1A0E; color: #FAF4EC; font-weight: 700; }
        /* Add-item picker — same pill, wrapped and exhaustive. Never scrolls. */
        .cat-grid { display: flex; flex-wrap: wrap; gap: 9px; }
        .cat-pill-new { background: transparent; border-style: dashed; }
        .picker-sub { font-family: 'Lato', sans-serif; font-size: 0.82rem; color: #8a7a60; margin-bottom: 12px; }
        .flat-header { font-family: 'Lato', sans-serif; font-size: 0.7rem; letter-spacing: 2.5px; text-transform: uppercase; color: #8a7a60; margin-top: 12px; padding-bottom: 6px; }
        .progress-bar { height: 4px; background: #E8D5B7; border-radius: 2px; margin-bottom: 24px; overflow: hidden; }
        .progress-fill { height: 100%; background: #A0724A; border-radius: 2px; transition: width 0.4s ease; }
        .list-cat-title { font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); letter-spacing: 2.5px; text-transform: uppercase; color: #8A5F3A; margin-bottom: 0; margin-top: 28px; padding-bottom: 6px; border-bottom: 2px solid #c8973a; }
        .list-item { display: flex; flex-wrap: wrap; align-items: center; gap: 14px; padding: 14px 4px; background: transparent; border: none; border-bottom: 1px solid #E8D5B7; transition: all 0.2s; user-select: none; -webkit-tap-highlight-color: transparent; }
        .list-item.done { opacity: 0.45; }
        .list-item.done .li-name { text-decoration: line-through; color: #a89878; }
        .checkbox { width: 22px; height: 22px; border-radius: 50%; border: 2px solid #c8b89a; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.15s; cursor: pointer; }
        .checkbox.checked { background: #c8973a; border-color: #c8973a; }
        .checkmark { color: white; font-size: 0.7rem; font-weight: bold; }
        .li-name { font-family: 'Lato', sans-serif; font-size: calc(0.95rem * var(--op-text-scale)); flex: 1; cursor: pointer; }
        .li-right { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; flex-shrink: 0; }
        .li-qty { font-family: 'Lato', sans-serif; font-size: calc(0.75rem * var(--op-text-scale)); color: #8a7a60; }
        .li-subtotal { font-family: 'Playfair Display', serif; font-size: calc(0.95rem * var(--op-text-scale)); color: #c8973a; font-weight: 700; }
        .li-subtotal.done { color: #a89878; }
        .clear-btn { font-family: 'Lato', sans-serif; font-size: 0.75rem; letter-spacing: 1px; text-transform: uppercase; padding: 8px 16px; border: 1.5px solid #c8b89a; background: transparent; color: #8a7a60; cursor: pointer; border-radius: 4px; transition: all 0.2s; }
        .clear-btn:hover { border-color: #e05c5c; color: #e05c5c; }
        /* All done card (mockup_shop_all_done.html) — the arc is the ONLY ornament: one 1.6px teal stroke. */
        .all-done { text-align: center; padding: 8px 12px 26px; }
        .all-done-arc { width: 150px; height: 12px; margin: 0 auto 14px; display: block; }
        .all-done-arc path { fill: none; stroke: #0D9488; stroke-width: 1.6; stroke-linecap: round; }
        /* All done on the knob (pass 2, Shop slice 2): heading = page-title rate k .4 at its
           33.6; subline = meta k 1 at its 16; stats + learning line = meta 14; the button = button
           k .8 raised from 11.5 to the 12.5 floor. Muted #8a7a60 → #8A5F3A. */
        .all-done h2 { font-family: 'Playfair Display', serif; font-weight: 400; font-size: calc(33.6px * (1 + (var(--op-text-scale) - 1) * 0.4)); margin: 0 0 6px; letter-spacing: -0.01em; color: #2C1A0E; }
        .all-done-sub { font-family: 'Playfair Display', serif; font-style: italic; font-size: calc(16px * var(--op-text-scale)); color: #8A5F3A; margin: 0 0 16px; }
        .all-done-meta { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8A5F3A; letter-spacing: 0.5px; }
        .all-done-meta b { color: #2C1A0E; font-weight: 700; }
        .all-done-learn { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8A5F3A; margin-top: 6px; }
        .all-done-btn { display: inline-block; margin-top: 20px; background: #0D9488; color: #fff; border: none; cursor: pointer;
                        font-family: 'Lato', sans-serif; font-size: calc(12.5px * (1 + (var(--op-text-scale) - 1) * 0.8)); letter-spacing: 1.6px; text-transform: uppercase; font-weight: 900;
                        padding: 13px 26px; border-radius: 24px; box-shadow: 0 6px 16px rgba(13,148,136,0.28); }
        /* The trip summary's dismiss. Same look as .all-done-btn, deliberately
           NOT that class: .all-done-btn is on the RUM prod unmask allow-list as
           the "Wrap up trip →" funnel signal, and folding a "Done" tap into it
           would double-count wrap-up intent. This carries .op-chrome instead
           (fixed copy, the sanctioned bucket). */
        .trip-summary-done { display: inline-block; margin-top: 20px; background: #0D9488; color: #fff; border: none; cursor: pointer;
                        font-family: 'Lato', sans-serif; font-size: calc(12.5px * (1 + (var(--op-text-scale) - 1) * 0.8)); letter-spacing: 1.6px; text-transform: uppercase; font-weight: 900;
                        padding: 13px 26px; border-radius: 24px; box-shadow: 0 6px 16px rgba(13,148,136,0.28); }
        /* Trip reality (SPEC_trip_qualification_v2; mockup_trip_reality_ask.html).
           Geometry from the mockup, colours from the app's own palette. The ask is
           left-aligned inside the centred card, as drawn. Two EQUAL outlined
           buttons, no primary — the app has no preferred answer. No teal. */
        .trip-reality-ask { margin: 16px 0 18px; padding: 18px 16px; border-radius: 14px; background: #F5EADA; border: 1px solid #E8D5B7; text-align: left; }
        /* Text size pass 2, slice 3: eyebrow 12.5 k 1; the question keeps 18.4 at k 0.5 (card-title
           rate); "why" and the Counted / Not counted line are meta 14 k 1 in #8A5F3A (#8a7a60 read
           3.51:1 on the card, 3.82:1 on cream); the two answer buttons are 14 k 0.8 in a wrapping
           row, the same pattern as the Wrap-up sheet's action row: auto basis + no-wrap labels
           (a min-content basis let both two-word labels break into two lines side by side), so
           they share a row when both fit and "Just testing" drops to a full line of its own at
           XXL. The three control class names are on CHROME_ALLOW_LIST (rum.js) and do not change. */
        .trip-reality-ask .tr-eyebrow { font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); letter-spacing: 0.14em; text-transform: uppercase; color: #8A5F3A; font-weight: 700; margin: 0 0 8px; }
        .trip-reality-ask .tr-q { font-family: 'Playfair Display', serif; font-style: italic; font-weight: 500; font-size: calc(18.4px * (1 + (var(--op-text-scale) - 1) * 0.5)); line-height: 1.25; margin: 0 0 6px; color: #2C1A0E; }
        .trip-reality-ask .tr-why { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8A5F3A; margin: 0 0 14px; line-height: 1.4; }
        .trip-reality-ask .tr-btns { display: flex; flex-wrap: wrap; gap: 10px; }
        .trip-reality-ask .tr-btns button { flex: 1 1 auto; white-space: nowrap; padding: 13px 10px; border-radius: 12px; border: 1.5px solid #C9A97A; background: transparent; color: #2C1A0E; font-family: 'Lato', sans-serif; font-weight: 500; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); cursor: pointer; }
        .trip-reality-line { font-family: 'Lato', sans-serif; font-size: calc(14px * var(--op-text-scale)); color: #8A5F3A; text-align: center; margin: 10px 0 14px; }
        .trip-reality-line .trip-reality-countit { background: none; border: 0; padding: 0 0 0 4px; color: #2C1A0E; font-family: 'Lato', sans-serif; font-weight: 500; font-size: calc(14px * var(--op-text-scale)); text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
        .list-total { background: #F5EDE0; border: 2px solid #c8973a; border-radius: 10px; padding: 16px 18px; margin-top: 24px; display: flex; justify-content: space-between; align-items: center; }
        .list-total.over { border-color: #e05c5c; }
        .lt-left .lt-label { font-family: 'Lato', sans-serif; font-size: 0.8rem; letter-spacing: 1px; text-transform: uppercase; color: #8a7a60; }
        .lt-amount { font-family: 'Playfair Display', serif; font-size: 1.6rem; font-weight: 700; color: #2C1A0E; }
        .lt-amount.over { color: #e05c5c; }
        .lt-checked { font-family: 'Lato', sans-serif; font-size: 0.78rem; color: #8a7a60; margin-top: 4px; }
        .lt-budget-row { font-family: 'Lato', sans-serif; font-size: 0.78rem; margin-top: 6px; font-weight: 700; }
        .lt-budget-row.ok { color: #4a9e4a; }
        .lt-budget-row.over { color: #e05c5c; }

        /* Modals */
        /* Scroll, never clip (2026-10-05: at XXL on a 375×553 screen Already on hand, Add new item,
           Filter meals and Hold a night ran past the overlay and lost their title, Close AND
           Save/Cancel). The overlay scrolls; the dialog is centred by margin: auto instead of
           align-items: center — a dialog that fits is centred exactly as before, a taller one
           starts at the 20px padding and the overlay scrolls to its footer. No JS, no swipe. */
        .modal-overlay { position: fixed; inset: 0; background: rgba(44,26,14,0.55); display: flex; justify-content: center; z-index: 100; padding: 20px; overflow-y: auto; overscroll-behavior: contain; }
        .modal-overlay > * { margin: auto; }
        /* New Meal while asking the Galley hides the fields: anchored at the top, never re-centred (2026-10-25). */
        .modal-overlay > .modal.op-anchor-top { margin: 0 auto auto; }
        .modal { position: relative; background: #FAF4EC; border-radius: 12px; padding: 28px 24px; width: 100%; max-width: 420px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
        .modal h2 { font-family: 'Playfair Display', serif; font-size: calc(1.4rem * (1 + (var(--op-text-scale) - 1) * 0.5)); font-weight: 700; color: #2C1A0E; margin-bottom: 8px; padding-right: 40px; }
        /* Every sheet's Close (SheetClose): 44px target, top-right, the first VoiceOver stop. Backdrop + Cancel remain for sighted users. */
        .sheet-close { position: absolute; top: 8px; right: 8px; width: 44px; height: 44px; border-radius: 50%; border: none; background: transparent; color: #8a7a60; cursor: pointer;
                       display: flex; align-items: center; justify-content: center; padding: 0; z-index: 1; }
        .sheet-close:hover { background: rgba(44,26,14,0.06); color: #2C1A0E; }
        .sheet-close:focus-visible { outline: 2px solid #A0724A; outline-offset: 2px; }
        .sheet-close:disabled { opacity: 0.4; cursor: default; }
        .modal-subtitle { font-family: 'Lato', sans-serif; font-size: 0.82rem; color: #8a7a60; margin-bottom: 20px; }
        .modal-field { margin-bottom: 16px; }
        .modal-label { font-family: 'Lato', sans-serif; font-size: calc(12.5px * var(--op-text-scale)); letter-spacing: 1.5px; text-transform: uppercase; color: #8a7a60; margin-bottom: 6px; display: block; }
        .modal-input { width: 100%; font-family: 'Lato', sans-serif; font-size: calc(0.95rem * var(--op-text-scale)); border: 1.5px solid #E8D5B7; border-radius: 6px; padding: 9px 12px; color: #2C1A0E; background: #F5EDE0; outline: none; transition: border-color 0.2s; }
        .modal-input:focus { border-color: #c8973a; }
        .modal-input-prefix { display: flex; align-items: center; border: 1.5px solid #E8D5B7; border-radius: 6px; background: #F5EDE0; overflow: hidden; transition: border-color 0.2s; }
        .modal-input-prefix:focus-within { border-color: #c8973a; }
        .modal-prefix-symbol { font-family: 'Lato', sans-serif; font-size: 0.95rem; color: #8a7a60; padding: 9px 0 9px 12px; }
        .modal-input-inner { flex: 1; font-family: 'Lato', sans-serif; font-size: 0.95rem; border: none; padding: 9px 12px; color: #2C1A0E; background: transparent; outline: none; }
        .modal-select { width: 100%; font-family: 'Lato', sans-serif; font-size: 0.95rem; border: 1.5px solid #E8D5B7; border-radius: 6px; padding: 9px 12px; color: #2C1A0E; background: #F5EDE0; outline: none; cursor: pointer; }
        .modal-error { font-family: 'Lato', sans-serif; font-size: 0.8rem; color: #e05c5c; margin-bottom: 12px; }
        /* Pinned footer (SPEC_global_text_size decision 9): the meal sheet's .modal scrolls (88vh), so its actions stick to the
           bottom edge and the list scrolls beneath; a cream fill and a soft top shadow keep the rows from showing through. Inert
           on sheets that don't scroll. */
        .modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 8px; position: sticky; bottom: -28px; padding: 10px 0 28px; margin-bottom: -28px;
                         background: #FAF4EC; box-shadow: 0 -10px 10px -10px rgba(44,26,14,0.18); z-index: 1; }
        .modal-actions-spaced { display: flex; gap: 10px; justify-content: space-between; align-items: center; margin-top: 8px; }
        .modal-cancel { font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); padding: 9px 18px; border: 1.5px solid #c8b89a; background: transparent; color: #8a7a60; cursor: pointer; border-radius: 5px; transition: all 0.2s; }
        .modal-cancel:hover { border-color: #2C1A0E; color: #2C1A0E; }
        .modal-confirm { font-family: 'Lato', sans-serif; font-size: calc(14px * (1 + (var(--op-text-scale) - 1) * 0.8)); padding: 9px 18px; background: #A0724A; color: #FAF4EC; border: none; cursor: pointer; border-radius: 5px; transition: all 0.2s; }
        .modal-confirm:hover { background: #c8973a; }
        .modal-remove { font-family: 'Lato', sans-serif; font-size: 0.8rem; padding: 9px 18px; border: 1.5px solid #e8d5d5; background: transparent; color: #e05c5c; cursor: pointer; border-radius: 5px; transition: all 0.2s; }
        .modal-remove:hover { background: #fff0f0; border-color: #e05c5c; }
      `}</style>

      {/* OurBanner region — household identity only. Nav lives in the Helm
          (floating pill / wide rail, SPEC_nav_helm.md); the strip that used to sit
          under the header is gone. When a photo exists AND the tab is Home
          (bannerHasPhoto), this wrapper carries the photo+gradient behind the
          header. Photo-less, or any other tab: an inert relative box and the
          header keeps its solid espresso — Row 1 alone off Home. The layer spans
          the wrapper's flow height = the header (the modals in between are
          position:fixed and add no height). */}
      <div style={{ position: "relative" }}>
        {bannerHasPhoto && (
          <>
            {/* .op-replay-block: excluded from session replay (the URL is a signed household-photo link). */}
            <div className="op-replay-block" aria-hidden="true" style={{
              position: "absolute", inset: 0, zIndex: 0,
              backgroundColor: "#2C1A0E",
              backgroundImage: `url("${bannerPhotoUrl}")`,
              backgroundSize: `${bannerZoom}% auto`,
              backgroundPosition: `${bannerX}% ${bannerY}%`,
              backgroundRepeat: "no-repeat",
            }} />
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 0, background: BANNER_DISSOLVE }} />
          </>
        )}
      <div className="safe-top" aria-hidden="true" />
      <div className="header" style={{ position: "relative", zIndex: bannerHasPhoto ? 1 : undefined, background: bannerHasPhoto ? "transparent" : undefined }}>
        {/* Row 1: Velayo bar — avatar left, three dots right, household centered */}
        <div style={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px 10px", minHeight: "44px", boxSizing: "border-box", background: bannerHasPhoto ? "transparent" : "#1a0e06" }}>
          {isSignedIn && household?.name && (
            <button
              onClick={() => setShowHouseholdModal(true)}
              aria-label="Manage place"
              style={{
                position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
                background: "none", border: "none", padding: "4px 8px", cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: "6px", maxWidth: "55%",
                fontFamily: "'Lato', sans-serif", fontSize: "13px", textTransform: "uppercase",
                letterSpacing: "0.6px", color: bannerHasPhoto ? "#FAF4EC" : "#C9A97A", whiteSpace: "nowrap", overflow: "hidden",
                fontWeight: bannerHasPhoto ? 700 : undefined,
                textShadow: CHROME_SHADOW,
              }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, opacity: bannerHasPhoto ? 0.8 : 0.5, marginInline: "3px" }}>
                <circle cx="12" cy="5" r="2.4" stroke="#C9A97A" strokeWidth="1.6"/>
                <path d="M12 7.4V21" stroke="#C9A97A" strokeWidth="1.6" strokeLinecap="round"/>
                <path d="M6 11h12" stroke="#C9A97A" strokeWidth="1.6" strokeLinecap="round"/>
                <path d="M3 13c0 5 4 7 9 7s9-2 9-7" stroke="#C9A97A" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{household.name}</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, opacity: bannerHasPhoto ? 0.8 : 0.5, marginInline: "3px", transform: "scaleX(-1)" }}>
                <circle cx="12" cy="5" r="2.4" stroke="#C9A97A" strokeWidth="1.6"/>
                <path d="M12 7.4V21" stroke="#C9A97A" strokeWidth="1.6" strokeLinecap="round"/>
                <path d="M6 11h12" stroke="#C9A97A" strokeWidth="1.6" strokeLinecap="round"/>
                <path d="M3 13c0 5 4 7 9 7s9-2 9-7" stroke="#C9A97A" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            </button>
          )}
          <div style={{ minHeight: "32px" }}>
            {/* The header avatar is the one Profile trigger at every width
                (the rail's foot avatar went with the rail, 2026-09-20). The
                wrapping div stays (32px tall, the avatar's height) so the row's
                space-between geometry is unchanged whether or not it is filled.
                Signed-out entry cleanup (2026-10-02): the header carries NO
                SIGN IN / SIGN UP in any signed-out state — signed_out, booting
                or session_lost. The welcome's pair is the one entry (disabled
                until Clerk loads); the session_lost sheet keeps its own Sign In. */}
            {sessionLive && (
              <button
                onClick={() => setShowProfileSheet(true)}
                style={{
                  width: "32px", height: "32px", borderRadius: "50%",
                  background: "#0D9488", border: "2px solid #0D9488",
                  color: "white", fontFamily: "'Lato', sans-serif",
                  fontSize: "12px", fontWeight: 600, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0
                }}
                aria-label="Open profile"
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </button>
            )}
          </div>
          <button
            onClick={() => setShowVelayoMenu(true)}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center" }}
            aria-label="Velayo menu"
          >
            <svg width="28" height="24" viewBox="0 0 28 24" fill="none">
              <circle cx="5" cy="4" r="3.5" fill="#C9A97A"/>
              <circle cx="14" cy="16" r="3.5" fill="#C9A97A"/>
              <circle cx="23" cy="4" r="3.5" fill="#C9A97A"/>
            </svg>
          </button>
        </div>

        {/* Row 2: OurProvisions wordmark band — HOME ONLY (bannerOnHome) and
            SIGNED IN ONLY (sessionLive — Signed-out entry cleanup step 3,
            2026-10-02: while signed out it read "Provisions" above the welcome's
            "OurProvisions", and its button was inert; the welcome's headline
            carries the brand, Row 1 keeps the Velayo dots). The other tabs end
            at Row 1. Over a photo it obeys the household's banner_wordmark:
            large (default), small (~⅔, ~80%), or hidden (not rendered — the
            middle band is photo only, spec D4). The band keeps its height when
            hidden so the photo has room to breathe. */}
        {bannerOnHome && sessionLive && (
        <div style={{
          position: "relative", zIndex: 1,
          padding: bannerWordmark === "small" ? "16px 16px" : "20px 16px",
          textAlign: "center",
          background: bannerHasPhoto ? "transparent" : "#2C1A0E",
          minHeight: bannerHasPhoto ? "64px" : undefined,
          boxSizing: "border-box",
        }}>
          {bannerWordmark === "hidden" ? null : (
            <h1 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: bannerWordmark === "small" ? "28px" : "42px",
              letterSpacing: "0.02em", color: "#FAF4EC", fontWeight: 400, margin: 0,
              // No longer gated on showSplash. The splash used to hide this element
              // and land a travelling clone on it (§6b); that machinery is deleted,
              // so the header simply renders on its own terms.
              opacity: bannerWordmark === "small" ? 0.8 : 1,
              textShadow: WORDMARK_SHADOW,
              transform: bannerHasPhoto ? "translateY(-5%)" : "none",
            }}>
              {/* EARNED "Our" (SPEC_wordmark_earned_our.md, reversing §8's always-Our).
                  The wordmark is brand AND diagnostic: the *Our* arrives with the
                  second person, so a solo household reads "Provisions" and a shared
                  one reads "OurProvisions".

                  BEHAVIOUR BEFORE LABEL: the default is "Provisions" in every unknown
                  state — signed out, membership still loading, or genuinely solo. That
                  falls out of the data rather than needing a guard: householdMembers
                  starts [] and fills per active household, so `> 1` is false until 2+
                  members actually resolve. Never claim "Our" until the data says so —
                  an always-Our header is what made a correct solo signup read as
                  "a stranger is in my household" (2026-08-24 field incident).

                  The transition is opacity + max-width on the "Our" span, not a text
                  swap: no reflow of "Provisions", and the band stays optically centred
                  in both end states (a phantom zero-opacity "Our" would push the solo
                  wordmark off-centre in a textAlign:center band). */}
              <button
                onClick={() => isSignedIn ? setShowHouseholdModal(true) : null}
                style={{ background: "none", border: "none", padding: 0, cursor: isSignedIn ? "pointer" : "default", color: "inherit", font: "inherit" }}
              >
                <span
                  aria-hidden={!hasEarnedOur}
                  style={{
                    fontWeight: 400, fontStyle: "italic",
                    display: "inline-block", overflow: "hidden", verticalAlign: "bottom",
                    whiteSpace: "nowrap",
                    opacity: hasEarnedOur ? 1 : 0,
                    maxWidth: hasEarnedOur ? "3em" : "0em",
                    transition: "opacity 600ms ease, max-width 600ms ease",
                  }}
                >Our</span><span style={{ fontWeight: 700, fontStyle: "italic" }}>Provisions</span>
              </button>
            </h1>
          )}
        </div>
        )}
      </div>

      {/* Velayo app menu */}
      {showVelayoMenu && (
        <div
          onClick={() => setShowVelayoMenu(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000, animation: "fadeIn 0.2s ease",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#FAF4EC", borderRadius: "16px", padding: "32px 28px 28px",
              width: "min(340px, 90vw)", boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
              display: "flex", flexDirection: "column", alignItems: "center", gap: "24px",
            }}
          >
            {/* Menu header */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
              <img src={VELAYO_LOGO_TEAL} alt="Velayo" style={{ width: "48px", height: "48px", objectFit: "contain" }} />
              <div style={{
                fontFamily: "'Lato', sans-serif", fontSize: "0.65rem", letterSpacing: "2.5px",
                textTransform: "uppercase", color: "#8a7a60",
              }}>Live Better. Live Smarter.</div>
            </div>

            {/* App list */}
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
              <button
                onClick={() => setShowVelayoMenu(false)}
                style={{
                  width: "100%", background: "#2C1A0E", border: "2px solid #c8973a",
                  borderRadius: "10px", padding: "14px 18px",
                  display: "flex", alignItems: "center", gap: "14px",
                  cursor: "pointer", textAlign: "left",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontFamily: "'Playfair Display', serif", fontSize: "1.1rem",
                    color: "#FAF4EC", fontWeight: 700,
                  }}>
                    <span style={{ fontWeight: 400 }}>Our</span>Provisions
                  </div>
                </div>
                <div style={{
                  fontFamily: "'Lato', sans-serif", fontSize: "0.6rem", letterSpacing: "1.5px",
                  textTransform: "uppercase", color: "#c8973a", fontWeight: 700,
                }}>Active</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage household sheet — two zones: Your Households (entity CRUD) above,
          {household} · Members (membership detail) below. One selection drives both;
          the membership zone recomputes on active-household switch. */}
      {showHouseholdModal && (
        <div
          onClick={() => {
            setShowHouseholdModal(false);
            setCreating(false); setNewHouseholdName("");
          }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000, animation: "fadeIn 0.2s ease",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#FAF4EC", borderRadius: "16px", padding: "28px 24px 24px",
              width: "min(360px, 92vw)", maxHeight: "85vh", overflowY: "auto",
              boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
              display: "flex", flexDirection: "column", gap: "22px",
            }}
          >

            {/* ── Zone 1: Your Households (name-only rows; bare pencil on the active row) ── */}
            <div>
              {/* Manage place on the knob (pass 2): eyebrows 12.5 k 1; place + member names body 15
                  (wrapping, never colliding with Edit / the badge); Edit, Create = button 14 k .8,
                  the pencil scaling with Edit; YOU badge = eyebrow 12.5 in #8A5F3A on #F1E7D8. Avatar stays 36px. */}
              <div style={{
                fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), letterSpacing: "2.5px",
                textTransform: "uppercase", color: "#8A5F3A", marginBottom: "10px",
              }}>Your Places</div>
              {/* A5b (Addendum 2026-09-27): "Your places" is never drawn empty
                  before it has loaded. Until the context's read has SUCCEEDED for
                  this user, the zone is the Members placeholder and "+ Create new
                  place" does not render — the empty list under a held read (V11)
                  reads as "you have no places" and invites a duplicate household.
                  A real (non-transient) failure gets one line and a Retry; still
                  no Create. A successful EMPTY read (a brand-new user) renders the
                  Create row as before. */}
              {!householdsLoaded ? (
                householdsReadFailed ? (
                  <div style={{ textAlign: "center", padding: "14px 20px", fontFamily: "'Lato', sans-serif", fontSize: "0.85rem", color: "#8a7a60", letterSpacing: "1px" }}>
                    Couldn't load your places
                    <button
                      onClick={retryHouseholds}
                      style={{
                        background: "none", border: "none", cursor: "pointer", marginLeft: "10px",
                        fontFamily: "'Lato', sans-serif", fontSize: "0.85rem", fontWeight: 700,
                        color: "#8A5F3A", letterSpacing: "1px", padding: 0,
                      }}
                    >Retry</button>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "14px 20px", fontFamily: "'Lato', sans-serif", fontSize: "0.85rem", color: "#8a7a60", letterSpacing: "1px" }}>
                    Loading…
                  </div>
                )
              ) : (
              <>
              {/* The fixed VERB half of the active row's aria-labelledby name, rendered once,
                  outside every button (2026-10-19, the library's 10-16 recipe); the household's
                  half is the row's masked name (id hh-name-{uuid}). */}
              <span id="hh-verb-edit" className="sr-only">Edit</span>
              {(myHouseholds || []).map((hh) => (
                <PlaceRow
                  key={hh.id}
                  hh={hh}
                  isActive={hh.id === activeHouseholdId}
                  onEdit={openEditHousehold}
                  onSwitch={() => { switchHousehold(hh.id); setShowHouseholdModal(false); }}
                />
              ))}

              {/* Create new household — below the list */}
              {!creating ? (
                <button
                  onClick={() => setCreating(true)}
                  style={{
                    width: "100%", background: "none", border: "1.5px dashed #A0724A",
                    borderRadius: "8px", padding: "11px 14px", marginTop: "2px",
                    fontFamily: "'Lato', sans-serif", fontSize: ts(14, 0.8), color: "#8A5F3A",
                    cursor: "pointer", textAlign: "center", boxSizing: "border-box",
                  }}
                >+ Create new place</button>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "2px" }}>
                  <input
                    autoFocus
                    value={newHouseholdName}
                    onChange={(e) => setNewHouseholdName(e.target.value)}
                    placeholder="Place name"
                    style={{
                      width: "100%", padding: "10px 12px", borderRadius: "8px",
                      border: "1.5px solid #A0724A", fontFamily: "'Lato', sans-serif",
                      fontSize: "0.9rem", boxSizing: "border-box", background: "#FAF4EC",
                    }}
                  />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => { setCreating(false); setNewHouseholdName(""); }}
                      style={{
                        flex: 1, padding: "10px", background: "#E8D5B7", border: "none",
                        borderRadius: "8px", fontFamily: "'Lato', sans-serif",
                        fontSize: "0.8rem", cursor: "pointer", color: "#2C1A0E",
                      }}
                    >Cancel</button>
                    <button
                      disabled={creatingInFlight}
                      onClick={async () => {
                        if (creatingInFlight) return;            // guard: ignore taps while in flight
                        const n = newHouseholdName.trim();
                        if (!n) return;
                        setCreatingInFlight(true);               // raise BEFORE the await
                        try {
                          const newId = await createHousehold(n);
                          if (newId) {
                            await refreshHouseholds();
                            switchHousehold(newId);
                            setShowHouseholdModal(false);
                            setCreating(false); setNewHouseholdName("");
                            showToast(`"${n}" created`);
                          }
                        } finally {
                          setCreatingInFlight(false);            // clear on every exit path
                        }
                      }}
                      style={{
                        flex: 2, padding: "10px",
                        background: creatingInFlight ? "#C9A87E" : "#A0724A",
                        border: "none", borderRadius: "8px", fontFamily: "'Lato', sans-serif",
                        fontSize: "0.8rem", fontWeight: 700,
                        cursor: creatingInFlight ? "default" : "pointer", color: "#FAF4EC",
                      }}
                    >{creatingInFlight ? "Creating…" : "Create"}</button>
                  </div>
                </div>
              )}
              </>
              )}
            </div>

            <hr style={{ border: "none", borderTop: "1px solid #f0e6d8", margin: 0 }} />

            {/* ── Zone 2: {household} · Members (roster + Invite; no count, no monogram) ── */}
            <div>
              <div style={{
                fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), letterSpacing: "2.5px",
                textTransform: "uppercase", color: "#8A5F3A", marginBottom: "14px",
              }}>{(household?.name || "This place")} · Members</div>

              {/* Member list. A5 (Amendment 2026-09-27): until this place's members
                  have been read, the zone is the existing quiet placeholder (the
                  Browse catalog's) and the two verbs below do not render — "not
                  loaded" is never drawn as "empty" (2026-09-27: an empty roster
                  under Invite aboard and Leave place, for minutes). */}
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {!membersLoaded ? (
                  <div style={{ textAlign: "center", padding: "14px 20px", fontFamily: "'Lato', sans-serif", fontSize: "0.85rem", color: "#8a7a60", letterSpacing: "1px" }}>
                    Loading…
                  </div>
                ) : householdMembers.map((m) => {
                  const clerkId = m.users?.clerk_id;
                  const isMe = clerkId === user?.id;
                  const displayName = m.users?.full_name
                    || (m.users?.email ? m.users.email.split("@")[0] : (isMe ? "You" : "Member"));
                  return (
                    <div key={m.id} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      {isMe && user.imageUrl ? (
                        <img className="op-replay-block" src={user.imageUrl} alt={displayName} style={{ width: "36px", height: "36px", borderRadius: "50%", objectFit: "cover" }} />
                      ) : (
                        <div style={{
                          width: "36px", height: "36px", borderRadius: "50%",
                          background: "#E8D5B7", display: "flex", alignItems: "center", justifyContent: "center",
                          fontFamily: "'Lato', sans-serif", fontSize: "0.85rem", fontWeight: 700, color: "#8a7a60",
                        }}>
                          {displayName[0].toUpperCase()}
                        </div>
                      )}
                      <span style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(15), color: "#2C1A0E", flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                        {displayName}
                      </span>
                      {isMe && (
                        <span style={{
                          fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), letterSpacing: "1px",
                          // One brown: #8A5F3A, so the chip lightens to #F1E7D8 (the deck card's tan) —
                          // 4.54:1 for the text, 1.12:1 against the sheet. #E8D5B7 gave 3.87:1.
                          textTransform: "uppercase", color: "#8A5F3A", flexShrink: 0,
                          background: "#F1E7D8", borderRadius: "4px", padding: "2px 7px",
                        }}>you</span>
                      )}
                      {!isMe && m.role !== 'owner' && (
                        <button
                          onClick={() => handleRemoveMember(m)}
                          title="Remove member"
                          style={{
                            background: "none", border: "none", cursor: "pointer",
                            color: "#b08968", padding: "4px", display: "flex", alignItems: "center",
                            borderRadius: "4px",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#8a3a2a'; e.currentTarget.style.background = '#f0e3d0'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = '#b08968'; e.currentTarget.style.background = 'none'; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Two share verbs (SPEC_solo_start_experience.md D6–D8), styled per
                  mockup_solo_start_v2.html screen 3. One link served two intents before
                  this — "join my list" and "check out this app" — and that collapse is
                  what made a correct solo signup read as broken. The verbs are now
                  structurally different, not just differently worded: espresso card vs.
                  outlined, ?invite= vs. ?ref=, names the place vs. never names it.
                  Hierarchy carries the distinction; neither verb gets a confirm. */}
              {membersLoaded && (
              <button
                onClick={handleInviteShare}
                disabled={invitePreparing}
                style={{
                  display: "flex", alignItems: "center", gap: "12px", width: "100%",
                  background: invitePreparing ? "#4a3524" : "#2C1A0E", border: "none",
                  borderRadius: "13px", padding: "14px 16px",
                  cursor: invitePreparing ? "default" : "pointer",
                  marginTop: "16px", marginBottom: "9px", textAlign: "left",
                }}
              >
                <div style={{
                  width: "34px", height: "34px", borderRadius: "9px", flex: "none",
                  background: "rgba(250,244,236,0.14)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#C9A97A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                {/* Both share cards on the same roles (pass 2): title = body 15 bold, sub-line =
                    meta 14, both k 1 so the title stays above its sub-line at every step. */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(15), fontWeight: 900, color: "#FAF4EC" }}>
                    {invitePreparing ? "Preparing…" : "Invite aboard"}
                  </div>
                  {/* Light-on-espresso: the sand stays — #8A5F3A on #2C1A0E would be ~2.6:1 (gold copy sweep, 2026-10-06). */}
                  <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#C9A97A", marginTop: "1px" }}>
                    Add someone to this place’s shared list
                  </div>
                </div>
              </button>
              )}

              {/* Hidden until the referral code is in hand. D8 says every in-app share
                  carries invite or ref and NEVER a naked URL, so with no code there is
                  no honest link to send — the verb withholds itself rather than
                  degrading into the ambiguous bare link the whole spec is about. */}
              {referralCode && (
                <button
                  onClick={handleAppShare}
                  style={{
                    display: "flex", alignItems: "center", gap: "12px", width: "100%",
                    background: "none", border: "1.5px solid rgba(44,26,14,0.10)",
                    borderRadius: "13px", padding: "12px 16px", cursor: "pointer",
                    marginBottom: "4px", textAlign: "left",
                  }}
                >
                  <div style={{
                    width: "30px", height: "30px", borderRadius: "8px", flex: "none",
                    background: "rgba(44,26,14,0.06)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#A0724A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(15), fontWeight: 700, color: "#2C1A0E" }}>
                      Share the app
                    </div>
                    <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#8A5F3A", marginTop: "1px" }}>
                      Recommend the app — not this place
                    </div>
                  </div>
                </button>
              )}

              {/* Leaving is a membership action (you removing yourself) → it lives in
                  the membership zone, for non-creators. Delete (the entity action) is
                  creator-only and lives inside Edit household (spec D3/D4). A5: not
                  until the members are loaded — "not the creator" is unknowable off
                  an empty list. */}
              {membersLoaded && !isHouseholdCreator && (
                <button
                  onClick={() => handleLeaveHousehold()}
                  style={{
                    width: "100%", fontFamily: "'Lato', sans-serif", fontSize: "0.78rem",
                    letterSpacing: "0.5px", padding: "10px",
                    background: "none", color: "#b08968", border: "none",
                    cursor: "pointer", marginTop: "10px",
                  }}
                >Leave place</button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* First-run welcome sheet (SPEC_solo_start_experience.md D1–D5).
          Structure follows mockup_solo_start_v2.html, which is the tiebreaker over the
          spec prose: when the code panel is expanded it sits ABOVE Continue/Skip and
          replaces the divider + toggle, rather than appearing below them.
          No backdrop onClick — this sheet is dismissed by Continue or Skip, never by a
          stray tap. Skip is right there and costs nothing; an accidental dismissal would
          silently spend the one time this sheet ever fires. */}
      {/* D3 (SPEC_auth_state_ui_gating.md) — session loss is a designed moment.
          The session went away with no reload: Clerk reports signed out after we
          were live, or getToken came back null LOST_STREAK ticks running. By the
          time this renders everything has stopped (§Sign-out reset in
          useProvisions, mirrored by the household context); under it is the
          signed-out rendering. Not dismissible — there is nothing to go back to,
          and the one way forward is right here. A deliberate Sign out never
          reaches this (markDeliberateSignOut). */}
      {authPhase === "session_lost" && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(20,12,6,0.55)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
            zIndex: 1200, animation: "fadeIn 0.2s ease",
          }}
        >
          <div
            style={{
              background: "#FAF4EC", borderRadius: "22px 22px 0 0", padding: "8px 0 0",
              width: "min(440px, 100vw)", boxShadow: "0 -12px 40px rgba(0,0,0,0.4)",
            }}
          >
            <div style={{
              width: "36px", height: "4px", borderRadius: "2px",
              background: "rgba(44,26,14,0.18)", margin: "0 auto 14px",
            }} />
            <div style={{ padding: "4px 22px 30px", textAlign: "center" }}>
              <div style={{
                fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: "20px",
                lineHeight: 1.3, color: "#2C1A0E", marginBottom: "8px",
              }}>You’ve been signed out</div>
              <div style={{
                fontFamily: "'Lato', sans-serif", fontSize: "13px", color: "#6f5a45",
                lineHeight: 1.5, marginBottom: "22px",
              }}>Sign in to pick up where you left off.</div>
              {/* Not a SignInButton: see onSheetSignIn — the modal is a no-op while
                  clerk-js still holds a session, so the button decides what "sign in"
                  means here (re-activate, else reload, else the modal). */}
              <button
                type="button"
                onClick={onSheetSignIn}
                style={{
                  width: "100%", padding: "14px", background: "#2C1A0E", border: "none",
                  borderRadius: "11px", fontFamily: "'Lato', sans-serif", fontSize: "14.5px",
                  fontWeight: 900, letterSpacing: "0.02em", color: "#FAF4EC", cursor: "pointer",
                }}
              >Sign In</button>
            </div>
          </div>
        </div>
      )}

      {welcomeOpen && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(20,12,6,0.55)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
            zIndex: 1200, animation: "fadeIn 0.2s ease",
          }}
        >
          <div
            style={{
              background: "#FAF4EC", borderRadius: "22px 22px 0 0", padding: "8px 0 0",
              width: "min(440px, 100vw)", maxHeight: "94vh", overflowY: "auto",
              boxShadow: "0 -12px 40px rgba(0,0,0,0.4)",
            }}
          >
            <div style={{
              width: "36px", height: "4px", borderRadius: "2px",
              background: "rgba(44,26,14,0.18)", margin: "0 auto 14px",
            }} />

            <div style={{ padding: "4px 22px 26px" }}>
              {/* Arch crest — the splash arch, quoted small. */}
              <div style={{ textAlign: "center", margin: "4px 0 10px" }}>
                <svg width="64" height="20" viewBox="0 0 64 20" fill="none" aria-hidden="true">
                  <path d="M4 18 C 14 2, 50 2, 60 18" stroke="#C9A97A" strokeWidth="2.4" strokeLinecap="round" opacity="0.85" />
                </svg>
              </div>

              <div style={{
                fontFamily: "'Lato', sans-serif", fontSize: "10px", fontWeight: 900,
                letterSpacing: "0.16em", textTransform: "uppercase", color: "#8A5F3A",
                marginBottom: "8px", textAlign: "center",
              }}>Welcome aboard</div>

              <div style={{
                fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: "22px",
                lineHeight: 1.3, color: "#2C1A0E", marginBottom: "6px", textAlign: "center",
              }}>What should we call your first place?</div>

              {/* The reassurance is load-bearing, not decoration: it defuses naming
                  paralysis, which is what makes a naming gate safe to put here at all. */}
              <div style={{
                fontFamily: "'Lato', sans-serif", fontSize: "13px", color: "#6f5a45",
                lineHeight: 1.5, marginBottom: "20px", textAlign: "center",
              }}>Don’t worry — you can rename it or add more places later.</div>

              {/* Fixed copy, no code value; NOT on CHROME_ALLOW_LIST (masked on prod like
                  the rest of the sheet). Shown once, when bootstrap's join retry failed. */}
              {welcomeInviteFailed && (
                <div style={{
                  fontFamily: "'Lato', sans-serif", fontSize: "13px", color: "#B3261E",
                  lineHeight: 1.5, margin: "-8px 0 20px", textAlign: "center",
                }}>That invite link didn’t work. Ask for a fresh one, or start here.</div>
              )}

              <div style={{
                fontFamily: "'Lato', sans-serif", fontSize: "11.5px", fontWeight: 700,
                color: "#2C1A0E", marginBottom: "7px",
              }}>Place name</div>
              <input
                value={welcomeName}
                onChange={(e) => setWelcomeName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && welcomeName.trim()) handleWelcomeContinue(); }}
                placeholder="Home, The Smiths, Lake house…"
                autoFocus
                style={{
                  width: "100%", padding: "13px 14px", borderRadius: "10px",
                  border: "1.5px solid #A0724A", fontFamily: "'Lato', sans-serif",
                  fontSize: "15px", background: "#fff", color: "#2C1A0E", marginBottom: "4px",
                }}
              />

              {welcomeCodeOpen ? (
                <div style={{
                  marginTop: "14px", padding: "16px", background: "rgba(13,148,136,0.06)",
                  border: "1.5px dashed #0D9488", borderRadius: "12px",
                }}>
                  <div style={{
                    fontFamily: "'Lato', sans-serif", fontSize: "11.5px", fontWeight: 700,
                    color: "#2C1A0E", marginBottom: "9px",
                  }}>Got a link or code from someone? Join their place instead:</div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      value={welcomeCode}
                      onChange={(e) => { setWelcomeCode(e.target.value); setWelcomeError(null); }}
                      onKeyDown={(e) => { if (e.key === "Enter") handleWelcomeJoin(); }}
                      placeholder="Invite code"
                      style={{
                        flex: 1, minWidth: 0, padding: "12px 13px", borderRadius: "9px",
                        border: "1.5px solid #0D9488", fontFamily: "'Lato', sans-serif",
                        fontSize: "14px", background: "#fff", color: "#2C1A0E",
                        textTransform: "uppercase", letterSpacing: "0.06em",
                      }}
                    />
                    <button
                      onClick={handleWelcomeJoin}
                      disabled={welcomeBusy}
                      style={{
                        flex: "none", padding: "12px 18px", background: welcomeBusy ? "#6ba3a0" : "#0D9488",
                        border: "none", borderRadius: "9px", fontFamily: "'Lato', sans-serif",
                        fontSize: "13px", fontWeight: 900, color: "#fff",
                        cursor: welcomeBusy ? "default" : "pointer",
                      }}
                    >{welcomeBusy ? "Joining…" : "Join"}</button>
                  </div>
                  {welcomeError && (
                    <div style={{
                      fontFamily: "'Lato', sans-serif", fontSize: "12px", color: "#B3261E",
                      marginTop: "9px", lineHeight: 1.4,
                    }}>{welcomeError}</div>
                  )}
                </div>
              ) : (
                <div style={{ fontFamily: "'Lato', sans-serif", fontSize: "11px", color: "#8a7968", marginBottom: "22px" }}>&nbsp;</div>
              )}

              <div style={{ marginTop: welcomeCodeOpen ? "20px" : 0 }}>
                <button
                  onClick={handleWelcomeContinue}
                  disabled={!welcomeName.trim() || welcomeBusy}
                  style={{
                    width: "100%", padding: "14px", background: "#2C1A0E", border: "none",
                    borderRadius: "11px", fontFamily: "'Lato', sans-serif", fontSize: "14.5px",
                    fontWeight: 900, letterSpacing: "0.02em", color: "#FAF4EC",
                    marginBottom: "14px",
                    // D4 — Skip IS the no-name path. An enabled empty Continue would be a
                    // second Skip wearing a misleading label, so it is visibly inert.
                    opacity: welcomeName.trim() && !welcomeBusy ? 1 : 0.4,
                    cursor: welcomeName.trim() && !welcomeBusy ? "pointer" : "default",
                  }}
                >Continue</button>
                <button
                  onClick={handleWelcomeSkip}
                  disabled={welcomeBusy}
                  style={{
                    width: "100%", background: "none", border: "none", textAlign: "center",
                    fontFamily: "'Lato', sans-serif", fontSize: "12.5px", fontWeight: 700,
                    color: "#8a7968", cursor: welcomeBusy ? "default" : "pointer",
                    padding: "4px", marginBottom: "4px",
                  }}
                >I'll do this later</button>
              </div>

              {!welcomeCodeOpen && (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "18px 0 16px" }}>
                    <div style={{ flex: 1, height: "1px", background: "rgba(44,26,14,0.10)" }} />
                    <div style={{
                      fontFamily: "'Lato', sans-serif", fontSize: "10.5px", fontWeight: 900,
                      letterSpacing: "0.1em", textTransform: "uppercase", color: "#b5a48d",
                    }}>or</div>
                    <div style={{ flex: 1, height: "1px", background: "rgba(44,26,14,0.10)" }} />
                  </div>
                  <button
                    onClick={() => setWelcomeCodeOpen(true)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                      width: "100%", background: "none", border: "none",
                      fontFamily: "'Lato', sans-serif", fontSize: "13px", fontWeight: 700,
                      color: "#0A776D", cursor: "pointer", padding: "6px",
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0D9488" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                      <path d="M9 12h6M9 16h6M9 8h2" />
                      <rect x="4" y="4" width="16" height="16" rx="3" />
                    </svg>
                    Already have an invite? Enter it here
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit household sheet (OurBanner) — photo framing + wordmark + name, then
          the creator-only Delete danger zone. One Save commits everything. */}
      {showEditHousehold && (
        <div
          onClick={closeEditHousehold}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
            zIndex: 1100, animation: "fadeIn 0.2s ease",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#FAF4EC", borderRadius: "18px 18px 0 0", padding: 0,
              width: "min(440px, 100vw)", maxHeight: "94vh", overflowY: "auto",
              boxShadow: "0 -12px 40px rgba(0,0,0,0.4)",
              display: "flex", flexDirection: "column",
            }}
          >
            {/* Hidden file input MUST live inside this stopPropagation container.
                edFileInputRef.current.click() dispatches a synthetic click on the
                input that bubbles to the ancestor with onClick; if the input sat
                directly under the backdrop, that bubbled click would fire
                closeEditHousehold and unmount the sheet mid-pick — the upload path
                (and its errors) would never run. */}
            <input
              ref={edFileInputRef}
              type="file"
              accept="image/*"
              onClick={(e) => e.stopPropagation()}
              onChange={onEdPickFile}
              style={{ display: "none" }}
            />
            {/* Header: Cancel · title · Save */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px 10px" }}>
              <button onClick={closeEditHousehold} style={{ background: "none", border: "none", color: "#8A5F3A", fontFamily: "'Lato', sans-serif", fontSize: "0.9rem", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.05rem", color: "#2C1A0E" }}>Edit place</div>
              <button
                onClick={saveEditHousehold}
                disabled={edSaving}
                style={{ background: edSaving ? "#6ba3a0" : "#2f7d7a", border: "none", color: "#FAF4EC", fontFamily: "'Lato', sans-serif", fontSize: "0.9rem", fontWeight: 900, borderRadius: "10px", padding: "8px 16px", cursor: edSaving ? "default" : "pointer" }}
              >{edSaving ? "Saving…" : "Save"}</button>
            </div>

            <div style={{ padding: "4px 18px 20px" }}>
              {/* Live preview — the control (spec D7). Drag to reposition when a
                  photo exists; empty state shows the real espresso header. */}
              <div
                className="op-replay-block"
                onPointerDown={onEdDragStart}
                onPointerMove={onEdDragMove}
                onPointerUp={onEdDragEnd}
                onPointerCancel={onEdDragEnd}
                style={{
                  position: "relative", height: "130px", borderRadius: "14px", overflow: "hidden",
                  background: "#2C1A0E",
                  backgroundImage: edHasPhoto && edPreviewUrl ? `url("${edPreviewUrl}")` : "none",
                  backgroundSize: `${edZoom}% auto`,
                  backgroundPosition: `${edX}% ${edY}%`,
                  backgroundRepeat: "no-repeat",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: edHasPhoto ? "grab" : "default",
                  touchAction: "none", userSelect: "none",
                }}
              >
                {edHasPhoto && (
                  <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: BAND_SCRIM }} />
                )}
                {/* Empty state renders the real espresso header → wordmark large,
                    ignoring the dormant choice (spec D3/D8). With a photo it obeys
                    the draft banner_wordmark. */}
                {(() => {
                  const pw = edHasPhoto ? edWordmark : "large";
                  if (pw === "hidden") return null;
                  return (
                    <div style={{
                      position: "relative",
                      fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 700,
                      color: "#FAF4EC",
                      fontSize: pw === "small" ? "18px" : "26px",
                      opacity: pw === "small" ? 0.8 : 1,
                      textShadow: edHasPhoto ? "0 2px 14px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.7)" : "none",
                      transform: edHasPhoto ? "translateY(-5%)" : "none",
                      pointerEvents: "none",
                    }}>OurProvisions</div>
                  );
                })()}
              </div>

              {edHasPhoto ? (
                <div style={{ fontSize: "0.7rem", color: "#9a8a78", textAlign: "center", margin: "8px 0 0" }}>
                  Drag to reposition
                </div>
              ) : (
                <button
                  onClick={() => edFileInputRef.current && edFileInputRef.current.click()}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "9px", width: "100%",
                    background: "#2C1A0E", color: "#FAF4EC", border: "none", borderRadius: "14px", padding: "14px",
                    fontFamily: "'Lato', sans-serif", fontSize: "0.9rem", fontWeight: 900, cursor: "pointer", marginTop: "14px",
                  }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#FAF4EC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h4l2-2h6l2 2h4v12H3z"/><circle cx="12" cy="13" r="3.5"/></svg>
                  Choose a photo
                </button>
              )}

              {/* Photo controls — only when a photo exists (spec D3) */}
              {edHasPhoto && (
                <>
                  <div style={{ fontFamily: "'Lato', sans-serif", fontSize: "12.5px", fontWeight: 900, letterSpacing: "1.5px", textTransform: "uppercase", color: "#8A5F3A", margin: "18px 0 8px" }}>Zoom</div>
                  <input
                    type="range" min="100" max="320" value={edZoom}
                    onChange={(e) => setEdZoom(Number(e.target.value))}
                    style={{ width: "100%" }}
                  />

                  <div style={{ display: "flex", gap: "10px", marginTop: "14px" }}>
                    <button
                      onClick={() => edFileInputRef.current && edFileInputRef.current.click()}
                      style={{ flex: 1, background: "#FBF7F0", border: "1px solid rgba(44,26,14,0.10)", borderRadius: "12px", padding: "12px", fontFamily: "'Lato', sans-serif", fontSize: "0.82rem", fontWeight: 700, color: "#2C1A0E", cursor: "pointer" }}
                    >Replace photo</button>
                    <button
                      onClick={onEdRemovePhoto}
                      style={{ flex: 1, background: "#FBF7F0", border: "1px solid rgba(44,26,14,0.10)", borderRadius: "12px", padding: "12px", fontFamily: "'Lato', sans-serif", fontSize: "0.82rem", fontWeight: 700, color: "#2C1A0E", cursor: "pointer" }}
                    >Remove photo</button>
                  </div>

                  <div style={{ fontFamily: "'Lato', sans-serif", fontSize: "12.5px", fontWeight: 900, letterSpacing: "1.5px", textTransform: "uppercase", color: "#8A5F3A", margin: "18px 0 8px" }}>Wordmark</div>
                  <div style={{ display: "flex", background: "#FBF7F0", border: "1px solid rgba(44,26,14,0.10)", borderRadius: "12px", overflow: "hidden" }}>
                    {["large", "small", "hidden"].map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setEdWordmark(opt)}
                        style={{
                          flex: 1, background: edWordmark === opt ? "#2C1A0E" : "none", border: "none",
                          padding: "11px", fontFamily: "'Lato', sans-serif", fontSize: "0.8rem", fontWeight: 700,
                          color: edWordmark === opt ? "#FAF4EC" : "#8a7660", cursor: "pointer",
                          textTransform: "capitalize",
                        }}
                      >{opt}</button>
                    ))}
                  </div>
                </>
              )}

              {/* Household name */}
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: "12.5px", fontWeight: 900, letterSpacing: "1.5px", textTransform: "uppercase", color: "#8A5F3A", margin: "18px 0 8px" }}>Place name</div>
              <input
                value={edName}
                onChange={(e) => setEdName(e.target.value)}
                style={{ width: "100%", background: "#FBF7F0", border: "1px solid rgba(44,26,14,0.10)", borderRadius: "12px", padding: "13px 14px", fontFamily: "'Lato', sans-serif", fontSize: "0.95rem", color: "#2C1A0E", boxSizing: "border-box" }}
              />

              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.72rem", color: "#9a8a78", lineHeight: 1.5, margin: "12px 2px 0" }}>
                Anyone in the place can change the photo, the name, and the wordmark.
              </div>

              {/* Creator-only Delete danger zone (spec D3/D4) */}
              {isHouseholdCreator && (
                <div style={{ marginTop: "22px", paddingTop: "16px", borderTop: "1px solid rgba(44,26,14,0.10)" }}>
                  {!edDeleteConfirm ? (
                    <button
                      onClick={() => setEdDeleteConfirm(true)}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "9px", width: "100%",
                        background: "none", border: "1.5px solid rgba(179,38,30,0.4)", borderRadius: "14px", padding: "14px",
                        color: "#c0392b", fontFamily: "'Lato', sans-serif", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer",
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="1.8" strokeLinecap="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/></svg>
                      Delete place
                    </button>
                  ) : (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => setEdDeleteConfirm(false)}
                        style={{ flex: 1, background: "#E8D5B7", border: "none", borderRadius: "12px", padding: "13px", fontFamily: "'Lato', sans-serif", fontSize: "0.82rem", color: "#2C1A0E", cursor: "pointer" }}
                      >Cancel</button>
                      <button
                        onClick={handleDeleteHousehold}
                        style={{ flex: 2, background: "#c0392b", border: "none", borderRadius: "12px", padding: "13px", fontFamily: "'Lato', sans-serif", fontSize: "0.82rem", fontWeight: 700, color: "#fff", cursor: "pointer" }}
                      >Yes, delete place</button>
                    </div>
                  )}
                  <div style={{ fontSize: "0.7rem", color: "#9a8a78", textAlign: "center", marginTop: "8px", lineHeight: 1.4 }}>
                    Deletes {household?.name || "this place"} for everyone aboard. This can't be undone.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast notification — now rendered in the bottom status stack above. */}

      {/* Join success banner */}
      {joinBanner && (
        <div style={{
          position: "relative", zIndex: 1,
          background: "#4a9e4a", color: "white", padding: "12px 20px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontFamily: "'Lato', sans-serif", fontSize: "0.85rem",
        }}>
          <span>🎉 You joined <strong>{joinBanner}</strong>! Your list is now shared.</span>
          <button onClick={() => setJoinBanner(null)} style={{
            background: "none", border: "none", color: "white", fontSize: "1.1rem", cursor: "pointer",
          }}>×</button>
        </div>
      )}

      {/* Invite is a system-share hand-off (handleInviteShare) — no in-app share
          UI. The old self-rendered panel + "Copy link instead" are removed. */}

      </div>{/* /OurBanner region wrapper */}

      {/* The budget banner renders on every door EXCEPT Home (SPEC_home_v1_essentials D1,
          2026-10-03): Home's own budget line replaces it there. And never while the welcome
          is up (signedOutWelcome — signed out, session lost, or booting): the welcome shows
          only the sign-in pair, and `view` keeps the hash's door (#/shop) while signed out,
          so the Home guard alone does not cover it. Every other view is unchanged. */}
      {!signedOutWelcome && view !== "home" && showPrices && totalItems > 0 && (
        <div className={`budget-banner ${overBudget ? "over" : ""}`}>
          <div style={{ flex: 1 }}>
            <div className="budget-label">Estimated Total</div>
            <div className={`budget-amount ${overBudget ? "over" : ""}`}>{hasEstimatedPrices ? "~" : ""}${totalCost.toFixed(2)}</div>
            <div className="budget-items">{totalItems} item{totalItems !== 1 ? "s" : ""} selected</div>
            {budgetNum !== null && (
              <div className="budget-bar-wrap">
                <div
                  className="budget-bar-fill"
                  style={{
                    width: `${budgetPct}%`,
                    background: overBudget ? "#e05c5c" : budgetPct > 85 ? "#C9A97A" : "#A0724A"
                  }}
                />
              </div>
            )}
          </div>
          <div className="budget-goal-section">
            {budgetNum !== null ? (
              <>
                <div className="budget-goal-label">Budget</div>
                <div className={`budget-goal-remaining ${overBudget ? "over" : "ok"}`}>
                  {overBudget
                    ? `−$${Math.abs(budgetRemaining).toFixed(2)} over`
                    : `$${budgetRemaining.toFixed(2)} left`}
                </div>
                <div style={{ marginTop: "4px" }}>
                  <button className="set-budget-btn" onClick={openBudgetModal}>
                    ${budgetNum.toFixed(0)} goal ✎
                  </button>
                </div>
              </>
            ) : (
              <button className="set-budget-btn" onClick={openBudgetModal}>+ Set Budget</button>
            )}
          </div>
        </div>
      )}

      {!signedOutWelcome && view !== "home" && showPrices && totalItems === 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 20px", background: "#2C1A0E", borderBottom: "2px solid #c8973a" }}>
          <button className="set-budget-btn" onClick={openBudgetModal}>
            {budgetNum !== null ? `Budget: $${budgetNum.toFixed(0)} ✎` : "+ Set Budget"}
          </button>
        </div>
      )}

      <div className="container">
        {signedOutWelcome && (pendingGiftCode
          ? <GiftCard code={pendingGiftCode} ready={isLoaded} onAddToMeals={() => openSignUp({ initialValues: signUpInitialValues })} onSignIn={() => openSignIn()} />
          : <HomeWelcome signUpInitialValues={signUpInitialValues} ready={isLoaded} />)}

        {view === "home" && !signedOutWelcome && (
          <Home
            firstName={user?.firstName}
            ready={homeReady}
            deck={{
              // The board's inputs, verbatim (D3) — the same props PlanBoard gets.
              boardMeals, boardCards, placements, rows: mealRowCounts, mealById, cookedIds: afterglowIds,
              busyMealId: busyMealId || addingMealId, switching: deckSwitching,
              holdNight: MEALS_ENABLED && isSignedIn,
              onAdd: () => { setPlanScreen("library"); goToDoor("plan"); },
              // The recipe view over Home (SPEC_recipe_view D1/D2): a sheet, no route change —
              // closing it lands back here. openRecipe ignores a held night itself.
              onView: (m) => openRecipe(m, "make"),
              onLockIn: handleAddMealToList,      // the board's Add to Shop — on-hand prompt included
              onCooked: handleCookedMeal,
              onSkip: handleSkipMeal,
              onSwitch: handleSwitchDeck,
              onLeftovers: openLeftoversSheet,
              onOut: () => setNoShopSheet({ kind: "out", name: "", fromMealIds: [] }),
              onOther: () => setNoShopSheet({ kind: "other", name: "", fromMealIds: [] }),
              afterglow: boardAfterglow,   // the same "How was it?" + "Add a meal photo" as the board (no thumbnail on Home)
            }}
            list={{
              openCount: totalItems - checkedCount,
              checkedCount,
              firstItem: pendingItems[0]?.name || "",
              shopping: !!activeSession,
              onStartList: () => goToDoor("input"),
              onShop: () => goToDoor("list"),
            }}
            // D7: only when Shop would show it — prices on, a budget set, a non-empty list.
            budget={showPrices && budgetNum !== null && totalItems > 0
              ? { totalCost, budgetNum, budgetPct, overBudget, budgetRemaining, hasEstimatedPrices }
              : null}
          />
        )}

        {view === "plan" && planScreen === "board" && !signedOutWelcome && (
          <>
            {/* THE BOARD (v2) under the title switch (SPEC_plan_week_meals_switch):
                "This Week" active, "Meals" one tap away, the subtitle carrying the
                count AND the batch action ("4 nights · 2 meals to add to Shop →" —
                see boardSubtitle), and ONE control top-right — the add button
                (--op-add: deep sand = adding things) into the library. The switch
                row is the door's control row, so it is the compact sentinel
                (controlRowRef): when it scrolls off, the Helm compacts and its +
                does the door's add — here, open the library (doorAdd.plan). Under
                the row, a banner when the week is set or stocked, else (2+ cards)
                the drag hint. No bar, no teal until the household has finished
                something. Three states (PATCH_plan_tab_welcome): until this
                household's meals and placements have loaded, the switch alone;
                loaded with nothing on the board, the welcome — switch, no
                subtitle, no add button, no foot; otherwise the working board. */}
            <PlanSwitch
              screen="board"
              onSwitch={setPlanScreen}
              rowRef={controlRowRef}
              className={`board${showDragHint ? " with-hint" : ""}`}
              subtitle={boardReady && !showWelcome ? (
                <>
                  {boardSubtitle.head}{boardSubtitle.tail}
                  {boardSubtitle.link > 0 && (isSignedIn ? (
                    <button type="button" className="plan-addall" disabled={lockingAll} onClick={handleLockInAll}>
                      {lockingAll ? "adding…" : "add to Shop →"}
                    </button>
                  ) : "add to Shop")}
                </>
              ) : null}
              action={boardReady && !showWelcome ? (
                <HeaderAction verb="add" label="Add a meal to this week" onClick={() => setPlanScreen("library")} />
              ) : null}
            />
            {!boardReady ? null : showWelcome ? (
              <PlanWelcome firstMeal={!everCooked} onAdd={() => setPlanScreen("library")}>
                {MEALS_ENABLED && isSignedIn && (
                  <HoldANightButtons
                    label="OR HOLD A NIGHT"
                    onLeftovers={openLeftoversSheet}
                    onOut={() => setNoShopSheet({ kind: "out", name: "", fromMealIds: [] })}
                    onOther={() => setNoShopSheet({ kind: "other", name: "", fromMealIds: [] })}
                  />
                )}
              </PlanWelcome>
            ) : (
              <>
                {boardBanner === "stocked" ? (
                  <div className="plan-banner stocked" role="status">
                    <span className="plan-banner-check" aria-hidden="true">✓</span>
                    <div>
                      <div className="plan-banner-title">Everything's in. Go cook.</div>
                      <div className="plan-banner-sub">{boardStats.cards.length} meal{boardStats.cards.length === 1 ? "" : "s"} stocked and ready.</div>
                    </div>
                  </div>
                ) : boardBanner === "set" ? (
                  <div className="plan-banner set" role="status">
                    <span className="plan-banner-check" aria-hidden="true">✓</span>
                    <div>
                      <div className="plan-banner-title">All set for the week</div>
                      <div className="plan-banner-sub">Everything you need is on your list.</div>
                    </div>
                  </div>
                ) : showDragHint ? (
                  <div className="plan-prompt">Drag meals into the order you want them.</div>
                ) : null}
                <PlanBoard
                  meals={boardCards}
                  rows={mealRowCounts}
                  placements={placements}
                  mealById={mealById}
                  cookedIds={afterglowIds}
                  onOpen={(m) => openRecipe(m, "make")}
                  onSkip={handleSkipMeal}
                  onCooked={handleCookedMeal}
                  onLockIn={handleAddMealToList}
                  onSeeList={() => goToDoor("list")}
                  onReorder={reorderBoard}
                  busyMealId={busyMealId || addingMealId}
                  afterglow={boardAfterglow}
                />
                {MEALS_ENABLED && isSignedIn && (
                  <div className="plan-foot">
                    <HoldANightButtons
                      label="HOLD A NIGHT"
                      onLeftovers={openLeftoversSheet}
                      onOut={() => setNoShopSheet({ kind: "out", name: "", fromMealIds: [] })}
                      onOther={() => setNoShopSheet({ kind: "other", name: "", fromMealIds: [] })}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}

        {view === "plan" && planScreen === "library" && !signedOutWelcome && (
          <>
            {/* THE LIBRARY under the title switch (SPEC_plan_week_meals_switch):
                "Meals" active, "This Week" one tap away — the switch is the route
                back (the v1.1 week line is gone, D8); no back arrow (location is the
                Helm's, PLAN lit). + New (D4) opens the New Meal sheet; Plan on a card
                never leaves Meals (D6) — add two or three, then tap This Week. The subtitle
                is "What sounds good? · {N} meals" (D3) — the invitation plus the
                library's size, N = the whole library (kind = 'meal'), never the
                filtered result. The create button opens the existing New Meal
                sheet unchanged (signed in only: creating a meal is an
                identity-requiring write). The switch row is the Helm's compact
                sentinel (controlRowRef). */}
            <PlanSwitch
              screen="library"
              onSwitch={setPlanScreen}
              rowRef={controlRowRef}
              className="meals"
              subtitle={<>What sounds good? · {libraryMeals.length} {libraryMeals.length === 1 ? "meal" : "meals"}</>}
              action={MEALS_ENABLED && isSignedIn ? (
                <HeaderAction verb="new" label="Create a new meal" onClick={() => setMealSheet({ mode: "create", meal: null })} />
              ) : null}
            />
            <MealsLens
              textSizeIdx={textSizeIdx}
              onSetOccasion={handleSetOccasion}
              meals={libraryCards}
              loading={mealsLoading}
              onPlan={handlePlanMeal}
              onUnplan={handleUnplanMeal}
              planningMealId={planningMealId}
              onCreate={() => setMealSheet({ mode: "create", meal: null })}
              isSignedIn={isSignedIn}
              onOpen={(m) => openRecipe(m, "top")}
              stages={libraryStages}
              madeBefore={madeBefore}
              householdId={household?.id}
              members={householdMembers}
            />
          </>
        )}

        {view === "input" && !signedOutWelcome && (
          <>
            <>
            {/* ── Search bar — sticky at top ── */}
            {/* Vertical scale (4px grid): banner→search 20px = .container's 24px
                top padding, less 12px here, plus 8px of own padding — the 8px is
                kept so the bar isn't flush to the viewport edge once it sticks.
                search→divider 16px, divider→rail 12px. */}
            <div style={{
              marginTop: "-12px",
              padding: "8px 0 16px",
              background: "#FAF4EC",
              position: "sticky",
              top: 0,
              zIndex: 10,
              borderBottom: "1px solid #E8D5B7",
              marginBottom: "12px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CatalogSearchBox
                  value={searchQuery}
                  onChange={(v) => { setSearchQuery(v); setSearchPickerOpen(false); }}
                  onClear={() => { setSearchQuery(""); setSearchPickerOpen(false); }}
                />
                <CycleIcon phase={browsePhase} onAdvance={() => setBrowsePhase((browsePhase + 1) % 3)} />
              </div>
            </div>

            {/* ── Category rail — single scrolling row; hidden when decluttered (phase 1/2) ── */}
            {/* Bottom gap is 8px when the descriptor follows (4px rail padding +
                4px here) and 28px when the slot collapses, so the space below the
                rail is stable whether the descriptor is present or not. */}
            {browsePhase === 0 && (
            <div
              className={`cat-rail-wrap${railEdges.atStart ? " at-start" : ""}${railEdges.atEnd ? " at-end" : ""}`}
              style={{ marginBottom: browseFilterDescriptor ? "4px" : "24px" }}
            >
              <div className="cat-rail" ref={catRailNode}>
                {/* Staples — cross-cutting filter */}
                <button
                  className={`cat-pill${stapleFilter ? " on" : ""}`}
                  data-active={stapleFilter ? "1" : undefined}
                  onClick={() => setStapleFilter(f => !f)}
                >
                  <span className="cat-em">{categoryGlyph("Staples")}</span>Staples
                </button>

                {/* Category pills — toggle to filter */}
                {categories.map(cat => {
                  const isActive = selectedCategories.has(cat.rawName);
                  return (
                    <button
                      key={cat.rawName}
                      className={`cat-pill${isActive ? " on" : ""}`}
                      data-active={isActive ? "1" : undefined}
                      onClick={() => {
                        setSelectedCategories(prev => {
                          const next = new Set(prev);
                          if (next.has(cat.rawName)) {
                            next.delete(cat.rawName);
                          } else {
                            next.add(cat.rawName);
                          }
                          return next;
                        });
                      }}
                    >
                      <span className="cat-em">{categoryGlyph(cat.rawName)}</span>
                      {categoryRailLabel(cat.rawName)}
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            {/* ── Descriptor — ONE slot, shared with the declutter cycle.
                 When categories are filtering, the filter line REPLACES the cycle
                 line entirely; otherwise the cycle line is untouched. Never both. */}
            {/* D9′ — the bottom edge of Browse's search + chips block. The search
                bar above is sticky and never leaves; this sentinel scrolls with the
                rail, so the pill compacts exactly when the chips have gone. */}
            <div ref={controlRowRef} className="control-row-end" aria-hidden="true" />
            {browseFilterDescriptor ? (
              <div className="declutter-desc" style={{ margin: "0 0 28px" }}>
                {/* ONE verb in every phase. "Filtering Produce" was backwards —
                    Produce is not what's filtered out, it's what survives. And the
                    user's actual question is never "are filters on," it's "where
                    did Bacon go": someone scanning A–Z with the rail hidden needs
                    to know something was withheld. The sentence's presence signals
                    that filters are on; "only" states the exclusion in one word,
                    with no second clause. */}
                Showing only{" "}
                {browseFilterDescriptor.names.length === 1 ? (
                  <b>{browseFilterDescriptor.names[0]}</b>
                ) : browseFilterDescriptor.names.length === 2 ? (
                  <><b>{browseFilterDescriptor.names[0]}</b> and <b>{browseFilterDescriptor.names[1]}</b></>
                ) : (
                  <>
                    <b>{browseFilterDescriptor.names[0]}</b>, <b>{browseFilterDescriptor.names[1]}</b>
                    {" "}and {browseFilterDescriptor.names.length - 2} more
                  </>
                )}
                {" "}— {browseFilterDescriptor.count} item{browseFilterDescriptor.count === 1 ? "" : "s"}
              </div>
            ) : browsePhase !== 0 ? (
              <div className="declutter-desc" style={{ margin: "0 0 28px" }}>
                {browseFilterCount > 0
                  ? `${browseFilterCount} filter${browseFilterCount === 1 ? "" : "s"} active · filters hidden`
                  : "filters hidden"}
              </div>
            ) : null}

            {/* ── Catalog body ── */}
            {catalogLoading ? (
              <div style={{ textAlign: "center", padding: "40px 20px", fontFamily: "'Lato', sans-serif", fontSize: "0.85rem", color: "#8a7a60", letterSpacing: "1px" }}>
                Loading your catalog…
              </div>
            ) : searchResults !== null ? (
              /* ── SEARCH MODE — shared with the Shop Add sheet (CatalogSearchBox / SearchResultsList) ── */
              <div>
                <SearchResultsList
                  query={searchQuery}
                  results={searchResults}
                  hiddenMatch={hiddenMatch}
                  onReveal={(m) => {
                    if (m.live) { unhideItem(m.item.id); setSearchQuery(""); }
                    else addSearchedItem(m.item.category);   // un-hide + add at 1 — the F1b branch
                  }}
                  listClassName="items-grid"
                  renderRow={(item) => {
                        const qty = qtyFor(item);
                        const rawFallback = categoryAvgPrices[item.rawCategory] || 3.00;
                        const price = prices[item.name] || (Math.round(rawFallback * 2) / 2);
                        const isEditing = editingPrice === item.name;
                        const isStaple = catalogMap[item.name]?.is_staple;
                        const isCustom = catalogMap[item.name]?.is_global === false;
                        // Catalog items only expose price editing — nothing to edit when pricing is off.
                        const canEdit = isCustom || showPrices;
                        return (
                          <SwipeToRemove key={item.name} onRemove={() => hideItem(item.name)} onEdit={() => openEditModal(item.name)} onStaple={() => toggleStaple(item.name)} isStaple={isStaple} canEdit={canEdit}>
                            <CatalogItemRow
                              item={item}
                              qty={qty}
                              rawCategory={item.rawCategory}
                              showPrices={showPrices}
                              price={price}
                              isEditing={isEditing}
                              priceInput={priceInput}
                              centsToDisplay={centsToDisplay}
                              onUpdateQty={updateQty}
                              onPriceInput={handlePriceInput}
                              onCommitPrice={commitPrice}
                              onCancelEditPrice={() => setEditingPrice(null)}
                            />
                          </SwipeToRemove>
                        );
                  }}
                  createSlot={
                  /* ── CREATE on Browse: inline add with category picker ── */
                  <>
                    <div style={{
                      borderRadius: "10px",
                      border: "1.5px dashed #C9A97A",
                      background: "rgba(201,169,122,0.06)",
                      overflow: "hidden",
                      marginBottom: "6px",
                    }}>
                      {!searchPickerOpen ? (
                        <div
                          onClick={() => setSearchPickerOpen(true)}
                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", cursor: "pointer" }}
                        >
                          {/* On the knob (pass 2): line = body 15, hint = meta 14 #8A5F3A, Add =
                              button k .8 — the same roles the meal sheet's copy of this card uses. */}
                          <div>
                            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(15), color: "#8A5F3A" }}>
                              Add <strong>"{searchQuery}"</strong> to your list
                            </div>
                            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#8A5F3A", marginTop: "2px" }}>
                              Tap to choose a category
                            </div>
                          </div>
                          <button className="add-btn" style={{ flexShrink: 0, fontSize: ts(14, 0.8) }}>Add</button>
                        </div>
                      ) : (
                        <div>
                          <div
                            onClick={() => setSearchPickerOpen(false)}
                            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px 8px", cursor: "pointer" }}
                          >
                            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(15), color: "#8A5F3A" }}>
                              Add <strong>"{searchQuery}"</strong> to your list
                            </div>
                            <span style={{ color: "#8A5F3A", fontSize: "18px" }}>−</span>
                          </div>
                          {/* Global entry — no section context, so nothing is
                              pre-selected and the copy asks rather than confirms. */}
                          <div className="picker-sub" style={{ padding: "0 14px", marginBottom: "10px" }}>
                            Pick a category and we'll file it for next time.
                          </div>
                          <div style={{ padding: "0 14px 10px" }}>
                            <CategoryPickerGrid
                              categories={categories.filter(cat => cat.rawName !== "⭐ My Custom Items")}
                              selected={null}
                              onSelect={addSearchedItem}
                              onNewCategory={() => setSearchNewCatOpen(true)}
                            />
                          </div>
                          {/* ── New category inline input — revealed by the tile ── */}
                          {searchNewCatOpen && (
                          <div style={{ padding: "0 14px 14px" }}>
                            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), letterSpacing: "1.5px", textTransform: "uppercase", color: "#8A5F3A", marginBottom: "7px" }}>
                              Name the new category
                            </div>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                              <input
                                type="text"
                                value={newCategoryInput}
                                onChange={e => setNewCategoryInput(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === "Enter" && newCategoryInput.trim()) {
                                    const newCat = newCategoryInput.trim();
                                    setHouseholdCategories(prev => new Set([...prev, newCat]));
                                    addSearchedItem(newCat);
                                  }
                                }}
                                placeholder="e.g. Pet Supplies"
                                style={{
                                  flex: 1, border: "1.5px solid #E8D5B7", borderRadius: "20px",
                                  padding: "6px 14px", fontFamily: "'Lato', sans-serif", fontSize: "12px",
                                  color: "#2C1A0E", background: "#F5EDE0", outline: "none",
                                }}
                              />
                              <button
                                onClick={() => {
                                  if (!newCategoryInput.trim()) return;
                                  const newCat = newCategoryInput.trim();
                                  setHouseholdCategories(prev => new Set([...prev, newCat]));
                                  addSearchedItem(newCat);
                                }}
                                style={{
                                  width: "30px", height: "30px", borderRadius: "50%",
                                  background: newCategoryInput.trim() ? "#A0724A" : "#E8D5B7",
                                  border: "none", color: "white",
                                  fontSize: "18px", display: "flex", alignItems: "center", justifyContent: "center",
                                  cursor: newCategoryInput.trim() ? "pointer" : "default",
                                  flexShrink: 0, transition: "background 0.15s",
                                }}
                              >+</button>
                            </div>
                          </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                  }
                />
              </div>
            ) : (
              /* ── NORMAL BROWSE MODE ── */
              <>
                {browsePhase === 2 ? (
                  /* ── FLAT A–Z (declutter phase 2) ── */
                  <>
                    <FlatHeader count={browseFlatItems.length} showCount={!browseFilterDescriptor} />
                    <div className="items-grid">
                      {browseFlatItems.map((item) => {
                        const qty = qtyFor(item);
                        const rawFallback = categoryAvgPrices[item.rawCategory] || 3.00;
                        const price = prices[item.name] || (Math.round(rawFallback * 2) / 2);
                        const isEditing = editingPrice === item.name;
                        const isStaple = catalogMap[item.name]?.is_staple;
                        const isCustom = catalogMap[item.name]?.is_global === false;
                        // Catalog items only expose price editing — nothing to edit when pricing is off.
                        const canEdit = isCustom || showPrices;
                        return (
                          <SwipeToRemove key={item.name} onRemove={() => hideItem(item.name)} onEdit={() => openEditModal(item.name)} onStaple={() => toggleStaple(item.name)} isStaple={isStaple} canEdit={canEdit}>
                            <CatalogItemRow
                              item={item}
                              qty={qty}
                              rawCategory={item.rawCategory}
                              showPrices={showPrices}
                              price={price}
                              isEditing={isEditing}
                              priceInput={priceInput}
                              centsToDisplay={centsToDisplay}
                              onUpdateQty={updateQty}
                              onPriceInput={handlePriceInput}
                              onCommitPrice={commitPrice}
                              onCancelEditPrice={() => setEditingPrice(null)}
                            />
                          </SwipeToRemove>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  displayCategories.map((cat) => (
                  <div className="category-block" key={cat.name}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "2px solid #E8D5B7", paddingBottom: "8px", marginBottom: "12px" }}>
                      <div className="cat-title" style={{ border: "none", padding: 0, margin: 0 }}>{cat.name}</div>
                      <button
                        onClick={() => {
                          setNewItemCategory(cat.rawName);
                          setShowAddModal(true);
                        }}
                        style={{
                          background: "none", border: "none", fontFamily: "'Lato', sans-serif",
                          fontSize: "12.5px", color: "#8A5F3A", cursor: "pointer",
                          letterSpacing: "0.5px", padding: "0",
                        }}
                      >＋ New item</button>
                    </div>
                    <div className="items-grid">
                      {cat.items.map((item) => {
                        const qty = qtyFor(item);
                        const rawFallback = categoryAvgPrices[cat.rawName] || 3.00;
                        const price = prices[item.name] || (Math.round(rawFallback * 2) / 2);
                        const isEditing = editingPrice === item.name;
                        const isStaple = catalogMap[item.name]?.is_staple;
                        const isCustom = catalogMap[item.name]?.is_global === false;
                        // Catalog items only expose price editing — nothing to edit when pricing is off.
                        const canEdit = isCustom || showPrices;
                        return (
                          <SwipeToRemove key={item.name} onRemove={() => hideItem(item.name)} onEdit={() => openEditModal(item.name)} onStaple={() => toggleStaple(item.name)} isStaple={isStaple} canEdit={canEdit}>
                            <CatalogItemRow
                              item={item}
                              qty={qty}
                              rawCategory={cat.rawName}
                              showPrices={showPrices}
                              price={price}
                              isEditing={isEditing}
                              priceInput={priceInput}
                              centsToDisplay={centsToDisplay}
                              onUpdateQty={updateQty}
                              onPriceInput={handlePriceInput}
                              onCommitPrice={commitPrice}
                              onCancelEditPrice={() => setEditingPrice(null)}
                            />
                          </SwipeToRemove>
                        );
                      })}
                    </div>
                  </div>
                  ))
                )}
                <div style={{ textAlign: "center", padding: "32px 0 16px" }}>
                  <button
                    onClick={() => setShowManageCategoriesModal(true)}
                    style={{
                      background: "none", border: "none", fontFamily: "'Lato', sans-serif",
                      fontSize: "12.5px", color: "#8A5F3A", cursor: "pointer",
                      letterSpacing: "1px", textDecoration: "underline",
                    }}
                  >Manage Categories</button>
                </div>
              </>
            )}
            </>
          </>
        )}

        {view === "list" && !signedOutWelcome && (
          <>
            {/* Store prompt (mockup frame D) — once per session, only while no
                store is set, or re-opened from the eyebrow. Eyebrow renders only
                while a session is open AND has a store: the session is a
                sentence, not a widget. */}
            {storePromptVisible && (
              <StorePrompt chips={storeChips} onPick={handleStorePick} onSkip={handleStoreSkip} busy={storeSaving} />
            )}
            {/* Rendered for the null store too ("Set store") — Skip must leave a
                way back to the prompt. Setting writes store_name_raw on the
                EXISTING open session, never a new row. */}
            {!storePromptVisible && activeSession && (
              <div className="store-line" onClick={() => setStorePromptOpen(true)} role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setStorePromptOpen(true); }}>
                {/* The name and its chevron are ONE flex item: with flex-wrap on the line, a long
                    name drops below "Shopping at" at XXL and the chevron stays on the name's last
                    word instead of landing alone on a line of its own. */}
                Shopping at <span>{activeSession.store_name_raw
                  ? <b>{activeSession.store_name_raw}</b>
                  : <span className="store-set">Set store</span>} <span className="chev">▾</span></span>
              </div>
            )}
            {totalItems === 0 ? (
              <>
                {/* Empty form of the header row: the "+ Add" pill alone (Add sheet,
                    in-store paths) — no count line (there is no trip to count), no
                    lens, no Wrap up. Still the compact sentinel (ref), though the
                    page is too short to scroll. */}
                <div className="list-header empty" ref={controlRowRef}>
                  <HeaderAction verb="add" label="Add something" onClick={openAddSheet} />
                </div>
                {/* Empty state — Home's list-line pattern (status · link). Planned
                    meals not yet on the list (boardStats.stillToAdd, the board
                    subtitle's N) point at This Week; otherwise at Browse — the
                    same door Home's "Start a list →" opens. The count sits in the
                    text span, never in the link (RUM: the link is fixed copy). */}
                <div className="list-empty">
                  <h2>Your list is empty</h2>
                  <div className="list-empty-line">
                    {boardStats.stillToAdd.length > 0 ? (
                      <>
                        <span>{boardStats.stillToAdd.length} {boardStats.stillToAdd.length === 1 ? "meal" : "meals"} to</span>
                        <button type="button" className="home-line-link" onClick={() => goToDoor("plan")}>add to Shop →</button>
                      </>
                    ) : (
                      <button type="button" className="home-line-link" onClick={() => goToDoor("input")}>Browse items →</button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="list-header" ref={controlRowRef}>
                  <span className="list-progress">{checkedCount} of {totalItems} in cart</span>
                  {activeCycle && <span style={{display:'none'}}>{activeCycle.id}</span>}
                  <div className="list-controls">
                  <ShopLensSegment lens={shopLens} onChange={setShopLens} />
                  {/* D4′ (v2): the trip's controls are part of the list — this row
                      scrolls away with it; the pill is chrome. "+ Add" opens the
                      Add sheet (add-from-the-aisle). The header holds LIST TOOLS
                      only (2026-10-05): Wrap up left this row for the WrapUpBar
                      above the Helm — one Wrap Up control per state, the All
                      done card's teal button at 100% (what D9 guards against). */}
                  <HeaderAction verb="add" label="Add something" onClick={openAddSheet} />
                  </div>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${(checkedCount / totalItems) * 100}%` }} />
                </div>
                {checkedCount === totalItems && totalItems > 0 && (() => {
                  /* All done (mockup_shop_all_done.html) — the one earned moment on
                     Shop; typography does the celebrating. States 1 and 2 built:
                     minutes = now − session.started_at (rounded), omitted with no
                     open session (a delivery-style check-off reads "N items" alone);
                     the learning line only when the session has store_name_raw —
                     omitted otherwise, never reworded. State 3 (learned order in
                     use, teal) is the same slot later, once the Phase 2 query
                     applies for this (household, store) — reserved, not built. */
                  const startedAt = activeSession?.started_at ? new Date(activeSession.started_at).getTime() : null;
                  // Under a minute reads as words, never "0 minutes" (mockup_shop_wrapup_bar.html, state 4).
                  const minutes = startedAt ? Math.max(0, Math.round((Date.now() - startedAt) / 60000)) : null;
                  const learning = !!activeSession?.store_name_raw;
                  return (
                    <div className="all-done">
                      <svg className="all-done-arc" viewBox="0 0 150 12" aria-hidden="true"><path d="M4 10 Q75 -6 146 10" /></svg>
                      <h2>All done.</h2>
                      <p className="all-done-sub">Everything on your list is in the cart.</p>
                      <div className="all-done-meta">
                        <b>{totalItems}</b> {totalItems === 1 ? "item" : "items"}
                        {minutes !== null && (minutes < 1
                          ? <>&nbsp;&nbsp;·&nbsp;&nbsp;<b>under a minute</b></>
                          : <>&nbsp;&nbsp;·&nbsp;&nbsp;<b>{minutes}</b> {minutes === 1 ? "minute" : "minutes"}</>)}
                      </div>
                      {learning && <div className="all-done-learn">We're learning how you shop this store.</div>}
                      {/* At 100% there is nothing to roll forward, so this goes
                          straight to the wrap-up with no modal (openWrapUp) —
                          which also means this button carries the in-flight
                          state the modal's confirm used to show. */}
                      <button type="button" className="all-done-btn" onClick={openWrapUp} disabled={wrappingUp}>
                        {wrappingUp ? "Wrapping up…" : "Wrap up trip →"}
                      </button>
                    </div>
                  );
                })()}
                {shopLens === "aisles" ? (
                  /* ── AISLES — today's grouped render, minus the phase machinery. Checked rows are in the tray. ── */
                  shopAisles.map((cat) => (
                    <div key={cat.category}>
                      <div className="list-cat-title">{cat.category}</div>
                      {cat.items.map((item) => (
                        <SwipeToRemove key={item.name} onRemove={() => handleSwipeRemove(item)} removeLabel="Remove" style={{ borderRadius: 0, background: "transparent" }}>
                          <div className={`list-item shop-row-in${rowMotionClass(item.listItemId)}`}>
                            <div className="checkbox" onClick={() => handleShopToggle(item)} />
                            <div style={{ flex: 1, cursor: "pointer" }} onClick={() => handleShopToggle(item)}>
                              <div className="li-name">
                                {item.name}
                                {addedHereIds.has(item.catalogItemId) && <span className="added-here-tag">added here</span>}
                              </div>
                              {provenanceLines(item)}
                            </div>
                            {item.qty > 1 && (
                              <span className="li-qty">×{item.qty}</span>
                            )}
                            {showPrices && item.price > 0 && (
                              <div className="li-right">
                                <span className="li-qty">@ ${item.price.toFixed(2)}</span>
                                <span className="li-subtotal">${item.subtotal.toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        </SwipeToRemove>
                      ))}
                    </div>
                  ))
                ) : (
                  /* ── A–Z — a different mode: one flat alphabetical list, no provenance, no per-row prices. ── */
                  <div>
                    {shopFlatItems.length > 0 && <div className="az-eyebrow">{shopFlatItems.length} to find</div>}
                    {shopFlatItems.map((item) => (
                      <SwipeToRemove key={item.name} onRemove={() => handleSwipeRemove(item)} removeLabel="Remove" style={{ borderRadius: 0, background: "transparent" }}>
                        <div className={`list-item az shop-row-in${rowMotionClass(item.listItemId)}`}>
                          <div className="checkbox" onClick={() => handleShopToggle(item)} />
                          <div className="li-name" onClick={() => handleShopToggle(item)}>
                            {item.name}
                            {addedHereIds.has(item.catalogItemId) && <span className="added-here-tag">added here</span>}
                          </div>
                          {item.qty > 1 && (
                            <span className="li-qty">×{item.qty}</span>
                          )}
                        </div>
                      </SwipeToRemove>
                    ))}
                  </div>
                )}
                <InCartTray
                  items={trayItems}
                  count={trayCount}
                  motionClass={rowMotionClass}
                  open={trayOpen}
                  onToggle={() => setTrayOpen(o => !o)}
                  onUncheck={handleShopToggle}
                  initialFor={initialFor}
                />
                {showPrices && (
                <div className={`list-total ${overBudget ? "over" : ""}`}>
                  <div className="lt-left">
                    <div className="lt-label">Trip Total</div>
                    {checkedCount > 0 && (
                      <div className="lt-checked">${checkedCost.toFixed(2)} in cart · ${(totalCost - checkedCost).toFixed(2)} remaining</div>
                    )}
                    {budgetNum !== null && (
                      <div className={`lt-budget-row ${overBudget ? "over" : "ok"}`}>
                        {overBudget
                          ? `⚠ $${Math.abs(budgetRemaining).toFixed(2)} over your $${budgetNum.toFixed(2)} budget`
                          : `✓ $${budgetRemaining.toFixed(2)} under your $${budgetNum.toFixed(2)} budget`}
                      </div>
                    )}
                  </div>
                  <div className={`lt-amount ${overBudget ? "over" : ""}`}>{hasEstimatedPrices ? "~" : ""}${totalCost.toFixed(2)}</div>
                </div>
                )}
                {/* The Wrap Up bar — the trip's one exit until 100%, when it hands
                    off to the All done card (the bar renders nothing there). The
                    spacer keeps the last rows above it; both go with the list. */}
                {checkedCount < totalItems && <div className="wrapbar-spacer" aria-hidden="true" />}
                <WrapUpBar checked={checkedCount} total={totalItems} onWrapUp={openWrapUp} busy={wrappingUp} />
              </>
            )}
          </>
        )}
      </div>


      {/* No-shop sheet (v2, 055) — one optional field, then "Hold the night". */}
      {MEALS_ENABLED && noShopSheet && (
        <div className="modal-overlay" onClick={() => { if (!noShopBusy) setNoShopSheet(null); }}>
          <div className="modal" role="dialog" aria-modal="true" aria-label={NO_SHOP_LABELS[noShopSheet.kind] || "Something else"} onClick={(e) => e.stopPropagation()}>
            <SheetClose onClose={() => { if (!noShopBusy) setNoShopSheet(null); }} disabled={noShopBusy} />
            <h2>{NO_SHOP_LABELS[noShopSheet.kind] || "Something else"}</h2>
            <div className="noshop-sub">Holds the night on the board. Nothing goes on your list.</div>
            {noShopSheet.kind === "leftovers" ? (
              leftoverSources.length === 0 ? (
                <div className="noshop-none">Nothing cooked yet — we'll just hold the night.</div>
              ) : (
              <>
                <div className="noshop-label">From which meals? <span>(pick any, or none)</span></div>
                <div className="noshop-checks">
                  {leftoverSources.map((m) => {
                    const on = (noShopSheet.fromMealIds || []).includes(m.id);
                    return (
                      <label key={m.id} className={`noshop-check${on ? " on" : ""}`}>
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => setNoShopSheet((prev) => {
                            if (!prev) return prev;
                            const cur = prev.fromMealIds || [];
                            return { ...prev, fromMealIds: on ? cur.filter((id) => id !== m.id) : [...cur, m.id] };
                          })}
                        />
                        <span className="noshop-check-box" aria-hidden="true">{on ? "✓" : ""}</span>
                        <span className="noshop-check-name">{m.name}</span>
                        {/* 058: the tag reads the cook log, the same source as the list, so a
                            meal cooked, re-planned and then x-skipped (placement cooked_at cleared,
                            skipped_at set) is listed AND tagged. */}
                        {!!cooks[m.id] && !boardMeals.some((b) => b.id === m.id) && (
                          <span className="noshop-check-meta">cooked</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </>
              )
            ) : (
              <input
                className="noshop-input"
                value={noShopSheet.name}
                onChange={(e) => setNoShopSheet((prev) => prev && ({ ...prev, name: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") commitNoShop(); }}
                placeholder={noShopSheet.kind === "out" ? "Where to? (optional)" : "soccer, Mom's, takeout, no idea yet"}
                maxLength={60}
                autoFocus
              />
            )}
            <button type="button" className="noshop-commit" disabled={noShopBusy} onClick={commitNoShop}>
              {noShopBusy ? "Adding…" : "Hold the night"}
            </button>
            <button type="button" className="op-never-mind" onClick={() => setNoShopSheet(null)} disabled={noShopBusy}>Never mind</button>
          </div>
        </div>
      )}

      {/* On-hand prompt — only for meals that have on-hand ingredients (SPEC_all_on_hand D4:
          one question, two answers; the recipe is changed in Edit, never here). */}
      {onHandPrompt && (
        <OnHandPrompt
          prompt={onHandPrompt}
          onChoose={(catalogItemId, key) => setOnHandPrompt((prev) => prev && ({ ...prev, choices: { ...prev.choices, [catalogItemId]: key } }))}
          onCancel={() => setOnHandPrompt(null)}
          onConfirm={confirmOnHandPrompt}
        />
      )}

      {/* Create / Edit Meal sheet — one component, both modes */}
      {MEALS_ENABLED && mealSheet && (
        <MealSheet
          mode={mealSheet.mode}
          meal={mealSheet.meal}
          catalogMap={catalogMap}
          categories={categories}
          saving={mealSaving}
          deleting={mealDeleting}
          onCancel={() => { if (!mealSaving && !mealDeleting) setMealSheet(null); }}
          onCommit={commitMealSheet}
          onDelete={commitMealDelete}
          onCreateCatalogItem={createCatalogItem}
          onRegisterCategory={(cat) => setHouseholdCategories((prev) => new Set([...prev, cat]))}
          onRequestSuggestion={requestMealSuggestion}
          onRequestImport={requestRecipeImport}
          isSignedIn={isSignedIn}
          gift={mealSheet.mode === "create" ? mealSheet.gift || null : null}
        />
      )}

      {/* A received gift that could not open the form (Commit 3), and the S24 thank-you after one that did. */}
      {giftArrival && (
        <GiftArrivalSheet arrival={giftArrival}
          onOpen={() => { const m = giftArrival.mealId ? mealById[giftArrival.mealId] : null; setGiftArrival(null); if (m) openRecipe(m, "top"); }}
          onClose={() => setGiftArrival(null)} />
      )}
      {thankYou && !mealSheet && (
        <ThankYouSheet ask={thankYou.ask}
          onSend={() => { const t = thankYou; setThankYou(null); handleShareGift({ title: t.title, text: t.text }); }}
          onSkip={() => setThankYou(null)} />
      )}

      {/* The recipe view (SPEC_recipe_view): read first. Edit opens the sheet above in
          its place; the view returns on Cancel / Save with the live row. */}
      {/* !onHandPrompt (Amendment B, R5): the board's Add to Shop opens the "I have this" prompt, a
          .modal-overlay (z 100) that would sit UNDER this view's scrim (z 1000) — the view steps aside
          while it is open and returns in its new state, the same pattern as the Edit sheet. */}
      {MEALS_ENABLED && rvMeal && !mealSheet && !onHandPrompt && (
        <RecipeView
          meal={rvMeal}
          state={rvState}
          members={householdMembers}
          cookCount={recipeCooks?.mealId === rvMeal.id ? Math.max(recipeCooks.rows.length, rvCookCount) : rvCookCount}
          lastCookedAt={cooks[rvMeal.id]?.lastCookedAt || (recipeCooks?.mealId === rvMeal.id ? recipeCooks.rows[0]?.cooked_at : null) || null}
          cookRows={recipeCooks?.mealId === rvMeal.id ? recipeCooks.rows : null}
          links={recipeLinks?.mealId === rvMeal.id ? recipeLinks.byCatalog : null}
          // 2026-10-18 correction: no tags and no summary state until the cook reduce AND this
          // meal's links have loaded; "bought" tags and "All here" only once this plan was added.
          tagsReady={!!cooksLoaded && recipeLinks?.mealId === rvMeal.id}
          addedThisPlan={recipeLinks?.mealId === rvMeal.id ? !!recipeLinks.addedThisPlan : false}
          viewerUserId={_internalUserId.current || null}
          busy={planningMealId === rvMeal.id || busyMealId === rvMeal.id || addingMealId === rvMeal.id}
          onClose={() => setRecipeView(null)}
          onEdit={(m) => setMealSheet({ mode: "edit", meal: m })}
          onPlan={handlePlanMeal}
          // B3 — the board's own handlers, the same three PlanBoard takes (onLockIn / onCooked / onSkip).
          onAddToShop={handleAddMealToList}
          onCooked={handleCookedMeal}
          onRemove={handleSkipMeal}
          // A5: the board card's own See on list (goToDoor("list")), after the view closes.
          onSeeList={() => { setRecipeView(null); goToDoor("list"); }}
          // Photos v1 slice 1 (R2): the hero strip. Each photo carries its signed view URL
          // (absent until minted — the tint shows behind it, never a broken image).
          photos={(photosByMealId[rvMeal.id] || []).map((p) => ({ ...p, url: photoUrls[p.view_path] || null }))}
          photosReady={photosReady}
          canEditPhotos={!!isSignedIn}
          photoBusy={photoBusyMealId === rvMeal.id}
          onAddPhoto={handleAddMealPhoto}
          onSetPhotoKind={handleSetPhotoKind}
          give={giveProps}
          onRemovePhoto={handleRemoveMealPhoto}
          onReplacePhoto={handleReplaceMealPhoto}
          // Slice 2 (S5): every member's rating rows per cook (read at open), and the viewer's own rating.
          ratingsFor={(cookId) => ratingsByCook[cookId] || []}
          onRate={handleRateCook}
          canRate={!!isSignedIn}
        />
      )}

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-label="Add new item" onClick={(e) => e.stopPropagation()}>
            <SheetClose onClose={() => setShowAddModal(false)} />
            <h2>Add New Item</h2>
            <div className="modal-field">
              <label className="modal-label">Item Name</label>
              <input className="modal-input" placeholder="e.g. Kombucha" value={newItemName} autoFocus
                onChange={(e) => setNewItemName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAddItem()} />
            </div>
            {showPrices && (
            <div className="modal-field" style={{ opacity: newItemName.trim() ? 1 : 0.4, transition: "opacity 0.15s" }}>
              <label className="modal-label">Price ($)</label>
              <input className="modal-input" type="text" inputMode="numeric" placeholder="0.00" value={centsToDisplay(newItemPrice)}
                onChange={(e) => newItemName.trim() && setNewItemPrice(e.target.value.replace(/\D/g, "").replace(/^0+/, "") || "")} onKeyDown={(e) => e.key === "Enter" && handleAddItem()} />
            </div>
            )}
            <div className="modal-field">
              {/* No "tap below to unhide" hint here (removed 2026-10-06): the "Unhide N hidden
                  {category} items" link under the picker carries the action on its own. */}
              <label className="modal-label">Category</label>
              {/* This modal is only ever reached from a section header's
                  "＋ New item", so the category is already known — the user
                  answered by choosing where they tapped. The step is a
                  confirmation, not a question. */}
              <div className="picker-sub">
                Filed under {categoryLabel(newItemCategory)}. Tap another to move it.
              </div>
              <CategoryPickerGrid
                categories={categories}
                selected={newItemCategory}
                onSelect={(raw) => {
                  setNewItemCategory(raw);
                  setAddModalResetDone(false);
                }}
                onNewCategory={() => setShowManageCategoriesModal(true)}
              />
              {isSignedIn && !newItemName.trim() && (
                addModalResetDone ? (
                  <div style={{ fontSize: "12.5px", color: "#8A5F3A", fontStyle: "italic", marginTop: "6px" }}>
                    ✓ {CATEGORY_DISPLAY[newItemCategory] || newItemCategory} items unhidden
                  </div>
                ) : hiddenCatalogItems.some(h => h.category === newItemCategory) ? (
                  <button
                    onClick={async () => {
                      await restoreHiddenByCategory(newItemCategory);
                      setAddModalResetDone(true);
                      setTimeout(() => setAddModalResetDone(false), 2000);
                    }}
                    style={{ background: "none", border: "none", borderBottom: "1px solid #C9A97A", color: "#8A5F3A", fontFamily: "'Lato', sans-serif", fontSize: ts(14, 0.8), padding: "0", marginTop: "6px", cursor: "pointer", display: "inline-block", textAlign: "left" }}
                  >
                    Unhide {hiddenCatalogItems.filter(h => h.category === newItemCategory).length} hidden {CATEGORY_DISPLAY[newItemCategory] || newItemCategory} {hiddenCatalogItems.filter(h => h.category === newItemCategory).length === 1 ? "item" : "items"}
                  </button>
                ) : null
              )}
            </div>
            {addError && <div className="modal-error">{addError}</div>}
            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button
                className="modal-confirm"
                onClick={newItemName.trim() ? handleAddItem : undefined}
                style={{ opacity: newItemName.trim() ? 1 : 0.5, pointerEvents: newItemName.trim() ? "auto" : "none" }}
              >Add to List</button>
            </div>
          </div>
        </div>
      )}

      {/* Budget Goal Modal */}
      {showBudgetModal && (
        <div className="modal-overlay" onClick={() => setShowBudgetModal(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-label="Set budget goal" onClick={(e) => e.stopPropagation()}>
            <SheetClose onClose={() => setShowBudgetModal(false)} />
            <h2>Set Budget Goal</h2>
            <p className="modal-subtitle">Get alerts when your list is approaching or over your limit.</p>
            <div className="modal-field">
              <label className="modal-label">My Budget</label>
              <div className="modal-input-prefix">
                <span className="modal-prefix-symbol">$</span>
                <input
                  className="modal-input-inner"
                  type="number" placeholder="e.g. 150.00" min="0" step="1"
                  value={budgetInput} autoFocus
                  onChange={(e) => setBudgetInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveBudget(); if (e.key === "Escape") setShowBudgetModal(false); }}
                />
              </div>
            </div>
            <div className="modal-actions-spaced">
              {budgetNum !== null
                ? <button className="modal-remove" onClick={clearBudget}>Remove Goal</button>
                : <div />}
              <div style={{ display: "flex", gap: "10px" }}>
                <button className="modal-cancel" onClick={() => setShowBudgetModal(false)}>Cancel</button>
                <button className="modal-confirm" onClick={saveBudget}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {removeConfirmItem && (
        <div className="modal-overlay" onClick={() => setRemoveConfirmItem(null)}>
          <div className="modal" role="dialog" aria-modal="true" aria-label="Remove from list?" onClick={(e) => e.stopPropagation()}>
            <SheetClose onClose={() => setRemoveConfirmItem(null)} />
            <h2>Remove from list?</h2>
            <p className="modal-subtitle">
              "{removeConfirmItem.name}" was {removeConfirmItem.verb} by {removeConfirmItem.byName}. Removing it takes it off the shared list for everyone.
            </p>
            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => setRemoveConfirmItem(null)}>Cancel</button>
              <button
                className="modal-remove"
                onClick={() => {
                  removeFromList(removeConfirmItem.name, removeConfirmItem.catalogItemId);
                  setRemoveConfirmItem(null);
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {editModalItem && (
        <div className="modal-overlay" onClick={() => setEditModalItem(null)}>
          <div className="modal" role="dialog" aria-modal="true" aria-label="Edit item" onClick={e => e.stopPropagation()}>
            <SheetClose onClose={() => setEditModalItem(null)} />
            <h2>Edit Item</h2>

            {editModalItem.isCustom && (
              <div className="modal-field">
                <label className="modal-label">Item Name</label>
                <input
                  className="modal-input"
                  value={editModalName}
                  onChange={e => setEditModalName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") commitEditModal(); if (e.key === "Escape") setEditModalItem(null); }}
                  autoFocus
                />
              </div>
            )}

            {showPrices && (
              <div className="modal-field">
                <label className="modal-label">Price</label>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span style={{ fontFamily: "'Lato', sans-serif", fontSize: "0.82rem", color: "#8a7a60" }}>$</span>
                  <input
                    className="modal-input"
                    type="tel"
                    inputMode="numeric"
                    value={centsToDisplay(editModalPrice)}
                    onChange={e => setEditModalPrice(e.target.value.replace(/[^0-9]/g, ""))}
                    onKeyDown={e => { if (e.key === "Enter") commitEditModal(); if (e.key === "Escape") setEditModalItem(null); }}
                    autoFocus={!editModalItem.isCustom}
                  />
                </div>
              </div>
            )}

            {!editModalItem.isCustom && showPrices && (
              <p style={{
                fontFamily: "'Lato', sans-serif",
                fontSize: "0.75rem",
                color: "#8a7a60",
                margin: "12px 0 0 0",
                fontStyle: "italic"
              }}>
                This is a catalog item — only the price can be edited.
              </p>
            )}

            <div className="modal-actions-spaced">
              <div>
                {editModalItem.isCustom && (
                  <button
                    onClick={() => {
                      const ok = window.confirm(
                        `Delete "${editModalItem.name}" for the whole place? This removes it from the current list and can't be undone.`
                      );
                      if (ok) {
                        deleteItem(editModalItem.name);
                        setEditModalItem(null);
                      }
                    }}
                    style={{
                      fontFamily: "'Lato', sans-serif",
                      fontSize: "0.82rem",
                      color: "#b04a3f",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "8px 4px"
                    }}
                  >
                    Delete item
                  </button>
                )}
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button className="modal-cancel" onClick={() => setEditModalItem(null)}>Cancel</button>
                <button className="modal-confirm" onClick={commitEditModal}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Categories Modal */}
      {showManageCategoriesModal && (
        <div className="modal-overlay" onClick={() => setShowManageCategoriesModal(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-label="Manage categories" onClick={e => e.stopPropagation()} style={{ maxHeight: "80vh", overflowY: "auto" }}>
            <SheetClose onClose={() => setShowManageCategoriesModal(false)} />
            <h2>Manage Categories</h2>

            {/* Create */}
            <div className="modal-field">
              <label className="modal-label">Create New Category</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  className="modal-input"
                  placeholder="e.g. Frozen Foods"
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && createCategory(newCategoryName)}
                  style={{ flex: 1 }}
                />
                <button
                  className="modal-confirm"
                  onClick={() => createCategory(newCategoryName)}
                  style={{ opacity: newCategoryName.trim() ? 1 : 0.5, pointerEvents: newCategoryName.trim() ? "auto" : "none" }}
                >Create</button>
              </div>
            </div>

            {/* Rename */}
            {isSignedIn && (
              <div className="modal-field">
                <label className="modal-label">Rename Category</label>
                {/* CategorySelect: option values are indexes, never the household's category names (replay, 2026-10-19). */}
                <CategorySelect
                  categories={categories.filter(cat => !CATEGORY_ORDER.includes(cat.rawName))}
                  value={renamingCategory || ""}
                  onChange={rawName => { setRenamingCategory(rawName || null); setRenameValue(rawName); }}
                  placeholder="— select category —"
                  style={{ marginBottom: "8px" }}
                />
                <p style={{ fontFamily: "'Lato', sans-serif", fontSize: "12.5px", color: "#8A5F3A", marginTop: "6px", fontStyle: "italic" }}>
                  Standard categories (Produce, Dairy, etc.) can't be renamed — only categories you've created.
                </p>
                {renamingCategory && (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      className="modal-input"
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && renameCategory(renamingCategory, renameValue)}
                      style={{ flex: 1 }}
                    />
                    <button
                      className="modal-confirm"
                      onClick={() => renameCategory(renamingCategory, renameValue)}
                      style={{ opacity: renameValue.trim() ? 1 : 0.5, pointerEvents: renameValue.trim() ? "auto" : "none" }}
                    >Rename</button>
                  </div>
                )}
              </div>
            )}

            {/* Delete */}
            {isSignedIn && (
              <div className="modal-field">
                <label className="modal-label">Delete Category</label>
                <CategorySelect
                  categories={categories.filter(cat => !CATEGORY_ORDER.includes(cat.rawName))}
                  value={deletingCategory?.rawName || ""}
                  onChange={rawName => {
                    if (!rawName) { setDeletingCategory(null); return; }
                    const itemCount = Object.values(catalogMap).filter(i => i.category === rawName).length;
                    setDeletingCategory({ rawName, itemCount });
                    setDeleteMoveTarget("");
                  }}
                  placeholder="— select category —"
                  style={{ marginBottom: "8px" }}
                />
                <p style={{ fontFamily: "'Lato', sans-serif", fontSize: "12.5px", color: "#8A5F3A", marginTop: "6px", fontStyle: "italic" }}>
                  Standard categories (Produce, Dairy, etc.) can't be renamed — only categories you've created.
                </p>
                {deletingCategory && (
                  <>
                    {deletingCategory.itemCount > 0 && (
                      <>
                        <div style={{ fontFamily: "'Lato', sans-serif", fontSize: "12px", color: "#8a7a60", marginBottom: "8px" }}>
                          {deletingCategory.itemCount} item{deletingCategory.itemCount !== 1 ? "s" : ""} in this category. Move them to:
                        </div>
                        <CategorySelect
                          categories={categories.filter(c => c.rawName !== deletingCategory.rawName)}
                          value={deleteMoveTarget}
                          onChange={rawName => setDeleteMoveTarget(rawName)}
                          placeholder="— delete all items too —"
                          style={{ marginBottom: "8px" }}
                        />
                      </>
                    )}
                    <button
                      className="modal-remove"
                      onClick={() => deleteCategory(deletingCategory.rawName, deleteMoveTarget || null)}
                    >
                      {deleteMoveTarget ? `Move & Delete Category` : `Delete${deletingCategory.itemCount > 0 ? " & Remove Items" : " Category"}`}
                    </button>
                  </>
                )}
              </div>
            )}

            {isSignedIn && (
              <div style={{ borderTop: "1px solid #e8ddd0", marginTop: "20px", paddingTop: "16px" }}>
                <div style={{ fontFamily: "'Lato', sans-serif", fontSize: "12.5px", letterSpacing: "2px", textTransform: "uppercase", color: "#8A5F3A", marginBottom: "8px" }}>Danger Zone</div>
                {!showResetConfirm ? (
                  <button
                    onClick={() => setShowResetConfirm(true)}
                    style={{ background: "none", border: "none", fontFamily: "'Lato', sans-serif", fontSize: "12px", color: "#c0392b", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                  >Restore default categories</button>
                ) : (
                  <div>
                    <p style={{ fontFamily: "'Lato', sans-serif", fontSize: "12px", color: "#2C1A0E", marginBottom: "10px" }}>
                      This will remove all custom categories and items you've created, and restore the original catalog. Items from the standard catalog on your current list will not be affected.
                    </p>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button onClick={() => setShowResetConfirm(false)} className="modal-cancel" style={{ fontSize: "12px" }}>Cancel</button>
                      <button onClick={handleResetCategories} style={{ background: "#c0392b", color: "white", border: "none", borderRadius: "4px", padding: "6px 12px", fontFamily: "'Lato', sans-serif", fontSize: "12px", cursor: "pointer" }}>Yes, restore defaults</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions">
              <button className="modal-confirm" onClick={() => { setShowManageCategoriesModal(false); setShowResetConfirm(false); }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Shop Add sheet (mockup frame C) — transactional, so the scrim closes it.
          Same search path Browse has, through the shared components: catalog hit
          adds at one tap, hidden-live item surfaces as the reveal card, no match
          creates under `Other`. The mic is designed in and NOT wired (LATER). ── */}
      {addSheetOpen && (
        <div className="add-sheet-scrim" onClick={closeAddSheet}>
          <div
            className="add-sheet"
            onClick={e => e.stopPropagation()}
            style={{
              transform: `translateY(-${addSheetKeyboard.inset}px)`,
              // Never taller than what is on screen, so it can't run under the status
              // bar with the keyboard up; 80vh stays the cap when nothing is covered.
              ...(addSheetKeyboard.viewportHeight
                ? { maxHeight: `min(80vh, ${Math.max(0, addSheetKeyboard.viewportHeight - 16)}px)` }
                : {}),
            }}
          >
            <div className="add-sheet-grab" />
            <div className="add-sheet-title">Add something</div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <CatalogSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                onClear={() => setSearchQuery("")}
                placeholder="Search your catalog…"
                inputRef={addSheetInputRef}
              />
              <span style={{ position: "relative", flex: "none" }}>
                <button type="button" className="op-mic-btn" disabled aria-label="Voice add — coming later" title="Voice add — coming later">
                  <svg viewBox="0 0 24 24" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="3" width="6" height="11" rx="3" />
                    <path d="M5 11a7 7 0 0 0 14 0" />
                    <line x1="12" y1="18" x2="12" y2="21" />
                  </svg>
                </button>
                <span className="op-later-badge">LATER</span>
              </span>
            </div>
            <div className="add-sheet-results">
              {searchResults !== null && (
                <SearchResultsList
                  query={searchQuery}
                  results={searchResults}
                  hiddenMatch={hiddenMatch}
                  onReveal={(m) => {
                    if (m.live) { unhideItem(m.item.id); closeAddSheet(); }
                    else handleShopAddNew(m.item.category);   // un-hide + add at 1, recorded as added_in_store
                  }}
                  showCount={false}
                  renderRow={(item) => (
                    <div key={item.name} className="add-result" onClick={() => handleShopAddResult(item)} role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter") handleShopAddResult(item); }}>
                      <div className="ar-main">
                        <div className="ar-name">{item.name}</div>
                        <div className="ar-cat">{item.category}</div>
                      </div>
                      <span className="ar-go">Add</span>
                    </div>
                  )}
                  createSlot={
                    <div className="add-result create" onClick={() => handleShopAddNew()} role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter") handleShopAddNew(); }}>
                      <div className="ar-main">
                        <div className="ar-name">Add <b>“{searchQuery.trim()}”</b> as a new item…</div>
                      </div>
                    </div>
                  }
                />
              )}
            </div>
            <div className="add-sheet-hint">
              Added items land in their aisle, unchecked
              {activeSession?.store_name_raw ? `, and are remembered as picked up at ${activeSession.store_name_raw}` : ""}.
            </div>
          </div>
        </div>
      )}

      {/* Profile Sheet — three ways out, all running the same close: backdrop
          tap (the scrim's onClick), the Close button (SheetClose: VoiceOver's
          way, focus returns to the avatar on unmount) and swipe-down
          (SheetPanel → useSheetDrag, 2026-10-05). */}
      {showProfileSheet && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(2,15,26,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}
          onClick={() => setShowProfileSheet(false)}
        >
          <SheetPanel
            onClose={() => setShowProfileSheet(false)}
            label="Account and preferences"
            style={{ position: "relative", background: "#FDF8F2", borderRadius: "20px 20px 0 0", width: "100%", paddingBottom: "32px" }}
          >
            {/* Close — the one way out VoiceOver can reach (the backdrop is a plain div). */}
            <SheetClose onClose={() => setShowProfileSheet(false)} />
            {/* Handle */}
            <div style={{ width: "36px", height: "4px", background: "#c8b89a", borderRadius: "2px", margin: "10px auto 0" }} />

            {/* Text in this sheet follows the text-size knob by role (the 10-04 role table):
                row text 14 and captions / eyebrows 12.5 at k 1, nothing under 12.5 at Default;
                the avatar monogram is chrome, fixed. No small text here is #A0724A any more. */}
            {/* User row */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 20px 12px", borderBottom: "0.5px solid #e8ddd0" }}>
              <div style={{ width: "44px", height: "44px", borderRadius: "50%", background: "#0D9488", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", fontWeight: 600, color: "white", fontFamily: "'Lato', sans-serif", flexShrink: 0 }}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              {/* Name + edit input (fontSize: inherit) — body, 16 px at k 1. The sheet has no
                  .modal h2; this row is its heading and stays 16 (not promoted to a sheet title).
                  paddingRight keeps the row clear of the 44 px Close at XXL (the .modal h2 rule's
                  40 px, same job); minWidth 0 lets a long name / email wrap instead of overflow. */}
              <div style={{ fontSize: ts(16), minWidth: 0, paddingRight: "36px" }}>
                {editingName ? (
              <input
                autoFocus
                defaultValue={profileName}
                onFocus={e => e.target.select()}
                onBlur={async (e) => {
                  const val = e.target.value.trim();
                  if (val && val !== profileName) {
                    const ok = await updateFullName(val, async (trimmed) => {
                      const parts = trimmed.split(" ");
                      const firstName = parts[0];
                      const lastName = parts.slice(1).join(" ") || undefined;
                      await user.update({ firstName, lastName });
                    });
                    if (ok) setProfileName(val);
                  }
                  setEditingName(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.target.blur();
                  if (e.key === "Escape") { setEditingName(false); }
                }}
                style={{
                  fontWeight: 600,
                  fontSize: "inherit",
                  fontFamily: "inherit",
                  background: "transparent",
                  border: "none",
                  borderBottom: "1px solid #C9A97A",
                  outline: "none",
                  width: "100%",
                  padding: "0 0 2px 0",
                  color: "inherit",
                }}
              />
            ) : (
              <div
                style={{ fontWeight: 600, cursor: "pointer" }}
                onClick={() => setEditingName(true)}
              >
                {profileName
                  || [user?.firstName, user?.lastName].filter(Boolean).join(" ")
                  || user?.primaryEmailAddress?.emailAddress}
              </div>
            )}
                <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), color: "#8A5F3A", marginTop: "2px", overflowWrap: "anywhere" }}>{user?.primaryEmailAddress?.emailAddress}</div>
              </div>
            </div>

            {/* Preferences */}
            <div style={{ padding: "14px 20px 8px" }}>
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), letterSpacing: "2px", textTransform: "uppercase", color: "#8A5F3A", marginBottom: "12px" }}>Preferences</div>

              {/* Show prices toggle */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "12px", borderBottom: "0.5px solid #e8ddd0" }}>
                <div>
                  <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#2C1A0E" }}>Show prices &amp; budget</div>
                  <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), color: "#8A5F3A", marginTop: "2px" }}>Display item prices and estimated total</div>
                </div>
                <div
                  onClick={() => setShowPrices(p => !p)}
                  style={{ width: "44px", height: "26px", borderRadius: "13px", background: showPrices ? "#c8973a" : "#d4c9b8", position: "relative", cursor: "pointer", flexShrink: 0, transition: "background 0.2s" }}
                >
                  <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: "white", position: "absolute", top: "3px", left: showPrices ? "21px" : "3px", transition: "left 0.15s" }} />
                </div>
              </div>

              {/* Text size stepper (SPEC_global_text_size decision 6: one knob, every surface).
                  The two A glyphs are the control's iconography — chrome, fixed (k 0) like the
                  glyph-only .qty-btn; the step readout is status text and follows the knob. */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "12px" }}>
                <div>
                  <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#2C1A0E" }}>Text size</div>
                  <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), color: "#8A5F3A", marginTop: "2px" }}>Bigger text across the app, on this device</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", border: "1px solid #E8D5B7", borderRadius: "7px", background: "#fff", padding: "5px 10px", flexShrink: 0 }}>
                  <button
                    onClick={() => setTextSizeIdx(i => Math.max(0, i - 1))}
                    disabled={textSizeIdx === 0}
                    style={{ background: "none", border: "none", cursor: textSizeIdx === 0 ? "default" : "pointer", fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "12.5px", color: textSizeIdx === 0 ? "#d8c8aa" : "#8A5F3A", lineHeight: 1, padding: 0 }}
                  >A</button>
                  <span style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), textTransform: "uppercase", letterSpacing: "1.5px", color: "#8A5F3A", minWidth: "56px", textAlign: "center" }}>{TEXT_LABELS[textSizeIdx]}</span>
                  <button
                    onClick={() => setTextSizeIdx(i => Math.min(TEXT_STEPS.length - 1, i + 1))}
                    disabled={textSizeIdx === TEXT_STEPS.length - 1}
                    style={{ background: "none", border: "none", cursor: textSizeIdx === TEXT_STEPS.length - 1 ? "default" : "pointer", fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "16.8px", color: textSizeIdx === TEXT_STEPS.length - 1 ? "#d8c8aa" : "#8A5F3A", lineHeight: 1, padding: 0 }}
                  >A</button>
                </div>
              </div>
            </div>

            {/* Sign out */}
            <div style={{ height: "0.5px", background: "#e8ddd0", margin: "0 20px" }} />
            <button
              onClick={() => { markDeliberateSignOut(); signOut(() => setShowProfileSheet(false)); }}
              style={{ display: "flex", alignItems: "center", gap: "10px", padding: "14px 20px", background: "none", border: "none", cursor: "pointer", width: "100%" }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              <span style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#c0392b" }}>Sign out</span>
            </button>
            {/* Attribution lives here now, not in a footer on every door — the doors
                carry no brand chrome; the mark stays on the landing page and sign-in. */}
            <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), letterSpacing: "0.5px", color: "#8a7a60", padding: "6px 20px 16px" }}>
              A Velayo app
            </div>
          </SheetPanel>
        </div>
      )}

      {showWrapUpModal && (
        <div className="modal-overlay" onClick={() => !wrappingUp && closeWrapUp()}>
          <div className="modal" role="dialog" aria-modal="true" aria-label="Wrap up this trip" onClick={e => e.stopPropagation()} style={{ maxWidth: "360px" }}>
            <SheetClose onClose={closeWrapUp} disabled={wrappingUp} />

            {/* Header */}
            <div style={{ marginBottom: "16px" }}>
              {/* Text size pass 2, slice 3: the title keeps 17.6 at the sheet-title rate (k 0.5,
                  the Profile-sheet call — it is not a .modal h2); paddingRight keeps it clear of
                  the 44 px Close at XXL. Sub-line and eyebrow are meta 14 / eyebrow 12.5 at k 1
                  in #8A5F3A (#8B6B4A read 4.47:1 on cream). */}
              <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: ts(17.6, 0.5), color: "#3D2B1F", marginBottom: "4px", paddingRight: "40px" }}>
                Wrap up this trip
              </div>
              <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#8A5F3A", letterSpacing: "0.5px" }}>
                {boughtItems.length} item{boughtItems.length !== 1 ? "s" : ""} bought
                {pendingItems.length > 0 && ` · ${pendingItems.length} still pending`}
              </div>
            </div>

            {/* Pending items — roll-forward selection */}
            {pendingItems.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), letterSpacing: "0.14em", textTransform: "uppercase", color: "#8A5F3A", marginBottom: "8px" }}>
                  Roll onto next list?
                </div>
                {/* The cap scales with the knob so about five rows show at every size. */}
                <div style={{ display: "flex", flexDirection: "column", gap: "4px", maxHeight: "calc(200px * var(--op-text-scale))", overflowY: "auto" }}>
                  {pendingItems.map(item => {
                    const selected = wrapUpRollItems.has(item.name);
                    return (
                      <div
                        key={item.name}
                        onClick={() => {
                          setWrapUpRollItems(prev => {
                            const next = new Set(prev);
                            if (next.has(item.name)) next.delete(item.name);
                            else next.add(item.name);
                            return next;
                          });
                        }}
                        // flex-wrap + the name at a min-content basis: the name wins the width and
                        // the ×qty drops below it, right-aligned, when a long word at XXL on a
                        // 320 phone would otherwise push the qty off the row (the search-row pattern).
                        style={{
                          display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px",
                          padding: "6px 8px", borderRadius: "4px", cursor: "pointer",
                          background: selected ? "rgba(13,148,136,0.08)" : "transparent",
                          border: `1px solid ${selected ? "#0D9488" : "rgba(160,114,74,0.2)"}`,
                          transition: "all 0.15s",
                        }}
                      >
                        <div style={{
                          width: "16px", height: "16px", borderRadius: "3px", flexShrink: 0,
                          border: `1.5px solid ${selected ? "#0D9488" : "#C9A97A"}`,
                          background: selected ? "#0D9488" : "transparent",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          {selected && <span style={{ color: "white", fontSize: "10px", lineHeight: 1 }}>✓</span>}
                        </div>
                        <span style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(15), color: "#3D2B1F", flex: "1 1 min-content" }}>
                          {item.name}
                        </span>
                        {item.qty > 1 && (
                          <span style={{ fontFamily: "'Lato', sans-serif", fontSize: ts(14), color: "#8A5F3A", marginLeft: "auto", flexShrink: 0 }}>
                            ×{item.qty}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Select all / none shortcuts */}
                <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
                  <button
                    onClick={() => setWrapUpRollItems(new Set(pendingItems.map(i => i.name)))}
                    style={{ background: "none", border: "none", fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), color: "#8A5F3A", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                  >
                    Select all
                  </button>
                  <button
                    onClick={() => setWrapUpRollItems(new Set())}
                    style={{ background: "none", border: "none", fontFamily: "'Lato', sans-serif", fontSize: ts(12.5), color: "#8A5F3A", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}

            {/* Action buttons — deliberately NOT .modal-actions (ruling 2026-10-06): the sheet is
                short enough not to need a sticky footer. A wrapping flex row on the Hold-a-night
                pattern, with an auto basis rather than min-content: the confirm's label is three
                words ("Roll 12 forward"), and a min-content basis let it wrap inside the button at
                every width (harnessed 2026-10-06). With basis auto and no-wrap labels, Cancel and
                the confirm share a row when both fit and the confirm drops to a full-width line of
                its own at XXL on a narrow phone instead of overrunning the sheet. */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "flex-end" }}>
              <button
                className="modal-cancel"
                onClick={closeWrapUp}
                disabled={wrappingUp}
                style={{ flex: "1 1 auto", whiteSpace: "nowrap" }}
              >
                Cancel
              </button>
              <button
                className="modal-confirm"
                onClick={handleWrapUp}
                disabled={wrappingUp}
                style={{ flex: "1 1 auto", whiteSpace: "nowrap" }}
              >
                {wrappingUp
                  ? "Saving..."
                  : wrapUpRollItems.size > 0
                    ? `Roll ${wrapUpRollItems.size} forward`
                    : "Close & clear"
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trip summary — rendered from the openWrapUp snapshot, never from live
          state (the trip it describes no longer exists). Facts only: what went
          in the cart, how long it took, where. Deliberately NO learning line
          and NO comparison ("faster than last time", "under budget") — those
          are claims about a trip's standing, and standing needs qualification.
          When get_wrap_up_summary lands (SPEC_wrapup_share.md) it replaces this
          snapshot as the source and brings the claims with it, qualified.
          Pending qualification v2. */}
      {tripSummary && (() => {
        const carried = tripSummary.carriedCount;
        // A trip can round to 0 minutes (Math.round over a sub-30s shop) and
        // "0 minutes" reads like a broken clock. Still omitted entirely with no
        // open session — null is "unknown", 0 is "very fast", and they are not
        // the same claim. No trailing period: this is a &middot;-separated
        // values line, not a sentence.
        const minutesPhrase = tripSummary.minutes === null
          ? null
          : tripSummary.minutes < 1
            ? <b>under a minute</b>
            : <><b>{tripSummary.minutes}</b> {tripSummary.minutes === 1 ? "minute" : "minutes"}</>;
        const dot = <>&nbsp;&nbsp;&middot;&nbsp;&nbsp;</>;
        return (
          <div className="modal-overlay" onClick={dismissTripSummary}>
            <div className="modal" role="dialog" aria-modal="true" aria-label="Trip wrapped" onClick={e => e.stopPropagation()} style={{ maxWidth: "340px" }}>
              <SheetClose onClose={dismissTripSummary} />
              <div className="all-done" style={{ padding: "4px 0 0" }}>
                <svg className="all-done-arc" viewBox="0 0 150 12" aria-hidden="true"><path d="M4 10 Q75 -6 146 10" /></svg>
                <h2>Trip wrapped.</h2>
                {/* The store line is the established pattern and survives the
                    carried case — it is the one place the trip says WHERE it
                    happened. The carried sentence is appended to it, not
                    swapped for it; with no store recognised, it stands alone. */}
                <p className="all-done-sub">
                  {tripSummary.storeName
                    ? <>Your trip to {tripSummary.storeName} is closed out.{carried > 0 && " The rest is on your next list."}</>
                    : carried > 0
                      ? "The rest is on your next list."
                      : "Your trip is closed out."}
                </p>
                <div className="all-done-meta">
                  {carried > 0 ? (
                    <>
                      <b>{tripSummary.itemCount}</b> bought
                      {dot}<b>{carried}</b> carried forward
                      {minutesPhrase && <>{dot}{minutesPhrase}</>}
                    </>
                  ) : (
                    <>
                      <b>{tripSummary.itemCount}</b> {tripSummary.itemCount === 1 ? "item" : "items"}
                      {minutesPhrase && <>{dot}{minutesPhrase}</>}
                    </>
                  )}
                </div>
                {/* Trip reality (SPEC_trip_qualification_v2; mockup_trip_reality_ask.html
                    is the tiebreaker). The server decides; the client shows one of
                    three states: real → nothing; pending, on the FIRST ask only → the
                    question; excluded by signals, or by a `testing` answer → the quiet
                    Count-it line. An admin-flagged account shows nothing at all.
                    Done without answering writes nothing — unanswered is real (D6).
                    After Real trip or Count it the control collapses to "Counted.";
                    after Just testing it becomes the EXCLUDED state's own line — the
                    same component and class as state 3 — so Count it is always one
                    tap away (D7: never silent, never irreversible). Fixed copy only on
                    the three controls; they are allow-listed by class (rum.js). No
                    teal: answering a question is not a celebrated completion. */}
                {realityAnswer === "real" ? (
                  <p className="trip-reality-line">Counted.</p>
                ) : realityAnswer === "testing"
                    || (!realityAnswer && tripReality?.verdict === "excluded" && tripReality?.verdict_source !== "admin_flag") ? (
                  <p className="trip-reality-line">Not counted as a shopping trip ·<button type="button" className="trip-reality-countit" onClick={() => handleRealityAnswer("real")}>Count it</button></p>
                ) : tripReality?.verdict === "pending" && tripReality?.first_ask ? (
                  <div className="trip-reality-ask">
                    <p className="tr-eyebrow">Quick question</p>
                    <p className="tr-q">Quick trip, or just testing?</p>
                    <p className="tr-why">We couldn't tell. Real trips teach the app your store.</p>
                    <div className="tr-btns">
                      <button type="button" className="trip-reality-real" onClick={() => handleRealityAnswer("real")}>Real trip</button>
                      <button type="button" className="trip-reality-testing" onClick={() => handleRealityAnswer("testing")}>Just testing</button>
                    </div>
                  </div>
                ) : null}
                <button type="button" className="op-chrome trip-summary-done" onClick={dismissTripSummary}>Done</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default function ShoppingListApp() {
  const { user, isSignedIn, isLoaded } = useUser();
  const { getToken, sessionId } = useAuth();
  // Same liveness the app body derives (authHealth is a shared store), so the
  // household context and useProvisions agree on when there is a session.
  const sessionLive = useSessionLive({ isLoaded, isSignedIn });
  const [systemMessage, setSystemMessage] = useState(null);
  const systemMsgTimerRef = useRef(null);

  const postSystemMessage = useCallback((msg) => {
    setSystemMessage(msg);
    if (systemMsgTimerRef.current) clearTimeout(systemMsgTimerRef.current);
    systemMsgTimerRef.current = setTimeout(() => setSystemMessage(null), msg.durationMs);
  }, []);

  const dismissSystemMessage = useCallback(() => {
    setSystemMessage(null);
    if (systemMsgTimerRef.current) clearTimeout(systemMsgTimerRef.current);
  }, []);

  const onRemoval = useCallback((householdName, provisioned) => {
    postSystemMessage({
      kind: 'info',
      householdName,
      subtext: provisioned ? "We've set you up with a fresh place." : undefined,
      durationMs: 30000,
      dismissible: true,
    });
  }, [postSystemMessage]);

  return (
    <ConnectivityProvider>
      {/* Desktop column (2026-09-20 — mockup A + background treatment 3). On phones
          all three wrappers are inert: no styles apply below COLUMN_MIN_WIDTH. On
          desktop: .desk-bg is the household photo full-bleed (scrim + blur on THIS
          layer, never on the container — filter on a parent blurs its children;
          overscanned so blurred edges never show a pale halo); .phone-frame is the
          fixed, centred, phone-width column with the radius and shadow, and its
          transform makes it the containing block for every position:fixed
          descendant (helm, sheets, toasts, overlays, the system message below) so
          they anchor to the column, not the window; .phone-scroll is the column's
          own scroll root. */}
      <div className="desk-bg op-replay-block" aria-hidden="true" />
      <div className="phone-frame">
        <div className="phone-scroll">
          <ActiveHouseholdProvider getToken={getToken} clerkId={sessionLive ? user?.id : undefined} sessionId={sessionId} onRemoval={onRemoval}>
            <HouseholdDebugLog />
            <ProvisionsApp />
          </ActiveHouseholdProvider>
          {systemMessage && (
        <>
          <style>{`@keyframes shrinkBar { from { width: 100% } to { width: 0% } }`}</style>
          <div style={{
            position: 'fixed', left: '14px', right: '14px', bottom: '18px',
            borderRadius: '10px', padding: '13px 15px',
            display: 'flex', gap: '12px', alignItems: 'flex-start',
            background: 'rgba(44, 26, 14, 0.5)',
            border: '1px solid rgba(13, 148, 136, 0.4)',
            borderLeft: '4px solid #0D9488',
            color: '#FAF4EC',
            boxShadow: '0 8px 26px rgba(44,26,14,0.18)',
            backdropFilter: 'blur(7px)',
            overflow: 'hidden',
            zIndex: 1500,
            fontFamily: "'Lato', sans-serif",
          }}>
            <div style={{
              position: 'absolute', left: 0, bottom: 0, height: '2px',
              background: '#0D9488', opacity: 0.9, borderBottomLeftRadius: '10px',
              animation: `shrinkBar ${systemMessage.durationMs}ms linear forwards`,
            }} />
            <span style={{ fontSize: '18px', color: '#5fd8c9', flexShrink: 0, lineHeight: '1.3' }}>ⓘ</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13.5px', fontWeight: 400, lineHeight: '1.4', color: '#FAF4EC' }}>
                No longer a member of{' '}
                <strong style={{ color: '#5fd8c9', fontWeight: 700 }}>
                  {systemMessage.householdName ?? 'that place'}
                </strong>.
              </div>
              {systemMessage.subtext && (
                <div style={{ fontSize: '12px', marginTop: '4px', lineHeight: '1.4', color: 'rgba(250,244,236,0.8)' }}>
                  {systemMessage.subtext}
                </div>
              )}
            </div>
            {systemMessage.dismissible && (
              <button
                onClick={dismissSystemMessage}
                style={{
                  background: 'none', border: 'none', color: '#FAF4EC',
                  opacity: 0.6, fontSize: '16px', flexShrink: 0,
                  cursor: 'pointer', padding: 0, lineHeight: 1,
                }}
              >
                ✕
              </button>
            )}
          </div>
        </>
      )}
        </div>
      </div>
      <style>{DESKTOP_COLUMN_CSS}</style>
    </ConnectivityProvider>
  );
}

// Desktop column CSS (2026-09-20). Every rule is inside the min-width query, so
// phones are untouched by construction — the wrappers exist there as plain
// blocks with no styling at all.
const DESKTOP_COLUMN_CSS = `
  .desk-bg { display: none; }
  @media (min-width: ${COLUMN_MIN_WIDTH}px) {
    /* The column scrolls, not the document — and the document's reserved scrollbar gutter
       (scrollbar-gutter: stable on html, app-wide) is released here: with nothing to scroll it
       only painted a pale 15px strip down the right edge that the fixed background never covers. */
    html, body { height: 100%; overflow: hidden; scrollbar-gutter: auto; }
    /* The cream field — what every household without a photo sees, i.e. every new household. */
    body { background: #FAF4EC; }
    /* The household photo as the room: scrim 58% + radial vignette + blur 7px on THIS layer (a
       filter on a parent would blur its children). inset -3% ≈ scale 1.06 so the blurred,
       transparent edges sit outside the viewport instead of leaving a pale halo. */
    .desk-bg { display: block; position: fixed; inset: -3%; z-index: 0; pointer-events: none;
               background: var(--op-desk-bg, #FAF4EC); filter: blur(7px) saturate(.92); }
    /* The honest frame (mockup A): phone-width, centred, soft radius, shadow, a hairline of light. */
    .phone-frame { position: fixed; top: 24px; bottom: 24px; left: 50%; z-index: 1;
                   width: ${COLUMN_MAX_WIDTH}px; max-width: calc(100vw - 48px);
                   transform: translateX(-50%);  /* centring — and the containing block for every fixed descendant */
                   border-radius: 22px; overflow: hidden; overflow: clip; background: #FAF4EC;
                   box-shadow: 0 26px 60px -14px rgba(20,10,4,.55), 0 0 0 1px rgba(255,255,255,.28); }
    .phone-scroll { height: 100%; overflow-y: auto; overflow-x: hidden; overscroll-behavior: contain; -webkit-overflow-scrolling: touch;
                    /* No visible scrollbar — the frame's whole argument is that it reads as a phone, and a desktop
                       bar down the inside edge is the one detail contradicting it. Wheel and trackpad are unaffected;
                       the content being visibly cut off carries the affordance. Scoped to THIS scroller only. */
                    scrollbar-width: none; -ms-overflow-style: none; }
    .phone-scroll::-webkit-scrollbar { display: none; width: 0; height: 0; }
    .app-root { min-height: 100%; }
  }
`;
